import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Settings, Database, Calendar, Activity, Plus, Pencil, Trash2, 
  RefreshCw, Eye, EyeOff, Check, X, AlertTriangle, BarChart3,
  LineChart, FlaskConical, Sparkles, Clock, Gauge, BookOpen, Shield,
  TrendingUp, DollarSign, Target, Waves, Calculator, Flame, FileText, RotateCcw,
  Wifi, CheckCircle, Brain, Lightbulb, TrendingDown, Sliders,
  ChevronRight, ChevronDown, Layers, Zap, Users, Timer, Play, Square, Download, Upload,  // SESSION 114: Time Machine icons
  Link2,  // SESSION 136: Broker Integration
  KeyRound,  // SESSION 146ar: Dhan multi-token manager
  GitMerge,  // SESSION 147e: Combined Model (Confluence)
  Landmark, Gem, Newspaper,  // 147j+16br: AlphaScout family + Order Wins rename
  Trophy,  // 147j+16da: Order Book Leaderboard
  Package,  // 147j+17p: Model Portfolio (LLM BUY tracker)
  Radar,   // 147j+17q: Pre-LODR intelligence
  Activity as SwingIcon,  // 147j+16dd: Swing Trading tab
  LogOut,  // 147j+16bs: missing logout button in admin sidebar
  BookOpen as BookOpenIcon  // 147j+16bx: in-app user manual (alias to avoid collision with existing BookOpen for API Reference)
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useAuth } from '../contexts/AuthContext';  // 147j+16bs: for logout handler

// Import refactored tab components
import APIReferenceTab from './admin/APIReferenceTab';
import AlgoHealthTab from './admin/AlgoHealthTab';
import SafeguardsTab from './admin/SafeguardsTab';
import PaperTradeTable from './PaperTradeTable';
import UserManagementTab from './admin/UserManagementTab';  // SESSION 109: SaaS User Management
import BrokerIntegrationTab from './admin/BrokerIntegrationTab';  // SESSION 136: Broker Integration WIP
import DhanHealthBanner from './admin/DhanHealthBanner';  // SESSION 141f: Dhan broker health banner (default import — matches every other admin import in this file; named-import caused HMR-stale ReferenceError)
import ConfluenceDashboard from './admin/ConfluenceDashboard';  // SESSION 137: Signal Confluence
import GUIHierarchyShadowTab from './admin/GUIHierarchyShadowTab';  // SESSION 145: GUI Hierarchy shadow-mode analytics
import DhanSwingTab from './admin/DhanSwingTab';  // SESSION 146aq: DhanSwing multi-day VCP engine
import DhanTokensTab from './admin/DhanTokensTab';  // SESSION 146ar: Dhan multi-token manager
import SmartFlowTab from './admin/SmartFlowTab';  // SESSION 146at: SmartFlow data pipeline + ML swing engine
import ConfluenceTab from './admin/ConfluenceTab';  // SESSION 147e: AlphaScout × SmartFlow combined
import PatternMatrixCard from './admin/PatternMatrixCard';  // SESSION 147j+1: per-symbol-per-pattern matrix
import OIFlowTab from './admin/OIFlowTab';  // SESSION 147j+2: OI Flow (ODX-equivalent) detector
import LODROrdersTab from './admin/LODROrdersTab';  // SESSION 147j+6: LODR order-book scanner
// SESSION 147j+17p — Model Portfolio (tracks every LLM BUY for 365d)
import ModelPortfolioTab from './admin/ModelPortfolioTab';
// Session 148 — Smart-Money Portfolio (delivery-divergence swing tracker)
import SmartMoneyPortfolioTab from './admin/SmartMoneyPortfolioTab';
// SESSION 147j+17q — Pre-LODR intelligence (public tender portals)
import PreLODRTab from './admin/PreLODRTab';
import OrderBookLeaderboardTab from './admin/OrderBookLeaderboardTab';  // 147j+16da: consolidated order-flow ranking
import SwingTradingTab from './admin/SwingTradingTab';  // 147j+16dd: swing universe + macro regime + setups
// Session 2026-02 — Phase 14 Smart Money Signals (3-consecutive Long
// Buildup × top-quartile sector strength). Phase A only ships the data
// ingestion foundation; Phase B enables live signals.
import SmartMoneySignalsTab from './admin/SmartMoneySignalsTab';
import AlphaScoutDashboard from './alphascout/AlphaScoutDashboard';  // SESSION 138: AlphaScout Phase 4
import TimeMachine from './TimeMachine';  // SESSION 121: Full Time Machine component
import HelpManual from './HelpManual';  // 147j+16bx: In-app user manual

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============================================
// SIDEBAR NAVIGATION COMPONENT
// ============================================
const Sidebar = ({ activeTab, setActiveTab }) => {
  // 147j+16bs — Logout wiring. AdminDashboard's sidebar lacked any
  // way to sign out — the global Sidebar.jsx isn't used inside /admin.
  const { user, logout } = useAuth();
  // Session 147i — Research section is collapsed by default. Auto-expands
  // when one of its children is the active tab so the user always sees
  // their current location.
  const researchIds = ['smartflow', 'dhan-swing', 'combined-model'];
  const [showResearch, setShowResearch] = useState(researchIds.includes(activeTab));

  // Session 147j+16bn — Focused-mode sidebar. When
  // REACT_APP_FOCUS_MODE=true, hide live-feed dependent tabs that
  // require TrueData (now cancelled). Admin still has full backend
  // access via direct URLs; only the navigation is decluttered.
  const _focusMode = (process.env.REACT_APP_FOCUS_MODE || 'true').toLowerCase() === 'true';

  const _allNavigationItems = [
    { id: 'dashboard', label: 'Live Dashboard', icon: BarChart3, focusHidden: true },
    { id: 'analytics', label: 'Analytics & History', icon: LineChart, focusHidden: true },
    { id: 'signal-intel', label: 'Signal Intelligence', icon: Brain, focusHidden: true },
    { id: 'confluence', label: 'Signal Confluence', icon: Layers, focusHidden: true },
    { id: 'alphascout', label: 'AlphaScout — Largecap', icon: Landmark },
    { id: 'alphascout-hunter', label: 'AlphaScout — Mid & Small', icon: TrendingUp },
    { id: 'alphascout-valuehunter', label: 'AlphaScout — Value', icon: Gem },
    { id: 'oi-flow', label: 'OI Flow', icon: Activity, focusHidden: true },
    { id: 'lodr-orders', label: 'Order Wins', icon: Newspaper },
    { id: 'lodr-leaderboard', label: 'Order Leaderboard', icon: Trophy },
    { id: 'pre-lodr',          label: 'Pre-LODR',          icon: Radar },
    { id: 'model-portfolio', label: 'Model Portfolio', icon: Package },
    { id: 'smart-money-portfolio', label: 'Smart-Money Portfolio', icon: Zap },
    { id: 'midcap-alpha-portfolio', label: 'Midcap Alpha (v5)', icon: Flame },
    { id: 'swing-trading', label: 'Swing Trading', icon: SwingIcon },
    { id: 'smart-money-signals', label: 'Smart Money Signals', icon: Zap },
    { id: 'help', label: 'Help / Manual', icon: BookOpenIcon },
    { id: 'algo-health', label: 'Algo Health', icon: Shield },   // 147j+16bn: promoted from admin to surface scheduler/floor diagnostics
  ];
  const navigationItems = _focusMode
    ? _allNavigationItems.filter((it) => !it.focusHidden)
    : _allNavigationItems.filter((it) => it.id !== 'algo-health');   // keep algo-health in admin section in full mode

  // Session 147j+16bn — Focused-mode admin items.
  // User asked for ONLY: User Management, Broker Integration in the admin
  // section (Algo Health is promoted up to nav). All other admin tools
  // (Safeguards, Data Health, Greeks Verify, Indices, etc.) are hidden
  // by default in focused mode but stay reachable via direct URL for the
  // owner. Toggle `REACT_APP_FOCUS_MODE=false` to restore the full list.
  const _allAdminItems = [
    { id: 'user-management', label: 'User Management', icon: Users, focusKeep: true },
    { id: 'broker', label: 'Broker Integration', icon: Link2, badge: 'WIP', focusKeep: true },
    { id: 'algo-health', label: 'Algo Health', icon: Shield },   // shown in nav in focus mode; here only when full
    { id: 'safeguards', label: 'Safeguards', icon: Shield },
    { id: 'gui-shadow', label: 'GUI Hierarchy Shadow', icon: FlaskConical },
    { id: 'dhan-tokens', label: 'Dhan Tokens', icon: KeyRound },
    { id: 'status', label: 'System Status', icon: Activity },
    { id: 'time-machine', label: 'Time Machine', icon: Timer },
    { id: 'data-health', label: 'Data Health', icon: Gauge },
    { id: 'greeks-verify', label: 'Greeks Verification', icon: Eye },
    { id: 'paper-trades', label: 'Paper Trades', icon: DollarSign },
    { id: 'settlement-logs', label: 'Settlement Logs', icon: FileText },
    { id: 'smlt-watchlist', label: 'SMLT Watchlist', icon: Target },
    { id: 'indices', label: 'Index Master', icon: Database },
    { id: 'holidays', label: 'Market Holidays', icon: Calendar },
    { id: 'strategy', label: 'Strategy Tuner', icon: FlaskConical },
    { id: 'signal-config', label: 'Signal Config', icon: Sliders },
    { id: 'config-history', label: 'Config History', icon: Clock },
    { id: 'special-days', label: 'Special Days', icon: Sparkles },
    { id: 'api-reference', label: 'API Reference', icon: BookOpen },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];
  const adminItems = _focusMode
    ? _allAdminItems.filter((it) => it.focusKeep)
    : _allAdminItems;

  // Session 147i — Research engines (input producers, not portfolios).
  // SmartFlow = trains the ML p_up badge that AlphaScout displays per pick.
  // DhanSwing (VCP) = breakout-pattern engine, backtest-only.
  // Combined Model = original AlphaScout × SmartFlow rank-merge UI.
  //   Demoted because the ML badge inside AlphaScout now delivers the
  //   same insight inline. Tab is hidden by default; reachable via
  //   `Research` accordion for occasional inspection.
  const researchItems = [
    { id: 'smartflow', label: 'SmartFlow (ML trainer)', icon: Activity },
    { id: 'dhan-swing', label: 'DhanSwing (VCP)', icon: TrendingUp },
    { id: 'combined-model', label: 'Combined Model', icon: GitMerge },
  ];

  return (
    <aside className="w-64 bg-surface border-r border-border flex flex-col h-full overflow-hidden" data-testid="sidebar">
      {/* Logo */}
      <div className="p-4 border-b border-border shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-accent/20 flex items-center justify-center">
            <span className="font-heading font-bold text-accent text-xl">AG</span>
          </div>
          <div>
            <h1 className="font-heading font-bold text-lg tracking-tight text-foreground leading-none">
              AlgoGreeks
            </h1>
            <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
              Admin Panel
            </span>
          </div>
        </div>
      </div>

      {/* 147j+16bs — Scrollable middle area so the footer (logout)
          stays pinned at the bottom even when Research expands and
          there are many admin items. Previously the sidebar overflowed
          off the viewport, burying the logout button. */}
      <div className="flex-1 overflow-y-auto min-h-0">
      {/* Navigation Section */}
      <div className="p-4">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          Navigation
        </h3>
        <nav className="space-y-1">
          {navigationItems.map((item) => (
            item.id === 'dashboard' ? (
              <a
                key={item.id}
                href="/"
                className={`flex items-center gap-3 px-3 py-2.5 text-sm transition-colors rounded-none ${
                  activeTab === item.id
                    ? 'bg-accent/10 text-accent border-l-2 border-accent -ml-px'
                    : 'text-muted-foreground hover:text-foreground hover:bg-surface-highlight'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </a>
            ) : (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors rounded-none text-left ${
                  activeTab === item.id
                    ? 'bg-accent/10 text-accent border-l-2 border-accent -ml-px'
                    : 'text-muted-foreground hover:text-foreground hover:bg-surface-highlight'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </button>
            )
          ))}
        </nav>
      </div>

      {/* Administration Section */}
      <div className="p-4">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          Administration
        </h3>
        <nav className="space-y-1">
          {adminItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors rounded-none text-left ${
                activeTab === item.id
                  ? 'bg-accent/10 text-accent border-l-2 border-accent -ml-px'
                  : 'text-muted-foreground hover:text-foreground hover:bg-surface-highlight'
              }`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon className="w-4 h-4" />
              <span className="flex-1">{item.label}</span>
              {item.badge && (
                <span className="px-1.5 py-0.5 text-[9px] font-bold bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded">
                  {item.badge}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Session 147i — Research Section (collapsed by default) */}
      <div className="p-4">
        <button
          type="button"
          onClick={() => setShowResearch((s) => !s)}
          className="w-full flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 hover:text-foreground transition-colors"
          data-testid="nav-research-toggle"
        >
          {showResearch ? (
            <ChevronDown className="w-3 h-3" />
          ) : (
            <ChevronRight className="w-3 h-3" />
          )}
          Research
          <span className="ml-auto normal-case lowercase font-normal text-muted-foreground/60 text-[10px]">
            {researchItems.length} engines
          </span>
        </button>
        {showResearch && (
          <nav className="space-y-1" data-testid="nav-research-section">
            {researchItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors rounded-none text-left ${
                  activeTab === item.id
                    ? 'bg-accent/10 text-accent border-l-2 border-accent -ml-px'
                    : 'text-muted-foreground hover:text-foreground hover:bg-surface-highlight'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-4 h-4" />
                <span className="flex-1">{item.label}</span>
              </button>
            ))}
          </nav>
        )}
      </div>
      </div>{/* end scrollable middle area */}

      {/* 147j+16bs — Footer with user identity + Logout. Pinned to
          the bottom of the sidebar (shrink-0) so it's always reachable
          regardless of how many admin sections are expanded. */}
      <div className="border-t border-border shrink-0">
        {user?.email && (
          <div className="px-4 py-2 text-[10px] text-muted-foreground font-mono truncate" title={user.email}>
            <span className="text-muted-foreground/60">signed in as</span>{' '}
            <span className="text-foreground/80">{user.email}</span>
          </div>
        )}
        <button
          type="button"
          onClick={logout}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-rose-300 hover:bg-rose-500/10 hover:text-rose-200 transition-colors border-t border-border/40"
          data-testid="nav-logout"
          title="Sign out"
        >
          <LogOut className="w-4 h-4" />
          <span>Logout</span>
        </button>
        <div className="px-4 py-2 text-[10px] text-muted-foreground/60 font-mono border-t border-border/40">
          v1.0.0
        </div>
      </div>
    </aside>
  );
};


// ============================================
// FEED CONTROL SECTION (Dev/Prod Token Management)
// ============================================
const FeedControlSection = ({ getAuthHeaders }) => {
  const [feedStatus, setFeedStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [actionResult, setActionResult] = useState(null);
  const [showConfirmStart, setShowConfirmStart] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [feedConfig, setFeedConfig] = useState({ configured: false, provider: null });
  
  // SESSION 92: Cache last known state for freezing after market close
  const lastKnownStateRef = useRef({
    ticksReceived: 0,
    isConnected: false,
    lastPrice: null
  });

  // Fetch current feed status - SESSION 67f: Use unified feed/config SSoT
  const fetchFeedStatus = useCallback(async () => {
    try {
      const [connRes, feedRes, configRes] = await Promise.all([
        fetch(`${API}/connection-state`),
        fetch(`${API}/feed-manager/stats`, { headers: getAuthHeaders() }),
        fetch(`${API}/feed/config`)  // SESSION 67f: Unified config SSoT
      ]);
      
      const connData = await connRes.json();
      const feedData = await feedRes.json();
      const configData = await configRes.json();
      
      // SESSION 67f: Update feed config state
      setFeedConfig({
        configured: configData.configured,
        provider: configData.provider,
        providerConnected: configData.provider_connected,
        canStartFeed: configData.can_start_feed,
        canStopFeed: configData.can_stop_feed
      });
      
      const feedManager = feedData.feed_manager || {};
      
      // SESSION 92: Check if market is open
      const isMarketOpen = connData.market_open || configData.market_open;
      
      // Get current tick count from API
      const currentTickCount = feedManager.tick_sequence || configData.tick_count || 0;
      const currentIsConnected = connData.feed_connected || configData.is_connected;
      const currentLastPrice = connData.last_price;
      
      // SESSION 92: Freeze UI state when market is closed
      // Only update cached values when market is OPEN and we have valid data
      if (isMarketOpen && currentTickCount > 0) {
        lastKnownStateRef.current = {
          ticksReceived: currentTickCount,
          isConnected: currentIsConnected,
          lastPrice: currentLastPrice
        };
      }
      
      // SESSION 92: When market is closed, use frozen/cached values
      const displayTicksReceived = isMarketOpen ? currentTickCount : lastKnownStateRef.current.ticksReceived;
      const displayIsConnected = isMarketOpen ? currentIsConnected : true; // Always show "connected" when market closed
      const displayLastPrice = isMarketOpen ? currentLastPrice : lastKnownStateRef.current.lastPrice;
      
      setFeedStatus({
        isConnected: displayIsConnected,
        lastPrice: displayLastPrice,
        marketOpen: isMarketOpen,
        pubsub: feedManager,
        ticksReceived: displayTicksReceived,
        ticksPerSecond: isMarketOpen ? (connData.ticks_per_second || 0) : 0,  // SESSION 92: Show 0 TPS when market closed
        sessionConflicts: feedManager.session_conflicts || 0,
        status: feedData.status,
        // Smart reconnect status
        autoReconnectEnabled: feedManager.auto_reconnect_enabled ?? true,
        manualReconnectRequired: feedManager.manual_reconnect_required || false,
        consecutiveFailures: feedManager.consecutive_failures || 0,
        lastFailureReason: feedManager.last_failure_reason || null
      });
    } catch (error) {
      console.error('Failed to fetch feed status:', error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchFeedStatus();
    const interval = setInterval(fetchFeedStatus, 1000); // SESSION 92: Update every 1 second for TPS gauge
    return () => clearInterval(interval);
  }, [fetchFeedStatus]);

  const handleStopFeed = async () => {
    setLoading(true);
    setActionResult(null);
    try {
      const res = await fetch(`${API}/feed/stop`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setActionResult({
        success: data.status === 'success',
        message: data.message
      });
      fetchFeedStatus();
    } catch (error) {
      setActionResult({ success: false, message: error.message });
    }
    setLoading(false);
  };

  // Reconnect handler - force reconnection to live feed
  const handleReconnect = async () => {
    setReconnecting(true);
    setActionResult(null);
    try {
      const res = await fetch(`${API}/data-source/reconnect`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setActionResult({
        success: data.success,
        message: data.message || (data.success ? 'Reconnected successfully!' : 'Reconnection failed')
      });
      // Refresh status after reconnect
      setTimeout(fetchFeedStatus, 2000);
    } catch (error) {
      setActionResult({ success: false, message: `Reconnect error: ${error.message}` });
    }
    setReconnecting(false);
  };

  const handleStartFeed = async () => {
    setValidating(true);
    setValidationResult(null);
    
    try {
      // Start the feed
      const res = await fetch(`${API}/feed/start`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      
      if (data.status === 'success') {
        // Wait 5 seconds and check for session conflicts
        setValidationResult({ status: 'checking', message: 'Checking for session conflicts...' });
        await new Promise(resolve => setTimeout(resolve, 5000));
        
        // Check feed status
        const statusRes = await fetch(`${API}/feed-manager/stats`, { headers: getAuthHeaders() });
        const statusData = await statusRes.json();
        
        const conflicts = statusData.feed_manager?.session_conflicts || 0;
        const isConnected = statusData.feed_manager?.is_connected;
        
        if (conflicts > 0) {
          setValidationResult({
            status: 'warning',
            message: `⚠️ Session conflict detected! Production may still be using the token. Conflicts: ${conflicts}`
          });
        } else if (!isConnected) {
          setValidationResult({
            status: 'error',
            message: '❌ Feed disconnected. Check if production is still running.'
          });
        } else {
          setValidationResult({
            status: 'success',
            message: '✅ Feed started successfully. No conflicts detected!'
          });
        }
      } else {
        setValidationResult({
          status: 'error',
          message: data.message || 'Failed to start feed'
        });
      }
      
      fetchFeedStatus();
    } catch (error) {
      setValidationResult({ status: 'error', message: error.message });
    }
    
    setValidating(false);
    setShowConfirmStart(false);
  };

  return (
    <div className="panel p-6 border-2 border-yellow-500/50" data-testid="feed-control-section">
      <h3 className="font-heading text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
        <Shield className="w-5 h-5 text-yellow-500" />
        Feed Control (Dev/Prod Management)
      </h3>
      
      <p className="text-sm text-muted-foreground mb-4">
        Use these controls to manage the live feed connection. Stop the feed here when running production to avoid session conflicts.
      </p>

      {/* Current Status */}
      <div className="mb-4 p-4 bg-surface rounded border border-border">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-foreground">Current Status:</span>
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${
              feedStatus?.isConnected 
                ? (feedStatus?.marketOpen ? 'bg-green-500 animate-pulse' : 'bg-yellow-500') 
                : 'bg-red-500'
            }`} />
            <span className={`text-sm font-bold ${
              feedStatus?.isConnected 
                ? (feedStatus?.marketOpen ? 'text-green-400' : 'text-yellow-400') 
                : 'text-red-400'
            }`}>
              {feedStatus?.isConnected 
                ? (feedStatus?.marketOpen ? 'CONNECTED' : 'IDLE (Market Closed)') 
                : 'DISCONNECTED'}
            </span>
          </div>
        </div>
        
        {/* Auto-Reconnect Paused Warning */}
        {feedStatus?.manualReconnectRequired && (
          <div className="mb-3 p-3 bg-red-500/20 border border-red-500/50 rounded">
            <div className="flex items-center gap-2 text-red-400 font-semibold text-sm">
              <AlertTriangle className="w-4 h-4" />
              Auto-Reconnect PAUSED
            </div>
            <p className="text-xs text-red-300 mt-1">
              {feedStatus.lastFailureReason || 'Max retries reached or critical error occurred.'}
            </p>
            <p className="text-xs text-yellow-400 mt-2 font-medium">
              👉 Click "Start Feed" below to manually reconnect.
            </p>
          </div>
        )}
        
        {feedStatus && (
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-muted-foreground">Market:</span>
              <span className={`ml-2 ${feedStatus.marketOpen ? 'text-green-400' : 'text-yellow-400'}`}>
                {feedStatus.marketOpen ? 'OPEN' : 'CLOSED'}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Ticks:</span>
              <span className="ml-2 text-foreground">{feedStatus.ticksReceived}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Tick Rate:</span>
              <span className={`ml-2 font-semibold ${
                feedStatus.ticksPerSecond >= 200 ? 'text-red-400' :
                feedStatus.ticksPerSecond >= 100 ? 'text-yellow-400' :
                'text-green-400'
              }`}>
                {feedStatus.ticksPerSecond >= 200 ? '🔴' :
                 feedStatus.ticksPerSecond >= 100 ? '🟡' : '🟢'} {feedStatus.ticksPerSecond}/sec
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Last Price:</span>
              <span className="ml-2 text-foreground">
                {feedStatus.lastPrice ? feedStatus.lastPrice.toFixed(2) : 'N/A'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Session Conflicts:</span>
              <span className={`${feedStatus.sessionConflicts > 0 ? 'text-red-400' : 'text-green-400'}`}>
                {feedStatus.sessionConflicts}
              </span>
              {feedStatus.sessionConflicts > 0 && (
                <button
                  onClick={async () => {
                    try {
                      const res = await fetch(`${API}/feed-manager/reset-conflicts`, {
                        method: 'POST',
                        headers: getAuthHeaders(),
                      });
                      if (res.ok) {
                        fetchFeedStatus();
                      }
                    } catch (e) {
                      console.error('Failed to reset conflicts:', e);
                    }
                  }}
                  className="text-xs text-accent hover:text-accent/80 underline"
                  title="Reset conflict counter"
                >
                  (reset)
                </button>
              )}
            </div>
            <div>
              <span className="text-muted-foreground">Auto-Reconnect:</span>
              <span className={`ml-2 ${feedStatus.autoReconnectEnabled ? 'text-green-400' : 'text-yellow-400'}`}>
                {feedStatus.autoReconnectEnabled ? 'ENABLED' : 'PAUSED'}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Failures:</span>
              <span className={`ml-2 ${feedStatus.consecutiveFailures > 0 ? 'text-yellow-400' : 'text-green-400'}`}>
                {feedStatus.consecutiveFailures || 0}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <Button
          onClick={handleStopFeed}
          disabled={loading || !feedStatus?.isConnected || !feedStatus?.marketOpen}
          variant="destructive"
          className="flex-1"
          data-testid="feed-stop-btn"
          title={!feedStatus?.marketOpen ? "Market is closed - no active feed" : "Stop the live feed"}
        >
          {loading ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Stopping...
            </>
          ) : (
            <>
              <X className="w-4 h-4 mr-2" />
              {feedStatus?.marketOpen ? 'Stop Feed (Free Token)' : 'Feed Idle (Market Closed)'}
            </>
          )}
        </Button>
        
        <Button
          onClick={() => setShowConfirmStart(true)}
          disabled={loading || feedStatus?.isConnected || !feedConfig.configured || !feedStatus?.marketOpen}
          variant="default"
          className={`flex-1 ${feedConfig.configured && feedStatus?.marketOpen ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-600 cursor-not-allowed'}`}
          data-testid="feed-start-btn"
          title={!feedStatus?.marketOpen ? 'Only available during market hours' : (!feedConfig.configured ? 'Configure TrueData or Accelpix credentials first' : '')}
        >
          <Activity className="w-4 h-4 mr-2" />
          {!feedStatus?.marketOpen ? 'Start Disabled (Market Closed)' : (feedConfig.configured ? 'Start Feed' : 'No Feed Configured')}
        </Button>
      </div>
      
      {/* SESSION 67f: Show active provider */}
      {feedConfig.configured && (
        <div className="mt-2 text-xs text-center text-muted-foreground">
          Provider: <span className="text-blue-400 font-mono">{feedConfig.providerConnected || feedConfig.provider || 'none'}</span>
          {feedConfig.providerConnected && <span className="ml-2 text-green-400">● Connected</span>}
        </div>
      )}

      {/* Reconnect Button - Use when feed is disconnected but should be live */}
      <div className="mt-3">
        <Button
          onClick={handleReconnect}
          disabled={reconnecting || loading || !feedStatus?.marketOpen}
          variant="outline"
          className="w-full border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10 disabled:opacity-50"
          data-testid="feed-reconnect-btn"
          title={!feedStatus?.marketOpen ? "Only available during market hours" : "Force reconnect the feed"}
        >
          {reconnecting ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Reconnecting...
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-2" />
              {feedStatus?.marketOpen ? 'Force Reconnect (Fix Disconnected State)' : 'Reconnect Disabled (Market Closed)'}
            </>
          )}
        </Button>
        <p className="text-xs text-muted-foreground mt-1 text-center">
          {feedStatus?.marketOpen 
            ? 'Use this if feed shows "Disconnected" but should be live'
            : 'This feature is only available during market hours (9:15 AM - 3:30 PM IST)'}
        </p>
      </div>

      {/* Action Result */}
      {actionResult && (
        <div className={`mt-4 p-3 rounded border ${
          actionResult.success 
            ? 'bg-green-500/10 border-green-500/30 text-green-400' 
            : 'bg-red-500/10 border-red-500/30 text-red-400'
        }`}>
          <div className="flex items-center gap-2">
            {actionResult.success ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
            <span className="text-sm">{actionResult.message}</span>
          </div>
        </div>
      )}

      {/* Start Confirmation Modal */}
      {showConfirmStart && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-surface border border-border rounded-lg p-6 max-w-md mx-4">
            <h4 className="font-heading text-lg font-bold text-foreground mb-3 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              Start Feed Confirmation
            </h4>
            
            <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded">
              <p className="text-sm text-yellow-300 mb-2">
                <strong>Important:</strong> Before starting the feed, ensure that:
              </p>
              <ul className="text-xs text-yellow-200 space-y-1 ml-4 list-disc">
                <li>Production feed has been <strong>stopped</strong></li>
                <li>No other application is using the same API token</li>
                <li>You are ready to test in this dev environment</li>
              </ul>
            </div>
            
            <p className="text-sm text-muted-foreground mb-4">
              After starting, we'll validate for 5 seconds to check if there are any session conflicts 
              (which would indicate production is still using the token).
            </p>

            {validating && (
              <div className="mb-4 p-3 bg-accent/10 border border-accent/30 rounded">
                <div className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin text-accent" />
                  <span className="text-sm text-accent">
                    {validationResult?.message || 'Starting feed and validating...'}
                  </span>
                </div>
              </div>
            )}

            {validationResult && !validating && (
              <div className={`mb-4 p-3 rounded border ${
                validationResult.status === 'success' 
                  ? 'bg-green-500/10 border-green-500/30 text-green-400'
                  : validationResult.status === 'warning'
                  ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400'
                  : 'bg-red-500/10 border-red-500/30 text-red-400'
              }`}>
                <span className="text-sm">{validationResult.message}</span>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowConfirmStart(false);
                  setValidationResult(null);
                }}
                variant="outline"
                className="flex-1"
                disabled={validating}
              >
                Cancel
              </Button>
              <Button
                onClick={handleStartFeed}
                disabled={validating}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {validating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Validating...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Confirm & Start
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ============================================
// SESSION 103: SIGNAL ROW WITH EXPANDABLE CONTEXT
// ============================================
const SignalRow = ({ signal }) => {
  const [expanded, setExpanded] = useState(false);
  const ctx = signal.signal_context || {};
  const hasContext = !!signal.signal_context && Object.keys(signal.signal_context).length > 0;
  
  // SESSION 104: Detect if this is a Stock signal (SMLT MiniGod v2.1) vs Index signal (God Score)
  const INDEX_SYMBOLS = ['NIFTY', 'BANKNIFTY', 'FINNIFTY', 'MIDCPNIFTY'];
  const isStockSignal = signal.minigod_v2 || !INDEX_SYMBOLS.includes(signal.symbol?.toUpperCase());
  const lifecycle = signal.lifecycle || {};
  
  // SESSION 103: Handle both nested exit_targets and flat fields
  const getExitTargets = () => {
    if (signal.exit_targets && (signal.exit_targets.target_1 || signal.exit_targets.stop_loss)) {
      return signal.exit_targets;
    }
    // Fallback to flat fields
    if (signal.target_1 || signal.stop_loss) {
      return {
        target_1: signal.target_1,
        target_2: signal.target_2,
        stop_loss: signal.stop_loss
      };
    }
    return null;
  };
  
  const exitTargets = getExitTargets();
  
  // Determine lifecycle state display
  const getLifecycleDisplay = () => {
    const state = lifecycle.state || signal.lifecycle_state || signal.outcome || 'PENDING';
    const exitReason = lifecycle.exit_reason || signal.lifecycle_exit_reason || '';
    
    if (state === 'ACTIVE') {
      return { text: 'ACTIVE', class: 'bg-blue-500/20 text-blue-400 animate-pulse', icon: '🔵' };
    } else if (state === 'CLOSED' || signal.outcome === 'WIN') {
      if (exitReason === 'T1_HIT') {
        return { text: 'T1 HIT', class: 'bg-green-500/20 text-green-400', icon: '✅' };
      } else if (exitReason === 'SL_HIT') {
        return { text: 'SL HIT', class: 'bg-red-500/20 text-red-400', icon: '❌' };
      } else if (exitReason === 'MANUAL') {
        return { text: 'MANUAL', class: 'bg-amber-500/20 text-amber-400', icon: '👆' };
      } else if (exitReason === 'EOD') {
        return { text: 'EOD', class: 'bg-gray-500/20 text-gray-400', icon: '🌙' };
      } else if (signal.outcome === 'WIN') {
        return { text: 'WIN', class: 'bg-green-500/20 text-green-400', icon: '✅' };
      } else if (signal.outcome === 'LOSS') {
        return { text: 'LOSS', class: 'bg-red-500/20 text-red-400', icon: '❌' };
      }
      return { text: 'CLOSED', class: 'bg-gray-500/20 text-gray-400', icon: '⬜' };
    }
    return { text: 'PENDING', class: 'bg-gray-500/20 text-gray-400', icon: '⏳' };
  };
  
  const lifecycleDisplay = getLifecycleDisplay();
  
  return (
    <>
      <tr className="hover:bg-gray-700/30 cursor-pointer" onClick={() => hasContext && setExpanded(!expanded)}>
        <td className="px-4 py-3 text-gray-400">
          {hasContext ? (
            <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          ) : null}
        </td>
        <td className="px-4 py-3 text-gray-300">
          {signal.signal_time ? new Date(signal.signal_time).toLocaleString('en-IN', { 
            hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short'
          }) : '-'}
        </td>
        <td className="px-4 py-3 text-white font-medium">{signal.symbol?.replace('-I', '')}</td>
        <td className="px-4 py-3">
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
            signal.direction === 'BUY' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
          }`}>
            {signal.direction}
          </span>
        </td>
        <td className="px-4 py-3 text-gray-300">{signal.entry_price?.toFixed(2)}</td>
        <td className="px-4 py-3">
          {exitTargets ? (
            <div className="text-xs">
              <span className="text-green-400">{exitTargets.target_1?.toFixed(0)}</span>
              <span className="text-gray-500 mx-1">/</span>
              <span className="text-blue-400 opacity-50">{exitTargets.target_2?.toFixed(0)}</span>
            </div>
          ) : '-'}
        </td>
        <td className="px-4 py-3">
          {exitTargets?.stop_loss ? (
            <span className="text-red-400 text-xs">{exitTargets.stop_loss?.toFixed(0)}</span>
          ) : '-'}
        </td>
        <td className="px-4 py-3 text-white">{(signal.total_score || signal.ema_score || 0)?.toFixed(1)}</td>
        <td className="px-4 py-3">
          {hasContext ? (
            <div className="flex flex-wrap gap-1">
              {ctx.gex_regime?.is_rocket_zone && (
                <span className="px-1.5 py-0.5 bg-green-500/20 text-green-400 text-[10px] rounded">ROCKET</span>
              )}
              {ctx.gex_regime?.is_waterfall_zone && (
                <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 text-[10px] rounded">WATERFALL</span>
              )}
              {ctx.profile_context?.context_locked && (
                <span className="px-1.5 py-0.5 bg-purple-500/20 text-purple-400 text-[10px] rounded">LOCKED</span>
              )}
              {ctx.order_flow?.has_stacked_buy && (
                <span className="px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 text-[10px] rounded">STACK↑</span>
              )}
              {ctx.order_flow?.has_stacked_sell && (
                <span className="px-1.5 py-0.5 bg-rose-500/20 text-rose-400 text-[10px] rounded">STACK↓</span>
              )}
              {!ctx.gex_regime?.is_rocket_zone && !ctx.gex_regime?.is_waterfall_zone && (
                <span className="px-1.5 py-0.5 bg-gray-500/20 text-gray-400 text-[10px] rounded">NEUTRAL</span>
              )}
            </div>
          ) : (
            <span className="text-gray-500 text-xs">-</span>
          )}
        </td>
        <td className="px-4 py-3">
          <span className={`px-2 py-0.5 rounded text-xs ${lifecycleDisplay.class}`}>
            {lifecycleDisplay.icon} {lifecycleDisplay.text}
          </span>
        </td>
        <td className={`px-4 py-3 font-medium ${
          (signal.pnl_points || lifecycle.pnl_points || 0) >= 0 ? 'text-green-400' : 'text-red-400'
        }`}>
          {(signal.pnl_points || lifecycle.pnl_points) ? 
            `${(signal.pnl_points || lifecycle.pnl_points) >= 0 ? '+' : ''}${(signal.pnl_points || lifecycle.pnl_points).toFixed(1)}` 
            : '-'}
        </td>
      </tr>
      
      {/* Expanded Context Row */}
      {expanded && hasContext && (
        <tr className="bg-gray-900/50">
          <td colSpan={11} className="px-4 py-4">
            {/* SESSION 104: Different breakdown for Stock signals vs Index signals */}
            {isStockSignal ? (
              /* ============ STOCK SIGNAL BREAKDOWN (MiniGod v2.1) ============ */
              <div className="grid grid-cols-4 gap-4 text-xs">
                {/* MiniGod v2.1 Score Breakdown */}
                <div className="bg-gray-800/50 p-3 rounded border border-cyan-700/50">
                  <h4 className="text-cyan-400 mb-2 font-medium flex items-center gap-1">
                    <Sliders className="w-3 h-3" /> MiniGod v2.1 Breakdown
                  </h4>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-amber-400">OI Flow:</span>
                      <span className={`font-medium ${(signal.oi_flow_score || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {signal.oi_flow_score?.toFixed(1) || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-purple-400">CVD Strength:</span>
                      <span className={`font-medium ${(signal.cvd_strength_score || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {signal.cvd_strength_score?.toFixed(1) || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-400">Volume:</span>
                      <span className={`font-medium ${(signal.volume_score || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {signal.volume_score?.toFixed(1) || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-400">Price Position:</span>
                      <span className={`font-medium ${(signal.price_position_score || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {signal.price_position_score?.toFixed(1) || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-orange-400">Index Align:</span>
                      <span className={`font-medium ${(signal.index_alignment_score || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {signal.index_alignment_score?.toFixed(1) || 0}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Stock Flow Context */}
                <div className="bg-gray-800/50 p-3 rounded border border-gray-700">
                  <h4 className="text-gray-400 mb-2 font-medium flex items-center gap-1">
                    <Activity className="w-3 h-3" /> Stock Flow
                  </h4>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-400">CVD:</span>
                      <span className={`${(ctx.stock_flow?.cvd || signal.delta_slope || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {(ctx.stock_flow?.cvd || signal.delta_slope || 0).toFixed(0)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">TPS:</span>
                      <span className="text-white">{ctx.stock_flow?.tps?.toFixed(1) || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Volume Ratio:</span>
                      <span className="text-white">{ctx.stock_flow?.volume_ratio?.toFixed(2) || '-'}x</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">OI Regime:</span>
                      <span className={ctx.stock_flow?.oi_regime === 'LONG_BUILDUP' || ctx.stock_flow?.oi_regime === 'SHORT_COVERING' ? 'text-green-400' : 'text-red-400'}>
                        {ctx.stock_flow?.oi_regime || '-'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Price Context */}
                <div className="bg-gray-800/50 p-3 rounded border border-gray-700">
                  <h4 className="text-gray-400 mb-2 font-medium flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> Price Context
                  </h4>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-400">VWAP:</span>
                      <span className="text-white">{ctx.price_context?.vwap?.toFixed(2) || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Price vs VWAP:</span>
                      <span className={ctx.price_context?.price_vs_vwap === 'ABOVE' ? 'text-green-400' : 'text-red-400'}>
                        {ctx.price_context?.price_vs_vwap || '-'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Day Change:</span>
                      <span className={`${(ctx.price_context?.day_change || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {ctx.price_context?.day_change?.toFixed(2) || '0'}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Traded Value:</span>
                      <span className="text-white">{ctx.price_context?.traded_value_lakhs?.toFixed(0) || '-'} L</span>
                    </div>
                  </div>
                </div>

                {/* Index Alignment */}
                <div className="bg-gray-800/50 p-3 rounded border border-gray-700">
                  <h4 className="text-gray-400 mb-2 font-medium flex items-center gap-1">
                    <Layers className="w-3 h-3" /> Index Context
                  </h4>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-400">NIFTY:</span>
                      <span className={`${(ctx.index_context?.nifty_change || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {ctx.index_context?.nifty_change?.toFixed(2) || '0'}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Aligned:</span>
                      <span className={ctx.index_context?.aligned ? 'text-green-400' : 'text-amber-400'}>
                        {ctx.index_context?.aligned ? 'YES' : 'NO'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Sector:</span>
                      <span className="text-white">{ctx.index_context?.sector || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Beta:</span>
                      <span className="text-white">{ctx.index_context?.beta?.toFixed(2) || '-'}</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* ============ INDEX SIGNAL BREAKDOWN (God Score) ============ */
              <div className="grid grid-cols-4 gap-4 text-xs">
                {/* Weight Breakdown */}
                <div className="bg-gray-800/50 p-3 rounded border border-gray-700">
                  <h4 className="text-gray-400 mb-2 font-medium flex items-center gap-1">
                    <Sliders className="w-3 h-3" /> 4-Weight Breakdown
                  </h4>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-amber-400">Order Flow:</span>
                      <span className="text-white">{ctx.weight_breakdown?.order_flow_score?.toFixed(1) || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-purple-400">Profile Context:</span>
                      <span className="text-white">{ctx.weight_breakdown?.profile_context_score?.toFixed(1) || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-400">Profile Levels:</span>
                      <span className="text-white">{ctx.weight_breakdown?.profile_levels_score?.toFixed(1) || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-400">GEX:</span>
                      <span className="text-white">{ctx.weight_breakdown?.gex_score?.toFixed(1) || 0}</span>
                    </div>
                  </div>
                </div>

                {/* GEX Regime */}
                <div className="bg-gray-800/50 p-3 rounded border border-gray-700">
                  <h4 className="text-gray-400 mb-2 font-medium flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> GEX Regime
                  </h4>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Flip Zone:</span>
                      <span className="text-white">{ctx.gex_regime?.flip_zone?.toFixed(0) || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Distance:</span>
                      <span className="text-white">{ctx.gex_regime?.distance_from_flip?.toFixed(1) || 0} pts</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">GEX Boosted:</span>
                      <span className={ctx.gex_regime?.gex_boosted ? 'text-green-400' : 'text-gray-500'}>
                        {ctx.gex_regime?.gex_boosted ? 'YES' : 'NO'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Eff. Weight:</span>
                      <span className="text-white">{ctx.gex_regime?.effective_gex_weight || 20}</span>
                    </div>
                  </div>
                </div>

                {/* Order Flow */}
                <div className="bg-gray-800/50 p-3 rounded border border-gray-700">
                  <h4 className="text-gray-400 mb-2 font-medium flex items-center gap-1">
                    <Activity className="w-3 h-3" /> Order Flow
                  </h4>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-400">CVD:</span>
                      <span className={`${(ctx.order_flow?.cvd || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {ctx.order_flow?.cvd?.toFixed(0) || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">CVD EMA:</span>
                      <span className="text-white">{ctx.order_flow?.cvd_ema?.toFixed(1) || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Stacked:</span>
                      <span className="text-white">
                        {ctx.order_flow?.has_stacked_buy ? '↑ Buy' : ctx.order_flow?.has_stacked_sell ? '↓ Sell' : '-'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Imbal Boost:</span>
                      <span className="text-white">{ctx.order_flow?.imbalance_boost || 0}</span>
                    </div>
                  </div>
                </div>

                {/* Profile Levels */}
                <div className="bg-gray-800/50 p-3 rounded border border-gray-700">
                  <h4 className="text-gray-400 mb-2 font-medium flex items-center gap-1">
                    <Layers className="w-3 h-3" /> Profile Levels
                  </h4>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-400">dPOC:</span>
                      <span className="text-white">{ctx.profile_levels?.dpoc?.toFixed(0) || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">VAH:</span>
                      <span className="text-green-400">{ctx.profile_levels?.vah?.toFixed(0) || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">VAL:</span>
                      <span className="text-red-400">{ctx.profile_levels?.val?.toFixed(0) || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">IB:</span>
                      <span className="text-white">
                        {ctx.profile_levels?.ib_high?.toFixed(0) || '-'} / {ctx.profile_levels?.ib_low?.toFixed(0) || '-'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Active Factors Row */}
            {ctx.active_factors && ctx.active_factors.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                <span className="text-gray-400 text-xs mr-2">Active Factors:</span>
                {ctx.active_factors.map((factor, i) => (
                  <span key={i} className="px-1.5 py-0.5 bg-blue-500/20 text-blue-300 text-[10px] rounded">
                    {factor}
                  </span>
                ))}
              </div>
            )}

            {/* Threshold & Stability */}
            <div className="mt-3 flex items-center gap-4 text-xs text-gray-400">
              <span>
                Gate: <span className="text-white">{ctx.threshold_context?.final_threshold || 70}</span>
              </span>
              <span>
                VIX: <span className="text-white">{ctx.threshold_context?.vix?.toFixed(1) || '-'}</span>
              </span>
              <span>
                ATR: <span className="text-white">{ctx.threshold_context?.atr?.toFixed(1) || '-'}</span>
              </span>
              <span>
                EMA α: <span className="text-white">{ctx.stability_state?.ema_alpha || 0.3}</span>
              </span>
              {ctx.stability_state?.in_cooldown && (
                <span className="text-amber-400">IN COOLDOWN</span>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};



// ============================================
// SESSION 120: ANALYTICS & HISTORY TAB
// Shows closed signals history with T1/SL/EOD outcomes
// ============================================
const AnalyticsTab = () => {
  const [signals, setSignals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(7);
  const [symbolFilter, setSymbolFilter] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('days', days);
      params.append('limit', 100);
      if (symbolFilter) params.append('symbol', symbolFilter);
      
      const response = await fetch(`${API}/analytics/closed-signals?${params}`);
      if (!response.ok) throw new Error('Failed to fetch analytics data');
      
      const data = await response.json();
      setSignals(data.signals || []);
      setSummary(data.summary || null);
    } catch (err) {
      setError(err.message);
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [days, symbolFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    try {
      // Session 147j+1 — backend collections sometimes serialize naive
      // datetimes (UTC implicit but no `Z` marker on the JSON wire).
      // JS Date() then interprets them as LOCAL → exit-time column
      // showed 17:44 IST for all rows when actual exit was 12:14 UTC
      // (= 17:44 IST is wrong; should have been 17:44 IST = market-close
      // sweep at 12:14 UTC). Append `Z` when no timezone marker present.
      let ts = isoString;
      if (typeof ts === 'string' && !/(Z|[+-]\d{2}:?\d{2})$/.test(ts)) {
        ts = ts + 'Z';
      }
      const date = new Date(ts);
      return date.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return isoString;
    }
  };

  const getStatusBadge = (signal) => {
    const status = signal.status || signal.exit_reason || 'UNKNOWN';
    const statusMap = {
      'T1': { color: 'bg-green-500/20 text-green-400 border-green-500/30', label: 'T1' },
      'T1_HIT': { color: 'bg-green-500/20 text-green-400 border-green-500/30', label: 'T1' },
      'SL': { color: 'bg-red-500/20 text-red-400 border-red-500/30', label: 'SL' },
      'SL_HIT': { color: 'bg-red-500/20 text-red-400 border-red-500/30', label: 'SL' },
      'EOD': { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', label: 'EOD' },
      'MANUAL': { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', label: 'MANUAL' },
      'CLOSED': { color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', label: 'CLOSED' },
    };
    const config = statusMap[status] || statusMap['CLOSED'];
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium border ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getOutcomeBadge = (outcome) => {
    if (outcome === 'WIN') {
      return <span className="text-green-400 font-medium">WIN</span>;
    } else if (outcome === 'LOSS') {
      return <span className="text-red-400 font-medium">LOSS</span>;
    }
    return <span className="text-gray-400">-</span>;
  };

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Analytics & History</h2>
          <p className="text-sm text-muted-foreground">Closed signals with T1, SL, and EOD outcomes</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm" disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="bg-background border border-border rounded px-3 py-1.5 text-sm"
          data-testid="days-filter"
        >
          <option value={1}>Today</option>
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
        </select>
        
        <select
          value={symbolFilter}
          onChange={(e) => setSymbolFilter(e.target.value)}
          className="bg-background border border-border rounded px-3 py-1.5 text-sm"
          data-testid="symbol-filter"
        >
          <option value="">All Symbols</option>
          <option value="NIFTY">NIFTY</option>
          <option value="BANKNIFTY">BANKNIFTY</option>
        </select>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="text-sm text-muted-foreground">Total Signals</div>
            <div className="text-2xl font-bold text-foreground">{summary.total_signals}</div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="text-sm text-muted-foreground">Total P&L</div>
            <div className={`text-2xl font-bold ${summary.total_pnl_points >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {summary.total_pnl_points >= 0 ? '+' : ''}{summary.total_pnl_points?.toFixed(1)} pts
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="text-sm text-muted-foreground">Win Rate</div>
            <div className="text-2xl font-bold text-foreground">{summary.win_rate?.toFixed(1)}%</div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="text-sm text-muted-foreground">T1 Hits</div>
            <div className="text-2xl font-bold text-green-400">{summary.t1_hits}</div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="text-sm text-muted-foreground">SL Hits</div>
            <div className="text-2xl font-bold text-red-400">{summary.sl_hits}</div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="text-sm text-muted-foreground">EOD Closes</div>
            <div className="text-2xl font-bold text-yellow-400">{summary.eod_closes}</div>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
          Error: {error}
        </div>
      )}

      {/* Signals Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left text-muted-foreground font-medium">Symbol</th>
                <th className="px-4 py-3 text-left text-muted-foreground font-medium">Signal</th>
                <th className="px-4 py-3 text-left text-muted-foreground font-medium">Entry</th>
                <th className="px-4 py-3 text-left text-muted-foreground font-medium">Exit</th>
                <th className="px-4 py-3 text-left text-muted-foreground font-medium">Status</th>
                <th className="px-4 py-3 text-left text-muted-foreground font-medium">Outcome</th>
                <th className="px-4 py-3 text-right text-muted-foreground font-medium">P&L</th>
                <th className="px-4 py-3 text-left text-muted-foreground font-medium">Entry Time</th>
                <th className="px-4 py-3 text-left text-muted-foreground font-medium">Exit Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">
                    <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                    Loading signals...
                  </td>
                </tr>
              ) : signals.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">
                    No closed signals found for the selected period.
                    <br />
                    <span className="text-xs">Signals will appear here when they close at T1, SL, or EOD.</span>
                  </td>
                </tr>
              ) : (
                signals.map((signal, idx) => (
                  <tr key={signal.signal_id || idx} className="hover:bg-muted/30">
                    <td className="px-4 py-3 font-medium text-foreground">{signal.symbol}</td>
                    <td className="px-4 py-3">
                      <span className={signal.signal === 'BUY' ? 'text-green-400' : 'text-red-400'}>
                        {signal.signal}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-foreground">{signal.entry_price?.toFixed(2)}</td>
                    <td className="px-4 py-3 text-foreground">{signal.exit_price?.toFixed(2)}</td>
                    <td className="px-4 py-3">{getStatusBadge(signal)}</td>
                    <td className="px-4 py-3">{getOutcomeBadge(signal.outcome)}</td>
                    <td className={`px-4 py-3 text-right font-medium ${
                      signal.pnl_points >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {signal.pnl_points >= 0 ? '+' : ''}{signal.pnl_points?.toFixed(1)}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">{formatTime(signal.entry_time)}</td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">{formatTime(signal.exit_time)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info Note */}
      <div className="text-xs text-muted-foreground bg-muted/30 rounded-lg p-3">
        <strong>Status Legend:</strong> T1 = Target 1 Hit (Win) | SL = Stop Loss Hit (Loss) | EOD = End of Day Close (3:25 PM IST)
      </div>
    </div>
  );
};



// ============================================
// SESSION 100: SIGNAL INTELLIGENCE TAB
// ============================================
const SignalIntelligenceTab = () => {
  const [stats, setStats] = useState(null);
  const [patterns, setPatterns] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [recentSignals, setRecentSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [days, setDays] = useState(7);
  
  // SESSION 105: Veto Insights state
  const [vetoInsights, setVetoInsights] = useState(null);
  const [showBlockedSignals, setShowBlockedSignals] = useState(false);
  const [vetoLoading, setVetoLoading] = useState(false);

  // Apr-2026 Session B: Strategy fire-rate roll-up (SMLT / God Score / IVSA / ORB / Hero Zero / Alpha Strike / Confluence)
  const [strategyStats, setStrategyStats] = useState(null);

  // Apr-2026 Session E: Near-miss gate tuning simulator
  const [simScenario, setSimScenario] = useState('god_score_override');
  const [simThreshold, setSimThreshold] = useState(85); // for god_score_override
  const [simMinBucket, setSimMinBucket] = useState(4); // bucket 4 = 10:15 IST
  const [simGateName, setSimGateName] = useState('ENTRY_RULE');
  const [simResult, setSimResult] = useState(null);
  const [simLoading, setSimLoading] = useState(false);

  // Apr-2026 Session F: Quantitative auto-insights (replaces canned Improvement Suggestions)
  const [autoInsights, setAutoInsights] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const symbolParam = selectedSymbol ? `&symbol=${selectedSymbol}` : '';
      
      const [statsRes, patternsRes, suggestionsRes, recentRes] = await Promise.all([
        fetch(`${API}/signal-intelligence/stats?days=${days}${symbolParam}`),
        fetch(`${API}/signal-intelligence/patterns?days=${Math.max(days, 14)}${symbolParam}`),
        fetch(`${API}/signal-intelligence/suggestions?days=${days}${symbolParam}`),
        // SESSION 104: Use today_only=false to show all recent signals including historical
        fetch(`${API}/signal-intelligence/recent?limit=20&today_only=false${symbolParam ? `&symbol=${selectedSymbol}` : ''}`),
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (patternsRes.ok) setPatterns(await patternsRes.json());
      if (suggestionsRes.ok) setSuggestions(await suggestionsRes.json());
      if (recentRes.ok) {
        const data = await recentRes.json();
        setRecentSignals(data.signals || []);
      }
    } catch (err) {
      console.error('Failed to fetch signal intelligence:', err);
    }
    setLoading(false);
  }, [selectedSymbol, days]);

  // SESSION 105: Fetch Veto Insights
  // Apr-2026 Session A: now respects symbol + days filters
  const fetchVetoInsights = useCallback(async () => {
    setVetoLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedSymbol) params.set('symbol', selectedSymbol);
      // days=0 means live today (in-memory, real-time what-if P&L).
      // days>0 hits MongoDB historical collection.
      params.set('days', String(days > 1 ? days : 0));
      const res = await fetch(`${API}/signal-intelligence/veto-insights?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        if (data.success) setVetoInsights(data);
      }
    } catch (err) {
      console.error('Failed to fetch veto insights:', err);
    }
    setVetoLoading(false);
  }, [selectedSymbol, days]);

  // Apr-2026 Session B: Fetch per-strategy fire-rate stats (always live today, no filters)
  const fetchStrategyStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/signal-intelligence/strategy-stats`);
      if (res.ok) {
        const data = await res.json();
        if (data.success) setStrategyStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch strategy stats:', err);
    }
  }, []);

  // Apr-2026 Session F: Quantitative auto-insights (replaces hard-coded Improvement Suggestions)
  const fetchAutoInsights = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedSymbol) params.set('symbol', selectedSymbol);
      params.set('days', String(days || 1));
      const res = await fetch(`${API}/signal-intelligence/auto-insights?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        if (data.success) setAutoInsights(data);
      }
    } catch (err) {
      console.error('Failed to fetch auto-insights:', err);
    }
  }, [selectedSymbol, days]);

  // Apr-2026 Session E: Run what-if simulation against historical blocks.
  // Uses the same symbol + days filters as Veto Insights for consistency.
  const runSimulation = useCallback(async () => {
    setSimLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('scenario', simScenario);
      if (simScenario === 'god_score_override') params.set('threshold', String(simThreshold));
      if (simScenario === 'min_time_of_day') params.set('min_bucket_idx', String(simMinBucket));
      if (simScenario === 'gate_off') params.set('gate_name', simGateName);
      if (selectedSymbol) params.set('symbol', selectedSymbol);
      params.set('days', String(days || 1));
      const res = await fetch(`${API}/signal-intelligence/simulate?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setSimResult(data);
      } else {
        const txt = await res.text();
        setSimResult({ success: false, error: txt.slice(0, 200) });
      }
    } catch (err) {
      console.error('Simulation failed:', err);
      setSimResult({ success: false, error: String(err).slice(0, 200) });
    }
    setSimLoading(false);
  }, [simScenario, simThreshold, simMinBucket, simGateName, selectedSymbol, days]);

  // Debounce slider-driven sim runs so we don't spam the backend while user drags
  useEffect(() => {
    const t = setTimeout(runSimulation, 250);
    return () => clearTimeout(t);
  }, [runSimulation]);

  useEffect(() => {
    fetchData();
    fetchVetoInsights();
    fetchStrategyStats();
    fetchAutoInsights();
    const interval = setInterval(fetchData, 30000); // Apr-2026: 30s for quicker Active Signals refresh post-EOD
    const vetoInterval = setInterval(fetchVetoInsights, 15000); // Refresh veto data every 15s
    const strategyInterval = setInterval(fetchStrategyStats, 30000); // Refresh strategy stats every 30s
    const insightsInterval = setInterval(fetchAutoInsights, 30000); // Refresh insights every 30s
    return () => { clearInterval(interval); clearInterval(vetoInterval); clearInterval(strategyInterval); clearInterval(insightsInterval); };
  }, [fetchData, fetchVetoInsights, fetchStrategyStats, fetchAutoInsights]);

  return (
    <div className="space-y-6" data-testid="signal-intelligence-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Brain className="w-8 h-8 text-purple-400" />
          <div>
            <h1 className="text-2xl font-bold text-white">Signal Intelligence</h1>
            <p className="text-sm text-gray-400">Analyze signal performance and get improvement suggestions</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={selectedSymbol}
            onChange={(e) => setSelectedSymbol(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white"
          >
            <option value="">All Symbols</option>
            <option value="NIFTY-I">NIFTY</option>
            <option value="BANKNIFTY-I">BANKNIFTY</option>
          </select>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white"
          >
            <option value={7}>Last 7 Days</option>
            <option value={14}>Last 14 Days</option>
            <option value={30}>Last 30 Days</option>
          </select>
          <Button onClick={fetchData} size="sm" variant="outline" className="gap-2">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
          <div className="text-gray-400 text-sm">Total Signals</div>
          <div className="text-2xl font-bold text-white">{stats?.total_signals || 0}</div>
        </div>
        <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
          <div className="text-gray-400 text-sm">Win Rate</div>
          <div className={`text-2xl font-bold ${(stats?.win_rate || 0) >= 50 ? 'text-green-400' : 'text-red-400'}`}>
            {(stats?.win_rate || 0).toFixed(1)}%
          </div>
        </div>
        <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
          <div className="text-gray-400 text-sm">Total P&L</div>
          <div className={`text-2xl font-bold ${(stats?.total_pnl_points || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {(stats?.total_pnl_points || 0) >= 0 ? '+' : ''}{(stats?.total_pnl_points || 0).toFixed(1)} pts
          </div>
        </div>
        <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
          <div className="text-gray-400 text-sm">Avg MFE / MAE</div>
          <div className="text-lg font-bold">
            <span className="text-green-400">+{(stats?.avg_mfe || 0).toFixed(1)}</span>
            <span className="text-gray-500 mx-1">/</span>
            <span className="text-red-400">-{(stats?.avg_mae || 0).toFixed(1)}</span>
          </div>
        </div>
      </div>

      {/* SESSION 105: Veto Insights Panel */}
      {vetoInsights && vetoInsights.total_blocks > 0 && (
        <div className="bg-red-950/30 border border-red-500/20 rounded-lg p-4" data-testid="veto-insights-panel">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-red-400" />
              <h3 className="font-semibold text-white" data-testid="veto-insights-title">Veto Insights <span className="text-xs text-gray-400 font-normal">({vetoInsights?.window_label || 'Today'}{vetoInsights?.symbol_filter ? ` · ${vetoInsights.symbol_filter}` : ''})</span></h3>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">{vetoLoading ? 'Updating...' : 'Live'}</span>
              <Button
                onClick={() => setShowBlockedSignals(!showBlockedSignals)}
                size="sm"
                variant="outline"
                className={`text-xs gap-1 ${showBlockedSignals ? 'border-red-500/50 text-red-300' : ''}`}
                data-testid="toggle-blocked-signals"
              >
                {showBlockedSignals ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                {showBlockedSignals ? 'Hide' : 'Show'} Blocked ({vetoInsights.total_blocks})
              </Button>
            </div>
          </div>

          {/* Summary Cards Row */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            {/* Blocked Count — Apr-2026 Session F: headline is episodes, blocks as subtitle */}
            <div className="bg-gray-900/60 rounded-lg p-3 border border-gray-700/50" data-testid="summary-blocked">
              <div className="text-[10px] text-gray-500 uppercase tracking-wide" title="Distinct trading episodes (same signal idea consolidated)">Blocked Episodes</div>
              <div className="text-xl font-bold text-red-400">{vetoInsights.total_episodes ?? '—'}</div>
              <div className="text-[10px] text-gray-400 mt-1">
                <span>{vetoInsights.total_blocks} raw</span>
                <span className="mx-1">·</span>
                <span className="text-gray-500" title="How many times the same signal idea was re-rejected on average">{(vetoInsights.inflation_factor ?? 1).toFixed(1)}x dedup</span>
              </div>
            </div>

            {/* Missed P&L — honest episode-level value */}
            <div className="bg-gray-900/60 rounded-lg p-3 border border-gray-700/50" data-testid="summary-missed-pnl">
              <div className="text-[10px] text-gray-500 uppercase tracking-wide" title="Cumulative max favorable excursion across unique episodes (de-duplicated)">Missed P&L</div>
              <div className={`text-xl font-bold ${(vetoInsights.what_if_summary?.episode_missed_pnl || 0) > 0 ? 'text-amber-400' : 'text-green-400'}`}>
                {(vetoInsights.what_if_summary?.episode_missed_pnl || 0) > 0 ? '+' : ''}{vetoInsights.what_if_summary?.episode_missed_pnl?.toFixed(0) || 0} pts
              </div>
              <div className="text-[10px] text-gray-400 mt-1" title={`Raw tick-level sum: ${vetoInsights.what_if_summary?.total_missed_pnl?.toFixed(0) || 0} pts (inflated due to tick repeats — use episode figure above)`}>
                Raw: {vetoInsights.what_if_summary?.total_missed_pnl?.toFixed(0) || 0} pts
              </div>
            </div>

            {/* Veto Accuracy — honest episode-level */}
            <div className="bg-gray-900/60 rounded-lg p-3 border border-gray-700/50" data-testid="summary-veto-accuracy">
              <div className="text-[10px] text-gray-500 uppercase tracking-wide" title="% of episodes where the veto avoided a losing trade">Veto Accuracy</div>
              <div className={`text-xl font-bold ${(vetoInsights.what_if_summary?.episode_accuracy_pct || 0) >= 50 ? 'text-green-400' : 'text-red-400'}`}>
                {vetoInsights.what_if_summary?.episode_accuracy_pct?.toFixed(0) || 0}%
              </div>
              <div className="text-[10px] text-gray-400 mt-1">
                <span className="text-green-400">{vetoInsights.what_if_summary?.episode_correct_count || 0} correct</span>
                <span className="mx-1">|</span>
                <span className="text-red-400">{vetoInsights.what_if_summary?.episode_wrong_count || 0} wrong</span>
              </div>
            </div>

            {/* Top Blocker */}
            <div className="bg-gray-900/60 rounded-lg p-3 border border-gray-700/50">
              <div className="text-[10px] text-gray-500 uppercase tracking-wide">Top Blocker</div>
              {(() => {
                const gates = vetoInsights.by_gate || {};
                const topGate = Object.entries(gates).sort((a, b) => b[1].count - a[1].count)[0];
                return topGate ? (
                  <>
                    <div className="text-sm font-bold text-amber-400 truncate" title={topGate[0]}>
                      {topGate[0].replace('_', ' ').replace('VETO', '').replace('BLOCKED', '').trim() || 'Unknown'}
                    </div>
                    <div className="text-[10px] text-gray-400 mt-1">
                      {topGate[1].count}x | Acc: {topGate[1].accuracy_pct}%
                    </div>
                  </>
                ) : <div className="text-sm text-gray-500">None</div>;
              })()}
            </div>

            {/* Worst Miss */}
            <div className="bg-gray-900/60 rounded-lg p-3 border border-gray-700/50">
              <div className="text-[10px] text-gray-500 uppercase tracking-wide">Worst Miss</div>
              {vetoInsights.worst_misses?.[0] ? (
                <>
                  <div className="text-sm font-bold text-amber-400">
                    {vetoInsights.worst_misses[0].symbol} {vetoInsights.worst_misses[0].direction === 'DOWN' || vetoInsights.worst_misses[0].direction === 'SHORT' ? 'SHORT' : 'LONG'}
                  </div>
                  <div className="text-[10px] text-gray-400 mt-1">
                    +{vetoInsights.worst_misses[0].what_if_pnl?.toFixed(1)} pts missed
                  </div>
                </>
              ) : <div className="text-sm text-gray-500">None</div>}
            </div>
          </div>

          {/* Gate Breakdown Table */}
          {Object.keys(vetoInsights.by_gate || {}).length > 0 && (
            <div className="mb-4">
              <h4 className="text-xs text-gray-400 mb-2 font-medium">Gate Performance</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {Object.entries(vetoInsights.by_gate).map(([gate, data]) => (
                  <div key={gate} className="bg-gray-900/40 rounded px-3 py-2 border border-gray-700/30 flex items-center justify-between">
                    <div>
                      <span className="text-xs text-gray-300 font-medium">{gate.replace(/_/g, ' ')}</span>
                      <span className="text-[10px] text-gray-500 ml-2">{data.count}x</span>
                    </div>
                    <div className="text-right">
                      <span className={`text-xs font-bold ${data.accuracy_pct >= 50 ? 'text-green-400' : 'text-red-400'}`}>
                        {data.accuracy_pct}%
                      </span>
                      {data.total_missed_pnl > 0 && (
                        <span className="text-[10px] text-amber-400 ml-2">-{data.total_missed_pnl.toFixed(0)}pts</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Blocked Signals Detail Table (expandable) */}
          {showBlockedSignals && vetoInsights.blocks?.length > 0 && (
            <div className="border-t border-gray-700/50 pt-3">
              <h4 className="text-xs text-gray-400 mb-2 font-medium">Blocked Signal Log ({vetoInsights.blocks.length})</h4>
              <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                <table className="w-full text-xs" data-testid="blocked-signals-table">
                  <thead className="bg-gray-900/50 sticky top-0">
                    <tr className="text-left text-gray-500">
                      <th className="px-2 py-2">Time</th>
                      <th className="px-2 py-2">Symbol</th>
                      <th className="px-2 py-2">Signal</th>
                      <th className="px-2 py-2">Score</th>
                      <th className="px-2 py-2">Gate</th>
                      <th className="px-2 py-2">Veto Reason</th>
                      <th className="px-2 py-2">Block Price</th>
                      <th className="px-2 py-2">What-If P&L</th>
                      <th className="px-2 py-2">Max Fav.</th>
                      <th className="px-2 py-2">Flow Score</th>
                      <th className="px-2 py-2">Regime</th>
                      <th className="px-2 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {vetoInsights.blocks.map((block, i) => {
                      const isPnlPositive = block.what_if_pnl > 0;
                      const isShort = block.direction === 'DOWN' || block.direction === 'SHORT';
                      return (
                        <tr key={block.id || i} className={`${isPnlPositive ? 'bg-red-950/20' : 'bg-gray-900/20'} hover:bg-gray-800/30`}>
                          <td className="px-2 py-1.5 text-gray-400 whitespace-nowrap">
                            {block.timestamp ? new Date(block.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '--'}
                          </td>
                          <td className="px-2 py-1.5">
                            <span className={`font-medium ${block.symbol === 'NIFTY' ? 'text-cyan-400' : 'text-purple-400'}`}>
                              {block.symbol}
                            </span>
                          </td>
                          <td className="px-2 py-1.5">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${isShort ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                              {block.proposed_signal}
                            </span>
                          </td>
                          <td className="px-2 py-1.5 font-mono">
                            <span className={isShort ? 'text-red-400' : 'text-green-400'}>
                              {block.raw_score >= 0 ? '+' : ''}{block.raw_score}
                            </span>
                            <span className="text-gray-600 text-[10px]">/{block.gate_threshold}</span>
                          </td>
                          <td className="px-2 py-1.5 text-amber-400 truncate max-w-[100px]" title={block.veto_gate}>
                            {block.veto_gate?.replace(/_/g, ' ') || '?'}
                          </td>
                          <td className="px-2 py-1.5 text-gray-400 truncate max-w-[180px]" title={block.veto_reason}>
                            {block.veto_reason}
                          </td>
                          <td className="px-2 py-1.5 text-white font-mono">
                            {block.price_at_block?.toLocaleString('en-IN')}
                          </td>
                          <td className={`px-2 py-1.5 font-mono font-bold ${isPnlPositive ? 'text-amber-400' : 'text-green-400'}`}>
                            {block.what_if_pnl >= 0 ? '+' : ''}{block.what_if_pnl?.toFixed(1)}
                          </td>
                          <td className="px-2 py-1.5 font-mono text-amber-400">
                            +{block.max_favorable?.toFixed(1)}
                          </td>
                          <td className="px-2 py-1.5 font-mono text-gray-400">
                            {block.flow_data?.flow_confirmation_score?.toFixed(2) || '--'}
                          </td>
                          <td className="px-2 py-1.5 text-gray-400">
                            {block.market_regime}
                          </td>
                          <td className="px-2 py-1.5">
                            {block.resolved ? (
                              <span className="text-gray-500 text-[10px]">Resolved</span>
                            ) : (
                              <span className="text-red-400 text-[10px] animate-pulse">Active</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Apr-2026 Session B: Strategy Fire Rates — standalone panel so it shows even when no vetoes yet today */}
      {strategyStats?.strategies && Object.keys(strategyStats.strategies).length > 0 && (
        <div className="bg-gray-800/40 border border-gray-700/50 rounded-lg p-4" data-testid="strategy-fire-rates-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Flame className="w-4 h-4 text-orange-400" />
              <h3 className="font-semibold text-white">Strategy Fire Rates <span className="text-xs text-gray-400 font-normal">(Today · live · {strategyStats.session_date})</span></h3>
            </div>
            <span className="text-[10px] text-gray-500">
              Updated {strategyStats.as_of ? new Date(strategyStats.as_of).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' }) : ''} IST
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="strategy-fire-rates-grid">
            {Object.entries(strategyStats.strategies).map(([key, s]) => {
              if (s.error) return (
                <div key={key} className="bg-red-950/20 rounded px-3 py-2 border border-red-700/30" data-testid={`strategy-row-${key}`}>
                  <div className="text-xs text-red-300">{s.label || key}</div>
                  <div className="text-[10px] text-red-400 mt-1">err: {String(s.error).slice(0, 60)}</div>
                </div>
              );
              const fired = s.signals_fired || 0;
              const vetoed = s.signals_vetoed || 0;
              const ign = s.ignitions || (fired + vetoed);
              const rate = s.fire_rate_pct || 0;
              const problematic = vetoed > 100 && fired === 0;
              const rateColor = problematic ? 'text-red-400'
                : rate >= 30 ? 'text-green-400'
                : rate >= 10 ? 'text-amber-400'
                : rate > 0 ? 'text-amber-500'
                : 'text-gray-500';
              return (
                <div key={key}
                     className={`rounded px-3 py-2 border ${problematic ? 'bg-red-950/30 border-red-700/40' : 'bg-gray-900/40 border-gray-700/30'}`}
                     data-testid={`strategy-row-${key}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-200 font-semibold">{s.label || key}</span>
                    <span className={`text-sm font-bold ${rateColor}`} data-testid={`strategy-fire-rate-${key}`}>{rate.toFixed(1)}%</span>
                  </div>
                  <div className="flex items-center gap-3 text-[10px] text-gray-400">
                    <span>Ign: <span className="text-gray-200 font-medium">{ign}</span></span>
                    <span>Fired: <span className="text-green-400 font-medium">{fired}</span></span>
                    <span>Vetoed: <span className="text-red-400 font-medium">{vetoed}</span></span>
                  </div>
                  {s.notes && (
                    <div className="text-[9px] text-gray-600 mt-1 italic truncate" title={s.notes}>{s.notes}</div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-3 text-[10px] text-gray-500 flex items-center gap-4 flex-wrap">
            <span><span className="text-green-400 font-semibold">Green</span> = ≥30% fire rate</span>
            <span><span className="text-amber-400 font-semibold">Amber</span> = 10–30%</span>
            <span><span className="text-red-400 font-semibold">Red</span> = &lt;10% or &gt;100 vetoed with 0 fired</span>
            <span className="ml-auto text-gray-600">Ign = ignitions detected · Fired = signals broadcast · Vetoed = blocked by gate</span>
          </div>
        </div>
      )}

      {/* Apr-2026 Session C: Time-of-Day Heatmap — visualises when during the session vetoes cluster */}
      {vetoInsights?.by_time_of_day?.length > 0 && (() => {
        const buckets = vetoInsights.by_time_of_day;
        const maxMissed = Math.max(...buckets.map(b => b.missed_pnl || 0), 1);
        const maxCount = Math.max(...buckets.map(b => b.count || 0), 1);
        const totalMissed = buckets.reduce((s, b) => s + (b.missed_pnl || 0), 0);
        const totalBlocks = buckets.reduce((s, b) => s + (b.count || 0), 0);
        // Top 3 buckets by missed_pnl for call-out
        const topBuckets = [...buckets]
          .filter(b => b.missed_pnl > 0)
          .sort((a, b) => b.missed_pnl - a.missed_pnl)
          .slice(0, 3);
        return (
          <div className="bg-gray-800/40 border border-gray-700/50 rounded-lg p-4" data-testid="time-of-day-heatmap-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-cyan-400" />
                <h3 className="font-semibold text-white">Time-of-Day Heatmap <span className="text-xs text-gray-400 font-normal">({vetoInsights.window_label || 'Today'}{vetoInsights.symbol_filter ? ` · ${vetoInsights.symbol_filter}` : ''})</span></h3>
              </div>
              <div className="text-[10px] text-gray-500 flex gap-3">
                <span>Total blocks: <span className="text-gray-200 font-semibold">{totalBlocks}</span></span>
                <span>Missed P&L: <span className={`font-semibold ${totalMissed > 0 ? 'text-amber-400' : 'text-gray-200'}`}>+{totalMissed.toFixed(0)} pts</span></span>
              </div>
            </div>

            {/* Heatmap grid — 25 cells in one row on desktop, wraps on mobile */}
            <div className="grid grid-cols-5 sm:grid-cols-13 lg:grid-cols-25 gap-[3px]" style={{gridTemplateColumns: 'repeat(25, minmax(0, 1fr))'}} data-testid="time-of-day-heatmap-grid">
              {buckets.map(b => {
                // Color intensity:
                //  - If missed_pnl > 0: red scale (darker = more missed)
                //  - If count > 0 but no missed_pnl: amber (neutral activity)
                //  - If count == 0: gray
                let bg = 'bg-gray-800/50';
                let intensity = 0;
                if (b.missed_pnl > 0) {
                  intensity = Math.min(1, b.missed_pnl / maxMissed);
                  // Map intensity to tailwind red shade
                  if (intensity > 0.75) bg = 'bg-red-600';
                  else if (intensity > 0.5) bg = 'bg-red-700';
                  else if (intensity > 0.25) bg = 'bg-red-800';
                  else bg = 'bg-red-900/70';
                } else if (b.count > 0) {
                  const cIntensity = Math.min(1, b.count / maxCount);
                  if (cIntensity > 0.5) bg = 'bg-amber-700/60';
                  else bg = 'bg-amber-900/50';
                }
                const tooltip = b.count === 0
                  ? `${b.bucket_label} — no activity`
                  : `${b.bucket_label}\nBlocks: ${b.count} (${b.long}L / ${b.short}S)\nMissed P&L: +${b.missed_pnl.toFixed(1)} pts\nAccuracy: ${b.accuracy_pct}%\nTop gate: ${b.dominant_gate || '—'}\nTop strategy: ${b.dominant_source || '—'}`;
                return (
                  <div
                    key={b.bucket_idx}
                    className={`aspect-square rounded-sm cursor-help transition-transform hover:scale-110 hover:z-10 relative ${bg}`}
                    title={tooltip}
                    data-testid={`tod-bucket-${b.bucket_idx}`}
                    data-count={b.count}
                    data-missed-pnl={b.missed_pnl}
                  >
                    {b.count > 0 && (
                      <span className="absolute inset-0 flex items-center justify-center text-[9px] font-semibold text-white/90">
                        {b.count}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* X-axis labels (every 3rd bucket: 09:15, 10:00, 10:45, 11:30, 12:15, 13:00, 13:45, 14:30, 15:15) */}
            <div className="grid gap-[3px] mt-1" style={{gridTemplateColumns: 'repeat(25, minmax(0, 1fr))'}}>
              {buckets.map((b, i) => (
                <div key={i} className="text-[8px] text-gray-500 text-center truncate">
                  {i % 3 === 0 ? b.bucket_label : ''}
                </div>
              ))}
            </div>

            {/* Top 3 highest-miss buckets — call-out */}
            {topBuckets.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-700/50">
                <div className="text-[10px] text-gray-400 mb-2 font-medium">⚠ Highest-miss windows (where gates cost us most)</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {topBuckets.map(b => (
                    <div key={b.bucket_idx} className="bg-red-950/30 border border-red-700/30 rounded px-3 py-2 text-xs" data-testid={`top-miss-${b.bucket_idx}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-red-300">{b.bucket_label} IST</span>
                        <span className="text-amber-400 font-bold">+{b.missed_pnl.toFixed(0)} pts</span>
                      </div>
                      <div className="text-[10px] text-gray-500 mt-1">
                        {b.count} blocks · {b.dominant_gate?.replace(/_/g, ' ') || '—'} · {b.dominant_source || '—'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-3 text-[10px] text-gray-500 flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-gray-800/50 border border-gray-700"></span>No activity</span>
              <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-amber-900/50"></span>Blocks, no miss</span>
              <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-red-900/70"></span>Low miss</span>
              <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-red-600"></span>High miss</span>
              <span className="ml-auto text-gray-600">Hover a cell for details · Size of number = block count</span>
            </div>
          </div>
        );
      })()}

      {/* Apr-2026 Session E: Gate Tuning Simulator — what-if slider for data-driven gate changes */}
      <div className="bg-gray-800/40 border border-gray-700/50 rounded-lg p-4" data-testid="simulator-panel">
        <div className="flex items-center gap-2 mb-3">
          <FlaskConical className="w-4 h-4 text-violet-400" />
          <h3 className="font-semibold text-white">Gate Tuning Simulator <span className="text-xs text-gray-400 font-normal">(what-if · {days === 1 ? 'Today' : `Last ${days} Days`}{selectedSymbol ? ` · ${selectedSymbol}` : ''})</span></h3>
        </div>

        {/* Scenario selector */}
        <div className="flex gap-2 mb-3" data-testid="simulator-scenarios">
          {[
            { id: 'god_score_override', label: 'God Score Override' },
            { id: 'min_time_of_day', label: 'Delay Entry Window' },
            { id: 'gate_off', label: 'Disable Gate' },
          ].map(s => (
            <button
              key={s.id}
              onClick={() => setSimScenario(s.id)}
              className={`text-xs px-3 py-1 rounded border transition ${simScenario === s.id ? 'bg-violet-900/40 border-violet-500 text-violet-200' : 'bg-gray-900/40 border-gray-700 text-gray-400 hover:border-gray-500'}`}
              data-testid={`sim-scenario-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Scenario-specific controls */}
        <div className="bg-gray-900/40 border border-gray-700/30 rounded p-3 mb-3">
          {simScenario === 'god_score_override' && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-gray-300 font-medium" htmlFor="sim-threshold-slider">Override threshold: |raw_score| ≥</label>
                <span className="text-lg font-bold text-violet-300" data-testid="sim-threshold-value">±{simThreshold}</span>
              </div>
              <input
                id="sim-threshold-slider"
                type="range" min="70" max="100" step="1"
                value={simThreshold}
                onChange={e => setSimThreshold(parseInt(e.target.value, 10))}
                className="w-full accent-violet-500"
                data-testid="sim-threshold-slider"
              />
              <div className="flex justify-between text-[9px] text-gray-500 mt-1">
                <span>70 (aggressive)</span>
                <span>85 (balanced)</span>
                <span>100 (off — current prod)</span>
              </div>
              <p className="text-[10px] text-gray-500 mt-2 italic">
                Only applies to ENTRY_RULE flow-confirmation vetoes. Lower threshold = more signals fire.
              </p>
            </div>
          )}
          {simScenario === 'min_time_of_day' && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-gray-300 font-medium" htmlFor="sim-bucket-slider">Earliest entry bucket</label>
                <span className="text-lg font-bold text-violet-300" data-testid="sim-bucket-value">
                  {String(9 + Math.floor((15 + simMinBucket * 15) / 60)).padStart(2, '0')}
                  :{String((15 + simMinBucket * 15) % 60).padStart(2, '0')} IST
                </span>
              </div>
              <input
                id="sim-bucket-slider"
                type="range" min="0" max="12" step="1"
                value={simMinBucket}
                onChange={e => setSimMinBucket(parseInt(e.target.value, 10))}
                className="w-full accent-violet-500"
                data-testid="sim-bucket-slider"
              />
              <div className="flex justify-between text-[9px] text-gray-500 mt-1">
                <span>09:15 (no delay)</span>
                <span>10:15 (IB complete)</span>
                <span>12:15 (post-lunch)</span>
              </div>
              <p className="text-[10px] text-gray-500 mt-2 italic">
                Shows which blocks BEFORE the window would be avoided if entries were delayed. Useful for validating the context-lock rule.
              </p>
            </div>
          )}
          {simScenario === 'gate_off' && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-gray-300 font-medium" htmlFor="sim-gate-select">Disable veto gate</label>
              </div>
              <select
                id="sim-gate-select"
                value={simGateName}
                onChange={e => setSimGateName(e.target.value)}
                className="w-full bg-gray-900 border border-gray-600 rounded px-2 py-1.5 text-xs text-gray-200"
                data-testid="sim-gate-select"
              >
                {Object.keys(vetoInsights?.by_gate || {ENTRY_RULE: {}}).map(g => (
                  <option key={g} value={g}>{g.replace(/_/g, ' ')}</option>
                ))}
              </select>
              <p className="text-[10px] text-gray-500 mt-2 italic">
                Shows the projected P&L if this veto gate were turned off entirely. Use with caution — gates exist for a reason.
              </p>
            </div>
          )}
        </div>

        {/* Projection result */}
        {simLoading && (
          <div className="text-xs text-gray-500 text-center py-4" data-testid="simulator-loading">Running simulation…</div>
        )}
        {!simLoading && simResult?.success && (
          <div data-testid="simulator-result">
            <div className="text-[10px] text-gray-400 mb-2 uppercase tracking-wider">{simResult.scenario_label}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
              <div className="bg-gray-900/60 rounded px-3 py-2" data-testid="sim-captured-count">
                <div className="text-[10px] text-gray-400">Signals captured</div>
                <div className="text-lg font-bold text-violet-300">{simResult.projection.captured_count}</div>
                <div className="text-[10px] text-gray-500">of {simResult.total_blocks_considered} blocks ({simResult.projection.captured_pct_of_blocks}%)</div>
              </div>
              <div className="bg-gray-900/60 rounded px-3 py-2" data-testid="sim-captured-pnl">
                <div className="text-[10px] text-gray-400">Projected P&L</div>
                <div className={`text-lg font-bold ${simResult.projection.total_captured_pnl > 0 ? 'text-green-400' : simResult.projection.total_captured_pnl < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                  {simResult.projection.total_captured_pnl > 0 ? '+' : ''}{simResult.projection.total_captured_pnl} pts
                </div>
                <div className="text-[10px] text-gray-500">avg {simResult.projection.avg_pnl_per_signal}/signal</div>
              </div>
              <div className="bg-gray-900/60 rounded px-3 py-2" data-testid="sim-win-rate">
                <div className="text-[10px] text-gray-400">Win rate</div>
                <div className={`text-lg font-bold ${simResult.projection.win_rate_pct >= 60 ? 'text-green-400' : simResult.projection.win_rate_pct >= 40 ? 'text-amber-400' : 'text-red-400'}`}>
                  {simResult.projection.win_rate_pct}%
                </div>
                <div className="text-[10px] text-gray-500">{simResult.projection.winners}W / {simResult.projection.losers}L</div>
              </div>
              <div className="bg-gray-900/60 rounded px-3 py-2">
                <div className="text-[10px] text-gray-400">Best / Worst</div>
                <div className="text-xs">
                  <span className="text-green-400 font-bold">+{simResult.projection.max_win}</span>
                  <span className="text-gray-500 mx-1">/</span>
                  <span className="text-red-400 font-bold">{simResult.projection.max_loss}</span>
                </div>
                <div className="text-[10px] text-gray-500">pts</div>
              </div>
            </div>
            {simResult.current_value_note && (
              <div className="text-[10px] text-gray-500 italic mb-2">ℹ {simResult.current_value_note}</div>
            )}
            {simResult.sample_captures?.length > 0 && (
              <div>
                <div className="text-[10px] text-gray-400 mb-1 uppercase tracking-wider">Top captures (by P&L)</div>
                <div className="space-y-1">
                  {simResult.sample_captures.map((s, i) => (
                    <div key={i} className="bg-gray-900/40 border border-gray-700/30 rounded px-3 py-1.5 flex items-center justify-between text-[11px]" data-testid={`sim-sample-${i}`}>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-200">{s.symbol}</span>
                        <span className={`${s.direction === 'DOWN' || s.direction === 'SHORT' ? 'text-red-300' : 'text-green-300'}`}>{s.direction === 'DOWN' ? 'SHORT' : s.direction === 'UP' ? 'LONG' : s.direction}</span>
                        <span className="text-gray-500">score {s.raw_score > 0 ? '+' : ''}{s.raw_score}</span>
                        <span className="text-gray-600">· {(s.veto_gate || '').replace(/_/g, ' ')}</span>
                      </div>
                      <span className={`font-bold ${s.what_if_pnl > 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {s.what_if_pnl > 0 ? '+' : ''}{s.what_if_pnl} pts
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {simResult.projection.captured_count === 0 && (
              <div className="text-xs text-gray-500 text-center py-3 bg-gray-900/30 rounded" data-testid="sim-empty">
                No blocks would have fired under this rule. Try a different threshold or wait for more data to accumulate.
              </div>
            )}
          </div>
        )}
        {!simLoading && simResult && !simResult.success && (
          <div className="text-xs text-red-400 bg-red-950/30 border border-red-700/30 rounded px-3 py-2" data-testid="simulator-error">
            Simulation error: {simResult.error}
          </div>
        )}
      </div>

      {/* Apr-2026 Session F: Data-Driven Auto-Insights (replaces hard-coded Improvement Suggestions) */}
      {autoInsights?.insights?.length > 0 ? (
        <div className="bg-gradient-to-br from-purple-900/20 to-violet-900/10 border border-purple-500/30 rounded-lg p-4" data-testid="auto-insights-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-yellow-400" />
              <h3 className="font-semibold text-white">Auto-Insights <span className="text-xs text-gray-400 font-normal">(quantitative · {days === 1 ? 'Today' : `Last ${days}d`}{selectedSymbol ? ` · ${selectedSymbol}` : ''})</span></h3>
            </div>
            <span className="text-[10px] text-gray-500">{autoInsights.insights.length} insight{autoInsights.insights.length > 1 ? 's' : ''}</span>
          </div>
          <div className="space-y-2">
            {autoInsights.insights.map((ins, i) => {
              const severityColor =
                ins.severity === 'high' ? 'border-red-500/40 bg-red-950/20' :
                ins.severity === 'medium' ? 'border-amber-500/40 bg-amber-950/20' :
                'border-gray-600/40 bg-gray-900/30';
              const severityBadge =
                ins.severity === 'high' ? 'bg-red-900/50 text-red-300' :
                ins.severity === 'medium' ? 'bg-amber-900/50 text-amber-300' :
                'bg-gray-700/50 text-gray-400';
              return (
                <div key={i} className={`rounded px-3 py-2 border ${severityColor}`} data-testid={`auto-insight-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase font-bold tracking-wider ${severityBadge}`}>{ins.severity}</span>
                        <span className="text-sm text-gray-100 font-medium">{ins.headline}</span>
                      </div>
                      <div className="text-[11px] text-gray-400 mt-1 ml-1">{ins.evidence}</div>
                    </div>
                    {ins.action && (
                      <button
                        className="text-[11px] px-2 py-1 rounded bg-violet-900/40 border border-violet-500/50 text-violet-200 hover:bg-violet-800/50 transition whitespace-nowrap"
                        onClick={() => {
                          if (ins.action.scenario) setSimScenario(ins.action.scenario);
                          if (ins.action.threshold != null) setSimThreshold(ins.action.threshold);
                          if (ins.action.min_bucket_idx != null) setSimMinBucket(ins.action.min_bucket_idx);
                          if (ins.action.gate_name) setSimGateName(ins.action.gate_name);
                          // Scroll to simulator panel
                          setTimeout(() => {
                            document.querySelector('[data-testid="simulator-panel"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }, 100);
                        }}
                        data-testid={`auto-insight-action-${i}`}
                      >
                        {ins.action.label || 'Run in Simulator'} →
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-[10px] text-gray-500 mt-3 italic">
            Data-driven insights refresh every 30s. Each insight cites real counts · click any action to auto-load the Simulator with matching params.
          </div>
        </div>
      ) : (
        <div className="bg-gradient-to-br from-purple-900/10 to-violet-900/5 border border-purple-500/20 rounded-lg p-4" data-testid="auto-insights-empty">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="w-5 h-5 text-yellow-400/60" />
            <h3 className="font-semibold text-gray-300">Auto-Insights <span className="text-xs text-gray-500 font-normal">(quantitative · {days === 1 ? 'Today' : `Last ${days}d`}{selectedSymbol ? ` · ${selectedSymbol}` : ''})</span></h3>
          </div>
          <p className="text-xs text-gray-400 ml-7">
            Gathering today's data — insights will populate after the first veto fires (typically 9:30–10:00 IST). Each insight cites real counts and links to the Simulator.
          </p>
        </div>
      )}

      {/* Recent Signals Table */}
      <div className="bg-gray-800/50 rounded-lg border border-gray-700">
        <div className="p-4 border-b border-gray-700">
          <h3 className="font-semibold text-white">Recent Signals</h3>
        </div>
        <div className="overflow-x-auto">
          {recentSignals.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <Brain className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No signals recorded yet.</p>
              <p className="text-sm mt-1">Signals will appear here as they are generated during trading.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-900/50">
                <tr className="text-left text-gray-400">
                  <th className="px-4 py-3 w-8"></th>
                  <th className="px-4 py-3">Time</th>
                  <th className="px-4 py-3">Symbol</th>
                  <th className="px-4 py-3">Direction</th>
                  <th className="px-4 py-3">Entry</th>
                  <th className="px-4 py-3">T1 / T2</th>
                  <th className="px-4 py-3">SL</th>
                  <th className="px-4 py-3">Score</th>
                  <th className="px-4 py-3">Context</th>
                  <th className="px-4 py-3">State</th>
                  <th className="px-4 py-3">P&L</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {recentSignals.map((signal, i) => (
                  <SignalRow key={i} signal={signal} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* No Data Message */}
      {stats?.total_signals === 0 && (
        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-6 text-center">
          <Brain className="w-16 h-16 mx-auto mb-4 text-blue-400 opacity-50" />
          <h3 className="text-lg font-semibold text-white mb-2">Collecting Data...</h3>
          <p className="text-gray-400 max-w-md mx-auto">
            Signal Intelligence is now active. As trading signals are generated, 
            they will be logged here with full breakdowns for analysis.
          </p>
          <p className="text-gray-500 text-sm mt-3">
            Check back after a few trading sessions to see patterns and suggestions.
          </p>
        </div>
      )}
    </div>
  );
};


// ============================================
// SETTINGS TAB
// ============================================
const SettingsTab = () => {
  // Greeks-only architecture: Single token for Greeks API
  const [greeksToken, setGreeksToken] = useState('');
  const [maskedGreeksToken, setMaskedGreeksToken] = useState('');
  const [showGreeksToken, setShowGreeksToken] = useState(false);
  
  // TrueData Native Authentication (Session 67)
  const [trueDataUsername, setTrueDataUsername] = useState('');
  const [trueDataPassword, setTrueDataPassword] = useState('');
  const [trueDataLicenseKey, setTrueDataLicenseKey] = useState('');
  const [showTrueDataPassword, setShowTrueDataPassword] = useState(false);
  const [trueDataConfigured, setTrueDataConfigured] = useState(false);
  // Session 67c: Track if username has been modified from the masked value
  const [trueDataUsernameMasked, setTrueDataUsernameMasked] = useState('');
  const [trueDataUsernameModified, setTrueDataUsernameModified] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [connectionDetails, setConnectionDetails] = useState(null);
  // Mock mode state removed - system is LIVE ONLY
  const [switchError, setSwitchError] = useState(false);
  const [showAllKeys, setShowAllKeys] = useState(false);
  const [allKeys, setAllKeys] = useState(null);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [deletingKeys, setDeletingKeys] = useState(false);

  // Get auth token from localStorage
  const getAuthHeaders = () => {
    const authToken = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': authToken ? `Bearer ${authToken}` : '',
    };
  };

  const fetchAllKeys = async () => {
    setLoadingKeys(true);
    setSelectedKeys([]);
    try {
      const res = await fetch(`${API}/admin/settings/all-keys`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      setAllKeys(data);
      setShowAllKeys(true);
    } catch (error) {
      console.error('Failed to fetch keys:', error);
      setError(error.message);
    }
    setLoadingKeys(false);
  };

  const toggleKeySelection = (field) => {
    setSelectedKeys(prev => 
      prev.includes(field) 
        ? prev.filter(f => f !== field)
        : [...prev, field]
    );
  };

  const handleDeleteSelectedKeys = async () => {
    if (selectedKeys.length === 0) {
      setError('Please select at least one key to delete');
      return;
    }
    
    const keyNames = allKeys.keys
      .filter(k => selectedKeys.includes(k.field))
      .map(k => k.name)
      .join(', ');
    
    if (!window.confirm(`Are you sure you want to delete: ${keyNames}?\n\nThis action cannot be undone.`)) {
      return;
    }
    
    setDeletingKeys(true);
    setError(null);
    try {
      const res = await fetch(`${API}/admin/settings/delete-keys`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ fields: selectedKeys }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${res.status}`);
      }
      const result = await res.json();
      
      // Clear masked token if Greeks token was deleted
      if (result.deleted && result.deleted.includes('accelpix_greeks_token')) {
        setMaskedGreeksToken('');
      }
      
      // Session 67c: Clear TrueData state if credentials were deleted
      if (result.deleted) {
        if (result.deleted.includes('truedata_username') || result.deleted.includes('truedata_password')) {
          setTrueDataConfigured(false);
          setTrueDataUsername('');
          setTrueDataUsernameMasked('');
          setTrueDataUsernameModified(false);
        }
      }
      
      // Clear connection status
      setConnectionStatus(null);
      setConnectionDetails(null);
      
      // Refresh the keys list and settings
      setSelectedKeys([]);
      await fetchAllKeys();
      await fetchSettings();
    } catch (error) {
      console.error('Failed to delete keys:', error);
      setError(error.message);
    }
    setDeletingKeys(false);
  };

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/settings`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      // Always update the masked token - clear it if not present
      setMaskedGreeksToken(data.accelpix_greeks_token_masked || '');
      // TrueData credentials status
      setTrueDataConfigured(data.truedata_configured || false);
      if (data.truedata_username_masked) {
        // Session 67c: Store masked version separately, clear the editable field
        setTrueDataUsernameMasked(data.truedata_username_masked);
        setTrueDataUsername(''); // Clear editable field
        setTrueDataUsernameModified(false); // Reset modified flag
      } else {
        setTrueDataUsernameMasked('');
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      setError(error.message);
    }
  }, []);

  // fetchDataSource removed - LIVE ONLY mode, no data source switching
  // Legacy function kept but made safe
  const fetchDataSource = useCallback(async () => {
    try {
      // Data source is now always LIVE, no need to fetch/set
      console.log('Data source: LIVE (TrueData)');
    } catch (error) {
      console.error('Failed to fetch data source:', error);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchDataSource();
  }, [fetchSettings, fetchDataSource]);

  const handleSave = async () => {
    if (!greeksToken.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const updateData = {};
      if (greeksToken.trim()) updateData.accelpix_greeks_token = greeksToken.trim();
      
      const res = await fetch(`${API}/admin/settings`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(updateData),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${res.status}`);
      }
      setSaved(true);
      setGreeksToken('');
      fetchSettings();
      // Clear connection status since token changed
      setConnectionStatus(null);
      setConnectionDetails(null);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setError(error.message);
    }
    setLoading(false);
  };

  const handleRemoveToken = async () => {
    if (!window.confirm('Are you sure you want to remove the Greeks API token? This will disable live data.')) {
      return;
    }
    
    setRemoving(true);
    setError(null);
    try {
      const res = await fetch(`${API}/admin/settings/greeks-token`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${res.status}`);
      }
      setMaskedGreeksToken('');
      setGreeksToken('');
      setConnectionStatus(null);
      setConnectionDetails(null);
      fetchSettings();
    } catch (error) {
      console.error('Failed to remove token:', error);
      setError(error.message);
    }
    setRemoving(false);
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus(null);
    setConnectionDetails(null);
    setError(null);
    try {
      const res = await fetch(`${API}/admin/test-greeks-api`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      setConnectionDetails(data);
      if (data.success) {
        setConnectionStatus('success');
      } else {
        setConnectionStatus('error');
        setError(data.message || data.error || 'Connection test failed');
      }
    } catch (error) {
      setConnectionStatus('error');
      setConnectionDetails({ error: 'NETWORK_ERROR', message: error.message });
      setError(`Network Error: ${error.message}`);
    }
    setTestingConnection(false);
  };

  // toggleDataSource function REMOVED - Mock mode permanently disabled
  // System operates in LIVE ONLY mode

  // TrueData Native Authentication Handlers (Session 67)
  const handleSaveTrueData = async () => {
    // Session 67c: Validate based on whether this is new config or update
    const isNewConfig = !trueDataConfigured && !trueDataUsernameMasked;
    const hasNewUsername = trueDataUsername.trim().length > 0;
    const hasNewPassword = trueDataPassword.trim().length > 0;
    
    // For new config: require both username and password
    // For update: require at least one (username OR password)
    if (isNewConfig) {
      if (!hasNewUsername || !hasNewPassword) {
        setError('Both username and password are required for initial setup');
        return;
      }
    } else {
      // For updates, must have password (always required) and optionally new username
      if (!hasNewPassword) {
        setError('Password is required');
        return;
      }
      if (!hasNewUsername && !trueDataUsernameMasked) {
        setError('Username is required');
        return;
      }
    }
    
    setLoading(true);
    setError(null);
    setSaved(false);
    try {
      const updateData = {
        // Session 67c: Only send new username if user modified it, otherwise backend keeps existing
        truedata_username: hasNewUsername ? trueDataUsername.trim() : '__KEEP_EXISTING__',
        truedata_password: trueDataPassword.trim(),
      };
      if (trueDataLicenseKey.trim()) {
        updateData.truedata_license_key = trueDataLicenseKey.trim();
      }
      
      const res = await fetch(`${API}/admin/settings/truedata`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(updateData),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${res.status}`);
      }
      const result = await res.json();
      setSaved(true);
      setTrueDataPassword(''); // Clear password after save
      setTrueDataUsername(''); // Clear username field
      setTrueDataUsernameModified(false);
      setTrueDataConfigured(true);
      setConnectionStatus(result.connection_test ? 'success' : 'warning');
      if (result.message) {
        setConnectionDetails({ message: result.message });
      }
      fetchSettings();
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Failed to save TrueData credentials:', error);
      setError(error.message);
      setConnectionStatus('error');
    }
    setLoading(false);
  };

  const handleTestTrueDataConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus(null);
    setConnectionDetails(null);
    setError(null);
    try {
      const res = await fetch(`${API}/admin/test-truedata`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      setConnectionDetails(data);
      if (data.success) {
        setConnectionStatus('success');
      } else {
        setConnectionStatus('error');
        setError(data.message || data.error || 'Connection test failed');
      }
    } catch (error) {
      setConnectionStatus('error');
      setConnectionDetails({ error: 'NETWORK_ERROR', message: error.message });
      setError(`Network Error: ${error.message}`);
    }
    setTestingConnection(false);
  };

  return (
    <div className="space-y-6" data-testid="settings-tab">
      <div>
        <h2 className="font-heading text-2xl font-bold text-foreground mb-1">Settings</h2>
        <p className="text-sm text-muted-foreground">Configure application settings and API credentials.</p>
      </div>

      {/* Data Source Status - LIVE ONLY (Mock Mode Permanently Removed) */}
      <div className="panel p-6 border-2 border-accent">
        <h3 className="font-heading text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Activity className="w-5 h-5 text-accent" />
          Data Source & API Mode
        </h3>
        
        {/* TrueData Architecture Notice */}
        <div className="mb-6 p-4 bg-blue-500/10 rounded border border-blue-500/30">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-3 h-3 rounded-full bg-blue-500" />
            <span className="font-semibold text-blue-400">TrueData Native Feed</span>
          </div>
          <p className="text-xs text-muted-foreground">
            System uses TrueData native authentication for all market data including:
          </p>
          <ul className="text-xs text-muted-foreground mt-1 ml-4 list-disc">
            <li>Real-time tick data (Futures, Options)</li>
            <li>Live Greeks (Gamma, Delta, Vega, IV)</li>
            <li>Open Interest updates (real-time)</li>
            <li>Spot index prices</li>
          </ul>
          <p className="text-xs text-blue-400 mt-2">
            Provider: <code>TrueData WebSocket API</code>
          </p>
        </div>
        
        {/* Data Source Status - LIVE ONLY */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-foreground mb-1">
              Mode: <span className="font-bold text-green-400">● LIVE ONLY</span>
              <span className="ml-2 text-xs text-blue-400">via TrueData</span>
            </p>
            <p className="text-xs text-muted-foreground">
              Real-time data from TrueData. Mock mode permanently disabled.
            </p>
          </div>
          <div className="px-4 py-2 bg-green-500/20 border border-green-500/50 rounded text-green-400 text-sm font-bold">
            LIVE ONLY
          </div>
        </div>
        
        {/* Mock Mode Removed Notice */}
        <div className="mt-4 p-3 bg-slate-500/10 border border-slate-500/30 rounded">
          <p className="text-xs text-muted-foreground">
            ⚠️ Mock/Simulation mode has been permanently removed from this system.
            When market is closed or feed is disconnected, data will freeze at last known values.
          </p>
        </div>
        
        {/* Legacy Error Display (kept for any API errors) */}
        {switchError && (
          <div className="mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-red-400">{switchError.error || 'Connection Failed'}</p>
                <p className="text-sm text-red-300 mt-1">{switchError.message}</p>
                {switchError.help && (
                  <p className="text-xs text-muted-foreground mt-2">{switchError.help}</p>
                )}
                {switchError.troubleshooting && (
                  <div className="mt-3 p-3 bg-surface rounded text-xs space-y-1">
                    <p className="font-semibold text-foreground">Troubleshooting Steps:</p>
                    {switchError.troubleshooting.map((step, i) => (
                      <p key={i} className="text-muted-foreground">{step}</p>
                    ))}
                  </div>
                )}
              </div>
              <button 
                onClick={() => setSwitchError(null)} 
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Feed Control - For Dev/Prod Token Management */}
      <FeedControlSection getAuthHeaders={getAuthHeaders} />

      <div className="panel p-6">
        <h3 className="font-heading text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5 text-accent" />
          TrueData Feed Configuration
        </h3>
        
        {/* Connection Status Summary */}
        <div className="mb-6 p-4 bg-surface rounded border border-border">
          <div className="text-sm font-semibold text-foreground mb-2">Connection Status:</div>
          <div className="flex items-center gap-2 text-sm">
            <div className={`w-2 h-2 rounded-full ${trueDataConfigured ? 'bg-buy' : 'bg-warning'}`} />
            <span className="text-muted-foreground">TrueData:</span>
            <span className={trueDataConfigured ? 'text-buy' : 'text-warning'}>
              {trueDataConfigured ? 'Configured' : 'Not configured'}
            </span>
          </div>
        </div>
        
        <div className="space-y-4">
          {/* TrueData Credentials Section */}
          <div className="space-y-4 p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded bg-blue-500/20 flex items-center justify-center">
                <Settings className="w-4 h-4 text-blue-400" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground">TrueData Native Authentication</h4>
                <p className="text-xs text-muted-foreground">Real-time market data with native credentials</p>
              </div>
            </div>
            
            {/* Username */}
            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground">
                TrueData Username
              </label>
              {/* Session 67c: Show masked username if configured, with option to change */}
              {trueDataUsernameMasked && !trueDataUsernameModified ? (
                <div className="flex items-center gap-2 max-w-md">
                  <div className="flex-1 bg-surface border border-border px-4 py-2.5 text-sm text-foreground rounded flex items-center justify-between">
                    <span className="font-mono">{trueDataUsernameMasked}</span>
                    <span className="text-green-400 text-xs">✓ Saved</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setTrueDataUsernameModified(true);
                      setTrueDataUsername('');
                    }}
                    className="px-3 py-2 text-xs text-blue-400 hover:text-blue-300 border border-blue-500/30 rounded hover:border-blue-500/50"
                    data-testid="change-username-btn"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <input
                  type="text"
                  value={trueDataUsername}
                  onChange={(e) => {
                    setTrueDataUsername(e.target.value);
                    setTrueDataUsernameModified(true);
                  }}
                  placeholder={trueDataUsernameMasked ? "Enter new username..." : "Enter TrueData username..."}
                  className="w-full max-w-md bg-surface border border-border px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-blue-500 rounded"
                  data-testid="truedata-username-input"
                />
              )}
              {trueDataUsernameModified && trueDataUsernameMasked && (
                <button
                  type="button"
                  onClick={() => {
                    setTrueDataUsernameModified(false);
                    setTrueDataUsername('');
                  }}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Cancel change
                </button>
              )}
            </div>
            
            {/* Password */}
            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground">
                TrueData Password
              </label>
              <div className="relative max-w-md">
                <input
                  type={showTrueDataPassword ? 'text' : 'password'}
                  value={trueDataPassword}
                  onChange={(e) => setTrueDataPassword(e.target.value)}
                  placeholder="Enter TrueData password..."
                  className="w-full bg-surface border border-border px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-blue-500 rounded pr-10"
                  data-testid="truedata-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowTrueDataPassword(!showTrueDataPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showTrueDataPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            {/* License Key / App ID (Optional) */}
            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground">
                License Key / App ID <span className="text-muted-foreground text-xs">(Optional)</span>
              </label>
              <input
                type="text"
                value={trueDataLicenseKey}
                onChange={(e) => setTrueDataLicenseKey(e.target.value)}
                placeholder="Enter license key (optional)..."
                className="w-full max-w-md bg-surface border border-border px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-blue-500 rounded"
                data-testid="truedata-license-input"
              />
              <p className="text-xs text-muted-foreground">For enterprise accounts or custom integrations</p>
            </div>
          </div>

          {/* Save & Reconnect Button */}
          <div className="pt-4 border-t border-border flex items-center gap-3">
            <Button
              onClick={handleSaveTrueData}
              disabled={!trueDataPassword.trim() || loading}
              className="px-6 bg-blue-600 hover:bg-blue-700"
              data-testid="save-truedata-btn"
            >
              {saved ? <Check className="w-4 h-4 mr-2" /> : loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
              {saved ? 'Saved!' : loading ? 'Saving...' : 'Save & Reconnect'}
            </Button>
            
            {trueDataConfigured && (
              <Button
                onClick={handleTestTrueDataConnection}
                disabled={testingConnection}
                variant="outline"
                className="px-6"
                data-testid="test-truedata-connection-btn"
              >
                {testingConnection ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Testing...
                  </>
                ) : (
                  'Test Connection'
                )}
              </Button>
            )}
            
            <Button
              onClick={fetchAllKeys}
              disabled={loadingKeys}
              variant="outline"
              className="px-6"
              data-testid="view-all-keys-btn"
            >
              {loadingKeys ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <Database className="w-4 h-4 mr-2" />
                  View All Keys
                </>
              )}
            </Button>
          </div>
          
          {/* All Keys Panel with Selection */}
          {showAllKeys && allKeys && (
            <div className="mt-4 p-4 bg-surface rounded border border-border">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-foreground flex items-center gap-2">
                  <Database className="w-4 h-4 text-accent" />
                  All API Keys in Database ({allKeys.total_count})
                </h4>
                <button
                  onClick={() => { setShowAllKeys(false); setSelectedKeys([]); }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              {allKeys.keys.length === 0 ? (
                <p className="text-sm text-muted-foreground">No API keys configured in database.</p>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground mb-3">
                    Select keys to delete by clicking on them:
                  </p>
                  <div className="space-y-2">
                    {allKeys.keys.map((key, index) => (
                      <div 
                        key={index} 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleKeySelection(key.field);
                        }}
                        data-testid={`key-card-${key.field}`}
                        className={`p-3 bg-background rounded border cursor-pointer transition-all select-none ${
                          selectedKeys.includes(key.field) 
                            ? 'border-red-500 bg-red-500/10' 
                            : 'border-border hover:border-accent/50'
                        }`}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <div 
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                              selectedKeys.includes(key.field)
                                ? 'border-red-500 bg-red-500'
                                : 'border-muted-foreground'
                            }`}
                          >
                            {selectedKeys.includes(key.field) && (
                              <Check className="w-3 h-3 text-white" />
                            )}
                          </div>
                          <span className="font-medium text-foreground flex-1">{key.name}</span>
                          <Badge 
                            variant={key.status === 'active' ? 'default' : 'secondary'}
                            className={key.status === 'active' ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'}
                          >
                            {key.status}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs ml-8">
                          <div>
                            <span className="text-muted-foreground">Value: </span>
                            <code className="font-mono text-accent">{key.masked_value}</code>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Length: </span>
                            <span className="text-foreground">{key.length} chars</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Field: </span>
                            <code className="font-mono text-muted-foreground">{key.field}</code>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Endpoint: </span>
                            <span className="text-foreground">{key.endpoint}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Delete Selected Button */}
                  <div className="mt-4 pt-3 border-t border-border flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      {selectedKeys.length > 0 
                        ? `${selectedKeys.length} key(s) selected`
                        : 'Click on keys to select them'
                      }
                    </span>
                    <Button
                      onClick={handleDeleteSelectedKeys}
                      disabled={selectedKeys.length === 0 || deletingKeys}
                      variant="destructive"
                      className="px-6"
                      data-testid="delete-selected-keys-btn"
                    >
                      {deletingKeys ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete Selected ({selectedKeys.length})
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
              
              <p className="mt-3 text-xs text-muted-foreground italic">{allKeys.note}</p>
            </div>
          )}
            
            {/* Test Connection Button */}
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex items-center gap-4">
                <Button
                  onClick={handleTestConnection}
                  disabled={testingConnection || !maskedGreeksToken}
                  variant="outline"
                  className="px-6"
                  data-testid="test-accelpix-connection-btn"
                >
                  {testingConnection ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Testing...
                    </>
                  ) : connectionStatus === 'success' ? (
                    <>
                      <Check className="w-4 h-4 mr-2 text-green-500" />
                      Connected!
                    </>
                  ) : connectionStatus === 'error' ? (
                    <>
                      <X className="w-4 h-4 mr-2 text-red-500" />
                      Failed
                    </>
                  ) : (
                    'Test Accelpix Connection'
                  )}
                </Button>
                <span className="text-xs text-muted-foreground">
                  Test if your saved token can connect to Accelpix API
                </span>
              </div>
              
              {/* Connection Test Results */}
              {connectionDetails && (
                <div className={`mt-4 p-4 rounded ${
                  connectionDetails.success 
                    ? 'bg-green-500/10 border border-green-500/30' 
                    : 'bg-red-500/10 border border-red-500/30'
                }`}>
                  <div className="flex items-start gap-3">
                    {connectionDetails.success ? (
                      <Check className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className={`font-semibold ${connectionDetails.success ? 'text-green-400' : 'text-red-400'}`}>
                        {connectionDetails.success ? 'Connection Successful!' : (connectionDetails.error || 'Connection Failed')}
                      </p>
                      <p className={`text-sm mt-1 ${connectionDetails.success ? 'text-green-300' : 'text-red-300'}`}>
                        {connectionDetails.message}
                      </p>
                      {connectionDetails.token_preview && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Token: <code className="font-mono bg-surface px-1">{connectionDetails.token_preview}</code>
                        </p>
                      )}
                      {connectionDetails.help && (
                        <p className="text-xs text-muted-foreground mt-2">{connectionDetails.help}</p>
                      )}
                      {connectionDetails.troubleshooting && (
                        <div className="mt-3 p-3 bg-surface rounded text-xs space-y-1">
                          <p className="font-semibold text-foreground">Troubleshooting Steps:</p>
                          {connectionDetails.troubleshooting.map((step, i) => (
                            <p key={i} className="text-muted-foreground">{step}</p>
                          ))}
                        </div>
                      )}
                    </div>
                    <button 
                      onClick={() => { setConnectionDetails(null); setConnectionStatus(null); }} 
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// SESSION 100: SMLT WATCHLIST TAB
// ============================================
// Manage stock futures watchlist for OI tracking
const SMTLWatchlistTab = () => {
  const [watchlist, setWatchlist] = useState([]);
  const [benchmarks, setBenchmarks] = useState({});  // SESSION 120: Stock -> NIFTY/BANKNIFTY mapping
  const [sectorMapping, setSectorMapping] = useState({});  // SESSION 120: Stock -> Sector mapping
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newSymbol, setNewSymbol] = useState('');
  const [newBenchmark, setNewBenchmark] = useState('NIFTY');  // SESSION 120
  const [newSector, setNewSector] = useState('');  // SESSION 120: Sector selection
  const [addLoading, setAddLoading] = useState(false);
  
  // SESSION 120: Available sectors from NIFTY indices + BANKNIFTY
  const SECTORS = [
    { value: 'NIFTY IT', label: 'IT', color: 'cyan' },
    { value: 'NIFTY PHARMA', label: 'Pharma', color: 'green' },
    { value: 'NIFTY AUTO', label: 'Auto', color: 'orange' },
    { value: 'NIFTY FIN SERVICE', label: 'Fin Service', color: 'purple' },
    { value: 'NIFTY METAL', label: 'Metal', color: 'slate' },
    { value: 'NIFTY FMCG', label: 'FMCG', color: 'yellow' },
    { value: 'NIFTY ENERGY', label: 'Energy', color: 'red' },
    { value: 'NIFTY OIL AND GAS', label: 'Oil & Gas', color: 'amber' },
    { value: 'NIFTY INFRA', label: 'Infra', color: 'blue' },
    { value: 'NIFTY BANK', label: 'Bank Nifty', color: 'indigo' },  // SESSION 120: Added for bank stocks
  ];
  
  // SMLT Engine Configuration State - SESSION 104: Expanded for v2.1
  const [smltConfig, setSmltConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [configSuccess, setConfigSuccess] = useState(null);
  
  // SESSION 120: Sector Filter Configuration State
  const [sectorFilterStatus, setSectorFilterStatus] = useState(null);
  const [sectorFilterConfig, setSectorFilterConfig] = useState({
    threshold_pct: '1.0',
    enabled: true
  });
  const [sectorFilterLoading, setSectorFilterLoading] = useState(false);
  const [sectorFilterSaving, setSectorFilterSaving] = useState(false);
  const [sectorFilterSuccess, setSectorFilterSuccess] = useState(null);
  const [editedConfig, setEditedConfig] = useState({
    // Stage 1: Ignition Detection
    ignition_spike_multiplier: '',
    volume_spike_multiplier: '',
    min_traded_value_lakhs: '',
    warmup_seconds: '',
    // Stage 2: MiniGod Score Weights
    cvd_direction_multiplier: '',
    oi_flow_max_points: '',
    cvd_strength_max_points: '',
    volume_max_points: '',
    price_position_max_points: '',
    index_alignment_max_points: '',
    // SESSION 107: Order Flow Imbalance
    order_flow_max_points: '',
    order_flow_window_seconds: '',
    order_flow_veto_threshold: '',
    // Stage 3: Signal Generation
    buy_threshold: '',
    sell_threshold: '',
    signal_cooldown_seconds: '',
    index_veto_enabled: true,
    index_veto_threshold_pct: '',
    time_filter_enabled: true,
    time_filter_start: '',
    time_filter_end: '',
    v2_enabled: true
  });

  // Predefined suggestions for F&O stocks
  const suggestedStocks = [
    'RELIANCE', 'HDFCBANK', 'ICICIBANK', 'INFY', 'TCS', 'SBIN', 'BAJFINANCE',
    'HINDUNILVR', 'ITC', 'KOTAKBANK', 'AXISBANK', 'LT', 'MARUTI', 'BHARTIARTL',
    'HCLTECH', 'ASIANPAINT', 'TITAN', 'SUNPHARMA', 'WIPRO', 'ULTRACEMCO',
    'TATAMOTORS', 'POWERGRID', 'TECHM', 'NESTLEIND', 'BAJAJFINSV', 'ADANIENT',
    'ADANIPORTS', 'DRREDDY', 'CIPLA', 'DIVISLAB', 'COALINDIA', 'NTPC', 'ONGC',
    'JSWSTEEL', 'TATASTEEL', 'HINDALCO', 'GRASIM', 'EICHERMOT', 'HEROMOTOCO'
  ];

  // Fetch SMLT Engine Configuration - SESSION 104: Load all v2.1 params
  const fetchSmltConfig = useCallback(async () => {
    try {
      setConfigLoading(true);
      const res = await fetch(`${API}/smlt/engine/config/current`);
      const data = await res.json();
      if (data.success) {
        setSmltConfig(data.config);
        setEditedConfig({
          // Stage 1: Ignition Detection
          ignition_spike_multiplier: data.config.ignition_spike_multiplier?.toString() || '',
          volume_spike_multiplier: data.config.volume_spike_multiplier?.toString() || '',
          min_traded_value_lakhs: data.config.min_traded_value_lakhs?.toString() || '',
          warmup_seconds: data.config.warmup_seconds?.toString() || '',
          // Stage 2: MiniGod Score Weights
          cvd_direction_multiplier: data.config.cvd_direction_multiplier?.toString() || '',
          oi_flow_max_points: data.config.oi_flow_max_points?.toString() || '',
          cvd_strength_max_points: data.config.cvd_strength_max_points?.toString() || '',
          volume_max_points: data.config.volume_max_points?.toString() || '',
          price_position_max_points: data.config.price_position_max_points?.toString() || '',
          index_alignment_max_points: data.config.index_alignment_max_points?.toString() || '',
          // SESSION 107: Order Flow Imbalance
          order_flow_max_points: data.config.order_flow_max_points?.toString() || '',
          order_flow_window_seconds: data.config.order_flow_window_seconds?.toString() || '',
          order_flow_veto_threshold: data.config.order_flow_veto_threshold?.toString() || '',
          // Stage 3: Signal Generation
          buy_threshold: data.config.buy_threshold?.toString() || '',
          sell_threshold: data.config.sell_threshold?.toString() || '',
          signal_cooldown_seconds: data.config.signal_cooldown_seconds?.toString() || '',
          index_veto_enabled: data.config.index_veto_enabled ?? true,
          index_veto_threshold_pct: data.config.index_veto_threshold_pct?.toString() || '',
          time_filter_enabled: data.config.time_filter_enabled ?? true,
          time_filter_start: data.config.time_filter_start || '09:30',
          time_filter_end: data.config.time_filter_end || '15:15',
          v2_enabled: data.config.v2_enabled ?? true
        });
      }
    } catch (err) {
      console.error('Failed to load SMLT config:', err);
    } finally {
      setConfigLoading(false);
    }
  }, []);

  // Save SMLT Configuration - SESSION 104: Save all v2.1 params
  const handleSaveConfig = async () => {
    try {
      setConfigSaving(true);
      setConfigSuccess(null);
      
      const updates = {};
      
      // Stage 1: Ignition Detection
      if (editedConfig.ignition_spike_multiplier) {
        updates.ignition_spike_multiplier = parseFloat(editedConfig.ignition_spike_multiplier);
      }
      if (editedConfig.volume_spike_multiplier) {
        updates.volume_spike_multiplier = parseFloat(editedConfig.volume_spike_multiplier);
      }
      if (editedConfig.min_traded_value_lakhs) {
        updates.min_traded_value_lakhs = parseInt(editedConfig.min_traded_value_lakhs);
      }
      if (editedConfig.warmup_seconds) {
        updates.warmup_seconds = parseInt(editedConfig.warmup_seconds);
      }
      
      // Stage 2: MiniGod Score Weights
      if (editedConfig.cvd_direction_multiplier) {
        updates.cvd_direction_multiplier = parseFloat(editedConfig.cvd_direction_multiplier);
      }
      if (editedConfig.oi_flow_max_points) {
        updates.oi_flow_max_points = parseInt(editedConfig.oi_flow_max_points);
      }
      if (editedConfig.cvd_strength_max_points) {
        updates.cvd_strength_max_points = parseInt(editedConfig.cvd_strength_max_points);
      }
      if (editedConfig.volume_max_points) {
        updates.volume_max_points = parseInt(editedConfig.volume_max_points);
      }
      if (editedConfig.price_position_max_points) {
        updates.price_position_max_points = parseInt(editedConfig.price_position_max_points);
      }
      if (editedConfig.index_alignment_max_points) {
        updates.index_alignment_max_points = parseInt(editedConfig.index_alignment_max_points);
      }
      
      // SESSION 107: Order Flow Imbalance Settings
      if (editedConfig.order_flow_max_points) {
        updates.order_flow_max_points = parseInt(editedConfig.order_flow_max_points);
      }
      if (editedConfig.order_flow_window_seconds) {
        updates.order_flow_window_seconds = parseInt(editedConfig.order_flow_window_seconds);
      }
      if (editedConfig.order_flow_veto_threshold) {
        updates.order_flow_veto_threshold = parseInt(editedConfig.order_flow_veto_threshold);
      }
      
      // Stage 3: Signal Generation
      if (editedConfig.buy_threshold) {
        updates.buy_threshold = parseInt(editedConfig.buy_threshold);
      }
      if (editedConfig.sell_threshold) {
        updates.sell_threshold = parseInt(editedConfig.sell_threshold);
      }
      if (editedConfig.signal_cooldown_seconds) {
        updates.signal_cooldown_seconds = parseInt(editedConfig.signal_cooldown_seconds);
      }
      // Boolean and string fields - always include
      updates.index_veto_enabled = editedConfig.index_veto_enabled;
      if (editedConfig.index_veto_threshold_pct) {
        updates.index_veto_threshold_pct = parseFloat(editedConfig.index_veto_threshold_pct);
      }
      updates.time_filter_enabled = editedConfig.time_filter_enabled;
      if (editedConfig.time_filter_start) {
        updates.time_filter_start = editedConfig.time_filter_start;
      }
      if (editedConfig.time_filter_end) {
        updates.time_filter_end = editedConfig.time_filter_end;
      }
      updates.v2_enabled = editedConfig.v2_enabled;
      
      const res = await fetch(`${API}/smlt/engine/config/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      
      const data = await res.json();
      
      if (data.success) {
        setConfigSuccess('Configuration saved successfully!');
        fetchSmltConfig(); // Refresh
        setTimeout(() => setConfigSuccess(null), 3000);
      } else {
        setError(data.message || 'Failed to save configuration');
      }
    } catch (err) {
      setError('Failed to save configuration');
      console.error(err);
    } finally {
      setConfigSaving(false);
    }
  };

  // SESSION 120: Fetch Sector Filter Status
  const fetchSectorFilterStatus = useCallback(async () => {
    try {
      setSectorFilterLoading(true);
      const res = await fetch(`${API}/smlt/sector-filter/status`);
      const data = await res.json();
      if (data.success) {
        setSectorFilterStatus(data);
        setSectorFilterConfig({
          threshold_pct: data.config?.threshold_pct?.toString() || '1.0',
          enabled: data.config?.enabled ?? true
        });
      }
    } catch (err) {
      console.error('Failed to load sector filter status:', err);
    } finally {
      setSectorFilterLoading(false);
    }
  }, []);

  // SESSION 120: Save Sector Filter Configuration
  const handleSaveSectorFilterConfig = async () => {
    try {
      setSectorFilterSaving(true);
      setSectorFilterSuccess(null);
      
      const updates = {
        threshold_pct: parseFloat(sectorFilterConfig.threshold_pct),
        enabled: sectorFilterConfig.enabled
      };
      
      const res = await fetch(`${API}/smlt/sector-filter/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      
      const data = await res.json();
      
      if (data.success) {
        setSectorFilterSuccess('Sector filter config saved!');
        fetchSectorFilterStatus();
        setTimeout(() => setSectorFilterSuccess(null), 3000);
      } else {
        setError(data.message || 'Failed to save sector filter config');
      }
    } catch (err) {
      setError('Failed to save sector filter config');
      console.error(err);
    } finally {
      setSectorFilterSaving(false);
    }
  };

  // SESSION 120: Manually refresh sector filter
  const handleRefreshSectorFilter = async () => {
    try {
      setSectorFilterLoading(true);
      const res = await fetch(`${API}/smlt/sector-filter/refresh`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setSectorFilterSuccess('Sector filter refreshed!');
        fetchSectorFilterStatus();
        setTimeout(() => setSectorFilterSuccess(null), 3000);
      } else {
        setError(data.message || 'Failed to refresh');
      }
    } catch (err) {
      setError('Failed to refresh sector filter');
    } finally {
      setSectorFilterLoading(false);
    }
  };

  // SESSION 120: Save today's sector closes
  const handleSaveSectorCloses = async () => {
    try {
      setSectorFilterLoading(true);
      const res = await fetch(`${API}/smlt/sector-filter/save-all-closes`, { method: 'POST' });
      const data = await res.json();
      if (data.success || data.saved?.length > 0) {
        setSectorFilterSuccess(`Saved ${data.saved?.length || 0} sector closes!`);
        setTimeout(() => setSectorFilterSuccess(null), 3000);
      } else {
        setError(data.message || 'Failed to save closes');
      }
    } catch (err) {
      setError('Failed to save sector closes');
    } finally {
      setSectorFilterLoading(false);
    }
  };

  // SESSION 120: Verify sector closes for production readiness
  const [sectorClosesVerification, setSectorClosesVerification] = useState(null);
  
  const handleVerifySectorCloses = async () => {
    try {
      setSectorFilterLoading(true);
      const res = await fetch(`${API}/smlt/sector-filter/verify-closes`);
      const data = await res.json();
      setSectorClosesVerification(data);
      if (data.success) {
        setSectorFilterSuccess('All sectors have close data!');
        setTimeout(() => setSectorFilterSuccess(null), 3000);
      }
    } catch (err) {
      setError('Failed to verify sector closes');
    } finally {
      setSectorFilterLoading(false);
    }
  };

  // SESSION 120: Fetch sector closes from TrueData
  const handleFetchFromTrueData = async () => {
    try {
      setSectorFilterLoading(true);
      setSectorFilterSuccess(null);
      const res = await fetch(`${API}/smlt/sector-filter/fetch-closes-from-truedata`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await res.json();
      if (data.success || data.saved_count > 0) {
        setSectorFilterSuccess(`Fetched ${data.saved_count} closes from TrueData for ${data.date}!`);
        handleVerifySectorCloses();  // Refresh verification
        setTimeout(() => setSectorFilterSuccess(null), 5000);
      } else {
        setError(data.detail || data.message || 'Failed to fetch from TrueData');
      }
    } catch (err) {
      setError('Failed to fetch from TrueData: ' + err.message);
    } finally {
      setSectorFilterLoading(false);
    }
  };

  const fetchWatchlist = useCallback(async () => {
    try {
      setLoading(true);
      const [watchlistRes, statsRes] = await Promise.all([
        fetch(`${API}/smlt/watchlist`),
        fetch(`${API}/smlt/stats`)
      ]);
      
      const watchlistData = await watchlistRes.json();
      const statsData = await statsRes.json();
      
      if (watchlistData.success) {
        setWatchlist(watchlistData.symbols || []);
        setBenchmarks(watchlistData.benchmarks || {});  // SESSION 120
        setSectorMapping(watchlistData.sector_mapping || {});  // SESSION 120: Sector mapping
      }
      if (statsData.success) {
        setStats(statsData);
      }
      setError(null);
    } catch (err) {
      setError('Failed to load watchlist');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWatchlist();
    fetchSmltConfig();
    fetchSectorFilterStatus();  // SESSION 120: Load sector filter status
    
    // SESSION 120: Auto-refresh sector filter status every 30 seconds for live prices
    const sectorRefreshInterval = setInterval(() => {
      fetchSectorFilterStatus();
    }, 30000);  // 30 seconds
    
    return () => clearInterval(sectorRefreshInterval);
  }, [fetchWatchlist, fetchSmltConfig, fetchSectorFilterStatus]);

  const handleAddSymbol = async () => {
    if (!newSymbol.trim()) return;
    
    try {
      setAddLoading(true);
      const response = await fetch(`${API}/smlt/watchlist/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          symbol: newSymbol.trim().toUpperCase(),
          benchmark: newBenchmark,  // SESSION 120
          sector: newSector || null  // SESSION 120: Sector mapping
        })
      });
      const data = await response.json();
      
      if (data.success) {
        setNewSymbol('');
        setNewBenchmark('NIFTY');
        setNewSector('');  // SESSION 120: Reset sector
        fetchWatchlist();
      } else {
        setError(data.message || 'Failed to add symbol');
      }
    } catch (err) {
      setError('Failed to add symbol');
    } finally {
      setAddLoading(false);
    }
  };

  const handleRemoveSymbol = async (symbol) => {
    if (!window.confirm(`Remove ${symbol} from watchlist?`)) return;
    
    try {
      const response = await fetch(`${API}/smlt/watchlist/${symbol}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      
      if (data.success) {
        fetchWatchlist();
      } else {
        setError(data.message || 'Failed to remove symbol');
      }
    } catch (err) {
      setError('Failed to remove symbol');
    }
  };

  const handleAddSuggested = async (symbol) => {
    if (watchlist.includes(symbol)) return;
    
    // SESSION 120: Auto-detect benchmark for suggested stocks
    const bankStocks = ['HDFCBANK', 'ICICIBANK', 'SBIN', 'KOTAKBANK', 'AXISBANK', 'BAJFINANCE', 'BAJAJFINSV', 'INDUSINDBK', 'PNB', 'BANKBARODA'];
    const benchmark = bankStocks.includes(symbol) ? 'BANKNIFTY' : 'NIFTY';
    
    try {
      const response = await fetch(`${API}/smlt/watchlist/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbol, benchmark })
      });
      const data = await response.json();
      if (data.success) {
        fetchWatchlist();
      }
    } catch (err) {
      console.error('Failed to add suggested symbol:', err);
    }
  };

  // SESSION 120: Toggle benchmark between NIFTY and BANKNIFTY
  const handleToggleBenchmark = async (symbol) => {
    const currentBenchmark = benchmarks[symbol] || 'NIFTY';
    const newBenchmark = currentBenchmark === 'NIFTY' ? 'BANKNIFTY' : 'NIFTY';
    
    try {
      const response = await fetch(`${API}/smlt/watchlist/benchmark`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbol, benchmark: newBenchmark })
      });
      const data = await response.json();
      if (data.success) {
        setBenchmarks(prev => ({ ...prev, [symbol]: newBenchmark }));
      } else {
        setError(data.message || 'Failed to update benchmark');
      }
    } catch (err) {
      setError('Failed to update benchmark');
    }
  };

  // SESSION 120: Update sector for a stock
  const handleUpdateSector = async (symbol, sector) => {
    try {
      const response = await fetch(`${API}/smlt/watchlist/sector`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbol, sector })
      });
      const data = await response.json();
      if (data.success) {
        setSectorMapping(prev => ({ ...prev, [symbol]: sector }));
      } else {
        setError(data.message || 'Failed to update sector');
      }
    } catch (err) {
      setError('Failed to update sector');
    }
  };

  // SESSION 120: Get sector color class
  const getSectorColor = (sector) => {
    const sectorObj = SECTORS.find(s => s.value === sector);
    if (!sectorObj) return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    const colorMap = {
      cyan: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
      green: 'bg-green-500/20 text-green-400 border-green-500/30',
      orange: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      slate: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
      yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      red: 'bg-red-500/20 text-red-400 border-red-500/30',
      amber: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    };
    return colorMap[sectorObj.color] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  // SESSION 120: Get sector short label
  const getSectorLabel = (sector) => {
    const sectorObj = SECTORS.find(s => s.value === sector);
    return sectorObj ? sectorObj.label : 'None';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="smlt-watchlist-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-heading font-bold text-foreground flex items-center gap-2">
            <Target className="w-5 h-5 text-accent" />
            SMLT Watchlist
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Stock futures tracked for OI regime analysis (MiniGod)
          </p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchWatchlist}
          data-testid="refresh-watchlist"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-3 gap-4">
          <div className="p-4 bg-surface rounded border border-border">
            <div className="text-2xl font-bold text-accent">{stats.watchlist_count}</div>
            <div className="text-sm text-muted-foreground">Watchlist Size</div>
          </div>
          <div className="p-4 bg-surface rounded border border-border">
            <div className="text-2xl font-bold text-green-400">{stats.tracking_count}</div>
            <div className="text-sm text-muted-foreground">Actively Tracking</div>
          </div>
          <div className="p-4 bg-surface rounded border border-border">
            <div className="text-2xl font-bold text-blue-400">{stats.symbols_with_data?.length || 0}</div>
            <div className="text-sm text-muted-foreground">With OI Data</div>
          </div>
        </div>
      )}

      {/* SESSION 120: SECTOR FILTER CONFIGURATION */}
      <div className="p-4 bg-surface rounded border-2 border-green-500/30">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Layers className="w-4 h-4 text-green-400" />
            Sector Momentum Filter
          </h3>
          <div className="flex items-center gap-2">
            {sectorFilterLoading && <RefreshCw className="w-4 h-4 animate-spin text-muted-foreground" />}
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={sectorFilterConfig.enabled}
                onChange={(e) => setSectorFilterConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                className="w-4 h-4 rounded border-border bg-background"
                data-testid="sector-filter-enabled"
              />
              <span className={sectorFilterConfig.enabled ? 'text-green-400' : 'text-muted-foreground'}>
                {sectorFilterConfig.enabled ? 'Enabled' : 'Disabled'}
              </span>
            </label>
          </div>
        </div>

        {sectorFilterSuccess && (
          <div className="mb-4 p-2 bg-green-500/10 border border-green-500/30 rounded text-green-400 text-sm flex items-center gap-2">
            <Check className="w-4 h-4" />
            {sectorFilterSuccess}
          </div>
        )}

        <p className="text-xs text-muted-foreground mb-4">
          Pre-qualifies stocks for trading based on sector performance. Only stocks in sectors with strong momentum will generate signals.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Threshold Configuration */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-foreground">Momentum Threshold (%)</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                step="0.1"
                min="0.1"
                max="5"
                value={sectorFilterConfig.threshold_pct}
                onChange={(e) => setSectorFilterConfig(prev => ({ ...prev, threshold_pct: e.target.value }))}
                className="flex-1 px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
                data-testid="sector-filter-threshold"
              />
              <span className="text-xs text-muted-foreground">%</span>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Sector must move ≥ <span className="text-green-400 font-mono">+{sectorFilterConfig.threshold_pct}%</span> for LONG or 
              ≤ <span className="text-red-400 font-mono">-{sectorFilterConfig.threshold_pct}%</span> for SHORT
            </p>
          </div>

          {/* Last Update Info */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-foreground">Update Status</label>
            <div className="p-2 bg-background/50 border border-border rounded">
              <div className="text-xs text-muted-foreground">
                Last Update: <span className="text-foreground font-mono">
                  {sectorFilterStatus?.eligible?.last_update 
                    ? new Date(sectorFilterStatus.eligible.last_update).toLocaleTimeString() 
                    : 'Never'}
                </span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Updates every 5 minutes during market hours
              </div>
            </div>
          </div>
        </div>

        {/* Active Sectors Display */}
        {sectorFilterStatus && (
          <div className="grid grid-cols-2 gap-4 mb-4">
            {/* Long Sectors */}
            <div className="p-3 bg-green-500/5 border border-green-500/20 rounded">
              <h4 className="text-xs font-semibold text-green-400 mb-2 flex items-center gap-2">
                <TrendingUp className="w-3 h-3" />
                LONG Eligible Sectors ({sectorFilterStatus.eligible?.long?.length || 0})
              </h4>
              {sectorFilterStatus.eligible?.long?.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {sectorFilterStatus.eligible.long.map(sector => {
                    const detail = sectorFilterStatus.sector_details?.[sector];
                    return (
                      <span 
                        key={sector} 
                        className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded font-mono"
                        title={detail ? `${detail.change_pct > 0 ? '+' : ''}${detail.change_pct}%` : ''}
                      >
                        {sector.replace('NIFTY ', '')}
                        {detail && <span className="ml-1 opacity-70">+{detail.change_pct?.toFixed(1)}%</span>}
                      </span>
                    );
                  })}
                </div>
              ) : (
                <span className="text-xs text-muted-foreground italic">No sectors qualify</span>
              )}
            </div>

            {/* Short Sectors */}
            <div className="p-3 bg-red-500/5 border border-red-500/20 rounded">
              <h4 className="text-xs font-semibold text-red-400 mb-2 flex items-center gap-2">
                <TrendingDown className="w-3 h-3" />
                SHORT Eligible Sectors ({sectorFilterStatus.eligible?.short?.length || 0})
              </h4>
              {sectorFilterStatus.eligible?.short?.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {sectorFilterStatus.eligible.short.map(sector => {
                    const detail = sectorFilterStatus.sector_details?.[sector];
                    return (
                      <span 
                        key={sector} 
                        className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded font-mono"
                        title={detail ? `${detail.change_pct}%` : ''}
                      >
                        {sector.replace('NIFTY ', '')}
                        {detail && <span className="ml-1 opacity-70">{detail.change_pct?.toFixed(1)}%</span>}
                      </span>
                    );
                  })}
                </div>
              ) : (
                <span className="text-xs text-muted-foreground italic">No sectors qualify</span>
              )}
            </div>
          </div>
        )}

        {/* All Sectors Status Table */}
        {sectorFilterStatus?.sector_details && Object.keys(sectorFilterStatus.sector_details).length > 0 && (
          <div className="mb-4 overflow-x-auto">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-muted-foreground">
                All Sector Indices
              </span>
              {sectorFilterStatus.live_prices_available && (
                <span className="text-[10px] text-green-400 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></span>
                  Live
                </span>
              )}
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 text-muted-foreground font-medium">Sector</th>
                  <th className="text-right py-2 text-muted-foreground font-medium">Live Price</th>
                  <th className="text-right py-2 text-muted-foreground font-medium">Prev Close</th>
                  <th className="text-right py-2 text-muted-foreground font-medium">Change %</th>
                  <th className="text-center py-2 text-muted-foreground font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(sectorFilterStatus.sector_details).map(([sector, data]) => {
                  const livePrice = data.live_price || data.current_price || 0;
                  const prevClose = data.live_prev_close || data.prev_close || 0;
                  const changePct = prevClose > 0 ? ((livePrice - prevClose) / prevClose * 100) : (data.change_pct || 0);
                  
                  return (
                    <tr key={sector} className="border-b border-border/50 hover:bg-background/50">
                      <td className="py-2 font-medium text-foreground">{sector.replace('NIFTY ', '')}</td>
                      <td className="py-2 text-right font-mono">
                        {livePrice > 0 ? livePrice.toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '-'}
                      </td>
                      <td className="py-2 text-right font-mono text-muted-foreground">
                        {prevClose > 0 ? prevClose.toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '-'}
                      </td>
                      <td className={`py-2 text-right font-mono ${
                        changePct > 0 ? 'text-green-400' : changePct < 0 ? 'text-red-400' : 'text-muted-foreground'
                      }`}>
                        {changePct !== 0 ? `${changePct > 0 ? '+' : ''}${changePct.toFixed(2)}%` : '-'}
                      </td>
                      <td className="py-2 text-center">
                        {data.is_long_eligible && (
                          <span className="px-1.5 py-0.5 bg-green-500/20 text-green-400 text-[10px] rounded">LONG</span>
                        )}
                        {data.is_short_eligible && (
                          <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 text-[10px] rounded">SHORT</span>
                        )}
                        {!data.is_long_eligible && !data.is_short_eligible && (
                          <span className="px-1.5 py-0.5 bg-gray-500/20 text-gray-400 text-[10px] rounded">NEUTRAL</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border">
          <Button
            size="sm"
            onClick={handleSaveSectorFilterConfig}
            disabled={sectorFilterSaving}
            className="bg-green-600 hover:bg-green-700"
            data-testid="save-sector-filter"
          >
            {sectorFilterSaving ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Save Config
              </>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefreshSectorFilter}
            disabled={sectorFilterLoading}
            data-testid="refresh-sector-filter"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${sectorFilterLoading ? 'animate-spin' : ''}`} />
            Refresh Now
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSaveSectorCloses}
            disabled={sectorFilterLoading}
            title="Save current sector prices as today's closing prices (for next day's filtering)"
            data-testid="save-sector-closes"
          >
            <Download className="w-4 h-4 mr-2" />
            Save EOD Closes
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleVerifySectorCloses}
            disabled={sectorFilterLoading}
            title="Verify all sectors have closing prices for the previous trading day"
            data-testid="verify-sector-closes"
          >
            <Shield className="w-4 h-4 mr-2" />
            Verify Data
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleFetchFromTrueData}
            disabled={sectorFilterLoading}
            title="Fetch previous day closes from TrueData REST API (requires TrueData credentials)"
            data-testid="fetch-truedata-closes"
            className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
          >
            <Database className="w-4 h-4 mr-2" />
            Fetch from TrueData
          </Button>
        </div>

        {/* SESSION 120: Verification Status Panel */}
        {sectorClosesVerification && (
          <div className={`mt-4 p-3 rounded border ${
            sectorClosesVerification.success 
              ? 'bg-green-500/5 border-green-500/20' 
              : 'bg-amber-500/5 border-amber-500/20'
          }`}>
            <h4 className={`text-xs font-semibold mb-2 flex items-center gap-2 ${
              sectorClosesVerification.success ? 'text-green-400' : 'text-amber-400'
            }`}>
              <Shield className="w-4 h-4" />
              Data Verification ({sectorClosesVerification.prev_trading_day})
            </h4>
            <div className="grid grid-cols-3 gap-2 mb-2">
              <div className="text-center p-2 bg-background/50 rounded">
                <div className="text-lg font-bold text-foreground">{sectorClosesVerification.total_sectors}</div>
                <div className="text-[10px] text-muted-foreground">Total</div>
              </div>
              <div className="text-center p-2 bg-background/50 rounded">
                <div className="text-lg font-bold text-green-400">{sectorClosesVerification.sectors_with_data}</div>
                <div className="text-[10px] text-muted-foreground">Have Data</div>
              </div>
              <div className="text-center p-2 bg-background/50 rounded">
                <div className={`text-lg font-bold ${sectorClosesVerification.sectors_missing_data > 0 ? 'text-red-400' : 'text-green-400'}`}>
                  {sectorClosesVerification.sectors_missing_data}
                </div>
                <div className="text-[10px] text-muted-foreground">Missing</div>
              </div>
            </div>
            {sectorClosesVerification.missing?.length > 0 && (
              <div className="text-xs text-amber-400">
                <AlertTriangle className="w-3 h-3 inline mr-1" />
                Missing: {sectorClosesVerification.missing.map(s => s.replace('NIFTY ', '')).join(', ')}
              </div>
            )}
            <p className="text-[10px] text-muted-foreground mt-2">
              For production: Save EOD closes at market close (15:30 IST) to ensure data for next trading day.
            </p>
          </div>
        )}
      </div>

      {/* SMLT Engine Configuration - SESSION 104: Reorganized into Stages */}
      <div className="p-4 bg-surface rounded border-2 border-accent/50">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Flame className="w-4 h-4 text-accent" />
            SMLT v2.1 Signal Generation Algorithm
          </h3>
          <div className="flex items-center gap-2">
            {configLoading && <RefreshCw className="w-4 h-4 animate-spin text-muted-foreground" />}
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={editedConfig.v2_enabled}
                onChange={(e) => setEditedConfig(prev => ({ ...prev, v2_enabled: e.target.checked }))}
                className="w-4 h-4 rounded border-border bg-background"
              />
              <span className={editedConfig.v2_enabled ? 'text-green-400' : 'text-muted-foreground'}>
                v2.1 {editedConfig.v2_enabled ? 'Enabled' : 'Disabled'}
              </span>
            </label>
          </div>
        </div>

        {configSuccess && (
          <div className="mb-4 p-2 bg-green-500/10 border border-green-500/30 rounded text-green-400 text-sm flex items-center gap-2">
            <Check className="w-4 h-4" />
            {configSuccess}
          </div>
        )}

        {/* ========== STAGE 1: IGNITION DETECTION ========== */}
        <div className="mb-6 p-3 bg-blue-500/5 border border-blue-500/20 rounded">
          <h4 className="text-xs font-semibold text-blue-400 mb-3 flex items-center gap-2">
            <span className="px-2 py-0.5 bg-blue-500/20 rounded">STAGE 1</span>
            Ignition Detection
          </h4>
          <p className="text-[10px] text-muted-foreground mb-3">
            Detects unusual activity spikes. All 3 conditions must pass to proceed.
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {/* TPS Spike Multiplier */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-foreground">TPS Spike Multiplier</label>
              <input
                type="number"
                step="0.1"
                min="0.5"
                max="10"
                value={editedConfig.ignition_spike_multiplier}
                onChange={(e) => setEditedConfig(prev => ({ ...prev, ignition_spike_multiplier: e.target.value }))}
                placeholder="2.2"
                className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
                data-testid="smlt-tps-multiplier"
              />
              <p className="text-[10px] text-muted-foreground">
                TPS must be ≥ <span className="text-accent font-mono">{smltConfig?.ignition_spike_multiplier || '2.2'}×</span> EMA
              </p>
            </div>

            {/* Volume Spike Multiplier */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-foreground">Volume Spike Multiplier</label>
              <input
                type="number"
                step="0.1"
                min="0.5"
                max="10"
                value={editedConfig.volume_spike_multiplier}
                onChange={(e) => setEditedConfig(prev => ({ ...prev, volume_spike_multiplier: e.target.value }))}
                placeholder="2.0"
                className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
                data-testid="smlt-volume-multiplier"
              />
              <p className="text-[10px] text-muted-foreground">
                Volume must be ≥ <span className="text-accent font-mono">{smltConfig?.volume_spike_multiplier || '2.0'}×</span> Avg
              </p>
            </div>

            {/* Min Traded Value */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-foreground">Min Traded Value (₹L)</label>
              <input
                type="number"
                step="5"
                min="0"
                max="1000"
                value={editedConfig.min_traded_value_lakhs}
                onChange={(e) => setEditedConfig(prev => ({ ...prev, min_traded_value_lakhs: e.target.value }))}
                placeholder="25"
                className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
                data-testid="smlt-min-value"
              />
              <p className="text-[10px] text-muted-foreground">
                Min ₹<span className="text-accent font-mono">{smltConfig?.min_traded_value_lakhs || '25'}L</span> to filter noise
              </p>
            </div>

            {/* Warmup Seconds */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-foreground">Warmup Period (sec)</label>
              <input
                type="number"
                step="30"
                min="0"
                max="3600"
                value={editedConfig.warmup_seconds}
                onChange={(e) => setEditedConfig(prev => ({ ...prev, warmup_seconds: e.target.value }))}
                placeholder="300"
                className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
                data-testid="smlt-warmup"
              />
              <p className="text-[10px] text-muted-foreground">
                Wait <span className="text-accent font-mono">{smltConfig?.warmup_seconds || '300'}s</span> before signals
              </p>
            </div>
          </div>
        </div>

        {/* ========== STAGE 2: MINIGOD SCORE WEIGHTS ========== */}
        <div className="mb-6 p-3 bg-purple-500/5 border border-purple-500/20 rounded">
          <h4 className="text-xs font-semibold text-purple-400 mb-3 flex items-center gap-2">
            <span className="px-2 py-0.5 bg-purple-500/20 rounded">STAGE 2</span>
            MiniGod Score Calculation (-100 to +100)
          </h4>
          <p className="text-[10px] text-muted-foreground mb-3">
            Calculates conviction score. Positive = BUY setup, Negative = SELL setup. Total points = 100.
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {/* OI Flow Max Points */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-foreground">OI Flow Max Points</label>
              <input
                type="number"
                step="5"
                min="0"
                max="50"
                value={editedConfig.oi_flow_max_points}
                onChange={(e) => setEditedConfig(prev => ({ ...prev, oi_flow_max_points: e.target.value }))}
                placeholder="35"
                className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
                data-testid="smlt-oi-points"
              />
              <p className="text-[10px] text-muted-foreground">
                OI regime weight: <span className="text-accent font-mono">±{smltConfig?.oi_flow_max_points || '35'}</span>
              </p>
            </div>

            {/* CVD Strength Max Points */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-foreground">CVD Strength Max Points</label>
              <input
                type="number"
                step="5"
                min="0"
                max="50"
                value={editedConfig.cvd_strength_max_points}
                onChange={(e) => setEditedConfig(prev => ({ ...prev, cvd_strength_max_points: e.target.value }))}
                placeholder="25"
                className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
                data-testid="smlt-cvd-points"
              />
              <p className="text-[10px] text-muted-foreground">
                CVD magnitude weight: <span className="text-accent font-mono">±{smltConfig?.cvd_strength_max_points || '25'}</span>
              </p>
            </div>

            {/* CVD Direction Multiplier */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-foreground">CVD Direction Multiplier</label>
              <input
                type="number"
                step="0.1"
                min="0.5"
                max="10"
                value={editedConfig.cvd_direction_multiplier}
                onChange={(e) => setEditedConfig(prev => ({ ...prev, cvd_direction_multiplier: e.target.value }))}
                placeholder="1.8"
                className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
                data-testid="smlt-cvd-multiplier"
              />
              <p className="text-[10px] text-muted-foreground">
                CVD must be ≥ <span className="text-accent font-mono">{smltConfig?.cvd_direction_multiplier || '1.8'}×</span> EMA for full points
              </p>
            </div>

            {/* Volume Max Points */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-foreground">Volume Max Points</label>
              <input
                type="number"
                step="5"
                min="0"
                max="50"
                value={editedConfig.volume_max_points}
                onChange={(e) => setEditedConfig(prev => ({ ...prev, volume_max_points: e.target.value }))}
                placeholder="15"
                className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
                data-testid="smlt-vol-points"
              />
              <p className="text-[10px] text-muted-foreground">
                Volume conviction: <span className="text-accent font-mono">±{smltConfig?.volume_max_points || '15'}</span>
              </p>
            </div>

            {/* Price Position Max Points */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-foreground">Price Position Max Points</label>
              <input
                type="number"
                step="5"
                min="0"
                max="50"
                value={editedConfig.price_position_max_points}
                onChange={(e) => setEditedConfig(prev => ({ ...prev, price_position_max_points: e.target.value }))}
                placeholder="10"
                className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
                data-testid="smlt-price-points"
              />
              <p className="text-[10px] text-muted-foreground">
                Price vs VWAP: <span className="text-accent font-mono">±{smltConfig?.price_position_max_points || '10'}</span>
              </p>
            </div>

            {/* Index Alignment Max Points */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-foreground">Index Alignment Max Points</label>
              <input
                type="number"
                step="5"
                min="0"
                max="50"
                value={editedConfig.index_alignment_max_points}
                onChange={(e) => setEditedConfig(prev => ({ ...prev, index_alignment_max_points: e.target.value }))}
                placeholder="15"
                className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
                data-testid="smlt-index-points"
              />
              <p className="text-[10px] text-muted-foreground">
                NIFTY alignment: <span className="text-accent font-mono">±{smltConfig?.index_alignment_max_points || '15'}</span>
              </p>
            </div>
          </div>
          
          {/* SESSION 107: Order Flow Imbalance Settings */}
          <div className="mt-4 pt-4 border-t border-purple-500/20">
            <h5 className="text-xs font-semibold text-cyan-400 mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-cyan-400"></span>
              Order Flow Imbalance (Buy/Sell Aggression)
            </h5>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              {/* Order Flow Max Points */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-foreground">Order Flow Max Points</label>
                <input
                  type="number"
                  step="5"
                  min="0"
                  max="50"
                  value={editedConfig.order_flow_max_points}
                  onChange={(e) => setEditedConfig(prev => ({ ...prev, order_flow_max_points: e.target.value }))}
                  placeholder="20"
                  className="w-full px-3 py-2 bg-background border border-cyan-500/30 rounded text-foreground text-sm"
                  data-testid="smlt-order-flow-points"
                />
                <p className="text-[10px] text-muted-foreground">
                  Imbalance weight: <span className="text-cyan-400 font-mono">±{smltConfig?.order_flow_max_points || '20'}</span>
                </p>
              </div>

              {/* Order Flow Window */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-foreground">Window (seconds)</label>
                <input
                  type="number"
                  step="60"
                  min="60"
                  max="900"
                  value={editedConfig.order_flow_window_seconds}
                  onChange={(e) => setEditedConfig(prev => ({ ...prev, order_flow_window_seconds: e.target.value }))}
                  placeholder="300"
                  className="w-full px-3 py-2 bg-background border border-cyan-500/30 rounded text-foreground text-sm"
                  data-testid="smlt-order-flow-window"
                />
                <p className="text-[10px] text-muted-foreground">
                  Rolling window: <span className="text-cyan-400 font-mono">{Math.round((parseInt(editedConfig.order_flow_window_seconds) || 300) / 60)} min</span>
                </p>
              </div>

              {/* Order Flow VETO Threshold */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-foreground">VETO Threshold (%)</label>
                <input
                  type="number"
                  step="5"
                  min="50"
                  max="100"
                  value={editedConfig.order_flow_veto_threshold}
                  onChange={(e) => setEditedConfig(prev => ({ ...prev, order_flow_veto_threshold: e.target.value }))}
                  placeholder="80"
                  className="w-full px-3 py-2 bg-background border border-red-500/30 rounded text-foreground text-sm"
                  data-testid="smlt-order-flow-veto"
                />
                <p className="text-[10px] text-muted-foreground">
                  Block if fighting <span className="text-red-400 font-mono">≥{smltConfig?.order_flow_veto_threshold || '80'}%</span> imbalance
                </p>
              </div>
            </div>
          </div>
          
          {/* Total Points Display - SESSION 107: Updated to include Order Flow */}
          <div className="mt-3 p-2 bg-background/50 rounded text-xs text-center">
            <span className="text-muted-foreground">Total Max Points: </span>
            {(() => {
              const total = (parseInt(editedConfig.oi_flow_max_points) || 28) + 
                           (parseInt(editedConfig.cvd_strength_max_points) || 20) + 
                           (parseInt(editedConfig.volume_max_points) || 12) + 
                           (parseInt(editedConfig.price_position_max_points) || 10) + 
                           (parseInt(editedConfig.index_alignment_max_points) || 10) +
                           (parseInt(editedConfig.order_flow_max_points) || 20);
              const isValid = total === 100;
              return (
                <>
                  <span className={`font-mono font-bold ${isValid ? 'text-green-400' : 'text-red-400'}`}>
                    {total}
                  </span>
                  <span className={`ml-1 ${isValid ? 'text-green-400' : 'text-red-400'}`}>
                    {isValid ? '✓' : `(should equal 100, ${total > 100 ? 'reduce' : 'increase'} OI Flow by ${Math.abs(100 - total)})`}
                  </span>
                </>
              );
            })()}
          </div>
        </div>

        {/* ========== STAGE 3: SIGNAL GENERATION ========== */}
        <div className="mb-4 p-3 bg-green-500/5 border border-green-500/20 rounded">
          <h4 className="text-xs font-semibold text-green-400 mb-3 flex items-center gap-2">
            <span className="px-2 py-0.5 bg-green-500/20 rounded">STAGE 3</span>
            Signal Generation & Filters
          </h4>
          <p className="text-[10px] text-muted-foreground mb-3">
            Final thresholds and filters for generating BUY/SELL signals.
          </p>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            {/* BUY Threshold */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-green-400">BUY Threshold</label>
              <input
                type="number"
                step="5"
                min="0"
                max="100"
                value={editedConfig.buy_threshold}
                onChange={(e) => setEditedConfig(prev => ({ ...prev, buy_threshold: e.target.value }))}
                placeholder="65"
                className="w-full px-3 py-2 bg-background border border-green-500/30 rounded text-foreground text-sm"
                data-testid="smlt-buy-threshold"
              />
              <p className="text-[10px] text-muted-foreground">
                MiniGod ≥ <span className="text-green-400 font-mono">+{smltConfig?.buy_threshold || '65'}</span> → BUY
              </p>
            </div>

            {/* SELL Threshold */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-red-400">SELL Threshold</label>
              <input
                type="number"
                step="5"
                min="-100"
                max="0"
                value={editedConfig.sell_threshold}
                onChange={(e) => setEditedConfig(prev => ({ ...prev, sell_threshold: e.target.value }))}
                placeholder="-65"
                className="w-full px-3 py-2 bg-background border border-red-500/30 rounded text-foreground text-sm"
                data-testid="smlt-sell-threshold"
              />
              <p className="text-[10px] text-muted-foreground">
                MiniGod ≤ <span className="text-red-400 font-mono">{smltConfig?.sell_threshold || '-65'}</span> → SELL
              </p>
            </div>

            {/* Signal Cooldown */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-foreground">Signal Cooldown (sec)</label>
              <input
                type="number"
                step="30"
                min="0"
                max="3600"
                value={editedConfig.signal_cooldown_seconds}
                onChange={(e) => setEditedConfig(prev => ({ ...prev, signal_cooldown_seconds: e.target.value }))}
                placeholder="300"
                className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
                data-testid="smlt-cooldown"
              />
              <p className="text-[10px] text-muted-foreground">
                Min <span className="text-accent font-mono">{smltConfig?.signal_cooldown_seconds || '300'}s</span> between signals
              </p>
            </div>

            {/* Index Veto Threshold */}
            <div className="space-y-1">
              <label className="text-xs font-medium text-foreground">Index Veto (%)</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="5"
                value={editedConfig.index_veto_threshold_pct}
                onChange={(e) => setEditedConfig(prev => ({ ...prev, index_veto_threshold_pct: e.target.value }))}
                placeholder="1.0"
                className="w-full px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
                data-testid="smlt-index-veto"
                disabled={!editedConfig.index_veto_enabled}
              />
              <p className="text-[10px] text-muted-foreground">
                NIFTY ≥ <span className="text-accent font-mono">{smltConfig?.index_veto_threshold_pct || '1.0'}%</span> blocks counter signals
              </p>
            </div>
          </div>

          {/* Toggles Row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-2 bg-background/30 rounded">
            {/* Index Veto Toggle */}
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={editedConfig.index_veto_enabled}
                onChange={(e) => setEditedConfig(prev => ({ ...prev, index_veto_enabled: e.target.checked }))}
                className="w-4 h-4 rounded border-border bg-background"
              />
              <span className={editedConfig.index_veto_enabled ? 'text-foreground' : 'text-muted-foreground'}>
                Index Veto {editedConfig.index_veto_enabled ? 'ON' : 'OFF'}
              </span>
            </label>

            {/* Time Filter Toggle */}
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={editedConfig.time_filter_enabled}
                onChange={(e) => setEditedConfig(prev => ({ ...prev, time_filter_enabled: e.target.checked }))}
                className="w-4 h-4 rounded border-border bg-background"
              />
              <span className={editedConfig.time_filter_enabled ? 'text-foreground' : 'text-muted-foreground'}>
                Time Filter {editedConfig.time_filter_enabled ? 'ON' : 'OFF'}
              </span>
            </label>

            {/* Time Filter Start */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Start:</span>
              <input
                type="text"
                value={editedConfig.time_filter_start}
                onChange={(e) => setEditedConfig(prev => ({ ...prev, time_filter_start: e.target.value }))}
                placeholder="09:30"
                className="w-16 px-2 py-1 bg-background border border-border rounded text-foreground text-xs"
                disabled={!editedConfig.time_filter_enabled}
              />
            </div>

            {/* Time Filter End */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">End:</span>
              <input
                type="text"
                value={editedConfig.time_filter_end}
                onChange={(e) => setEditedConfig(prev => ({ ...prev, time_filter_end: e.target.value }))}
                placeholder="15:15"
                className="w-16 px-2 py-1 bg-background border border-border rounded text-foreground text-xs"
                disabled={!editedConfig.time_filter_enabled}
              />
            </div>
          </div>
        </div>

        {/* Save/Refresh Buttons */}
        <div className="pt-3 border-t border-border flex items-center justify-between">
          <Button
            onClick={handleSaveConfig}
            disabled={configSaving}
            className="px-4"
            data-testid="save-smlt-config"
          >
            {configSaving ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Save Configuration
              </>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchSmltConfig}
            disabled={configLoading}
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${configLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Add Symbol */}
      <div className="p-4 bg-surface rounded border border-border">
        <h3 className="text-sm font-medium text-foreground mb-3">Add Symbol</h3>
        <div className="flex gap-2 flex-wrap">
          <input
            type="text"
            value={newSymbol}
            onChange={(e) => setNewSymbol(e.target.value.toUpperCase())}
            placeholder="e.g., RELIANCE"
            className="flex-1 min-w-[120px] px-3 py-2 bg-background border border-border rounded text-foreground placeholder-muted-foreground text-sm"
            data-testid="add-symbol-input"
            onKeyDown={(e) => e.key === 'Enter' && handleAddSymbol()}
          />
          {/* SESSION 120: Benchmark selector */}
          <select
            value={newBenchmark}
            onChange={(e) => setNewBenchmark(e.target.value)}
            className="px-3 py-2 bg-background border border-border rounded text-foreground text-sm"
            data-testid="add-benchmark-select"
          >
            <option value="NIFTY">NIFTY</option>
            <option value="BANKNIFTY">BANKNIFTY</option>
          </select>
          {/* SESSION 120: Sector selector */}
          <select
            value={newSector}
            onChange={(e) => setNewSector(e.target.value)}
            className="px-3 py-2 bg-background border border-border rounded text-foreground text-sm min-w-[140px]"
            data-testid="add-sector-select"
          >
            <option value="">Select Sector</option>
            {SECTORS.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <Button 
            onClick={handleAddSymbol} 
            disabled={addLoading || !newSymbol.trim()}
            data-testid="add-symbol-btn"
          >
            {addLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add
          </Button>
        </div>
        
        {/* Quick Add Suggestions */}
        <div className="mt-3">
          <div className="text-xs text-muted-foreground mb-2">Quick Add (click to add):</div>
          <div className="flex flex-wrap gap-1">
            {suggestedStocks.filter(s => !watchlist.includes(s)).slice(0, 15).map(symbol => (
              <button
                key={symbol}
                onClick={() => handleAddSuggested(symbol)}
                className="px-2 py-1 text-xs bg-accent/10 text-accent hover:bg-accent/20 rounded transition-colors"
              >
                + {symbol}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Current Watchlist - SESSION 120: Added benchmark & sector display */}
      <div className="p-4 bg-surface rounded border border-border">
        <h3 className="text-sm font-medium text-foreground mb-3">
          Current Watchlist ({watchlist.length} stocks)
        </h3>
        <p className="text-xs text-muted-foreground mb-3">
          Click badges to toggle benchmark. Use dropdown to change sector.
        </p>
        
        {/* Group by Sector */}
        <div className="space-y-4">
          {SECTORS.map(sector => {
            const sectorStocks = watchlist.filter(s => sectorMapping[s] === sector.value);
            if (sectorStocks.length === 0) return null;
            
            return (
              <div key={sector.value} className="border border-border rounded p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-0.5 text-xs rounded font-medium border ${getSectorColor(sector.value)}`}>
                    {sector.label}
                  </span>
                  <span className="text-xs text-muted-foreground">({sectorStocks.length} stocks)</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {sectorStocks.map(symbol => {
                    const benchmark = benchmarks[symbol] || 'NIFTY';
                    const isBankNifty = benchmark === 'BANKNIFTY';
                    return (
                      <div
                        key={symbol}
                        className="flex items-center gap-2 px-3 py-1.5 bg-background border border-border rounded group"
                      >
                        <span className="text-sm font-mono text-foreground">{symbol}</span>
                        <button
                          onClick={() => handleToggleBenchmark(symbol)}
                          className={`px-1.5 py-0.5 text-xs rounded font-medium transition-colors ${
                            isBankNifty 
                              ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30' 
                              : 'bg-blue-500/20 text-blue-400 border border-blue-500/30 hover:bg-blue-500/30'
                          }`}
                          title={`Click to switch to ${isBankNifty ? 'NIFTY' : 'BANKNIFTY'}`}
                        >
                          {isBankNifty ? 'BNF' : 'NF'}
                        </button>
                        <button
                          onClick={() => handleRemoveSymbol(symbol)}
                          className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-opacity"
                          title="Remove"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          
          {/* Unassigned stocks (no sector) */}
          {(() => {
            const unassigned = watchlist.filter(s => !sectorMapping[s] || !SECTORS.find(sec => sec.value === sectorMapping[s]));
            if (unassigned.length === 0) return null;
            
            return (
              <div className="border border-border rounded p-3 border-dashed">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 text-xs rounded font-medium border bg-gray-500/20 text-gray-400 border-gray-500/30">
                    Unassigned
                  </span>
                  <span className="text-xs text-muted-foreground">({unassigned.length} stocks - please assign sectors)</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {unassigned.map(symbol => {
                    const benchmark = benchmarks[symbol] || 'NIFTY';
                    const isBankNifty = benchmark === 'BANKNIFTY';
                    return (
                      <div
                        key={symbol}
                        className="flex items-center gap-2 px-3 py-1.5 bg-background border border-border rounded group"
                      >
                        <span className="text-sm font-mono text-foreground">{symbol}</span>
                        <button
                          onClick={() => handleToggleBenchmark(symbol)}
                          className={`px-1.5 py-0.5 text-xs rounded font-medium transition-colors ${
                            isBankNifty 
                              ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30' 
                              : 'bg-blue-500/20 text-blue-400 border border-blue-500/30 hover:bg-blue-500/30'
                          }`}
                          title={`Click to switch to ${isBankNifty ? 'NIFTY' : 'BANKNIFTY'}`}
                        >
                          {isBankNifty ? 'BNF' : 'NF'}
                        </button>
                        {/* Sector dropdown for unassigned */}
                        <select
                          value={sectorMapping[symbol] || ''}
                          onChange={(e) => handleUpdateSector(symbol, e.target.value)}
                          className="px-1 py-0.5 text-xs bg-background border border-border rounded text-foreground"
                          title="Assign sector"
                        >
                          <option value="">Sector?</option>
                          {SECTORS.map(s => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => handleRemoveSymbol(symbol)}
                          className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-opacity"
                          title="Remove"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
        
        {watchlist.length === 0 && (
          <div className="text-center text-muted-foreground py-8">
            No symbols in watchlist. Add some above.
          </div>
        )}
        
        {/* Legend */}
        <div className="mt-4 pt-3 border-t border-border">
          <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded">NF</span>
              = NIFTY benchmark
            </span>
            <span className="flex items-center gap-1">
              <span className="px-1.5 py-0.5 bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded">BNF</span>
              = BANKNIFTY benchmark
            </span>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {SECTORS.map(s => (
              <span key={s.value} className={`px-1.5 py-0.5 text-xs rounded border ${getSectorColor(s.value)}`}>
                {s.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Note */}
      <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded text-amber-400 text-sm">
        <AlertTriangle className="w-4 h-4 inline mr-2" />
        <strong>Note:</strong> After adding/removing symbols, restart the backend to subscribe to new TrueData feeds.
      </div>
    </div>
  );
};

// ============================================
// INDEX MASTER TAB
// ============================================
const IndexMasterTab = () => {
  const [indices, setIndices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    symbol: '',
    expiry_cycle: 'Weekly',
    base_expiry_day: 'Thursday',
    lot_size: '',
    scrip_id: '',
    active: true,
  });

  // Get auth token from localStorage
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  };

  const fetchIndices = useCallback(async () => {
    try {
      setError(null);
      const res = await fetch(`${API}/admin/index-master`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setIndices(data);
    } catch (error) {
      console.error('Failed to fetch indices:', error);
      setError(error.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchIndices();
  }, [fetchIndices]);

  const handleCreate = async () => {
    try {
      setError(null);
      const res = await fetch(`${API}/admin/index-master`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...formData,
          lot_size: parseInt(formData.lot_size),
          scrip_id: formData.scrip_id.trim(),
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${res.status}`);
      }
      setShowAddForm(false);
      setFormData({ symbol: '', expiry_cycle: 'Weekly', base_expiry_day: 'Thursday', lot_size: '', scrip_id: '', active: true });
      fetchIndices();
    } catch (error) {
      console.error('Failed to create index:', error);
      setError(error.message);
    }
  };

  const handleUpdate = async (id) => {
    try {
      setError(null);
      const res = await fetch(`${API}/admin/index-master/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...formData,
          lot_size: parseInt(formData.lot_size),
          scrip_id: formData.scrip_id.trim(),
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${res.status}`);
      }
      setEditingId(null);
      fetchIndices();
    } catch (error) {
      console.error('Failed to update index:', error);
      setError(error.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this index?')) return;
    try {
      setError(null);
      const res = await fetch(`${API}/admin/index-master/${id}`, { 
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${res.status}`);
      }
      fetchIndices();
    } catch (error) {
      console.error('Failed to delete index:', error);
      setError(error.message);
    }
  };

  const startEdit = (index) => {
    setEditingId(index.id);
    setFormData({
      symbol: index.symbol,
      expiry_cycle: index.expiry_cycle,
      base_expiry_day: index.base_expiry_day,
      lot_size: index.lot_size.toString(),
      scrip_id: index.scrip_id,  // Already a string
      active: index.active,
    });
  };

  const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  const CYCLES = ['Weekly', 'Monthly'];

  return (
    <div className="space-y-6" data-testid="index-master-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-foreground flex items-center gap-2">
            <Database className="w-6 h-6 text-accent" />
            Index Master Config
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Define expiry rules for each index. The system will automatically calculate next expiry dates.
          </p>
        </div>
        <Button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2"
          data-testid="add-index-btn"
        >
          <Plus className="w-4 h-4" />
          Add Index
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="panel p-4 bg-sell/10 border-sell/30 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-sell" />
          <div>
            <p className="text-sm font-medium text-sell">Error</p>
            <p className="text-xs text-muted-foreground">{error}</p>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setError(null)}
            className="ml-auto"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Symbol Format Guide */}
      <div className="panel p-4 bg-accent/5 border-accent/30">
        <h4 className="font-semibold text-foreground mb-2 text-sm">Accelpix Ticker Format Guide</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
          <div>
            <span className="text-muted-foreground">Continuous Future:</span>
            <code className="ml-1 font-mono text-accent">NIFTY_I</code>
          </div>
          <div>
            <span className="text-muted-foreground">Next Month:</span>
            <code className="ml-1 font-mono text-accent">NIFTY_II</code>
          </div>
          <div>
            <span className="text-muted-foreground">Contract Future:</span>
            <code className="ml-1 font-mono text-accent">NIFTY24JAN</code>
          </div>
          <div>
            <span className="text-muted-foreground">Index Spot:</span>
            <code className="ml-1 font-mono text-accent">NIFTY 50</code>
          </div>
        </div>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="panel p-4 border-accent">
          <h4 className="font-medium text-foreground mb-4">Add New Index</h4>
          <div className="grid grid-cols-6 gap-3">
            <input
              placeholder="Symbol (e.g., NIFTY)"
              value={formData.symbol}
              onChange={(e) => setFormData({ ...formData, symbol: e.target.value.toUpperCase() })}
              className="bg-surface border border-border px-3 py-2 text-sm font-mono"
              data-testid="new-index-symbol"
            />
            <select
              value={formData.expiry_cycle}
              onChange={(e) => setFormData({ ...formData, expiry_cycle: e.target.value })}
              className="bg-surface border border-border px-3 py-2 text-sm"
              data-testid="new-index-cycle"
            >
              {CYCLES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              value={formData.base_expiry_day}
              onChange={(e) => setFormData({ ...formData, base_expiry_day: e.target.value })}
              className="bg-surface border border-border px-3 py-2 text-sm"
              data-testid="new-index-day"
            >
              {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <input
              type="number"
              placeholder="Lot Size"
              value={formData.lot_size}
              onChange={(e) => setFormData({ ...formData, lot_size: e.target.value })}
              className="bg-surface border border-border px-3 py-2 text-sm font-mono"
              data-testid="new-index-lot"
            />
            <input
              type="text"
              placeholder="Ticker (e.g., NIFTY_I)"
              value={formData.scrip_id}
              onChange={(e) => setFormData({ ...formData, scrip_id: e.target.value.toUpperCase() })}
              className="bg-surface border border-border px-3 py-2 text-sm font-mono"
              data-testid="new-index-scrip"
            />
            <div className="flex gap-2">
              <Button onClick={handleCreate} className="flex-1" data-testid="confirm-add-index">
                <Check className="w-4 h-4" />
              </Button>
              <Button variant="outline" onClick={() => setShowAddForm(false)} className="flex-1">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="panel overflow-hidden">
        <table className="w-full" data-testid="index-master-table">
          <thead>
            <tr className="border-b border-border bg-surface-highlight">
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Symbol</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Expiry Cycle</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Base Expiry Day</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Lot Size</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Ticker</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Active</th>
              <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Loading...</td></tr>
            ) : indices.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No indices configured. Add your first index above.</td></tr>
            ) : indices.map((index) => (
              <tr key={index.id} className="hover:bg-surface-highlight/50 transition-colors" data-testid={`index-row-${index.id}`}>
                {editingId === index.id ? (
                  <>
                    <td className="px-4 py-3">
                      <input
                        value={formData.symbol}
                        onChange={(e) => setFormData({ ...formData, symbol: e.target.value.toUpperCase() })}
                        className="bg-surface border border-border px-2 py-1 text-sm font-mono w-full"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={formData.expiry_cycle}
                        onChange={(e) => setFormData({ ...formData, expiry_cycle: e.target.value })}
                        className="bg-surface border border-border px-2 py-1 text-sm w-full"
                      >
                        {CYCLES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={formData.base_expiry_day}
                        onChange={(e) => setFormData({ ...formData, base_expiry_day: e.target.value })}
                        className="bg-surface border border-border px-2 py-1 text-sm w-full"
                      >
                        {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        value={formData.lot_size}
                        onChange={(e) => setFormData({ ...formData, lot_size: e.target.value })}
                        className="bg-surface border border-border px-2 py-1 text-sm font-mono w-full"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        placeholder="e.g., NIFTY_I"
                        value={formData.scrip_id}
                        onChange={(e) => setFormData({ ...formData, scrip_id: e.target.value.toUpperCase() })}
                        className="bg-surface border border-border px-2 py-1 text-sm font-mono w-full"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setFormData({ ...formData, active: !formData.active })}
                        className={`flex items-center gap-2 ${formData.active ? 'text-buy' : 'text-muted-foreground'}`}
                      >
                        <span className={`w-2 h-2 rounded-full ${formData.active ? 'bg-buy' : 'bg-muted-foreground'}`} />
                        {formData.active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => handleUpdate(index.id)} className="text-buy hover:text-buy/80">
                          <Check className="w-4 h-4" />
                        </button>
                        <button onClick={() => setEditingId(null)} className="text-muted-foreground hover:text-foreground">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-3 font-mono font-semibold text-foreground">{index.symbol}</td>
                    <td className="px-4 py-3">
                      <Badge variant={index.expiry_cycle === 'Monthly' ? 'default' : 'secondary'} className={index.expiry_cycle === 'Monthly' ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' : 'bg-blue-500/20 text-blue-300 border border-blue-500/30'}>
                        {index.expiry_cycle}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-foreground">{index.base_expiry_day}</td>
                    <td className="px-4 py-3 font-mono text-foreground">{index.lot_size}</td>
                    <td className="px-4 py-3 font-mono text-muted-foreground">{index.scrip_id}</td>
                    <td className="px-4 py-3">
                      <span className={`flex items-center gap-2 ${index.active ? 'text-buy' : 'text-muted-foreground'}`}>
                        <span className={`w-2 h-2 rounded-full ${index.active ? 'bg-buy' : 'bg-muted-foreground'}`} />
                        {index.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-3">
                        <button onClick={() => startEdit(index)} className="text-muted-foreground hover:text-foreground" data-testid={`edit-index-${index.id}`}>
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(index.id)} className="text-sell hover:text-sell/80" data-testid={`delete-index-${index.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================
// HOLIDAYS TAB
// ============================================
const HolidaysTab = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newDate, setNewDate] = useState('');
  const [newDescription, setNewDescription] = useState('');

  // Get auth token from localStorage
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  };

  const fetchHolidays = useCallback(async () => {
    try {
      setError(null);
      const res = await fetch(`${API}/admin/holidays`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setHolidays(data);
    } catch (error) {
      console.error('Failed to fetch holidays:', error);
      setError(error.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchHolidays();
  }, [fetchHolidays]);

  const handleAdd = async () => {
    if (!newDate) return;
    try {
      setError(null);
      const res = await fetch(`${API}/admin/holidays`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ date: newDate, description: newDescription || null }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${res.status}`);
      }
      setNewDate('');
      setNewDescription('');
      fetchHolidays();
    } catch (error) {
      console.error('Failed to add holiday:', error);
      setError(error.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      setError(null);
      const res = await fetch(`${API}/admin/holidays/${id}`, { 
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${res.status}`);
      }
      fetchHolidays();
    } catch (error) {
      console.error('Failed to delete holiday:', error);
      setError(error.message);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  return (
    <div className="space-y-6" data-testid="holidays-tab">
      <div>
        <h2 className="font-heading text-2xl font-bold text-foreground flex items-center gap-2">
          <Calendar className="w-6 h-6 text-accent" />
          Market Holidays
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Manage market holidays to ensure accurate expiry calculations. Expiry dates falling on holidays will shift backward.
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="panel p-4 bg-sell/10 border-sell/30 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-sell" />
          <div>
            <p className="text-sm font-medium text-sell">Error</p>
            <p className="text-xs text-muted-foreground">{error}</p>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setError(null)}
            className="ml-auto"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      <div className="panel p-6">
        {/* Add Holiday Form */}
        <div className="flex gap-4 items-end mb-6">
          <div className="flex-1 max-w-xs">
            <label className="block text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">Holiday Date</label>
            <input
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              className="w-full bg-surface border border-border px-4 py-2.5 text-sm text-foreground focus:outline-none focus:border-accent"
              data-testid="holiday-date-input"
            />
          </div>
          <div className="flex-1 max-w-sm">
            <label className="block text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">Description (optional)</label>
            <input
              type="text"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="e.g., Republic Day"
              className="w-full bg-surface border border-border px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-accent"
              data-testid="holiday-description-input"
            />
          </div>
          <Button onClick={handleAdd} disabled={!newDate} className="flex items-center gap-2" data-testid="add-holiday-btn">
            <Plus className="w-4 h-4" />
            Add Holiday
          </Button>
        </div>

        {/* Holidays List */}
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-3">
            Upcoming Holidays ({holidays.length})
          </h4>
          
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : holidays.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No upcoming holidays configured.
            </div>
          ) : (
            <div className="space-y-2">
              {holidays.map((holiday) => (
                <div
                  key={holiday.id}
                  className="flex items-center justify-between bg-surface-highlight px-4 py-3 border border-border"
                  data-testid={`holiday-item-${holiday.id}`}
                >
                  <div className="flex items-center gap-4">
                    <span className="font-mono font-semibold text-foreground">
                      {formatDate(holiday.date)}
                    </span>
                    {holiday.description && (
                      <span className="text-muted-foreground">— {holiday.description}</span>
                    )}
                  </div>
                  <button
                    onClick={() => handleDelete(holiday.id)}
                    className="text-sell hover:text-sell/80"
                    data-testid={`delete-holiday-${holiday.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// SESSION 85: SETTLEMENT LOGS TAB
// ============================================
// EOD Market Profile snapshots, Naked POCs, Market Regimes
// Persistent ledger for auditing historical data
const SettlementLogsTab = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterSymbol, setFilterSymbol] = useState(null);

  // Fetch settlement logs
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const symbolParam = filterSymbol ? `?symbol=${filterSymbol}` : '';
      
      const response = await fetch(`${API}/settlement-logs${symbolParam}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setLogs(data.records || []);
      } else {
        setError(data.error || 'Failed to fetch logs');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filterSymbol]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/settlement-logs/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStats(data);
        }
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [fetchLogs, fetchStats]);

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-IN', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Get regime color classes
  const getRegimeColor = (regime) => {
    switch (regime?.toUpperCase()) {
      case 'TREND_MODE':
        return 'bg-green-500/20 text-green-400 border-green-500/50';
      case 'CHOP_MODE':
        return 'bg-amber-500/20 text-amber-400 border-amber-500/50';
      case 'NORMAL':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
      default:
        return 'bg-slate-500/20 text-slate-400 border-slate-500/50';
    }
  };

  // Get symbol badge color
  const getSymbolColor = (symbol) => {
    return symbol === 'NIFTY' 
      ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/50'
      : 'bg-purple-500/20 text-purple-400 border-purple-500/50';
  };

  return (
    <div className="space-y-6" data-testid="settlement-logs-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-heading font-bold text-foreground flex items-center gap-2">
            <FileText className="w-6 h-6 text-cyan-400" />
            Settlement Logs
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            SESSION 85: EOD Market Profile snapshots saved for Naked POC targets
          </p>
        </div>
        
        <Button
          onClick={() => { fetchLogs(); fetchStats(); }}
          disabled={loading}
          variant="outline"
          className="flex items-center gap-2"
          data-testid="refresh-logs-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-3 gap-4" data-testid="settlement-stats">
          <div className="panel p-4 text-center">
            <div className="text-3xl font-bold font-mono text-foreground">
              {stats.total_records || 0}
            </div>
            <div className="text-xs text-muted-foreground mt-1">Total Records</div>
          </div>
          
          <div className="panel p-4 text-center border-l-4 border-cyan-500">
            <div className="text-3xl font-bold font-mono text-cyan-400">
              {stats.by_symbol?.NIFTY?.count || 0}
            </div>
            <div className="text-xs text-muted-foreground mt-1">NIFTY Sessions</div>
            <div className="text-[10px] text-cyan-400/70">
              Latest: {stats.by_symbol?.NIFTY?.latest_date || '--'}
            </div>
          </div>
          
          <div className="panel p-4 text-center border-l-4 border-purple-500">
            <div className="text-3xl font-bold font-mono text-purple-400">
              {stats.by_symbol?.BANKNIFTY?.count || 0}
            </div>
            <div className="text-xs text-muted-foreground mt-1">BANKNIFTY Sessions</div>
            <div className="text-[10px] text-purple-400/70">
              Latest: {stats.by_symbol?.BANKNIFTY?.latest_date || '--'}
            </div>
          </div>
        </div>
      )}

      {/* Filter Buttons */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Filter:</span>
        <Button
          variant={filterSymbol === null ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilterSymbol(null)}
          className="text-xs"
        >
          All
        </Button>
        <Button
          variant={filterSymbol === 'NIFTY' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilterSymbol('NIFTY')}
          className="text-xs"
        >
          NIFTY
        </Button>
        <Button
          variant={filterSymbol === 'BANKNIFTY' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilterSymbol('BANKNIFTY')}
          className="text-xs"
        >
          BANKNIFTY
        </Button>
      </div>

      {/* Error State */}
      {error && (
        <div className="panel p-4 border-l-4 border-red-500 bg-red-500/10">
          <div className="flex items-center gap-2 text-red-400">
            <AlertTriangle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="panel p-8 text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-accent mx-auto mb-2" />
          <p className="text-muted-foreground">Loading settlement logs...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && logs.length === 0 && (
        <div className="panel p-8 text-center">
          <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Settlement Logs Yet</h3>
          <p className="text-sm text-muted-foreground">
            EOD snapshots are saved automatically at 15:30 IST each trading day.
            <br />
            Historical data will appear here after the first market close.
          </p>
        </div>
      )}

      {/* Data Table */}
      {!loading && !error && logs.length > 0 && (
        <div className="panel overflow-hidden" data-testid="settlement-logs-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-surface/50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Symbol</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Final dPOC</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">VAH</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">VAL</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">IB Width</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Market Regime</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {logs.map((log, index) => (
                  <tr 
                    key={`${log.date}-${log.symbol}-${index}`}
                    className="hover:bg-surface/30 transition-colors"
                  >
                    <td className="px-4 py-3 text-sm font-mono text-foreground">
                      {formatDate(log.date)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-bold border ${getSymbolColor(log.symbol)}`}>
                        {log.symbol}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-mono font-bold text-foreground">
                        {log.dpoc?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '--'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-mono text-green-400">
                        {log.vah?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '--'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-mono text-red-400">
                        {log.val?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '--'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-mono text-muted-foreground">
                        {log.ib_width ? `${log.ib_width.toFixed(0)} pts` : '--'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-semibold border ${getRegimeColor(log.market_regime)}`}>
                        {log.market_regime || 'UNKNOWN'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] text-muted-foreground uppercase">
                        {log.source || 'EOD'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Table Footer */}
          <div className="px-4 py-3 border-t border-border bg-surface/30 flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              Showing {logs.length} record{logs.length !== 1 ? 's' : ''}
            </span>
            <span className="text-xs text-muted-foreground">
              EOD snapshots saved at 15:30 IST daily
            </span>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="panel p-4">
        <h3 className="text-sm font-semibold text-foreground mb-3">Market Regime Legend</h3>
        <div className="grid grid-cols-3 gap-4 text-xs">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded border bg-green-500/20 text-green-400 border-green-500/50 font-semibold">
              TREND_MODE
            </span>
            <span className="text-muted-foreground">Narrow IB → Expect breakouts</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded border bg-amber-500/20 text-amber-400 border-amber-500/50 font-semibold">
              CHOP_MODE
            </span>
            <span className="text-muted-foreground">Wide IB → Expect mean reversion</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded border bg-blue-500/20 text-blue-400 border-blue-500/50 font-semibold">
              NORMAL
            </span>
            <span className="text-muted-foreground">Standard behavior</span>
          </div>
        </div>
      </div>
    </div>
  );
};


// ============================================
// STRATEGY TUNER TAB (The 100 Point Rule)
// ============================================
// ============================================
// SESSION 79: DUAL-INDEX STRATEGY TUNER
// ============================================
// Re-architected for Volume Liquidity (Market Profile) + Aggression (Order Flow)
// Each index (NIFTY/BANKNIFTY) has completely independent tuning profiles
const StrategyTunerTab = () => {
  const [activeSymbol, setActiveSymbol] = useState('NIFTY');
  const [configs, setConfigs] = useState({
    NIFTY: {
      // SESSION 103: 4-Weight Distribution (must sum to 100)
      order_flow_weight: 25,
      profile_context_weight: 25,
      profile_levels_weight: 30,
      gex_weight: 20,
      gex_boost_days: 1,  // Expiry day only for NIFTY (weekly)
      // VIX Gate Settings
      vix_low_threshold: 12.0,
      vix_high_threshold: 20.0,
      gate_low_vix: 60,
      gate_high_vix: 80,
      // Stacked Imbalance Settings
      imbalance_ratio: 3.0,
      imbalance_tick_count: 3,
      // SESSION 80: PI Zone Settings
      pi_zone_tolerance: 3,
      pi_zone_boost: 20,
      // CVD Threshold
      cvd_threshold: 10000,
      // Structural Config
      structural_step: 20,
      structural_factor: 2.5,
      structural_max_adj: 40,
      // SESSION 82: Initial Balance (IB) Thresholds
      ib_narrow_threshold: 40,
      ib_wide_threshold: 100,
      // SESSION 105: Entry Rule Configuration
      entry_flow_slope_weight: 0.7,
      entry_flow_cvd_trend_weight: 0.3,
      entry_flow_confirm_threshold: 0.15,
      entry_cvd_trend_window: 30,
      // SESSION 105: CVD Divergence Detector
      cvd_divergence_enabled: true,
      divergence_price_threshold: -5.0,
      divergence_cvd_threshold: 0.1,
      divergence_trending_override: true,
      divergence_chop_relaxation_pct: 50,
      // SESSION 105: Candle-Based Signal Evaluation
      signal_candle_interval_min: 5,
      // SESSION 116: Order Flow Modifiers (GoCharting-style)
      delta_divergence_enabled: true,
      min_delta_divergence: 500,
      divergence_persistence: 2,
      market_weakness_enabled: true,
      weakness_lookback: 3,
      // SESSION 116b: Exhaustion Prints
      exhaustion_enabled: true,
      exhaustion_volume_threshold: 500,
      exhaustion_edge_pct: 15,
      // SESSION 116c: Stacked Imbalances
      stacked_imbalance_enabled: true,
      // SESSION 116d: Extreme Delta
      extreme_delta_enabled: true,
      extreme_delta_threshold_pct: 95,
      // SESSION 117: Entry Rule Modifiers (James Dalton & GoCharting methodology)
      entry_dpoc_veto_enabled: false,  // false = use modifier instead of hard veto
      entry_dpoc_penalty: 15,
      entry_dpoc_at_poc_bonus: 10,
      entry_dpoc_tolerance: 5,
      entry_gex_veto_enabled: false,   // false = use modifier instead of hard veto
      entry_gex_aligned_bonus: 10,
      entry_gex_misaligned_penalty: 10,
      // SESSION 117b: Additional modifiers (replacing more vetoes)
      chop_mode_ib_penalty: 15,        // Penalty for trading at IB boundaries in CHOP_MODE
      gamma_wall_penalty: 10,          // Penalty for trading into gamma wall
      gamma_wall_bonus: 10,            // Bonus when gamma wall supports direction
      // SESSION 121: Exit Target Configuration (per GUI Trading 23-video methodology)
      min_t1_atr_multiplier: 0.3,      // Min T1 distance as ATR multiplier (0.3 = 15pts for 50 ATR)
      sl_tick_buffer: 15,              // SL buffer in points (1-2 boxes beyond zone)
      // SESSION 121: LQP (Liquidity Pump) Configuration
      lqp_enabled: false,              // LQP detection enabled
      lqp_lot_threshold: 25,           // Void zone if < 25 lots on one side
      lqp_price_step: 5,               // Aggregate to 5-point price levels
      lqp_target_points: 60,           // Expected minimum move
      lqp_god_score_bonus: 25,         // Points added to God Score
      // SESSION 128: God Score Override for Micro-Structure Vetoes
      god_score_override_threshold: 100,  // Bypass flow/absorption vetoes when score >= this (95-100)
      // SESSION 128b: Signal Time Window Configuration
      signal_start_time: "09:20",      // Signal start time in IST (HH:MM) - set to "09:45" or "10:15" for IB
      signal_end_time: "15:25",        // Signal end time in IST (HH:MM)
    },
    BANKNIFTY: {
      // SESSION 103: 4-Weight Distribution (must sum to 100)
      order_flow_weight: 20,
      profile_context_weight: 30,
      profile_levels_weight: 40,
      gex_weight: 10,
      gex_boost_days: 3,  // 3 days before expiry for BANKNIFTY (monthly)
      // VIX Gate Settings
      vix_low_threshold: 14.0,
      vix_high_threshold: 22.0,
      gate_low_vix: 65,
      gate_high_vix: 85,
      // Stacked Imbalance Settings
      imbalance_ratio: 2.5,
      imbalance_tick_count: 3,
      // SESSION 80: PI Zone Settings
      pi_zone_tolerance: 10,
      pi_zone_boost: 20,
      // CVD Threshold
      cvd_threshold: 3000,
      // Structural Config
      structural_step: 30,
      structural_factor: 2.5,
      structural_max_adj: 60,
      // SESSION 82: Initial Balance (IB) Thresholds
      ib_narrow_threshold: 100,
      ib_wide_threshold: 250,
      // SESSION 105: Entry Rule Configuration
      entry_flow_slope_weight: 0.7,
      entry_flow_cvd_trend_weight: 0.3,
      entry_flow_confirm_threshold: 0.15,
      entry_cvd_trend_window: 30,
      // SESSION 105: CVD Divergence Detector
      cvd_divergence_enabled: true,
      divergence_price_threshold: -5.0,
      divergence_cvd_threshold: 0.1,
      divergence_trending_override: true,
      divergence_chop_relaxation_pct: 50,
      // SESSION 105: Candle-Based Signal Evaluation
      signal_candle_interval_min: 5,
      // SESSION 116: Order Flow Modifiers (GoCharting-style)
      delta_divergence_enabled: true,
      min_delta_divergence: 1500,
      divergence_persistence: 2,
      market_weakness_enabled: true,
      weakness_lookback: 3,
      // SESSION 116b: Exhaustion Prints
      exhaustion_enabled: true,
      exhaustion_volume_threshold: 1500,
      exhaustion_edge_pct: 15,
      // SESSION 116c: Stacked Imbalances
      stacked_imbalance_enabled: true,
      // SESSION 116d: Extreme Delta
      extreme_delta_enabled: true,
      extreme_delta_threshold_pct: 95,
      // SESSION 117: Entry Rule Modifiers (James Dalton & GoCharting methodology)
      entry_dpoc_veto_enabled: false,  // false = use modifier instead of hard veto
      entry_dpoc_penalty: 18,          // Higher for BNF due to wider range
      entry_dpoc_at_poc_bonus: 12,
      entry_dpoc_tolerance: 15,        // BNF has wider POC zones
      entry_gex_veto_enabled: false,   // false = use modifier instead of hard veto
      entry_gex_aligned_bonus: 8,      // Lower weight for BNF (less GEX impact)
      entry_gex_misaligned_penalty: 8,
      // SESSION 117b: Additional modifiers (replacing more vetoes)
      chop_mode_ib_penalty: 18,        // Higher for BNF due to wider range
      gamma_wall_penalty: 8,           // Lower for BNF (less GEX impact)
      gamma_wall_bonus: 8,
      // SESSION 121: Exit Target Configuration (per GUI Trading 23-video methodology)
      min_t1_atr_multiplier: 0.3,      // Min T1 distance as ATR multiplier (0.3 = 45pts for 150 ATR)
      sl_tick_buffer: 40,              // SL buffer in points (1-2 boxes beyond zone) - higher for BNF
      // SESSION 121: LQP (Liquidity Pump) Configuration
      lqp_enabled: false,              // LQP detection enabled
      lqp_lot_threshold: 25,           // Void zone if < 25 lots on one side
      lqp_price_step: 10,              // Aggregate to 10-point price levels (larger for BNF)
      lqp_target_points: 100,          // Expected minimum move (larger for BNF)
      lqp_god_score_bonus: 25,         // Points added to God Score
      // SESSION 128: God Score Override for Micro-Structure Vetoes
      god_score_override_threshold: 100,  // Bypass flow/absorption vetoes when score >= this (95-100)
      // SESSION 128b: Signal Time Window Configuration
      signal_start_time: "09:20",      // Signal start time in IST (HH:MM) - set to "09:45" or "10:15" for IB
      signal_end_time: "15:25",        // Signal end time in IST (HH:MM)
    }
  });
  // SESSION 103: GEX Boost Multiplier (shared across symbols)
  const [gexBoostMultiplier, setGexBoostMultiplier] = useState(2.0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // SESSION 85: Hybrid ATR Stop-Loss Configuration
  const [slConfig, setSlConfig] = useState(null);
  const [slLoading, setSlLoading] = useState(true);
  const [slSaving, setSlSaving] = useState(false);
  const [slEditing, setSlEditing] = useState(false);
  const [slEditValues, setSlEditValues] = useState({
    nifty_open_sl: 45,
    nifty_min_sl: 25,
    banknifty_open_sl: 150,
    banknifty_min_sl: 75,
    opening_window_mins: 15
  });

  // SESSION 104: IV Skew Dislocation Hunter Configuration
  const [ivSkewConfig, setIvSkewConfig] = useState({
    NIFTY: {
      z_score_buy: -2.0,
      z_score_sell: 2.0,
      persistence: 3,
      atm_proximity: 4
    },
    BANKNIFTY: {
      z_score_buy: -2.0,
      z_score_sell: 2.0,
      persistence: 3,
      atm_proximity: 4
    }
  });
  const [ivSkewLoading, setIvSkewLoading] = useState(true);
  const [ivSkewSaving, setIvSkewSaving] = useState(false);
  const [ivSkewStatus, setIvSkewStatus] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  };
  
  // SESSION 85: Fetch SL Configuration
  const fetchSlConfig = useCallback(async () => {
    try {
      setSlLoading(true);
      const res = await fetch(`${API}/execution/sl-config`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setSlConfig(data);
        // Update edit values from fetched config
        setSlEditValues({
          nifty_open_sl: data.nifty?.open_sl || 45,
          nifty_min_sl: data.nifty?.min_sl || 25,
          banknifty_open_sl: data.banknifty?.open_sl || 150,
          banknifty_min_sl: data.banknifty?.min_sl || 75,
          opening_window_mins: data.opening_window_mins || 15
        });
      }
    } catch (err) {
      console.warn('Failed to fetch SL config:', err.message);
    }
    setSlLoading(false);
  }, []);
  
  // SESSION 85: Save SL Configuration
  const saveSlConfig = async () => {
    setSlSaving(true);
    try {
      const res = await fetch(`${API}/execution/sl-config`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(slEditValues)
      });
      const data = await res.json();
      if (data.success) {
        setSuccess('SL Configuration saved successfully!');
        setSlEditing(false);
        fetchSlConfig(); // Refresh
      } else {
        setError(data.error || 'Failed to save SL config');
      }
    } catch (err) {
      setError('Failed to save SL config: ' + err.message);
    }
    setSlSaving(false);
  };
  
  // SESSION 85: Reset SL Configuration to env defaults
  const resetSlConfig = async () => {
    if (!window.confirm('Reset SL configuration to environment variable defaults?')) return;
    setSlSaving(true);
    try {
      const res = await fetch(`${API}/execution/sl-config`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.success) {
        setSuccess('SL Configuration reset to defaults!');
        setSlEditing(false);
        fetchSlConfig(); // Refresh
      } else {
        setError(data.error || 'Failed to reset SL config');
      }
    } catch (err) {
      setError('Failed to reset SL config: ' + err.message);
    }
    setSlSaving(false);
  };

  // SESSION 104: Fetch IV Skew Configuration and Status
  const fetchIvSkewConfig = useCallback(async () => {
    try {
      setIvSkewLoading(true);
      const [configRes, statusRes] = await Promise.all([
        fetch(`${API}/iv-skew/config`, { headers: getAuthHeaders() }),
        fetch(`${API}/iv-skew/status`)
      ]);
      
      if (configRes.ok) {
        const data = await configRes.json();
        const config = data.config || {};
        setIvSkewConfig({
          NIFTY: {
            z_score_buy: config.IV_SKEW_NIFTY_Z_SCORE_BUY ?? -2.0,
            z_score_sell: config.IV_SKEW_NIFTY_Z_SCORE_SELL ?? 2.0,
            persistence: config.IV_SKEW_NIFTY_PERSISTENCE ?? 3,
            atm_proximity: config.IV_SKEW_NIFTY_ATM_PROXIMITY ?? 4
          },
          BANKNIFTY: {
            z_score_buy: config.IV_SKEW_BANKNIFTY_Z_SCORE_BUY ?? -2.0,
            z_score_sell: config.IV_SKEW_BANKNIFTY_Z_SCORE_SELL ?? 2.0,
            persistence: config.IV_SKEW_BANKNIFTY_PERSISTENCE ?? 3,
            atm_proximity: config.IV_SKEW_BANKNIFTY_ATM_PROXIMITY ?? 4
          }
        });
      }
      
      if (statusRes.ok) {
        const status = await statusRes.json();
        setIvSkewStatus(status.status);
      }
    } catch (err) {
      console.warn('Failed to fetch IV Skew config:', err.message);
    }
    setIvSkewLoading(false);
  }, []);

  // SESSION 104: Save IV Skew Configuration
  const saveIvSkewConfig = async () => {
    setIvSkewSaving(true);
    try {
      const updates = {
        IV_SKEW_NIFTY_Z_SCORE_BUY: ivSkewConfig.NIFTY.z_score_buy,
        IV_SKEW_NIFTY_Z_SCORE_SELL: ivSkewConfig.NIFTY.z_score_sell,
        IV_SKEW_NIFTY_PERSISTENCE: ivSkewConfig.NIFTY.persistence,
        IV_SKEW_NIFTY_ATM_PROXIMITY: ivSkewConfig.NIFTY.atm_proximity,
        IV_SKEW_BANKNIFTY_Z_SCORE_BUY: ivSkewConfig.BANKNIFTY.z_score_buy,
        IV_SKEW_BANKNIFTY_Z_SCORE_SELL: ivSkewConfig.BANKNIFTY.z_score_sell,
        IV_SKEW_BANKNIFTY_PERSISTENCE: ivSkewConfig.BANKNIFTY.persistence,
        IV_SKEW_BANKNIFTY_ATM_PROXIMITY: ivSkewConfig.BANKNIFTY.atm_proximity
      };
      
      const res = await fetch(`${API}/iv-skew/config`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates)
      });
      const data = await res.json();
      if (data.success) {
        setSuccess('IV Skew Configuration saved successfully!');
        fetchIvSkewConfig(); // Refresh
      } else {
        setError(data.error || 'Failed to save IV Skew config');
      }
    } catch (err) {
      setError('Failed to save IV Skew config: ' + err.message);
    }
    setIvSkewSaving(false);
  };

  // SESSION 104: Toggle IV Skew Analyzer On/Off
  const toggleIvSkewAnalyzer = async (start) => {
    try {
      const endpoint = start ? 'start' : 'stop';
      const res = await fetch(`${API}/iv-skew/${endpoint}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.success) {
        setSuccess(`IV Skew Analyzer ${start ? 'started' : 'stopped'}!`);
        fetchIvSkewConfig(); // Refresh status
      } else {
        setError(data.message || `Failed to ${endpoint} IV Skew analyzer`);
      }
    } catch (err) {
      setError(`Failed to ${start ? 'start' : 'stop'} IV Skew analyzer: ` + err.message);
    }
  };

  const fetchConfig = useCallback(async () => {
    try {
      setError(null);
      // Fetch symbol config from backend
      const res = await fetch(`${API}/admin/symbol-config`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      // SESSION 100: Fix - config is nested under data.config
      const configData = data.config || data;
      // Merge fetched data with defaults
      if (configData.NIFTY || configData.BANKNIFTY) {
        setConfigs(prev => ({
          NIFTY: { ...prev.NIFTY, ...(configData.NIFTY || {}) },
          BANKNIFTY: { ...prev.BANKNIFTY, ...(configData.BANKNIFTY || {}) }
        }));
      }
    } catch (err) {
      console.warn('Using default config:', err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchConfig();
    fetchSlConfig();  // SESSION 85: Fetch SL configuration
    fetchIvSkewConfig();  // SESSION 104: Fetch IV Skew configuration
  }, [fetchConfig, fetchSlConfig, fetchIvSkewConfig]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await fetch(`${API}/admin/symbol-config`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(configs),
      });
      const data = await res.json();

      if (data.error) {
        setError(data.detail || data.error);
      } else {
        setSuccess('Session 79 configuration saved successfully!');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const handleReset = async () => {
    if (!window.confirm('Reset to default Session 103 configuration (4-weight system)?')) return;
    setConfigs({
      NIFTY: {
        order_flow_weight: 25, profile_context_weight: 25, profile_levels_weight: 30, gex_weight: 20,
        gex_boost_days: 1,
        ema_alpha: 0.3, context_lock_time: '10:15', signal_cooldown_seconds: 30,
        vix_low_threshold: 12.0, vix_high_threshold: 20.0, gate_low_vix: 60, gate_high_vix: 80,
        imbalance_ratio: 3.0, imbalance_tick_count: 3, cvd_threshold: 10000,
        structural_step: 20, structural_factor: 2.5, structural_max_adj: 40,
        pi_zone_tolerance: 3, pi_zone_boost: 20,
        ib_narrow_threshold: 40, ib_wide_threshold: 100,
        entry_flow_slope_weight: 0.7, entry_flow_cvd_trend_weight: 0.3,
        entry_flow_confirm_threshold: 0.15, entry_cvd_trend_window: 30,
        cvd_divergence_enabled: true, divergence_price_threshold: -5.0,
        divergence_cvd_threshold: 0.1, divergence_trending_override: true,
        divergence_chop_relaxation_pct: 50,
      },
      BANKNIFTY: {
        order_flow_weight: 20, profile_context_weight: 30, profile_levels_weight: 40, gex_weight: 10,
        gex_boost_days: 3,
        ema_alpha: 0.3, context_lock_time: '10:15', signal_cooldown_seconds: 30,
        vix_low_threshold: 14.0, vix_high_threshold: 22.0, gate_low_vix: 65, gate_high_vix: 85,
        imbalance_ratio: 2.5, imbalance_tick_count: 3, cvd_threshold: 3000,
        structural_step: 30, structural_factor: 2.5, structural_max_adj: 60,
        pi_zone_tolerance: 10, pi_zone_boost: 20,
        ib_narrow_threshold: 100, ib_wide_threshold: 250,
        entry_flow_slope_weight: 0.7, entry_flow_cvd_trend_weight: 0.3,
        entry_flow_confirm_threshold: 0.15, entry_cvd_trend_window: 30,
        cvd_divergence_enabled: true, divergence_price_threshold: -5.0,
        divergence_cvd_threshold: 0.1, divergence_trending_override: true,
        divergence_chop_relaxation_pct: 50,
        signal_candle_interval_min: 5,
      }
    });
    setGexBoostMultiplier(2.0);
    setSuccess('Configuration reset to Session 103 defaults (4-weight + stability features)');
    setTimeout(() => setSuccess(null), 3000);
  };

  const updateConfig = (symbol, field, value) => {
    setConfigs(prev => ({
      ...prev,
      [symbol]: { ...prev[symbol], [field]: value }
    }));
  };

  const config = configs[activeSymbol];
  // SESSION 103: Updated to 4-weight system
  const weightsTotal = config.order_flow_weight + config.profile_context_weight + config.profile_levels_weight + config.gex_weight;
  const isWeightsValid = weightsTotal === 100;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-accent animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="strategy-tuner-tab">
      <div>
        <h2 className="font-heading text-2xl font-bold text-foreground flex items-center gap-2">
          <FlaskConical className="w-6 h-6 text-accent" />
          Session 103: Dual-Index Strategy Tuner
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Configure independent profiles for NIFTY and BANKNIFTY. 4-Weight System: Order Flow + Context + Levels + GEX = 100
        </p>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="panel p-4 bg-sell/10 border-sell/30 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-sell" />
          <p className="text-sm text-sell">{error}</p>
          <Button variant="ghost" size="sm" onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {success && (
        <div className="panel p-4 bg-buy/10 border-buy/30 flex items-center gap-3">
          <Check className="w-5 h-5 text-buy" />
          <p className="text-sm text-buy">{success}</p>
        </div>
      )}

      {/* Session 147j+1 — Per-symbol-per-pattern execution matrix */}
      <PatternMatrixCard />

      {/* Symbol Tabs */}
      <div className="flex gap-2 border-b border-border pb-2">
        {['NIFTY', 'BANKNIFTY'].map(symbol => (
          <button
            key={symbol}
            onClick={() => setActiveSymbol(symbol)}
            className={`px-6 py-3 font-semibold text-sm transition-all rounded-t ${
              activeSymbol === symbol
                ? 'bg-accent text-accent-foreground'
                : 'bg-surface text-muted-foreground hover:bg-surface-highlight'
            }`}
            data-testid={`tab-${symbol.toLowerCase()}`}
          >
            {symbol}
          </button>
        ))}
      </div>

      {/* Active Symbol Config */}
      <div className="space-y-6">
        {/* Core Weights Section - SESSION 103: 4-Weight System */}
        <div className="panel p-6">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-accent" />
            {activeSymbol} Core Weights (Must Sum to 100)
          </h3>
          <p className="text-xs text-amber-400 mb-4 bg-amber-500/10 border border-amber-500/30 p-2 rounded">
            SESSION 103: 4-Weight Architecture: Order Flow + Profile Context + Profile Levels + GEX = 100
          </p>

          <div className="grid grid-cols-4 gap-4">
            {/* Order Flow Weight */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-cyan-400">Order Flow</label>
              <input
                type="number"
                min="0"
                max="100"
                value={config.order_flow_weight}
                onChange={(e) => updateConfig(activeSymbol, 'order_flow_weight', parseInt(e.target.value) || 0)}
                className="w-full bg-surface border border-cyan-500/30 px-3 py-2 rounded text-foreground font-mono"
                data-testid={`${activeSymbol.toLowerCase()}-order-flow-weight`}
              />
              <p className="text-xs text-muted-foreground">CVD/Delta/Imbalances</p>
            </div>

            {/* Profile Context Weight */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-purple-400">Profile Context</label>
              <input
                type="number"
                min="0"
                max="100"
                value={config.profile_context_weight}
                onChange={(e) => updateConfig(activeSymbol, 'profile_context_weight', parseInt(e.target.value) || 0)}
                className="w-full bg-surface border border-purple-500/30 px-3 py-2 rounded text-foreground font-mono"
                data-testid={`${activeSymbol.toLowerCase()}-profile-context-weight`}
              />
              <p className="text-xs text-muted-foreground">Day Type/Open/Shape</p>
            </div>

            {/* Profile Levels Weight */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-orange-400">Profile Levels</label>
              <input
                type="number"
                min="0"
                max="100"
                value={config.profile_levels_weight}
                onChange={(e) => updateConfig(activeSymbol, 'profile_levels_weight', parseInt(e.target.value) || 0)}
                className="w-full bg-surface border border-orange-500/30 px-3 py-2 rounded text-foreground font-mono"
                data-testid={`${activeSymbol.toLowerCase()}-profile-levels-weight`}
              />
              <p className="text-xs text-muted-foreground">VA/POC/DHOL/LPZ</p>
            </div>

            {/* GEX Weight */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-green-400">GEX Weight</label>
              <input
                type="number"
                min="0"
                max="100"
                value={config.gex_weight}
                onChange={(e) => updateConfig(activeSymbol, 'gex_weight', parseInt(e.target.value) || 0)}
                className="w-full bg-surface border border-green-500/30 px-3 py-2 rounded text-foreground font-mono"
                data-testid={`${activeSymbol.toLowerCase()}-gex-weight`}
              />
              <p className="text-xs text-muted-foreground">Gamma Walls/Regime</p>
            </div>
          </div>

          {/* Total Indicator */}
          <div className={`mt-4 p-3 rounded ${isWeightsValid ? 'bg-buy/10 border border-buy/30' : 'bg-sell/10 border border-sell/30'}`}>
            <p className={`text-sm font-mono ${isWeightsValid ? 'text-buy' : 'text-sell'}`}>
              Total: {config.order_flow_weight} + {config.profile_context_weight} + {config.profile_levels_weight} + {config.gex_weight} = {weightsTotal} / 100 
              {isWeightsValid ? ' ✓' : ' ✗'}
            </p>
          </div>
        </div>

        {/* SESSION 103: GEX Auto-Adjustment Section */}
        <div className="panel p-6">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-400" />
            {activeSymbol} GEX Expiry Auto-Adjustment
          </h3>
          <p className="text-xs text-muted-foreground mb-4">
            GEX weight automatically increases near expiry. {activeSymbol === 'NIFTY' ? 'Weekly expiry - boost on expiry day.' : 'Monthly expiry - boost 3 days before.'}
          </p>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-green-400">GEX Boost Days Before Expiry</label>
              <input
                type="number"
                min="0"
                max="7"
                value={config.gex_boost_days}
                onChange={(e) => updateConfig(activeSymbol, 'gex_boost_days', parseInt(e.target.value) || 0)}
                className="w-full bg-surface border border-green-500/30 px-3 py-2 rounded text-foreground font-mono"
                data-testid={`${activeSymbol.toLowerCase()}-gex-boost-days`}
              />
              <p className="text-xs text-muted-foreground">
                {activeSymbol === 'NIFTY' ? 'Default: 1 (expiry day only)' : 'Default: 3 (3 days before)'}
              </p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-green-400">GEX Boost Multiplier</label>
              <input
                type="number"
                min="1"
                max="4"
                step="0.5"
                value={gexBoostMultiplier}
                onChange={(e) => setGexBoostMultiplier(parseFloat(e.target.value) || 2.0)}
                className="w-full bg-surface border border-green-500/30 px-3 py-2 rounded text-foreground font-mono"
                data-testid="gex-boost-multiplier"
              />
              <p className="text-xs text-muted-foreground">Default: 2.0x (doubles GEX weight near expiry)</p>
            </div>
          </div>
        </div>

        {/* SESSION 103: God Score Stability Settings */}
        <div className="panel p-6">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-400" />
            {activeSymbol} God Score Stability
          </h3>
          <p className="text-xs text-muted-foreground mb-4">
            Configure EMA smoothing, context locking, and signal cooldown to prevent score jumping.
          </p>

          <div className="grid grid-cols-3 gap-4">
            {/* EMA Alpha */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-blue-400">EMA Alpha (α)</label>
              <input
                type="number"
                min="0.1"
                max="0.5"
                step="0.05"
                value={config.ema_alpha || 0.3}
                onChange={(e) => updateConfig(activeSymbol, 'ema_alpha', parseFloat(e.target.value) || 0.3)}
                className="w-full bg-surface border border-blue-500/30 px-3 py-2 rounded text-foreground font-mono"
                data-testid={`${activeSymbol.toLowerCase()}-ema-alpha`}
              />
              <p className="text-xs text-muted-foreground">0.1 = Smooth, 0.5 = Responsive</p>
            </div>

            {/* Context Lock Time */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-purple-400">Context Lock Time</label>
              <input
                type="text"
                placeholder="HH:MM"
                value={config.context_lock_time || '10:15'}
                onChange={(e) => updateConfig(activeSymbol, 'context_lock_time', e.target.value)}
                className="w-full bg-surface border border-purple-500/30 px-3 py-2 rounded text-foreground font-mono"
                data-testid={`${activeSymbol.toLowerCase()}-context-lock-time`}
              />
              <p className="text-xs text-muted-foreground">Lock Day Type after this IST time</p>
            </div>

            {/* Signal Cooldown */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-amber-400">Signal Cooldown (sec)</label>
              <input
                type="number"
                min="0"
                max="120"
                value={config.signal_cooldown_seconds || 30}
                onChange={(e) => updateConfig(activeSymbol, 'signal_cooldown_seconds', parseInt(e.target.value) || 30)}
                className="w-full bg-surface border border-amber-500/30 px-3 py-2 rounded text-foreground font-mono"
                data-testid={`${activeSymbol.toLowerCase()}-signal-cooldown`}
              />
              <p className="text-xs text-muted-foreground">Min seconds between signals</p>
            </div>
          </div>

          <div className="mt-4 p-3 rounded bg-blue-500/10 border border-blue-500/30">
            <p className="text-xs text-blue-300">
              <span className="font-semibold">Stability Rules:</span> EMA α={config.ema_alpha || 0.3} smooths score changes. 
              Context signals lock at {config.context_lock_time || '10:15'} IST. 
              {config.signal_cooldown_seconds || 30}s cooldown between signals.
            </p>
          </div>
        </div>

        {/* VIX Gate Settings */}
        <div className="panel p-6">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Gauge className="w-5 h-5 text-amber-400" />
            {activeSymbol} Dynamic VIX Gate
          </h3>
          <p className="text-xs text-muted-foreground mb-4">
            TARGET_GATE interpolates between low and high thresholds based on VIX value.
          </p>

          <div className="grid grid-cols-2 gap-6">
            {/* VIX Thresholds */}
            <div className="space-y-4">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase">VIX Thresholds</h4>
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Low VIX Threshold</label>
                <input
                  type="number"
                  step="0.5"
                  value={config.vix_low_threshold}
                  onChange={(e) => updateConfig(activeSymbol, 'vix_low_threshold', parseFloat(e.target.value) || 12)}
                  className="w-full bg-surface border border-border px-3 py-2 rounded text-foreground font-mono"
                />
                <p className="text-xs text-muted-foreground">VIX ≤ this = Low Volatility</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">High VIX Threshold</label>
                <input
                  type="number"
                  step="0.5"
                  value={config.vix_high_threshold}
                  onChange={(e) => updateConfig(activeSymbol, 'vix_high_threshold', parseFloat(e.target.value) || 20)}
                  className="w-full bg-surface border border-border px-3 py-2 rounded text-foreground font-mono"
                />
                <p className="text-xs text-muted-foreground">VIX ≥ this = High Volatility</p>
              </div>
            </div>

            {/* Gate Sizes */}
            <div className="space-y-4">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase">Target Gate Sizes</h4>
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Gate (Low VIX)</label>
                <input
                  type="number"
                  min="40"
                  max="100"
                  value={config.gate_low_vix}
                  onChange={(e) => updateConfig(activeSymbol, 'gate_low_vix', parseInt(e.target.value) || 60)}
                  className="w-full bg-surface border border-border px-3 py-2 rounded text-foreground font-mono"
                />
                <p className="text-xs text-muted-foreground">Threshold when VIX is low (calmer)</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Gate (High VIX)</label>
                <input
                  type="number"
                  min="40"
                  max="100"
                  value={config.gate_high_vix}
                  onChange={(e) => updateConfig(activeSymbol, 'gate_high_vix', parseInt(e.target.value) || 80)}
                  className="w-full bg-surface border border-border px-3 py-2 rounded text-foreground font-mono"
                />
                <p className="text-xs text-muted-foreground">Threshold when VIX is high (volatile)</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stacked Imbalance Settings */}
        <div className="panel p-6">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-cyan-400" />
            {activeSymbol} Stacked Imbalance Detection
          </h3>
          <p className="text-xs text-muted-foreground mb-4">
            STRICT: N consecutive ticks where delta &gt; ratio. No forgiveness - resets on any non-imbalance tick.
          </p>

          <div className="grid grid-cols-3 gap-4">
            {/* Imbalance Ratio */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Imbalance Ratio</label>
              <input
                type="number"
                step="0.5"
                min="1"
                max="10"
                value={config.imbalance_ratio}
                onChange={(e) => updateConfig(activeSymbol, 'imbalance_ratio', parseFloat(e.target.value) || 3)}
                className="w-full bg-surface border border-border px-3 py-2 rounded text-foreground font-mono"
              />
              <p className="text-xs text-muted-foreground">Delta must exceed avg × this</p>
            </div>

            {/* Consecutive Tick Count */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Consecutive Ticks</label>
              <input
                type="number"
                min="2"
                max="10"
                value={config.imbalance_tick_count}
                onChange={(e) => updateConfig(activeSymbol, 'imbalance_tick_count', parseInt(e.target.value) || 3)}
                className="w-full bg-surface border border-border px-3 py-2 rounded text-foreground font-mono"
              />
              <p className="text-xs text-muted-foreground">Must be consecutive (strict)</p>
            </div>

            {/* CVD Threshold */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">CVD Threshold</label>
              <input
                type="number"
                step="1000"
                value={config.cvd_threshold}
                onChange={(e) => updateConfig(activeSymbol, 'cvd_threshold', parseInt(e.target.value) || 10000)}
                className="w-full bg-surface border border-border px-3 py-2 rounded text-foreground font-mono"
              />
              <p className="text-xs text-muted-foreground">Min CVD for signal validation</p>
            </div>
          </div>
        </div>

        {/* SESSION 80: PI Zone Settings */}
        <div className="panel p-6">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Flame className="w-5 h-5 text-orange-500" />
            {activeSymbol} PI Zone (Point of Control + Imbalance)
          </h3>
          <p className="text-xs text-amber-400 mb-4 bg-amber-500/10 border border-amber-500/30 p-2 rounded">
            SESSION 80: When Stacked Imbalance occurs within tolerance of dPOC, apply massive +/-20 boost for highest probability setup.
          </p>

          <div className="grid grid-cols-2 gap-4">
            {/* PI Zone Tolerance */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-orange-400">PI Zone Tolerance (points)</label>
              <input
                type="number"
                min="1"
                max="50"
                value={config.pi_zone_tolerance}
                onChange={(e) => updateConfig(activeSymbol, 'pi_zone_tolerance', parseInt(e.target.value) || 3)}
                className="w-full bg-surface border border-orange-500/30 px-3 py-2 rounded text-foreground font-mono"
              />
              <p className="text-xs text-muted-foreground">
                Max distance from dPOC for PI Zone trigger (NIFTY: 3, BNF: 10)
              </p>
            </div>

            {/* PI Zone Boost */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-orange-400">PI Zone Boost (points)</label>
              <input
                type="number"
                min="5"
                max="50"
                value={config.pi_zone_boost}
                onChange={(e) => updateConfig(activeSymbol, 'pi_zone_boost', parseInt(e.target.value) || 20)}
                className="w-full bg-surface border border-orange-500/30 px-3 py-2 rounded text-foreground font-mono"
              />
              <p className="text-xs text-muted-foreground">
                Score boost applied when PI Zone active (+20 default)
              </p>
            </div>
          </div>
        </div>

        {/* SESSION 82: Initial Balance (IB) Regime Classification */}
        <div className="panel p-6 border-l-4 border-teal-500" data-testid="ib-settings-panel">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-teal-400" />
            {activeSymbol} Initial Balance (IB) - Market Regime
          </h3>
          <p className="text-xs text-teal-400 mb-4 bg-teal-500/10 border border-teal-500/30 p-2 rounded">
            SESSION 82: Track first hour (09:15-10:15) to classify Day Type. TREND_MODE widens gate 20%, CHOP_MODE tightens + strict absorption veto.
          </p>

          <div className="grid grid-cols-2 gap-4">
            {/* IB Narrow Threshold */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-teal-400">IB Narrow Threshold (points)</label>
              <input
                type="number"
                min="10"
                max="200"
                value={config.ib_narrow_threshold}
                onChange={(e) => updateConfig(activeSymbol, 'ib_narrow_threshold', parseInt(e.target.value) || 40)}
                className="w-full bg-surface border border-teal-500/30 px-3 py-2 rounded text-foreground font-mono"
                data-testid="ib-narrow-threshold"
              />
              <p className="text-xs text-muted-foreground">
                IB Width ≤ this = TREND_MODE (expect breakouts)
              </p>
            </div>

            {/* IB Wide Threshold */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-teal-400">IB Wide Threshold (points)</label>
              <input
                type="number"
                min="50"
                max="500"
                value={config.ib_wide_threshold}
                onChange={(e) => updateConfig(activeSymbol, 'ib_wide_threshold', parseInt(e.target.value) || 100)}
                className="w-full bg-surface border border-teal-500/30 px-3 py-2 rounded text-foreground font-mono"
                data-testid="ib-wide-threshold"
              />
              <p className="text-xs text-muted-foreground">
                IB Width ≥ this = CHOP_MODE (expect mean reversion)
              </p>
            </div>
          </div>

          {/* Regime Effects Explanation */}
          <div className="mt-4 grid grid-cols-3 gap-2 text-center">
            <div className="bg-green-500/10 border border-green-500/30 rounded p-2">
              <div className="text-[10px] font-bold text-green-400">TREND_MODE</div>
              <div className="text-[8px] text-muted-foreground">Gate × 1.2</div>
            </div>
            <div className="bg-slate-500/10 border border-slate-500/30 rounded p-2">
              <div className="text-[10px] font-bold text-slate-400">NORMAL</div>
              <div className="text-[8px] text-muted-foreground">Gate × 1.0</div>
            </div>
            <div className="bg-red-500/10 border border-red-500/30 rounded p-2">
              <div className="text-[10px] font-bold text-red-400">CHOP_MODE</div>
              <div className="text-[8px] text-muted-foreground">Gate × 0.9 + Strict Veto</div>
            </div>
          </div>
        </div>


        {/* SESSION 105: Entry Rule Configuration */}
        <div className="panel p-6 border-l-4 border-cyan-500" data-testid="entry-rule-panel">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Waves className="w-5 h-5 text-cyan-400" />
            {activeSymbol} Entry Rule - Candle-Based Evaluation
          </h3>
          <p className="text-xs text-cyan-400 mb-4 bg-cyan-500/10 border border-cyan-500/30 p-2 rounded">
            SESSION 105: Signals fire ONLY at candle close. The candle's net delta confirms direction — no tick-level noise.
          </p>

          {/* Candle Interval - Main Config */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="space-y-2 col-span-2 md:col-span-1">
              <label className="text-sm font-medium text-cyan-400">Candle Interval</label>
              <select
                value={config.signal_candle_interval_min}
                onChange={(e) => updateConfig(activeSymbol, 'signal_candle_interval_min', parseInt(e.target.value))}
                className="w-full bg-surface border border-cyan-500/30 px-3 py-2 rounded text-foreground font-mono"
                data-testid="candle-interval-select"
              >
                <option value={1}>1 min</option>
                <option value={3}>3 min</option>
                <option value={5}>5 min (default)</option>
                <option value={15}>15 min</option>
              </select>
              <p className="text-[10px] text-muted-foreground">How often signal decisions are made</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {/* Flow Slope Weight */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-cyan-400">Slope Weight</label>
              <input
                type="number" step="0.1" min="0" max="1"
                value={config.entry_flow_slope_weight}
                onChange={(e) => updateConfig(activeSymbol, 'entry_flow_slope_weight', parseFloat(e.target.value) || 0.7)}
                className="w-full bg-surface border border-cyan-500/30 px-3 py-2 rounded text-foreground font-mono"
                data-testid="entry-slope-weight"
              />
              <p className="text-[10px] text-muted-foreground">EMA delta slope importance (0-1)</p>
            </div>

            {/* CVD Trend Weight */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-cyan-400">CVD Trend Weight</label>
              <input
                type="number" step="0.1" min="0" max="1"
                value={config.entry_flow_cvd_trend_weight}
                onChange={(e) => updateConfig(activeSymbol, 'entry_flow_cvd_trend_weight', parseFloat(e.target.value) || 0.3)}
                className="w-full bg-surface border border-cyan-500/30 px-3 py-2 rounded text-foreground font-mono"
                data-testid="entry-cvd-weight"
              />
              <p className="text-[10px] text-muted-foreground">CVD rate-of-change importance (0-1)</p>
            </div>

            {/* Flow Confirm Threshold */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-cyan-400">Flow Threshold</label>
              <input
                type="number" step="0.01" min="0.01" max="0.50"
                value={config.entry_flow_confirm_threshold}
                onChange={(e) => updateConfig(activeSymbol, 'entry_flow_confirm_threshold', parseFloat(e.target.value) || 0.15)}
                className="w-full bg-surface border border-cyan-500/30 px-3 py-2 rounded text-foreground font-mono"
                data-testid="entry-flow-threshold"
              />
              <p className="text-[10px] text-muted-foreground">Min score to confirm (0.05-0.50)</p>
            </div>

            {/* CVD Trend Window */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-cyan-400">CVD Window (ticks)</label>
              <input
                type="number" min="10" max="60"
                value={config.entry_cvd_trend_window}
                onChange={(e) => updateConfig(activeSymbol, 'entry_cvd_trend_window', parseInt(e.target.value) || 30)}
                className="w-full bg-surface border border-cyan-500/30 px-3 py-2 rounded text-foreground font-mono"
                data-testid="entry-cvd-window"
              />
              <p className="text-[10px] text-muted-foreground">Ticks for CVD trend calc (10-60)</p>
            </div>
          </div>

          {/* CVD Divergence Detector */}
          <div className="border-t border-gray-700/50 pt-4 mt-2">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-semibold text-amber-400 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                CVD Divergence Detector
              </h4>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="divergence-toggle">
                <input
                  type="checkbox"
                  checked={config.cvd_divergence_enabled}
                  onChange={(e) => updateConfig(activeSymbol, 'cvd_divergence_enabled', e.target.checked)}
                  className="w-4 h-4 rounded border-gray-600"
                />
                <span className="text-xs text-gray-300">{config.cvd_divergence_enabled ? 'Enabled' : 'Disabled'}</span>
              </label>
            </div>

            {config.cvd_divergence_enabled && (
              <>
                <p className="text-[10px] text-amber-400/70 mb-3 bg-amber-500/10 border border-amber-500/20 p-2 rounded">
                  Detects Distribution (price down + CVD up = confirms SHORT) and Accumulation (price up + CVD down = confirms LONG). In trending regimes, can override the flow requirement entirely.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* Divergence Price Threshold */}
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-amber-400">Price EMA Threshold</label>
                    <input
                      type="number" step="0.5" min="-30" max="-1"
                      value={config.divergence_price_threshold}
                      onChange={(e) => updateConfig(activeSymbol, 'divergence_price_threshold', parseFloat(e.target.value) || -5.0)}
                      className="w-full bg-surface border border-amber-500/30 px-3 py-2 rounded text-foreground font-mono text-sm"
                      data-testid="div-price-threshold"
                    />
                    <p className="text-[10px] text-muted-foreground">Min EMA slope for price direction</p>
                  </div>

                  {/* Divergence CVD Threshold */}
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-amber-400">CVD Trend Threshold</label>
                    <input
                      type="number" step="0.05" min="0.01" max="1.0"
                      value={config.divergence_cvd_threshold}
                      onChange={(e) => updateConfig(activeSymbol, 'divergence_cvd_threshold', parseFloat(e.target.value) || 0.1)}
                      className="w-full bg-surface border border-amber-500/30 px-3 py-2 rounded text-foreground font-mono text-sm"
                      data-testid="div-cvd-threshold"
                    />
                    <p className="text-[10px] text-muted-foreground">Min CVD trend for divergence</p>
                  </div>

                  {/* Trending Override */}
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-amber-400">Trending Override</label>
                    <label className="flex items-center gap-2 cursor-pointer mt-2" data-testid="trending-override-toggle">
                      <input
                        type="checkbox"
                        checked={config.divergence_trending_override}
                        onChange={(e) => updateConfig(activeSymbol, 'divergence_trending_override', e.target.checked)}
                        className="w-4 h-4 rounded border-gray-600"
                      />
                      <span className="text-xs text-gray-300">{config.divergence_trending_override ? 'Auto-Pass' : 'Off'}</span>
                    </label>
                    <p className="text-[10px] text-muted-foreground">Override flow in trending regime</p>
                  </div>

                  {/* Chop Relaxation % */}
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-amber-400">Chop Relaxation %</label>
                    <input
                      type="number" min="0" max="100"
                      value={config.divergence_chop_relaxation_pct}
                      onChange={(e) => updateConfig(activeSymbol, 'divergence_chop_relaxation_pct', parseInt(e.target.value) || 50)}
                      className="w-full bg-surface border border-amber-500/30 px-3 py-2 rounded text-foreground font-mono text-sm"
                      data-testid="div-chop-relaxation"
                    />
                    <p className="text-[10px] text-muted-foreground">% threshold reduction in chop</p>
                  </div>
                </div>

                {/* Regime Effects Explanation */}
                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <div className="bg-green-500/10 border border-green-500/30 rounded p-2">
                    <div className="text-[10px] font-bold text-green-400">TRENDING</div>
                    <div className="text-[8px] text-muted-foreground">{config.divergence_trending_override ? 'Flow Override' : 'Normal'}</div>
                  </div>
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2">
                    <div className="text-[10px] font-bold text-amber-400">CHOP / NORMAL</div>
                    <div className="text-[8px] text-muted-foreground">Threshold -{config.divergence_chop_relaxation_pct}%</div>
                  </div>
                  <div className="bg-slate-500/10 border border-slate-500/30 rounded p-2">
                    <div className="text-[10px] font-bold text-slate-400">FORMING IB</div>
                    <div className="text-[8px] text-muted-foreground">No Adjustment</div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* SESSION 128: God Score Override Configuration */}
        <div className="panel p-6 border-l-4 border-emerald-500" data-testid="god-score-override-panel">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-emerald-400" />
            {activeSymbol} God Score Override
            <span className="text-xs font-normal text-emerald-400">(SESSION 128)</span>
          </h3>
          <p className="text-xs text-emerald-400 mb-4 bg-emerald-500/10 border border-emerald-500/30 p-2 rounded">
            When God Score ≥ threshold, bypass micro-structure vetoes (Flow Confirmation & Absorption Divergence).
            <br />
            <strong>Default: 100</strong> (disabled). Set to 95 to allow extreme scores to override vetoes.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-emerald-400">Override Threshold</label>
              <input
                type="number" step="1" min="90" max="100"
                value={config.god_score_override_threshold}
                onChange={(e) => updateConfig(activeSymbol, 'god_score_override_threshold', parseInt(e.target.value) || 100)}
                className="w-full bg-surface border border-emerald-500/30 px-3 py-2 rounded text-foreground font-mono"
                data-testid="god-score-override-threshold"
              />
              <p className="text-[10px] text-muted-foreground">100 = disabled, 95 = enabled (90-100)</p>
            </div>
            
            {/* Visual Status */}
            <div className="space-y-2 col-span-2 md:col-span-2">
              <label className="text-sm font-medium text-gray-400">Status</label>
              <div className={`p-3 rounded border ${
                config.god_score_override_threshold < 100 
                  ? 'bg-emerald-500/10 border-emerald-500/30' 
                  : 'bg-gray-500/10 border-gray-500/30'
              }`}>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${
                    config.god_score_override_threshold < 100 ? 'bg-emerald-500' : 'bg-gray-500'
                  }`} />
                  <span className="text-sm font-medium">
                    {config.god_score_override_threshold < 100 
                      ? `ACTIVE: Scores ≥ ${config.god_score_override_threshold} bypass micro-structure vetoes` 
                      : 'DISABLED: All micro-structure vetoes enforced'}
                  </span>
                </div>
                {config.god_score_override_threshold < 100 && (
                  <p className="text-[10px] text-emerald-400 mt-2">
                    ⚡ When God Score reaches {config.god_score_override_threshold}+, Flow Confirmation and Absorption Divergence vetoes are bypassed for aligned directions.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* SESSION 128b: Signal Time Window Configuration */}
        <div className="panel p-6 border-l-4 border-amber-500" data-testid="signal-time-window-panel">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-400" />
            {activeSymbol} Signal Time Window
            <span className="text-xs font-normal text-amber-400">(SESSION 128b)</span>
          </h3>
          <p className="text-xs text-amber-400 mb-4 bg-amber-500/10 border border-amber-500/30 p-2 rounded">
            Configure when signals are allowed. Set start time to <strong>09:45</strong> for partial IB or <strong>10:15</strong> for full IB completion.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Signal Start Time */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-amber-400">Signal Start Time (IST)</label>
              <select
                value={config.signal_start_time}
                onChange={(e) => updateConfig(activeSymbol, 'signal_start_time', e.target.value)}
                className="w-full bg-surface border border-amber-500/30 px-3 py-2 rounded text-foreground font-mono"
                data-testid="signal-start-time"
              >
                <option value="09:15">09:15 - Market Open</option>
                <option value="09:20">09:20 - Default (5 min buffer)</option>
                <option value="09:30">09:30 - 15 min buffer</option>
                <option value="09:45">09:45 - Partial IB (30 min)</option>
                <option value="10:00">10:00 - 45 min buffer</option>
                <option value="10:15">10:15 - Full IB (1 hour)</option>
              </select>
              <p className="text-[10px] text-muted-foreground">When to start generating signals</p>
            </div>

            {/* Signal End Time */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-amber-400">Signal End Time (IST)</label>
              <select
                value={config.signal_end_time}
                onChange={(e) => updateConfig(activeSymbol, 'signal_end_time', e.target.value)}
                className="w-full bg-surface border border-amber-500/30 px-3 py-2 rounded text-foreground font-mono"
                data-testid="signal-end-time"
              >
                <option value="15:00">15:00 - 30 min before close</option>
                <option value="15:15">15:15 - 15 min before close</option>
                <option value="15:25">15:25 - Default (5 min before close)</option>
                <option value="15:29">15:29 - 1 min before close</option>
              </select>
              <p className="text-[10px] text-muted-foreground">When to stop generating signals</p>
            </div>
            
            {/* Visual Status */}
            <div className="space-y-2 col-span-2">
              <label className="text-sm font-medium text-gray-400">Active Window</label>
              <div className="bg-amber-500/10 border border-amber-500/30 p-3 rounded">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-400" />
                  <span className="text-sm font-medium text-amber-300">
                    {config.signal_start_time} → {config.signal_end_time} IST
                  </span>
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">
                  {config.signal_start_time === "09:45" && "⏳ Waiting for partial IB (30 min range)"}
                  {config.signal_start_time === "10:15" && "⏳ Waiting for full IB completion (1 hour range)"}
                  {config.signal_start_time === "09:20" && "⚡ Standard mode - signals start 5 min after open"}
                  {config.signal_start_time === "09:15" && "🚀 Aggressive mode - signals start at market open"}
                  {!["09:15", "09:20", "09:45", "10:15"].includes(config.signal_start_time) && `Custom start time: ${config.signal_start_time}`}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* SESSION 116: Order Flow Modifiers (GoCharting-style) */}
        <div className="panel p-6 border-2 border-cyan-500/30">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-cyan-400" />
            {activeSymbol} Order Flow Modifiers
            <span className="text-xs font-normal text-cyan-400">(SESSION 116)</span>
          </h3>
          <p className="text-xs text-muted-foreground mb-4">
            GoCharting-style delta divergence and market weakness detection. Modifies score based on order flow patterns.
          </p>

          <div className="grid grid-cols-2 gap-6">
            {/* Delta Divergence Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-cyan-400">Delta Divergence (Absorption)</label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.delta_divergence_enabled}
                    onChange={(e) => updateConfig(activeSymbol, 'delta_divergence_enabled', e.target.checked)}
                    className="w-4 h-4 accent-cyan-500"
                    data-testid="delta-divergence-enabled"
                  />
                  <span className="text-xs text-muted-foreground">Enabled</span>
                </label>
              </div>
              
              <div className="space-y-2">
                <label className="text-xs font-medium text-foreground">Min Delta Threshold</label>
                <input
                  type="number"
                  value={config.min_delta_divergence}
                  onChange={(e) => updateConfig(activeSymbol, 'min_delta_divergence', parseInt(e.target.value) || 500)}
                  className="w-full bg-surface border border-cyan-500/30 px-3 py-2 rounded text-foreground font-mono text-sm"
                  data-testid="min-delta-divergence"
                />
                <p className="text-[10px] text-muted-foreground">Min |delta| to consider ({activeSymbol === 'NIFTY' ? 'lower vol' : 'higher vol'})</p>
              </div>
              
              <div className="space-y-2">
                <label className="text-xs font-medium text-foreground">Persistence (candles)</label>
                <input
                  type="number" min="1" max="5"
                  value={config.divergence_persistence}
                  onChange={(e) => updateConfig(activeSymbol, 'divergence_persistence', parseInt(e.target.value) || 2)}
                  className="w-full bg-surface border border-cyan-500/30 px-3 py-2 rounded text-foreground font-mono text-sm"
                  data-testid="divergence-persistence"
                />
                <p className="text-[10px] text-muted-foreground">Consecutive candles required</p>
              </div>

              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded p-3 mt-2">
                <div className="text-[10px] font-bold text-cyan-400 mb-1">DIVERGENCE TYPES</div>
                <div className="text-[9px] text-muted-foreground space-y-1">
                  <div><span className="text-green-400">BULLISH:</span> Green candle + negative delta = passive buying</div>
                  <div><span className="text-red-400">BEARISH:</span> Red candle + positive delta = passive selling</div>
                </div>
              </div>
            </div>

            {/* Market Weakness Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-amber-400">Market Weakness Detector</label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.market_weakness_enabled}
                    onChange={(e) => updateConfig(activeSymbol, 'market_weakness_enabled', e.target.checked)}
                    className="w-4 h-4 accent-amber-500"
                    data-testid="market-weakness-enabled"
                  />
                  <span className="text-xs text-muted-foreground">Enabled</span>
                </label>
              </div>
              
              <div className="space-y-2">
                <label className="text-xs font-medium text-foreground">Lookback (candles)</label>
                <input
                  type="number" min="2" max="10"
                  value={config.weakness_lookback}
                  onChange={(e) => updateConfig(activeSymbol, 'weakness_lookback', parseInt(e.target.value) || 3)}
                  className="w-full bg-surface border border-amber-500/30 px-3 py-2 rounded text-foreground font-mono text-sm"
                  data-testid="weakness-lookback"
                />
                <p className="text-[10px] text-muted-foreground">Candles for weakness pattern (default: 3 = 15 min)</p>
              </div>

              <div className="bg-amber-500/10 border border-amber-500/30 rounded p-3 mt-2">
                <div className="text-[10px] font-bold text-amber-400 mb-1">WEAKNESS TYPES</div>
                <div className="text-[9px] text-muted-foreground space-y-1">
                  <div><span className="text-red-400">BUYING WEAKNESS:</span> Higher highs + declining delta = buyers exhausting</div>
                  <div><span className="text-green-400">SELLING WEAKNESS:</span> Lower lows + improving delta = sellers exhausting</div>
                </div>
              </div>
            </div>
          </div>

          {/* SESSION 116b: Exhaustion Prints Section */}
          <div className="mt-6 pt-6 border-t border-purple-500/30">
            <div className="flex items-center justify-between mb-4">
              <label className="text-sm font-medium text-purple-400">Exhaustion Prints</label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.exhaustion_enabled}
                  onChange={(e) => updateConfig(activeSymbol, 'exhaustion_enabled', e.target.checked)}
                  className="w-4 h-4 accent-purple-500"
                  data-testid="exhaustion-enabled"
                />
                <span className="text-xs text-muted-foreground">Enabled</span>
              </label>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-xs font-medium text-foreground">Volume Threshold</label>
                <input
                  type="number"
                  value={config.exhaustion_volume_threshold}
                  onChange={(e) => updateConfig(activeSymbol, 'exhaustion_volume_threshold', parseInt(e.target.value) || 500)}
                  className="w-full bg-surface border border-purple-500/30 px-3 py-2 rounded text-foreground font-mono text-sm"
                  data-testid="exhaustion-volume-threshold"
                />
                <p className="text-[10px] text-muted-foreground">Min volume at edge zone ({activeSymbol === 'NIFTY' ? '500' : '1500'} default)</p>
              </div>
              
              <div className="space-y-2">
                <label className="text-xs font-medium text-foreground">Edge Zone %</label>
                <input
                  type="number" min="5" max="30"
                  value={config.exhaustion_edge_pct}
                  onChange={(e) => updateConfig(activeSymbol, 'exhaustion_edge_pct', parseInt(e.target.value) || 15)}
                  className="w-full bg-surface border border-purple-500/30 px-3 py-2 rounded text-foreground font-mono text-sm"
                  data-testid="exhaustion-edge-pct"
                />
                <p className="text-[10px] text-muted-foreground">Top/bottom % of candle range (default: 15%)</p>
              </div>
            </div>

            <div className="bg-purple-500/10 border border-purple-500/30 rounded p-3 mt-4">
              <div className="text-[10px] font-bold text-purple-400 mb-1">EXHAUSTION TYPES</div>
              <div className="text-[9px] text-muted-foreground space-y-1">
                <div><span className="text-green-400">BULLISH:</span> Heavy selling at LOW of green candle = sellers exhausted, price rebounded</div>
                <div><span className="text-red-400">BEARISH:</span> Heavy buying at HIGH of red candle = buyers exhausted, price rejected</div>
              </div>
            </div>
          </div>

          {/* SESSION 116c: Stacked Imbalances Section */}
          <div className="mt-6 pt-6 border-t border-emerald-500/30">
            <div className="flex items-center justify-between mb-4">
              <label className="text-sm font-medium text-emerald-400">Stacked Imbalances</label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.stacked_imbalance_enabled}
                  onChange={(e) => updateConfig(activeSymbol, 'stacked_imbalance_enabled', e.target.checked)}
                  className="w-4 h-4 accent-emerald-500"
                  data-testid="stacked-imbalance-enabled"
                />
                <span className="text-xs text-muted-foreground">Enabled</span>
              </label>
            </div>
            
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-3">
              <div className="text-[10px] font-bold text-emerald-400 mb-1">STACKED IMBALANCE TYPES</div>
              <div className="text-[9px] text-muted-foreground space-y-1">
                <div><span className="text-green-400">STACKED_BUY:</span> 3+ price levels dominated by buyers = strong bullish conviction</div>
                <div><span className="text-red-400">STACKED_SELL:</span> 3+ price levels dominated by sellers = strong bearish conviction</div>
              </div>
              <div className="text-[9px] text-muted-foreground mt-2 pt-2 border-t border-emerald-500/20">
                <span className="text-emerald-400">Note:</span> Uses existing Stacked Imbalance settings from the imbalance config above.
              </div>
            </div>
          </div>

          {/* SESSION 116d: Extreme Delta Section */}
          <div className="mt-6 pt-6 border-t border-rose-500/30">
            <div className="flex items-center justify-between mb-4">
              <label className="text-sm font-medium text-rose-400">Extreme Delta</label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.extreme_delta_enabled}
                  onChange={(e) => updateConfig(activeSymbol, 'extreme_delta_enabled', e.target.checked)}
                  className="w-4 h-4 accent-rose-500"
                  data-testid="extreme-delta-enabled"
                />
                <span className="text-xs text-muted-foreground">Enabled</span>
              </label>
            </div>
            
            <div className="space-y-2">
              <label className="text-xs font-medium text-foreground">Threshold %</label>
              <input
                type="number" min="80" max="99"
                value={config.extreme_delta_threshold_pct}
                onChange={(e) => updateConfig(activeSymbol, 'extreme_delta_threshold_pct', parseInt(e.target.value) || 95)}
                className="w-32 bg-surface border border-rose-500/30 px-3 py-2 rounded text-foreground font-mono text-sm"
                data-testid="extreme-delta-threshold"
              />
              <p className="text-[10px] text-muted-foreground">Final delta must be ≥ this % of max/min reached (default: 95%)</p>
            </div>

            <div className="bg-rose-500/10 border border-rose-500/30 rounded p-3 mt-4">
              <div className="text-[10px] font-bold text-rose-400 mb-1">EXTREME DELTA TYPES</div>
              <div className="text-[9px] text-muted-foreground space-y-1">
                <div><span className="text-green-400">EXTREME_BULLISH:</span> Final delta ≥ 95% of max = buyers dominated start to finish</div>
                <div><span className="text-red-400">EXTREME_BEARISH:</span> Final delta ≤ 95% of min = sellers dominated start to finish</div>
              </div>
              <div className="text-[9px] text-muted-foreground mt-2 pt-2 border-t border-rose-500/20">
                <span className="text-rose-400">Note:</span> This is a <strong>momentum confirmation</strong> signal, not a reversal signal.
              </div>
            </div>
          </div>

          {/* Score Impact Summary */}
          <div className="mt-4 p-3 bg-slate-800/50 rounded border border-slate-600/30">
            <div className="text-xs font-bold text-foreground mb-2">SCORE IMPACT</div>
            <div className="grid grid-cols-5 gap-3 text-[10px] text-muted-foreground">
              <div>
                <span className="text-cyan-400">Delta Divergence:</span> ±8 pts
              </div>
              <div>
                <span className="text-amber-400">Market Weakness:</span> ±10 pts
              </div>
              <div>
                <span className="text-purple-400">Exhaustion Prints:</span> ±12 pts
              </div>
              <div>
                <span className="text-emerald-400">Stacked Imbalance:</span> ±10 pts
              </div>
              <div>
                <span className="text-rose-400">Extreme Delta:</span> ±15 pts
              </div>
            </div>
          </div>
        </div>

        {/* SESSION 117: Entry Rule Modifiers */}
        <div className="panel p-6">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-400" />
            {activeSymbol} Entry Rule Modifiers
            <span className="text-xs font-normal text-blue-400">(SESSION 117)</span>
          </h3>
          
          <div className="text-sm text-muted-foreground mb-4">
            Based on James Dalton & GoCharting methodology validation. Converts hard vetoes to score modifiers.
          </div>

          {/* POC Entry Rule */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-blue-400">dPOC Entry Rule</label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.entry_dpoc_veto_enabled || false}
                  onChange={(e) => updateConfig(activeSymbol, 'entry_dpoc_veto_enabled', e.target.checked)}
                  className="w-4 h-4 accent-blue-500"
                  data-testid="entry-dpoc-veto-enabled"
                />
                <span className="text-xs text-muted-foreground">Hard Veto Mode</span>
              </label>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-xs font-medium text-foreground">Wrong Side Penalty</label>
                <input
                  type="number" min="0" max="30"
                  value={config.entry_dpoc_penalty || 15}
                  onChange={(e) => updateConfig(activeSymbol, 'entry_dpoc_penalty', parseInt(e.target.value) || 15)}
                  className="w-full bg-surface border border-blue-500/30 px-3 py-2 rounded text-foreground font-mono text-sm"
                  data-testid="entry-dpoc-penalty"
                />
                <p className="text-[10px] text-muted-foreground">Points subtracted when price on wrong side of POC</p>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium text-foreground">At POC Bonus</label>
                <input
                  type="number" min="0" max="20"
                  value={config.entry_dpoc_at_poc_bonus || 10}
                  onChange={(e) => updateConfig(activeSymbol, 'entry_dpoc_at_poc_bonus', parseInt(e.target.value) || 10)}
                  className="w-full bg-surface border border-blue-500/30 px-3 py-2 rounded text-foreground font-mono text-sm"
                  data-testid="entry-dpoc-at-poc-bonus"
                />
                <p className="text-[10px] text-muted-foreground">Bonus when price at POC pullback zone</p>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium text-foreground">POC Tolerance (pts)</label>
                <input
                  type="number" min="1" max="30"
                  value={config.entry_dpoc_tolerance || 5}
                  onChange={(e) => updateConfig(activeSymbol, 'entry_dpoc_tolerance', parseInt(e.target.value) || 5)}
                  className="w-full bg-surface border border-blue-500/30 px-3 py-2 rounded text-foreground font-mono text-sm"
                  data-testid="entry-dpoc-tolerance"
                />
                <p className="text-[10px] text-muted-foreground">Points tolerance for "at POC" detection</p>
              </div>
            </div>
            
            <div className="bg-blue-500/10 border border-blue-500/20 rounded p-2 mt-3">
              <div className="text-[10px] text-muted-foreground">
                <span className="text-blue-400 font-bold">James Dalton Methodology:</span> POC is a pullback entry zone, not a directional filter.
                "Wait for price to move away from POC, then enter at first touch of POC."
              </div>
            </div>
          </div>

          {/* GEX Entry Rule */}
          <div className="bg-orange-500/10 border border-orange-500/30 rounded p-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-orange-400">GEX Entry Rule</label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.entry_gex_veto_enabled || false}
                  onChange={(e) => updateConfig(activeSymbol, 'entry_gex_veto_enabled', e.target.checked)}
                  className="w-4 h-4 accent-orange-500"
                  data-testid="entry-gex-veto-enabled"
                />
                <span className="text-xs text-muted-foreground">Hard Veto Mode</span>
              </label>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-xs font-medium text-foreground">Aligned Bonus</label>
                <input
                  type="number" min="0" max="20"
                  value={config.entry_gex_aligned_bonus || 10}
                  onChange={(e) => updateConfig(activeSymbol, 'entry_gex_aligned_bonus', parseInt(e.target.value) || 10)}
                  className="w-full bg-surface border border-orange-500/30 px-3 py-2 rounded text-foreground font-mono text-sm"
                  data-testid="entry-gex-aligned-bonus"
                />
                <p className="text-[10px] text-muted-foreground">Bonus when GEX aligns with direction</p>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium text-foreground">Misaligned Penalty</label>
                <input
                  type="number" min="0" max="20"
                  value={config.entry_gex_misaligned_penalty || 10}
                  onChange={(e) => updateConfig(activeSymbol, 'entry_gex_misaligned_penalty', parseInt(e.target.value) || 10)}
                  className="w-full bg-surface border border-orange-500/30 px-3 py-2 rounded text-foreground font-mono text-sm"
                  data-testid="entry-gex-misaligned-penalty"
                />
                <p className="text-[10px] text-muted-foreground">Penalty when GEX opposes direction</p>
              </div>
            </div>
            
            <div className="bg-orange-500/10 border border-orange-500/20 rounded p-2 mt-3">
              <div className="text-[10px] text-muted-foreground">
                <span className="text-orange-400 font-bold">Options-Based Signal:</span> GEX measures market maker hedging behavior.
                Not part of core Order Flow methodology - should inform score, not veto valid signals.
              </div>
            </div>
          </div>

          {/* SESSION 117b: CHOP_MODE IB Modifier */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-yellow-400">CHOP_MODE IB Boundary</label>
              <span className="text-xs text-muted-foreground">(SESSION 117b)</span>
            </div>
            
            <div className="space-y-2">
              <label className="text-xs font-medium text-foreground">IB Boundary Penalty</label>
              <input
                type="number" min="0" max="30"
                value={config.chop_mode_ib_penalty || 15}
                onChange={(e) => updateConfig(activeSymbol, 'chop_mode_ib_penalty', parseInt(e.target.value) || 15)}
                className="w-full bg-surface border border-yellow-500/30 px-3 py-2 rounded text-foreground font-mono text-sm"
                data-testid="chop-mode-ib-penalty"
              />
              <p className="text-[10px] text-muted-foreground">Penalty for buying at IB High or selling at IB Low in CHOP_MODE</p>
            </div>
            
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded p-2 mt-3">
              <div className="text-[10px] text-muted-foreground">
                <span className="text-yellow-400 font-bold">Dalton's IB:</span> Initial Balance boundaries matter in rotational days.
                Converted from hard veto to modifier - strong flow can still break IB boundaries.
              </div>
            </div>
          </div>

          {/* SESSION 117b: Gamma Wall Modifier */}
          <div className="bg-rose-500/10 border border-rose-500/30 rounded p-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-rose-400">Gamma Wall</label>
              <span className="text-xs text-muted-foreground">(SESSION 117b)</span>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-xs font-medium text-foreground">Into Wall Penalty</label>
                <input
                  type="number" min="0" max="20"
                  value={config.gamma_wall_penalty || 10}
                  onChange={(e) => updateConfig(activeSymbol, 'gamma_wall_penalty', parseInt(e.target.value) || 10)}
                  className="w-full bg-surface border border-rose-500/30 px-3 py-2 rounded text-foreground font-mono text-sm"
                  data-testid="gamma-wall-penalty"
                />
                <p className="text-[10px] text-muted-foreground">Penalty for trading INTO gamma wall</p>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-medium text-foreground">Wall Support Bonus</label>
                <input
                  type="number" min="0" max="20"
                  value={config.gamma_wall_bonus || 10}
                  onChange={(e) => updateConfig(activeSymbol, 'gamma_wall_bonus', parseInt(e.target.value) || 10)}
                  className="w-full bg-surface border border-rose-500/30 px-3 py-2 rounded text-foreground font-mono text-sm"
                  data-testid="gamma-wall-bonus"
                />
                <p className="text-[10px] text-muted-foreground">Bonus when gamma wall supports direction</p>
              </div>
            </div>
            
            <div className="bg-rose-500/10 border border-rose-500/20 rounded p-2 mt-3">
              <div className="text-[10px] text-muted-foreground">
                <span className="text-rose-400 font-bold">Options-Based:</span> Gamma walls (Call Wall/Put Wall) from options hedging.
                Previously hard veto - now a modifier that informs but doesn't block Order Flow signals.
              </div>
            </div>
          </div>
        </div>

        {/* Structural Physics */}
        <div className="panel p-6">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Calculator className="w-5 h-5 text-purple-400" />
            {activeSymbol} Structural Physics
          </h3>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Step Size (pts)</label>
              <input
                type="number"
                value={config.structural_step}
                onChange={(e) => updateConfig(activeSymbol, 'structural_step', parseInt(e.target.value) || 20)}
                className="w-full bg-surface border border-border px-3 py-2 rounded text-foreground font-mono"
              />
              <p className="text-xs text-muted-foreground">Distance per decay step</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Decay Factor</label>
              <input
                type="number"
                step="0.5"
                value={config.structural_factor}
                onChange={(e) => updateConfig(activeSymbol, 'structural_factor', parseFloat(e.target.value) || 2.5)}
                className="w-full bg-surface border border-border px-3 py-2 rounded text-foreground font-mono"
              />
              <p className="text-xs text-muted-foreground">Score per step</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Max Adjustment</label>
              <input
                type="number"
                value={config.structural_max_adj}
                onChange={(e) => updateConfig(activeSymbol, 'structural_max_adj', parseInt(e.target.value) || 40)}
                className="w-full bg-surface border border-border px-3 py-2 rounded text-foreground font-mono"
              />
              <p className="text-xs text-muted-foreground">Max structural adjustment</p>
            </div>
          </div>
        </div>

        {/* SESSION 121: Exit Target Configuration */}
        <div className="panel p-6 border-2 border-emerald-500/30">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-emerald-500" />
            {activeSymbol} Exit Target Configuration
            <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded">GUI Trading 23-Video</span>
          </h3>
          <p className="text-xs text-emerald-400 mb-4 bg-emerald-500/10 border border-emerald-500/30 p-2 rounded">
            SESSION 121: Structure-based exit targets per GUI Trading methodology. T1 uses nearest structural level (Ledge → VAH/VAL → POC → IB), SL uses zone buffer.
          </p>

          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Min T1 ATR Multiplier</label>
              <input
                type="number"
                step="0.05"
                min="0.1"
                max="1.0"
                value={config.min_t1_atr_multiplier || 0.3}
                onChange={(e) => updateConfig(activeSymbol, 'min_t1_atr_multiplier', parseFloat(e.target.value) || 0.3)}
                className="w-full bg-surface border border-emerald-500/30 px-3 py-2 rounded text-foreground font-mono"
                data-testid="min-t1-atr-multiplier"
              />
              <p className="text-xs text-muted-foreground">
                Min T1 distance = ATR × {config.min_t1_atr_multiplier || 0.3} = <span className="text-emerald-400 font-mono">{activeSymbol === 'NIFTY' ? Math.round(50 * (config.min_t1_atr_multiplier || 0.3)) : Math.round(150 * (config.min_t1_atr_multiplier || 0.3))} pts</span>
              </p>
              <p className="text-[10px] text-muted-foreground">If structural level is closer than this, use ATR-based fallback instead</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">SL Tick Buffer (pts)</label>
              <input
                type="number"
                step="5"
                min="5"
                max="100"
                value={config.sl_tick_buffer || (activeSymbol === 'NIFTY' ? 15 : 40)}
                onChange={(e) => updateConfig(activeSymbol, 'sl_tick_buffer', parseInt(e.target.value) || 15)}
                className="w-full bg-surface border border-emerald-500/30 px-3 py-2 rounded text-foreground font-mono"
                data-testid="sl-tick-buffer"
              />
              <p className="text-xs text-muted-foreground">
                SL placed <span className="text-red-400 font-mono">{config.sl_tick_buffer || (activeSymbol === 'NIFTY' ? 15 : 40)} pts</span> beyond nearest structure
              </p>
              <p className="text-[10px] text-muted-foreground">Per video: 1-2 boxes beyond entry zone for safety buffer</p>
            </div>
          </div>

          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded p-3 mt-4">
            <div className="text-xs text-muted-foreground space-y-1">
              <div><span className="text-emerald-400 font-bold">T1 Priority:</span> Ledge (87% accuracy) → VAH/VAL → dPOC → IB High/Low → ATR Fallback</div>
              <div><span className="text-emerald-400 font-bold">SL Logic:</span> Nearest opposing structure + tick buffer (zone-based per video methodology)</div>
              <div><span className="text-emerald-400 font-bold">Fallback:</span> If no structure available, uses LIVE ATR from engine</div>
            </div>
          </div>
        </div>

        {/* SESSION 121: LQP (Liquidity Pump) Configuration */}
        <div className="panel p-6 border-2 border-cyan-500/30">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-cyan-500" />
            {activeSymbol} LQP (Liquidity Pump) Detection
          </h3>
          <p className="text-xs text-cyan-400 mb-4 bg-cyan-500/10 border border-cyan-500/30 p-2 rounded">
            SESSION 121: Detects low-liquidity void zones that precede explosive moves. Based on GUI Trading methodology (3 consecutive levels with &lt; threshold lots on one side).
          </p>

          <div className="grid grid-cols-5 gap-4">
            {/* LQP Enabled Toggle */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-cyan-400">Enabled</label>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={config.lqp_enabled || false}
                  onChange={(e) => updateConfig(activeSymbol, 'lqp_enabled', e.target.checked)}
                  className="w-5 h-5 accent-cyan-500"
                  data-testid="lqp-enabled"
                />
                <span className={`ml-2 text-sm font-mono ${config.lqp_enabled ? 'text-cyan-400' : 'text-muted-foreground'}`}>
                  {config.lqp_enabled ? 'ON' : 'OFF'}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">Enable LQP detection</p>
            </div>

            {/* Lot Threshold */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-cyan-400">Lot Threshold</label>
              <input
                type="number"
                min="5"
                max="100"
                value={config.lqp_lot_threshold || 25}
                onChange={(e) => updateConfig(activeSymbol, 'lqp_lot_threshold', parseInt(e.target.value) || 25)}
                className="w-full bg-surface border border-cyan-500/30 px-3 py-2 rounded text-foreground font-mono"
                data-testid="lqp-lot-threshold"
              />
              <p className="text-xs text-muted-foreground">Void if &lt; {config.lqp_lot_threshold || 25} lots</p>
            </div>

            {/* Price Step */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-cyan-400">Price Step</label>
              <input
                type="number"
                min="1"
                max="50"
                value={config.lqp_price_step || (activeSymbol === 'NIFTY' ? 5 : 10)}
                onChange={(e) => updateConfig(activeSymbol, 'lqp_price_step', parseInt(e.target.value) || 5)}
                className="w-full bg-surface border border-cyan-500/30 px-3 py-2 rounded text-foreground font-mono"
                data-testid="lqp-price-step"
              />
              <p className="text-xs text-muted-foreground">Price level granularity</p>
            </div>

            {/* Target Points */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-cyan-400">Target Points</label>
              <input
                type="number"
                min="20"
                max="300"
                value={config.lqp_target_points || (activeSymbol === 'NIFTY' ? 60 : 100)}
                onChange={(e) => updateConfig(activeSymbol, 'lqp_target_points', parseInt(e.target.value) || 60)}
                className="w-full bg-surface border border-cyan-500/30 px-3 py-2 rounded text-foreground font-mono"
                data-testid="lqp-target-points"
              />
              <p className="text-xs text-muted-foreground">Expected minimum move</p>
            </div>

            {/* God Score Bonus */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-cyan-400">Score Bonus</label>
              <input
                type="number"
                min="5"
                max="50"
                value={config.lqp_god_score_bonus || 25}
                onChange={(e) => updateConfig(activeSymbol, 'lqp_god_score_bonus', parseInt(e.target.value) || 25)}
                className="w-full bg-surface border border-cyan-500/30 px-3 py-2 rounded text-foreground font-mono"
                data-testid="lqp-god-score-bonus"
              />
              <p className="text-xs text-muted-foreground">God Score boost</p>
            </div>
          </div>

          <div className="bg-cyan-500/10 border border-cyan-500/20 rounded p-3 mt-4">
            <div className="text-xs text-muted-foreground space-y-1">
              <div><span className="text-cyan-400 font-bold">Detection:</span> 3 consecutive price levels with &lt; threshold lots on one side = void zone</div>
              <div><span className="text-cyan-400 font-bold">Signal:</span> Sell-side void → LQP BUY | Buy-side void → LQP SELL</div>
              <div><span className="text-cyan-400 font-bold">Bonus:</span> Adds {config.lqp_god_score_bonus || 25} pts to God Score (can flip direction)</div>
            </div>
          </div>
        </div>

        {/* SESSION 85: Hybrid ATR Stop-Loss Configuration */}
        <div className="panel p-6 border-2 border-orange-500/30">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-orange-500" />
            Hybrid ATR Stop-Loss Configuration
            {slConfig?.source === 'db' && (
              <span className="text-[10px] bg-green-500/20 text-green-400 px-2 py-0.5 rounded">DB Override</span>
            )}
            {slConfig?.source === 'env' && (
              <span className="text-[10px] bg-slate-500/20 text-slate-400 px-2 py-0.5 rounded">ENV Default</span>
            )}
          </h3>
          <p className="text-xs text-amber-400 mb-4 bg-amber-500/10 border border-amber-500/30 p-2 rounded">
            SESSION 85: Configurable stop-loss thresholds with opening window protection. Conservative SL during first 15 mins, MIN_SL floor after.
            {!slEditing && <span className="ml-2 text-green-400">Click "Edit" to modify values.</span>}
          </p>

          {slLoading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="w-6 h-6 text-accent animate-spin" />
            </div>
          ) : slConfig ? (
            <div className="space-y-4">
              {/* Current SL Mode Status */}
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div className="bg-surface rounded p-3 border border-border">
                  <div className="text-xs text-muted-foreground">Current Time (IST)</div>
                  <div className="text-lg font-mono text-foreground">{slConfig.current_time_ist}</div>
                </div>
                <div className="bg-surface rounded p-3 border border-border">
                  <div className="text-xs text-muted-foreground">Market Open</div>
                  <div className="text-lg font-mono text-foreground">{slConfig.market_open_ist}</div>
                </div>
                <div className="bg-surface rounded p-3 border border-border">
                  <div className="text-xs text-muted-foreground">Opening Window Ends</div>
                  <div className="text-lg font-mono text-foreground">{slConfig.opening_window_end_ist}</div>
                </div>
                <div className={`rounded p-3 border ${
                  slConfig.sl_mode === 'PREMARKET' ? 'bg-red-500/20 border-red-500/50' :
                  slConfig.sl_mode === 'OPENING' ? 'bg-yellow-500/20 border-yellow-500/50' :
                  'bg-green-500/20 border-green-500/50'
                }`}>
                  <div className="text-xs text-muted-foreground">SL Mode</div>
                  <div className={`text-lg font-bold ${
                    slConfig.sl_mode === 'PREMARKET' ? 'text-red-400' :
                    slConfig.sl_mode === 'OPENING' ? 'text-yellow-400' :
                    'text-green-400'
                  }`}>
                    {slConfig.sl_mode === 'PREMARKET' ? '🚫 PREMARKET' :
                     slConfig.sl_mode === 'OPENING' ? '🔔 OPENING' :
                     '✅ NORMAL'}
                  </div>
                </div>
              </div>

              {/* Editable SL Values */}
              <div className="grid grid-cols-2 gap-6">
                {/* NIFTY */}
                <div className="space-y-3 p-4 bg-blue-500/5 border border-blue-500/20 rounded">
                  <h4 className="text-sm font-semibold text-blue-400 flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    NIFTY Stop-Loss (Points)
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Opening SL</label>
                      {slEditing ? (
                        <input
                          type="number"
                          value={slEditValues.nifty_open_sl}
                          onChange={(e) => setSlEditValues(prev => ({...prev, nifty_open_sl: parseFloat(e.target.value) || 0}))}
                          className="w-full bg-surface border border-yellow-500/50 px-3 py-2 rounded text-yellow-400 font-mono text-lg"
                          data-testid="nifty-open-sl-input"
                        />
                      ) : (
                        <div className="text-xl font-mono text-yellow-400 bg-surface rounded p-2 border border-border">{slConfig.nifty?.open_sl || 45}</div>
                      )}
                      <div className="text-[10px] text-muted-foreground mt-1">First 15 mins</div>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Min SL Floor</label>
                      {slEditing ? (
                        <input
                          type="number"
                          value={slEditValues.nifty_min_sl}
                          onChange={(e) => setSlEditValues(prev => ({...prev, nifty_min_sl: parseFloat(e.target.value) || 0}))}
                          className="w-full bg-surface border border-green-500/50 px-3 py-2 rounded text-green-400 font-mono text-lg"
                          data-testid="nifty-min-sl-input"
                        />
                      ) : (
                        <div className="text-xl font-mono text-green-400 bg-surface rounded p-2 border border-border">{slConfig.nifty?.min_sl || 25}</div>
                      )}
                      <div className="text-[10px] text-muted-foreground mt-1">Normal trading</div>
                    </div>
                  </div>
                </div>

                {/* BANKNIFTY */}
                <div className="space-y-3 p-4 bg-orange-500/5 border border-orange-500/20 rounded">
                  <h4 className="text-sm font-semibold text-orange-400 flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    BANKNIFTY Stop-Loss (Points)
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Opening SL</label>
                      {slEditing ? (
                        <input
                          type="number"
                          value={slEditValues.banknifty_open_sl}
                          onChange={(e) => setSlEditValues(prev => ({...prev, banknifty_open_sl: parseFloat(e.target.value) || 0}))}
                          className="w-full bg-surface border border-yellow-500/50 px-3 py-2 rounded text-yellow-400 font-mono text-lg"
                          data-testid="banknifty-open-sl-input"
                        />
                      ) : (
                        <div className="text-xl font-mono text-yellow-400 bg-surface rounded p-2 border border-border">{slConfig.banknifty?.open_sl || 150}</div>
                      )}
                      <div className="text-[10px] text-muted-foreground mt-1">First 15 mins</div>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Min SL Floor</label>
                      {slEditing ? (
                        <input
                          type="number"
                          value={slEditValues.banknifty_min_sl}
                          onChange={(e) => setSlEditValues(prev => ({...prev, banknifty_min_sl: parseFloat(e.target.value) || 0}))}
                          className="w-full bg-surface border border-green-500/50 px-3 py-2 rounded text-green-400 font-mono text-lg"
                          data-testid="banknifty-min-sl-input"
                        />
                      ) : (
                        <div className="text-xl font-mono text-green-400 bg-surface rounded p-2 border border-border">{slConfig.banknifty?.min_sl || 75}</div>
                      )}
                      <div className="text-[10px] text-muted-foreground mt-1">Normal trading</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Opening Window Duration */}
              <div className="p-4 bg-purple-500/5 border border-purple-500/20 rounded">
                <h4 className="text-sm font-semibold text-purple-400 flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4" />
                  Opening Window Duration
                </h4>
                <div className="flex items-center gap-4">
                  {slEditing ? (
                    <input
                      type="number"
                      min="5"
                      max="60"
                      value={slEditValues.opening_window_mins}
                      onChange={(e) => setSlEditValues(prev => ({...prev, opening_window_mins: parseInt(e.target.value) || 15}))}
                      className="w-24 bg-surface border border-purple-500/50 px-3 py-2 rounded text-purple-400 font-mono text-lg"
                      data-testid="opening-window-input"
                    />
                  ) : (
                    <div className="text-xl font-mono text-purple-400">{slConfig.opening_window_mins || 15}</div>
                  )}
                  <span className="text-sm text-muted-foreground">minutes after market open (09:15 IST)</span>
                </div>
              </div>

              {/* SL Mode Explanation */}
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <div className={`rounded p-2 border ${slConfig.is_premarket ? 'bg-red-500/20 border-red-500/50 ring-2 ring-red-500' : 'bg-red-500/10 border-red-500/30'}`}>
                  <div className="text-[10px] font-bold text-red-400">PREMARKET</div>
                  <div className="text-[8px] text-muted-foreground">Before 09:15 IST</div>
                  <div className="text-[8px] text-red-300">Entries BLOCKED</div>
                </div>
                <div className={`rounded p-2 border ${slConfig.is_opening_window ? 'bg-yellow-500/20 border-yellow-500/50 ring-2 ring-yellow-500' : 'bg-yellow-500/10 border-yellow-500/30'}`}>
                  <div className="text-[10px] font-bold text-yellow-400">OPENING</div>
                  <div className="text-[8px] text-muted-foreground">09:15 - 09:30 IST</div>
                  <div className="text-[8px] text-yellow-300">Use OPEN_SL (Conservative)</div>
                </div>
                <div className={`rounded p-2 border ${!slConfig.is_premarket && !slConfig.is_opening_window ? 'bg-green-500/20 border-green-500/50 ring-2 ring-green-500' : 'bg-green-500/10 border-green-500/30'}`}>
                  <div className="text-[10px] font-bold text-green-400">NORMAL</div>
                  <div className="text-[8px] text-muted-foreground">After 09:30 IST</div>
                  <div className="text-[8px] text-green-300">max(Calculated, MIN_SL)</div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-4 flex justify-between items-center">
                <div className="flex gap-2">
                  {slEditing ? (
                    <>
                      <Button
                        onClick={saveSlConfig}
                        disabled={slSaving}
                        className="bg-green-600 hover:bg-green-700 text-white"
                        data-testid="save-sl-config"
                      >
                        {slSaving ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                        Save SL Config
                      </Button>
                      <Button
                        onClick={() => {
                          setSlEditing(false);
                          // Reset edit values to current config
                          setSlEditValues({
                            nifty_open_sl: slConfig.nifty?.open_sl || 45,
                            nifty_min_sl: slConfig.nifty?.min_sl || 25,
                            banknifty_open_sl: slConfig.banknifty?.open_sl || 150,
                            banknifty_min_sl: slConfig.banknifty?.min_sl || 75,
                            opening_window_mins: slConfig.opening_window_mins || 15
                          });
                        }}
                        variant="outline"
                        disabled={slSaving}
                        data-testid="cancel-sl-edit"
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={() => setSlEditing(true)}
                      variant="outline"
                      className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                      data-testid="edit-sl-config"
                    >
                      <Settings className="w-4 h-4 mr-1" />
                      Edit SL Values
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  {slConfig?.source === 'db' && (
                    <Button
                      onClick={resetSlConfig}
                      variant="outline"
                      size="sm"
                      className="text-xs border-red-500/50 text-red-400 hover:bg-red-500/10"
                      disabled={slSaving}
                      data-testid="reset-sl-to-env"
                    >
                      <RotateCcw className="w-3 h-3 mr-1" />
                      Reset to ENV
                    </Button>
                  )}
                  <Button
                    onClick={fetchSlConfig}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    disabled={slSaving}
                    data-testid="refresh-sl-config"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Refresh
                  </Button>
                </div>
              </div>

              {/* Config Source Info */}
              <div className="mt-4 p-3 bg-slate-500/10 border border-slate-500/30 rounded text-xs">
                <div className="font-semibold text-foreground mb-1">
                  {slConfig?.source === 'db' ? '🗄️ Database Override Active' : '📝 Using Environment Defaults'}
                </div>
                <code className="text-muted-foreground font-mono block">
                  NIFTY: Open={slConfig.nifty?.open_sl}pts | Min={slConfig.nifty?.min_sl}pts<br/>
                  BANKNIFTY: Open={slConfig.banknifty?.open_sl}pts | Min={slConfig.banknifty?.min_sl}pts<br/>
                  Window={slConfig.opening_window_mins}min
                </code>
                {slConfig?.source === 'db' ? (
                  <p className="mt-1 text-green-400">
                    Changes are saved to database and applied immediately. No restart needed.
                  </p>
                ) : (
                  <p className="mt-1 text-muted-foreground">
                    Edit and save to override env defaults. Changes apply immediately.
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center text-muted-foreground py-8">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
              <p>Failed to load SL configuration. Check backend connection.</p>
            </div>
          )}
        </div>
      </div>

      {/* SESSION 104: IV Skew Dislocation Hunter Configuration */}
      <div className="panel p-6 bg-gradient-to-br from-violet-500/10 to-purple-500/5 border-violet-500/30">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-violet-400" />
            <h3 className="font-semibold text-foreground">{activeSymbol} IV Skew Dislocation Hunter</h3>
            <span className={`px-2 py-0.5 text-xs rounded-full ${ivSkewStatus?.is_running ? 'bg-green-500/20 text-green-400' : 'bg-slate-500/20 text-slate-400'}`}>
              {ivSkewStatus?.is_running ? 'RUNNING' : 'STOPPED'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant={ivSkewStatus?.is_running ? "destructive" : "default"}
              onClick={() => toggleIvSkewAnalyzer(!ivSkewStatus?.is_running)}
              className="text-xs"
              data-testid="toggle-iv-skew"
            >
              {ivSkewStatus?.is_running ? 'Stop' : 'Start'} Analyzer
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={fetchIvSkewConfig}
              className="text-xs"
              data-testid="refresh-iv-skew"
            >
              <RefreshCw className="w-3 h-3" />
            </Button>
          </div>
        </div>

        <p className="text-xs text-muted-foreground mb-4">
          Detects mispriced {activeSymbol} options by fitting SVI curves to the volatility skew. BUY when IV is below expected (negative Z-score), SELL when above.
        </p>

        {ivSkewLoading ? (
          <div className="text-center py-4">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto text-violet-400" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Status Summary for Active Symbol */}
            {ivSkewStatus && (
              <div className="flex items-center justify-center gap-6 p-2 bg-slate-500/10 rounded border border-slate-500/20">
                <div className="flex items-center gap-2">
                  <span className="text-lg font-mono text-violet-400">{ivSkewStatus.stats?.calibrations || 0}</span>
                  <span className="text-[10px] text-muted-foreground">Calibrations</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-mono text-cyan-400">{ivSkewStatus.stats?.dislocations_detected || 0}</span>
                  <span className="text-[10px] text-muted-foreground">Dislocations</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-lg font-mono ${ivSkewStatus.current_curves?.[activeSymbol]?.CE?.is_valid ? 'text-green-400' : 'text-slate-400'}`}>
                    {ivSkewStatus.current_curves?.[activeSymbol]?.CE?.n_points || 0}pts
                  </span>
                  <span className="text-[10px] text-muted-foreground">CE</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-lg font-mono ${ivSkewStatus.current_curves?.[activeSymbol]?.PE?.is_valid ? 'text-green-400' : 'text-slate-400'}`}>
                    {ivSkewStatus.current_curves?.[activeSymbol]?.PE?.n_points || 0}pts
                  </span>
                  <span className="text-[10px] text-muted-foreground">PE</span>
                </div>
              </div>
            )}

            {/* Active Symbol Configuration Only */}
            <div className={`p-4 ${activeSymbol === 'NIFTY' ? 'bg-blue-500/5 border-blue-500/20' : 'bg-orange-500/5 border-orange-500/20'} border rounded`}>
              <h4 className={`text-sm font-semibold ${activeSymbol === 'NIFTY' ? 'text-blue-400' : 'text-orange-400'} flex items-center gap-2 mb-3`}>
                <Zap className="w-4 h-4" />
                {activeSymbol} Dislocation Parameters
              </h4>
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-medium text-foreground">Z-Score BUY (Underpriced)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="-5"
                    max="0"
                    value={ivSkewConfig[activeSymbol].z_score_buy}
                    onChange={(e) => setIvSkewConfig(prev => ({
                      ...prev,
                      [activeSymbol]: { ...prev[activeSymbol], z_score_buy: parseFloat(e.target.value) || -2.0 }
                    }))}
                    className="w-full bg-surface border border-border px-3 py-2 rounded text-foreground font-mono"
                    data-testid={`${activeSymbol.toLowerCase()}-zscore-buy`}
                  />
                  <p className="text-[10px] text-muted-foreground">Negative = IV below curve</p>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-foreground">Z-Score SELL (Overpriced)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="5"
                    value={ivSkewConfig[activeSymbol].z_score_sell}
                    onChange={(e) => setIvSkewConfig(prev => ({
                      ...prev,
                      [activeSymbol]: { ...prev[activeSymbol], z_score_sell: parseFloat(e.target.value) || 2.0 }
                    }))}
                    className="w-full bg-surface border border-border px-3 py-2 rounded text-foreground font-mono"
                    data-testid={`${activeSymbol.toLowerCase()}-zscore-sell`}
                  />
                  <p className="text-[10px] text-muted-foreground">Positive = IV above curve</p>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-foreground">Persistence (Readings)</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={ivSkewConfig[activeSymbol].persistence}
                    onChange={(e) => setIvSkewConfig(prev => ({
                      ...prev,
                      [activeSymbol]: { ...prev[activeSymbol], persistence: parseInt(e.target.value) || 3 }
                    }))}
                    className="w-full bg-surface border border-border px-3 py-2 rounded text-foreground font-mono"
                    data-testid={`${activeSymbol.toLowerCase()}-persistence`}
                  />
                  <p className="text-[10px] text-muted-foreground">Consecutive readings needed</p>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-foreground">ATM Proximity (Strikes)</label>
                  <input
                    type="number"
                    min="1"
                    max="15"
                    value={ivSkewConfig[activeSymbol].atm_proximity}
                    onChange={(e) => setIvSkewConfig(prev => ({
                      ...prev,
                      [activeSymbol]: { ...prev[activeSymbol], atm_proximity: parseInt(e.target.value) || 4 }
                    }))}
                    className="w-full bg-surface border border-border px-3 py-2 rounded text-foreground font-mono"
                    data-testid={`${activeSymbol.toLowerCase()}-atm-proximity`}
                  />
                  <p className="text-[10px] text-muted-foreground">Max strikes from ATM</p>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button
                onClick={saveIvSkewConfig}
                disabled={ivSkewSaving}
                className="bg-violet-600 hover:bg-violet-700 text-white"
                data-testid="save-iv-skew-config"
              >
                {ivSkewSaving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Save IV Skew Configuration
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <Button
          onClick={handleSave}
          disabled={saving || !isWeightsValid}
          className="bg-accent text-accent-foreground hover:bg-accent/90"
          data-testid="save-strategy-config"
        >
          {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
          Save {activeSymbol} Configuration
        </Button>

        <Button
          onClick={handleReset}
          variant="outline"
          disabled={saving}
          data-testid="reset-strategy-config"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Reset All to Defaults
        </Button>
      </div>
    </div>
  );
};


// ============================================
// DETECTION PARAMETERS PANEL (HFT Thresholds)
// ============================================
const DetectionParametersPanel = () => {
  const [detectionConfig, setDetectionConfig] = useState({
    HFT_VELOCITY_THRESHOLD: 3.0,
    HFT_VOLUME_FILTER: 0,
    HFT_IMBALANCE_RATIO: 3.0,
    HFT_IMBALANCE_LEVELS: 3,
    HFT_SWEEP_TRADE_THRESHOLD: 5,
    HFT_SWEEP_TIME_WINDOW_MS: 5,
    GAMMA_SCORING_MODE: 'ABSOLUTE',
    GAMMA_WALL_DISTANCE_NIFTY: 50,
    GAMMA_WALL_DISTANCE_BANKNIFTY: 100,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  };

  const fetchDetectionConfig = useCallback(async () => {
    try {
      setError(null);
      const res = await fetch(`${API}/admin/config`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.config) {
        setDetectionConfig(prev => ({
          ...prev,
          ...data.config
        }));
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchDetectionConfig();
  }, [fetchDetectionConfig]);

  const handleSaveDetection = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await fetch(`${API}/admin/config`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          HFT_VELOCITY_THRESHOLD: detectionConfig.HFT_VELOCITY_THRESHOLD,
          HFT_VOLUME_FILTER: detectionConfig.HFT_VOLUME_FILTER,
          HFT_IMBALANCE_RATIO: detectionConfig.HFT_IMBALANCE_RATIO,
          HFT_IMBALANCE_LEVELS: detectionConfig.HFT_IMBALANCE_LEVELS,
          HFT_SWEEP_TRADE_THRESHOLD: detectionConfig.HFT_SWEEP_TRADE_THRESHOLD,
          HFT_SWEEP_TIME_WINDOW_MS: detectionConfig.HFT_SWEEP_TIME_WINDOW_MS,
          GAMMA_SCORING_MODE: detectionConfig.GAMMA_SCORING_MODE,
          GAMMA_WALL_DISTANCE_NIFTY: detectionConfig.GAMMA_WALL_DISTANCE_NIFTY,
          GAMMA_WALL_DISTANCE_BANKNIFTY: detectionConfig.GAMMA_WALL_DISTANCE_BANKNIFTY,
        }),
      });
      const data = await res.json();

      if (data.success) {
        setSuccess('Detection parameters saved! Changes effective immediately.');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.message || 'Failed to save');
      }
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const handleResetDetection = async () => {
    if (!window.confirm('Reset detection parameters to defaults?')) return;
    setSaving(true);
    setError(null);

    try {
      const res = await fetch(`${API}/admin/config/reset`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.current_config) {
        setDetectionConfig(prev => ({
          ...prev,
          ...data.current_config
        }));
        setSuccess('Detection parameters reset to defaults');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="panel p-6">
        <div className="flex items-center gap-2 text-muted-foreground">
          <RefreshCw className="w-4 h-4 animate-spin" />
          Loading detection parameters...
        </div>
      </div>
    );
  }

  return (
    <div className="panel p-6 border-accent/30">
      <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
        <Activity className="w-5 h-5 text-cyan-400" />
        HFT Detection Parameters
        <span className="text-xs text-muted-foreground ml-2">(Changes take effect immediately)</span>
      </h3>

      {error && (
        <div className="mb-4 p-3 bg-sell/10 border border-sell/30 rounded flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-sell" />
          <span className="text-sm text-sell">{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-buy/10 border border-buy/30 rounded flex items-center gap-2">
          <Check className="w-4 h-4 text-buy" />
          <span className="text-sm text-buy">{success}</span>
        </div>
      )}

      <div className="grid grid-cols-3 gap-4 mb-6">
        {/* Velocity Threshold */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Velocity Threshold</label>
          <input
            type="number"
            step="0.1"
            min="0.1"
            max="10"
            value={detectionConfig.HFT_VELOCITY_THRESHOLD}
            onChange={(e) => setDetectionConfig(prev => ({ 
              ...prev, 
              HFT_VELOCITY_THRESHOLD: parseFloat(e.target.value) || 3.0 
            }))}
            className="w-full bg-surface border border-border px-3 py-2 rounded text-foreground font-mono"
          />
          <p className="text-xs text-muted-foreground">Multiplier (lower = more sensitive)</p>
        </div>

        {/* Imbalance Ratio */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Imbalance Ratio</label>
          <input
            type="number"
            step="0.1"
            min="1.5"
            max="10"
            value={detectionConfig.HFT_IMBALANCE_RATIO}
            onChange={(e) => setDetectionConfig(prev => ({ 
              ...prev, 
              HFT_IMBALANCE_RATIO: parseFloat(e.target.value) || 3.0 
            }))}
            className="w-full bg-surface border border-border px-3 py-2 rounded text-foreground font-mono"
          />
          <p className="text-xs text-muted-foreground">Bid/Ask ratio for imbalance</p>
        </div>

        {/* Imbalance Levels */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Imbalance Levels</label>
          <input
            type="number"
            min="1"
            max="10"
            value={detectionConfig.HFT_IMBALANCE_LEVELS}
            onChange={(e) => setDetectionConfig(prev => ({ 
              ...prev, 
              HFT_IMBALANCE_LEVELS: parseInt(e.target.value) || 3 
            }))}
            className="w-full bg-surface border border-border px-3 py-2 rounded text-foreground font-mono"
          />
          <p className="text-xs text-muted-foreground">Consecutive levels needed</p>
        </div>

        {/* Sweep Trade Threshold */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Sweep Trades</label>
          <input
            type="number"
            min="1"
            max="20"
            value={detectionConfig.HFT_SWEEP_TRADE_THRESHOLD}
            onChange={(e) => setDetectionConfig(prev => ({ 
              ...prev, 
              HFT_SWEEP_TRADE_THRESHOLD: parseInt(e.target.value) || 5 
            }))}
            className="w-full bg-surface border border-border px-3 py-2 rounded text-foreground font-mono"
          />
          <p className="text-xs text-muted-foreground">Min trades for sweep</p>
        </div>

        {/* Sweep Time Window */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Sweep Window (ms)</label>
          <input
            type="number"
            min="1"
            max="100"
            value={detectionConfig.HFT_SWEEP_TIME_WINDOW_MS}
            onChange={(e) => setDetectionConfig(prev => ({ 
              ...prev, 
              HFT_SWEEP_TIME_WINDOW_MS: parseInt(e.target.value) || 5 
            }))}
            className="w-full bg-surface border border-border px-3 py-2 rounded text-foreground font-mono"
          />
          <p className="text-xs text-muted-foreground">Time window for sweep</p>
        </div>

        {/* Volume Filter */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Volume Filter</label>
          <input
            type="number"
            min="0"
            max="1000"
            value={detectionConfig.HFT_VOLUME_FILTER}
            onChange={(e) => setDetectionConfig(prev => ({ 
              ...prev, 
              HFT_VOLUME_FILTER: parseInt(e.target.value) || 0 
            }))}
            className="w-full bg-surface border border-border px-3 py-2 rounded text-foreground font-mono"
          />
          <p className="text-xs text-muted-foreground">Min volume (0 = all)</p>
        </div>
      </div>

      {/* Gamma Section */}
      <h4 className="font-medium text-foreground mb-3 flex items-center gap-2 border-t border-border pt-4">
        <TrendingUp className="w-4 h-4 text-amber-400" />
        Gamma/GEX Parameters
      </h4>

      <div className="grid grid-cols-3 gap-4 mb-6">
        {/* Gamma Scoring Mode */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Scoring Mode</label>
          <select
            value={detectionConfig.GAMMA_SCORING_MODE}
            onChange={(e) => setDetectionConfig(prev => ({ 
              ...prev, 
              GAMMA_SCORING_MODE: e.target.value 
            }))}
            className="w-full bg-surface border border-border px-3 py-2 rounded text-foreground"
          >
            <option value="ABSOLUTE">ABSOLUTE</option>
            <option value="RELATIVE">RELATIVE</option>
          </select>
          <p className="text-xs text-muted-foreground">How GEX values are scored</p>
        </div>

        {/* Wall Distance NIFTY */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">NIFTY Wall Distance</label>
          <input
            type="number"
            min="10"
            max="200"
            value={detectionConfig.GAMMA_WALL_DISTANCE_NIFTY}
            onChange={(e) => setDetectionConfig(prev => ({ 
              ...prev, 
              GAMMA_WALL_DISTANCE_NIFTY: parseInt(e.target.value) || 50 
            }))}
            className="w-full bg-surface border border-border px-3 py-2 rounded text-foreground font-mono"
          />
          <p className="text-xs text-muted-foreground">Points for "at wall"</p>
        </div>

        {/* Wall Distance BANKNIFTY */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">BANKNIFTY Wall Distance</label>
          <input
            type="number"
            min="20"
            max="500"
            value={detectionConfig.GAMMA_WALL_DISTANCE_BANKNIFTY}
            onChange={(e) => setDetectionConfig(prev => ({ 
              ...prev, 
              GAMMA_WALL_DISTANCE_BANKNIFTY: parseInt(e.target.value) || 100 
            }))}
            className="w-full bg-surface border border-border px-3 py-2 rounded text-foreground font-mono"
          />
          <p className="text-xs text-muted-foreground">Points for "at wall"</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <Button
          onClick={handleSaveDetection}
          disabled={saving}
          className="bg-cyan-600 text-white hover:bg-cyan-700"
          data-testid="save-detection-config"
        >
          {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
          Apply Detection Settings
        </Button>

        <Button
          onClick={handleResetDetection}
          variant="outline"
          disabled={saving}
          data-testid="reset-detection-config"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Reset to Defaults
        </Button>
      </div>
    </div>
  );
};


// ============================================
// SYMBOL CONFIGURATION PANEL (Dynamic Engine Params)
// ============================================
const SymbolConfigPanel = () => {
  const [symbolConfig, setSymbolConfig] = useState({
    NIFTY: {
      cvd_thresh: 10000,
      imbalance_ratio: 3.0,
      absorp_vol: 5000,
      vix_scale: 1.0,
      strike_interval: 50,
      lot_size: 25,
    },
    BANKNIFTY: {
      cvd_thresh: 3000,
      imbalance_ratio: 2.5,
      absorp_vol: 1500,
      vix_scale: 1.2,
      strike_interval: 100,
      lot_size: 15,
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Get auth token from localStorage
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  };

  const fetchConfig = useCallback(async () => {
    try {
      setError(null);
      const res = await fetch(`${API}/admin/symbol-config`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      // Only update if we have valid config data
      if (data.NIFTY && data.BANKNIFTY) {
        setSymbolConfig({
          NIFTY: data.NIFTY,
          BANKNIFTY: data.BANKNIFTY
        });
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await fetch(`${API}/admin/symbol-config`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(symbolConfig),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.detail || 'Failed to save configuration');
      } else {
        setSuccess('Symbol configuration saved successfully!');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const updateSymbolParam = (symbol, field, value) => {
    setSymbolConfig(prev => ({
      ...prev,
      [symbol]: {
        ...prev[symbol],
        [field]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="panel p-6 flex items-center justify-center h-32">
        <RefreshCw className="w-6 h-6 text-accent animate-spin" />
      </div>
    );
  }

  return (
    <div className="panel p-6" data-testid="symbol-config-panel">
      <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
        <Database className="w-5 h-5 text-cyan-400" />
        Symbol Configuration
        <span className="text-xs text-muted-foreground font-normal ml-2">
          (Per-symbol engine parameters)
        </span>
      </h3>

      <p className="text-xs text-muted-foreground mb-4">
        Configure CVD thresholds, imbalance detection ratios, and other parameters for each index.
        Changes take effect immediately after saving.
      </p>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-4 p-3 bg-sell/10 border border-sell/30 rounded flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-sell" />
          <span className="text-sm text-sell">{error}</span>
          <Button variant="ghost" size="sm" onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-buy/10 border border-buy/30 rounded flex items-center gap-2">
          <Check className="w-4 h-4 text-buy" />
          <span className="text-sm text-buy">{success}</span>
        </div>
      )}

      {/* Parameter Grid Table */}
      <div className="overflow-hidden border border-border rounded">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-surface-highlight border-b border-border">
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase">Symbol</th>
              <th className="text-center px-3 py-2.5 text-xs font-semibold text-muted-foreground uppercase">CVD Threshold</th>
              <th className="text-center px-3 py-2.5 text-xs font-semibold text-muted-foreground uppercase">Imbalance Ratio</th>
              <th className="text-center px-3 py-2.5 text-xs font-semibold text-muted-foreground uppercase">Absorption Vol</th>
              <th className="text-center px-3 py-2.5 text-xs font-semibold text-muted-foreground uppercase">VIX Scale</th>
              <th className="text-center px-3 py-2.5 text-xs font-semibold text-muted-foreground uppercase">Strike Interval</th>
              <th className="text-center px-3 py-2.5 text-xs font-semibold text-muted-foreground uppercase">Lot Size</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {['NIFTY', 'BANKNIFTY'].map(symbol => (
              <tr key={symbol} className="hover:bg-surface-highlight/50">
                <td className="px-4 py-3">
                  <span className={`font-mono font-bold ${symbol === 'NIFTY' ? 'text-blue-400' : 'text-purple-400'}`}>
                    {symbol}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    value={symbolConfig[symbol]?.cvd_thresh || 0}
                    onChange={(e) => updateSymbolParam(symbol, 'cvd_thresh', parseInt(e.target.value) || 0)}
                    className="w-full bg-surface border border-border px-2 py-1 rounded text-foreground font-mono text-center text-sm"
                    data-testid={`${symbol.toLowerCase()}-cvd-thresh`}
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    step="0.1"
                    value={symbolConfig[symbol]?.imbalance_ratio || 0}
                    onChange={(e) => updateSymbolParam(symbol, 'imbalance_ratio', parseFloat(e.target.value) || 0)}
                    className="w-full bg-surface border border-border px-2 py-1 rounded text-foreground font-mono text-center text-sm"
                    data-testid={`${symbol.toLowerCase()}-imbalance-ratio`}
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    value={symbolConfig[symbol]?.absorp_vol || 0}
                    onChange={(e) => updateSymbolParam(symbol, 'absorp_vol', parseInt(e.target.value) || 0)}
                    className="w-full bg-surface border border-border px-2 py-1 rounded text-foreground font-mono text-center text-sm"
                    data-testid={`${symbol.toLowerCase()}-absorp-vol`}
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    step="0.1"
                    value={symbolConfig[symbol]?.vix_scale || 1}
                    onChange={(e) => updateSymbolParam(symbol, 'vix_scale', parseFloat(e.target.value) || 1)}
                    className="w-full bg-surface border border-border px-2 py-1 rounded text-foreground font-mono text-center text-sm"
                    data-testid={`${symbol.toLowerCase()}-vix-scale`}
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    value={symbolConfig[symbol]?.strike_interval || 0}
                    onChange={(e) => updateSymbolParam(symbol, 'strike_interval', parseInt(e.target.value) || 0)}
                    className="w-full bg-surface border border-border px-2 py-1 rounded text-foreground font-mono text-center text-sm"
                    data-testid={`${symbol.toLowerCase()}-strike-interval`}
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    value={symbolConfig[symbol]?.lot_size || 0}
                    onChange={(e) => updateSymbolParam(symbol, 'lot_size', parseInt(e.target.value) || 0)}
                    className="w-full bg-surface border border-border px-2 py-1 rounded text-foreground font-mono text-center text-sm"
                    data-testid={`${symbol.toLowerCase()}-lot-size`}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Save Button */}
      <div className="mt-4 flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-cyan-600 hover:bg-cyan-700"
          data-testid="save-symbol-config"
        >
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              Saving...
            </>
          ) : (
            <>
              <Check className="w-4 h-4 mr-2" />
              Save Configuration
            </>
          )}
        </Button>
      </div>
    </div>
  );
};


// ============================================
// SESSION 69: STRUCTURAL PHYSICS PANEL
// ============================================
const StructuralPhysicsPanel = () => {
  const [config, setConfig] = useState({
    NIFTY: { step: 20, factor: 2.5, max_adj: 40 },
    BANKNIFTY: { step: 30, factor: 2.5, max_adj: 60 }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  };

  const fetchConfig = useCallback(async () => {
    try {
      setError(null);
      const res = await fetch(`${API}/config/structural`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.NIFTY && data.BANKNIFTY) {
        setConfig({
          NIFTY: data.NIFTY,
          BANKNIFTY: data.BANKNIFTY
        });
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSaveSymbol = async (symbol) => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await fetch(`${API}/config/structural`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          symbol,
          step: parseFloat(config[symbol].step),
          factor: parseFloat(config[symbol].factor),
          max_adj: parseFloat(config[symbol].max_adj)
        }),
      });

      if (!res.ok) {
        throw new Error('Failed to save');
      }
      
      setSuccess(`${symbol} structural config saved! Applied immediately.`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const handleReset = async () => {
    if (!window.confirm('Reset structural config to defaults?')) return;
    setSaving(true);
    setError(null);

    try {
      const res = await fetch(`${API}/config/structural/reset`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.config) {
        setConfig({
          NIFTY: data.config.NIFTY,
          BANKNIFTY: data.config.BANKNIFTY
        });
        setSuccess('Structural config reset to defaults');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const updateParam = (symbol, field, value) => {
    setConfig(prev => ({
      ...prev,
      [symbol]: {
        ...prev[symbol],
        [field]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="panel p-6">
        <div className="flex items-center justify-center h-32">
          <RefreshCw className="w-6 h-6 text-accent animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="panel p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg font-bold text-foreground flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-cyan-400" />
            Structural Physics Configuration
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Buoyancy (above flip) / Gravity (below flip) decay settings. Changes apply immediately.
          </p>
        </div>
        <Button
          onClick={handleReset}
          variant="outline"
          size="sm"
          disabled={saving}
        >
          <RefreshCw className="w-3 h-3 mr-1" />
          Reset
        </Button>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="p-3 bg-sell/10 border border-sell/30 rounded flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-sell" />
          <span className="text-sm text-sell">{error}</span>
        </div>
      )}
      {success && (
        <div className="p-3 bg-buy/10 border border-buy/30 rounded flex items-center gap-2">
          <Check className="w-4 h-4 text-buy" />
          <span className="text-sm text-buy">{success}</span>
        </div>
      )}

      {/* Symbol Configs Side by Side */}
      <div className="grid grid-cols-2 gap-6">
        {['NIFTY', 'BANKNIFTY'].map(symbol => (
          <div key={symbol} className="bg-surface/50 border border-border rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="font-bold text-accent">{symbol}</span>
              <Button
                onClick={() => handleSaveSymbol(symbol)}
                size="sm"
                disabled={saving}
                className="bg-cyan-500 hover:bg-cyan-400 text-black"
              >
                {saving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                <span className="ml-1">Save</span>
              </Button>
            </div>

            {/* Step Size */}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Step Size (points)</label>
              <input
                type="number"
                value={config[symbol].step}
                onChange={(e) => updateParam(symbol, 'step', e.target.value)}
                className="w-full bg-black/40 border border-gray-700 px-3 py-2 rounded text-sm font-mono text-foreground"
                step="5"
                min="5"
              />
              <p className="text-[10px] text-gray-600">Points per adjustment step</p>
            </div>

            {/* Decay Factor */}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Decay Factor</label>
              <input
                type="number"
                value={config[symbol].factor}
                onChange={(e) => updateParam(symbol, 'factor', e.target.value)}
                className="w-full bg-black/40 border border-gray-700 px-3 py-2 rounded text-sm font-mono text-foreground"
                step="0.5"
                min="0.5"
              />
              <p className="text-[10px] text-gray-600">Score adjustment per step</p>
            </div>

            {/* Max Adjustment */}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Max Adjustment</label>
              <input
                type="number"
                value={config[symbol].max_adj}
                onChange={(e) => updateParam(symbol, 'max_adj', e.target.value)}
                className="w-full bg-black/40 border border-gray-700 px-3 py-2 rounded text-sm font-mono text-foreground"
                step="10"
                min="10"
              />
              <p className="text-[10px] text-gray-600">Maximum absolute cap</p>
            </div>

            {/* Formula Preview */}
            <div className="p-2 bg-black/30 rounded border border-gray-800">
              <p className="text-[9px] text-gray-500 mb-1">FORMULA</p>
              <p className="text-xs font-mono text-cyan-300">
                adj = clamp(dist/{config[symbol].step} × {config[symbol].factor}, ±{config[symbol].max_adj})
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


// ============================================
// SESSION 69: THRESHOLD CAPS PANEL
// ============================================
const ThresholdCapsPanel = () => {
  const [config, setConfig] = useState({
    NIFTY: { max_cap: 70, min_floor: 25, acceleration_bonus: 10 },
    BANKNIFTY: { max_cap: 75, min_floor: 30, acceleration_bonus: 10 }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  };

  const fetchConfig = useCallback(async () => {
    try {
      setError(null);
      const res = await fetch(`${API}/config/threshold-caps`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.NIFTY && data.BANKNIFTY) {
        setConfig({
          NIFTY: {
            max_cap: data.NIFTY.max_cap || 70,
            min_floor: data.NIFTY.min_floor || 25,
            acceleration_bonus: data.NIFTY.acceleration_bonus || 10
          },
          BANKNIFTY: {
            max_cap: data.BANKNIFTY.max_cap || 75,
            min_floor: data.BANKNIFTY.min_floor || 30,
            acceleration_bonus: data.BANKNIFTY.acceleration_bonus || 10
          }
        });
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSaveSymbol = async (symbol) => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await fetch(`${API}/config/threshold-caps`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          symbol,
          max_cap: parseInt(config[symbol].max_cap),
          min_floor: parseInt(config[symbol].min_floor),
          acceleration_bonus: parseInt(config[symbol].acceleration_bonus)
        }),
      });

      if (!res.ok) throw new Error('Failed to save');
      
      setSuccess(`${symbol} threshold caps saved! Applied immediately.`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const handleReset = async () => {
    if (!window.confirm('Reset threshold caps to defaults?')) return;
    setSaving(true);
    setError(null);

    try {
      const res = await fetch(`${API}/config/threshold-caps/reset`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.config) {
        setConfig({
          NIFTY: {
            max_cap: data.config.NIFTY?.max_cap || 70,
            min_floor: data.config.NIFTY?.min_floor || 25,
            acceleration_bonus: data.config.NIFTY?.acceleration_bonus || 10
          },
          BANKNIFTY: {
            max_cap: data.config.BANKNIFTY?.max_cap || 75,
            min_floor: data.config.BANKNIFTY?.min_floor || 30,
            acceleration_bonus: data.config.BANKNIFTY?.acceleration_bonus || 10
          }
        });
        setSuccess('Threshold caps reset to defaults');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const updateParam = (symbol, field, value) => {
    setConfig(prev => ({
      ...prev,
      [symbol]: {
        ...prev[symbol],
        [field]: value
      }
    }));
  };

  // Calculate preview values
  const getPreviewValues = (symbol) => {
    const { max_cap, min_floor, acceleration_bonus } = config[symbol];
    const base = 80; // Default raw threshold
    const clamped = Math.max(Math.min(base, max_cap), min_floor);
    const inTrend = Math.max(clamped - acceleration_bonus, min_floor);
    return { base, clamped, inTrend };
  };

  if (loading) {
    return (
      <div className="panel p-6">
        <div className="flex items-center justify-center h-32">
          <RefreshCw className="w-6 h-6 text-accent animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="panel p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg font-bold text-foreground flex items-center gap-2">
            <Target className="w-5 h-5 text-amber-400" />
            Execution Threshold Controls
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Max cap, min floor, and trend acceleration. Changes apply immediately.
          </p>
        </div>
        <Button
          onClick={handleReset}
          variant="outline"
          size="sm"
          disabled={saving}
        >
          <RefreshCw className="w-3 h-3 mr-1" />
          Reset
        </Button>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="p-3 bg-sell/10 border border-sell/30 rounded flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-sell" />
          <span className="text-sm text-sell">{error}</span>
        </div>
      )}
      {success && (
        <div className="p-3 bg-buy/10 border border-buy/30 rounded flex items-center gap-2">
          <Check className="w-4 h-4 text-buy" />
          <span className="text-sm text-buy">{success}</span>
        </div>
      )}

      {/* Symbol Configs Side by Side */}
      <div className="grid grid-cols-2 gap-6">
        {['NIFTY', 'BANKNIFTY'].map(symbol => {
          const preview = getPreviewValues(symbol);
          return (
            <div key={symbol} className="bg-surface/50 border border-border rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-bold text-accent">{symbol}</span>
                <Button
                  onClick={() => handleSaveSymbol(symbol)}
                  size="sm"
                  disabled={saving}
                  className="bg-amber-500 hover:bg-amber-400 text-black"
                  data-testid={`save-threshold-${symbol.toLowerCase()}`}
                >
                  {saving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                  <span className="ml-1">Save</span>
                </Button>
              </div>

              {/* Max Threshold Cap */}
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Max Threshold Cap</label>
                <input
                  type="number"
                  value={config[symbol].max_cap}
                  onChange={(e) => updateParam(symbol, 'max_cap', e.target.value)}
                  className="w-full bg-black/40 border border-gray-700 px-3 py-2 rounded text-sm font-mono text-foreground"
                  data-testid={`input-max-cap-${symbol.toLowerCase()}`}
                  step="5"
                  min="50"
                  max="100"
                />
                <p className="text-[10px] text-gray-600">Upper limit (default: {symbol === 'NIFTY' ? 70 : 75})</p>
              </div>

              {/* Min Threshold Floor */}
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Min Threshold Floor</label>
                <input
                  type="number"
                  value={config[symbol].min_floor}
                  onChange={(e) => updateParam(symbol, 'min_floor', e.target.value)}
                  className="w-full bg-black/40 border border-gray-700 px-3 py-2 rounded text-sm font-mono text-foreground"
                  data-testid={`input-min-floor-${symbol.toLowerCase()}`}
                  step="5"
                  min="10"
                  max="50"
                />
                <p className="text-[10px] text-gray-600">Lower limit to prevent overtrading (default: {symbol === 'NIFTY' ? 25 : 30})</p>
              </div>

              {/* Trend Acceleration Bonus */}
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Trend Acceleration Bonus</label>
                <input
                  type="number"
                  value={config[symbol].acceleration_bonus}
                  onChange={(e) => updateParam(symbol, 'acceleration_bonus', e.target.value)}
                  className="w-full bg-black/40 border border-gray-700 px-3 py-2 rounded text-sm font-mono text-foreground"
                  data-testid={`input-accel-bonus-${symbol.toLowerCase()}`}
                  step="5"
                  min="0"
                  max="30"
                />
                <p className="text-[10px] text-gray-600">Reduce threshold in Waterfall/Rocket zones (default: 10)</p>
              </div>

              {/* Formula Preview */}
              <div className="p-3 bg-black/30 rounded border border-gray-800 space-y-2">
                <p className="text-[9px] text-gray-500 uppercase font-semibold">Threshold Calculation</p>
                <div className="text-xs font-mono space-y-1">
                  <p className="text-gray-400">
                    <span className="text-gray-500">1. Clamp:</span> max(min({preview.base}, <span className="text-amber-300">{config[symbol].max_cap}</span>), <span className="text-cyan-300">{config[symbol].min_floor}</span>) = <span className="text-white">{preview.clamped}</span>
                  </p>
                  <p className="text-gray-400">
                    <span className="text-gray-500">2. Normal:</span> Final Gate = <span className="text-green-400 font-bold">{preview.clamped}</span>
                  </p>
                  <p className="text-gray-400">
                    <span className="text-gray-500">3. Trend Zone:</span> {preview.clamped} - <span className="text-pink-300">{config[symbol].acceleration_bonus}</span> = <span className="text-green-400 font-bold">{preview.inTrend}</span>
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};


// ============================================
// SESSION 70: BIMODAL PERSISTENCE PANEL
// ============================================
const BimodalPanel = () => {
  const [config, setConfig] = useState({
    NIFTY: { ema_period: 3, confirmation_window: 10, hysteresis_gap: 40, trailing_sl_atr_mult: 1.5 },
    BANKNIFTY: { ema_period: 3, confirmation_window: 10, hysteresis_gap: 40, trailing_sl_atr_mult: 1.5 }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  };

  const fetchConfig = useCallback(async () => {
    try {
      setError(null);
      const res = await fetch(`${API}/config/bimodal`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.NIFTY && data.BANKNIFTY) {
        setConfig({
          NIFTY: {
            ema_period: data.NIFTY.ema_period || 3,
            confirmation_window: data.NIFTY.confirmation_window || 10,
            hysteresis_gap: data.NIFTY.hysteresis_gap || 40,
            trailing_sl_atr_mult: data.NIFTY.trailing_sl_atr_mult || 1.5
          },
          BANKNIFTY: {
            ema_period: data.BANKNIFTY.ema_period || 3,
            confirmation_window: data.BANKNIFTY.confirmation_window || 10,
            hysteresis_gap: data.BANKNIFTY.hysteresis_gap || 40,
            trailing_sl_atr_mult: data.BANKNIFTY.trailing_sl_atr_mult || 1.5
          }
        });
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSaveSymbol = async (symbol) => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await fetch(`${API}/config/bimodal`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          symbol,
          ema_period: parseInt(config[symbol].ema_period),
          confirmation_window: parseInt(config[symbol].confirmation_window),
          hysteresis_gap: parseInt(config[symbol].hysteresis_gap),
          trailing_sl_atr_mult: parseFloat(config[symbol].trailing_sl_atr_mult)
        }),
      });

      if (!res.ok) throw new Error('Failed to save');
      
      setSuccess(`${symbol} bimodal config saved! Applied immediately.`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const handleReset = async () => {
    if (!window.confirm('Reset bimodal config to defaults?')) return;
    setSaving(true);
    setError(null);

    try {
      const res = await fetch(`${API}/config/bimodal/reset`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.config) {
        setConfig({
          NIFTY: data.config.NIFTY,
          BANKNIFTY: data.config.BANKNIFTY
        });
        setSuccess('Bimodal config reset to defaults');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const updateParam = (symbol, field, value) => {
    setConfig(prev => ({
      ...prev,
      [symbol]: {
        ...prev[symbol],
        [field]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="panel p-6">
        <div className="flex items-center justify-center h-32">
          <RefreshCw className="w-6 h-6 text-accent animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="panel p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg font-bold text-foreground flex items-center gap-2">
            <Waves className="w-5 h-5 text-purple-400" />
            Bimodal Persistence Engine
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            EMA smoothing, confirmation windows, and trailing SL settings. Changes apply immediately.
          </p>
        </div>
        <Button
          onClick={handleReset}
          variant="outline"
          size="sm"
          disabled={saving}
        >
          <RefreshCw className="w-3 h-3 mr-1" />
          Reset
        </Button>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="p-3 bg-sell/10 border border-sell/30 rounded flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-sell" />
          <span className="text-sm text-sell">{error}</span>
        </div>
      )}
      {success && (
        <div className="p-3 bg-buy/10 border border-buy/30 rounded flex items-center gap-2">
          <Check className="w-4 h-4 text-buy" />
          <span className="text-sm text-buy">{success}</span>
        </div>
      )}

      {/* Symbol Configs Side by Side */}
      <div className="grid grid-cols-2 gap-6">
        {['NIFTY', 'BANKNIFTY'].map(symbol => (
          <div key={symbol} className="bg-surface/50 border border-border rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="font-bold text-accent">{symbol}</span>
              <Button
                onClick={() => handleSaveSymbol(symbol)}
                size="sm"
                disabled={saving}
                className="bg-purple-500 hover:bg-purple-400 text-white"
                data-testid={`save-bimodal-${symbol.toLowerCase()}`}
              >
                {saving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                <span className="ml-1">Save</span>
              </Button>
            </div>

            {/* EMA Period */}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">God Score Smoothing (EMA Period)</label>
              <input
                type="number"
                value={config[symbol].ema_period}
                onChange={(e) => updateParam(symbol, 'ema_period', e.target.value)}
                className="w-full bg-black/40 border border-gray-700 px-3 py-2 rounded text-sm font-mono text-foreground"
                data-testid={`input-ema-period-${symbol.toLowerCase()}`}
                step="1"
                min="1"
                max="10"
              />
              <p className="text-[10px] text-gray-600">EMA period for score smoothing (default: 3)</p>
            </div>

            {/* Confirmation Window */}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Confirmation Window (Debounce)</label>
              <input
                type="number"
                value={config[symbol].confirmation_window}
                onChange={(e) => updateParam(symbol, 'confirmation_window', e.target.value)}
                className="w-full bg-black/40 border border-gray-700 px-3 py-2 rounded text-sm font-mono text-foreground"
                data-testid={`input-confirm-window-${symbol.toLowerCase()}`}
                step="5"
                min="5"
                max="60"
              />
              <p className="text-[10px] text-gray-600">Seconds to confirm soft exit (default: 10s)</p>
            </div>

            {/* Hysteresis Gap */}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Exit Hysteresis Gap</label>
              <input
                type="number"
                value={config[symbol].hysteresis_gap}
                onChange={(e) => updateParam(symbol, 'hysteresis_gap', e.target.value)}
                className="w-full bg-black/40 border border-gray-700 px-3 py-2 rounded text-sm font-mono text-foreground"
                data-testid={`input-hysteresis-${symbol.toLowerCase()}`}
                step="5"
                min="10"
                max="80"
              />
              <p className="text-[10px] text-gray-600">Points below gate for soft exit (default: 40)</p>
            </div>

            {/* Trailing SL ATR Multiplier */}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Trailing SL (ATR Multiplier)</label>
              <input
                type="number"
                value={config[symbol].trailing_sl_atr_mult}
                onChange={(e) => updateParam(symbol, 'trailing_sl_atr_mult', e.target.value)}
                className="w-full bg-black/40 border border-gray-700 px-3 py-2 rounded text-sm font-mono text-foreground"
                data-testid={`input-atr-mult-${symbol.toLowerCase()}`}
                step="0.1"
                min="0.5"
                max="3.0"
              />
              <p className="text-[10px] text-gray-600">ATR multiplier for hard stop (default: 1.5x)</p>
            </div>

            {/* Exit Logic Preview */}
            <div className="p-3 bg-black/30 rounded border border-gray-800 space-y-2">
              <p className="text-[9px] text-gray-500 uppercase font-semibold">Exit Logic</p>
              <div className="text-xs font-mono space-y-1">
                <p className="text-gray-400">
                  <span className="text-purple-400">SOFT:</span> EMA &lt; (Gate - <span className="text-cyan-300">{config[symbol].hysteresis_gap}</span>) for <span className="text-amber-300">{config[symbol].confirmation_window}s</span>
                </p>
                <p className="text-gray-400">
                  <span className="text-red-400">HARD:</span> Price crosses (Max_Price - ATR × <span className="text-pink-300">{config[symbol].trailing_sl_atr_mult}</span>)
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


// ============================================
// EXECUTION COSTS PANEL
// ============================================
const ExecutionCostsPanel = () => {
  const [config, setConfig] = useState({
    estimated_slippage: 50,  // Default ₹50 per trade
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  };

  const fetchConfig = useCallback(async () => {
    try {
      setError(null);
      const res = await fetch(`${API}/config/execution-costs`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setConfig({
          estimated_slippage: data.estimated_slippage ?? 50,
        });
      }
    } catch (err) {
      console.error('Failed to fetch execution costs config:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await fetch(`${API}/config/execution-costs`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          estimated_slippage: parseFloat(config.estimated_slippage),
        }),
      });

      if (!res.ok) throw new Error('Failed to save');
      
      setSuccess('Execution costs config saved!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="panel p-6">
        <div className="flex items-center justify-center h-16">
          <RefreshCw className="w-5 h-5 text-accent animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="panel p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg font-bold text-foreground flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-amber-400" />
            Pricing & Execution Costs
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Configure slippage and fee estimates for P&L analytics calculations.
          </p>
        </div>
        <Button
          onClick={handleSave}
          size="sm"
          disabled={saving}
          className="bg-amber-500 hover:bg-amber-400 text-black"
          data-testid="save-execution-costs"
        >
          {saving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
          <span className="ml-1">Save</span>
        </Button>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="p-3 bg-sell/10 border border-sell/30 rounded flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-sell" />
          <span className="text-sm text-sell">{error}</span>
        </div>
      )}
      {success && (
        <div className="p-3 bg-buy/10 border border-buy/30 rounded flex items-center gap-2">
          <Check className="w-4 h-4 text-buy" />
          <span className="text-sm text-buy">{success}</span>
        </div>
      )}

      {/* Slippage Input */}
      <div className="bg-surface/50 border border-border rounded-lg p-4">
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-sm text-muted-foreground flex items-center gap-2">
              <Calculator className="w-4 h-4" />
              Estimated Slippage/Fees (₹ per trade)
            </label>
            <input
              type="number"
              value={config.estimated_slippage}
              onChange={(e) => setConfig(prev => ({ ...prev, estimated_slippage: e.target.value }))}
              className="w-full bg-black/40 border border-gray-700 px-4 py-3 rounded text-lg font-mono text-foreground"
              data-testid="input-estimated-slippage"
              step="5"
              min="0"
              max="500"
            />
            <p className="text-[10px] text-gray-600">
              Total per-trade cost (brokerage + slippage + STT). Used to calculate Net P&L in analytics.
            </p>
          </div>
          
          <div className="flex flex-col justify-center">
            <div className="p-4 bg-black/30 rounded border border-gray-800 space-y-2">
              <p className="text-[9px] text-gray-500 uppercase font-semibold">Net P&L Formula</p>
              <p className="text-sm font-mono text-gray-400">
                Net_PNL = <span className="text-green-400">Gross_PNL</span> - <span className="text-amber-400">₹{config.estimated_slippage || 50}</span>
              </p>
              <p className="text-[10px] text-gray-600 mt-2">
                Applied to each closed trade in Analytics & History
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


// ============================================
// SESSION 103: SIGNAL CONFIG TAB (Parameterized God Score)
// ============================================
const SignalConfigTab = () => {
  const [config, setConfig] = useState({});
  const [defaults, setDefaults] = useState({});
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editedValues, setEditedValues] = useState({});
  const [error, setError] = useState(null);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/admin/signal-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to fetch config');
      
      const data = await res.json();
      setConfig(data.config || {});
      setDefaults(data.defaults || {});
      setCategories(data.categories || {});
      setEditedValues({});
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleValueChange = (key, value) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      setEditedValues(prev => ({
        ...prev,
        [key]: numValue
      }));
    }
  };

  const handleSave = async () => {
    if (Object.keys(editedValues).length === 0) return;
    
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/admin/signal-config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(editedValues)
      });
      
      if (!res.ok) throw new Error('Failed to save config');
      
      await fetchConfig();
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset all signal parameters to defaults?')) return;
    
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/admin/signal-config/reset`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to reset config');
      
      await fetchConfig();
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const getValue = (key) => {
    if (editedValues[key] !== undefined) return editedValues[key];
    return config[key] ?? defaults[key] ?? 0;
  };

  const isModified = (key) => {
    return editedValues[key] !== undefined && editedValues[key] !== config[key];
  };

  const hasChanges = Object.keys(editedValues).length > 0;

  const categoryLabels = {
    phase1_core: { title: 'Phase 1: Core Signals', desc: 'High-accuracy trade triggers' },
    phase2_high_accuracy: { title: 'Phase 2: High Accuracy', desc: '65-87% backtested accuracy' },
    phase3_day_type: { title: 'Phase 3: Day Type', desc: 'Market structure classification' },
    phase3_profile: { title: 'Phase 3: Profile Shape', desc: 'TPO profile classification' },
    phase3_open_auction: { title: 'Phase 3: Open Auction', desc: 'Opening range classification' },
    phase4_advanced: { title: 'Phase 4: Advanced', desc: 'IB extensions, LPZ, spikes' },
    phase5_multiday: { title: 'Phase 5: Multi-Day', desc: 'Historical context signals' },
    core_engine: { title: 'Core Engine', desc: 'PI Zone, SMLT, Gamma signals' },
    order_flow_modifiers: { title: 'Order Flow Modifiers', desc: 'SESSION 116: GoCharting-style delta divergence, exhaustion, weakness' },
    caps_limits: { title: 'Caps & Limits', desc: 'Maximum score constraints' }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'directional': return 'text-blue-400';
      case 'bullish_only': return 'text-green-400';
      case 'bearish_only': return 'text-red-400';
      case 'penalty': return 'text-orange-400';
      case 'context': return 'text-purple-400';
      case 'neutral': return 'text-gray-400';
      case 'cap': return 'text-yellow-400';
      default: return 'text-foreground';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="signal-config-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Sliders className="w-6 h-6 text-accent" />
            Signal Configuration
          </h2>
          <p className="text-sm text-muted mt-1">
            Configure God Score signal parameters. Bullish signals add points (+), bearish signals subtract (-).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            disabled={saving}
            data-testid="signal-config-reset-btn"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Reset Defaults
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className={hasChanges ? 'bg-green-600 hover:bg-green-700' : ''}
            data-testid="signal-config-save-btn"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-400" />
          <span className="text-red-400">{error}</span>
        </div>
      )}

      {/* Changes Banner */}
      {hasChanges && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-yellow-400" />
          <span className="text-yellow-400">
            {Object.keys(editedValues).length} unsaved change(s)
          </span>
        </div>
      )}

      {/* Config Tables by Category */}
      <div className="grid gap-6">
        {Object.entries(categories).map(([categoryKey, signals]) => {
          const label = categoryLabels[categoryKey] || { title: categoryKey, desc: '' };
          
          return (
            <div key={categoryKey} className="bg-surface border border-border rounded-lg overflow-hidden">
              <div className="px-4 py-3 bg-surface-light border-b border-border">
                <h3 className="font-semibold text-foreground">{label.title}</h3>
                <p className="text-xs text-muted">{label.desc}</p>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-xs text-muted border-b border-border">
                      <th className="text-left px-4 py-2 w-1/3">Signal</th>
                      <th className="text-center px-4 py-2 w-1/6">Type</th>
                      <th className="text-center px-4 py-2 w-1/6">Accuracy</th>
                      <th className="text-center px-4 py-2 w-1/6">Bullish</th>
                      <th className="text-center px-4 py-2 w-1/6">Bearish</th>
                    </tr>
                  </thead>
                  <tbody>
                    {signals.map((signal) => {
                      const value = getValue(signal.key);
                      const modified = isModified(signal.key);
                      const isBullishOnly = signal.type === 'bullish_only';
                      const isBearishOnly = signal.type === 'bearish_only';
                      const isPenalty = signal.type === 'penalty';
                      const isCap = signal.type === 'cap';
                      
                      return (
                        <tr 
                          key={signal.key} 
                          className={`border-b border-border/50 hover:bg-surface-light/50 ${modified ? 'bg-yellow-500/5' : ''}`}
                        >
                          <td className="px-4 py-2">
                            <span className="text-sm text-foreground">{signal.label}</span>
                          </td>
                          <td className="px-4 py-2 text-center">
                            <span className={`text-xs ${getTypeColor(signal.type)}`}>
                              {signal.type?.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-center">
                            <span className="text-xs text-muted">{signal.accuracy || '-'}</span>
                          </td>
                          <td className="px-4 py-2 text-center">
                            {isBearishOnly ? (
                              <span className="text-muted">-</span>
                            ) : isPenalty ? (
                              // SESSION 103: Penalty signals show warning icon and negative impact
                              <div className="flex items-center justify-center gap-1">
                                <span className="text-orange-400 text-sm">⚠️</span>
                                <span className="text-orange-400 text-sm font-mono">-{value}</span>
                              </div>
                            ) : (
                              <div className="flex items-center justify-center gap-1">
                                <span className="text-green-400 text-sm">+</span>
                                <input
                                  type="number"
                                  value={value}
                                  onChange={(e) => handleValueChange(signal.key, e.target.value)}
                                  className={`w-16 bg-background border rounded px-2 py-1 text-center text-sm
                                    ${modified ? 'border-yellow-500 text-yellow-400' : 'border-border text-green-400'}`}
                                  data-testid={`signal-${signal.key}-bullish`}
                                />
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-2 text-center">
                            {isBullishOnly || isCap ? (
                              <span className="text-muted">-</span>
                            ) : isPenalty ? (
                              // SESSION 103: Penalty signals - editable value, shows as negative
                              <div className="flex items-center justify-center gap-1">
                                <span className="text-orange-400 text-sm">-</span>
                                <input
                                  type="number"
                                  value={value}
                                  onChange={(e) => handleValueChange(signal.key, e.target.value)}
                                  className={`w-16 bg-background border border-orange-500/30 rounded px-2 py-1 text-center text-sm
                                    ${modified ? 'border-yellow-500 text-yellow-400' : 'text-orange-400'}`}
                                  data-testid={`signal-${signal.key}-penalty`}
                                />
                                <span className="text-orange-400 text-xs">(penalty)</span>
                              </div>
                            ) : (
                              <div className="flex items-center justify-center gap-1">
                                <span className="text-red-400 text-sm">-</span>
                                <input
                                  type="number"
                                  value={value}
                                  onChange={(e) => handleValueChange(signal.key, e.target.value)}
                                  className={`w-16 bg-background border rounded px-2 py-1 text-center text-sm
                                    ${modified ? 'border-yellow-500 text-yellow-400' : 'border-border text-red-400'}`}
                                  data-testid={`signal-${signal.key}-bearish`}
                                  disabled={isBullishOnly || isCap}
                                />
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="bg-surface border border-border rounded-lg p-4">
        <h4 className="text-sm font-semibold text-foreground mb-2">Signal Types Legend</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded bg-blue-400"></span>
            <span className="text-muted">Directional: +X bullish, -X bearish</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded bg-green-400"></span>
            <span className="text-muted">Bullish Only: Always adds points</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded bg-red-400"></span>
            <span className="text-muted">Bearish Only: Always subtracts points</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded bg-orange-400"></span>
            <span className="text-muted">Penalty: Counter-trend penalty</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded bg-purple-400"></span>
            <span className="text-muted">Context: Direction-aware boost</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded bg-yellow-400"></span>
            <span className="text-muted">Cap: Maximum allowed value</span>
          </div>
        </div>
      </div>
    </div>
  );
};


// ============================================
// SPECIAL TRADING DAYS TAB
// ============================================
const SpecialDaysTab = () => {
  const [specialDays, setSpecialDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDate, setNewDate] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newStartTime, setNewStartTime] = useState('09:15');
  const [newEndTime, setNewEndTime] = useState('15:30');

  const fetchSpecialDays = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/admin/special-days`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();
      // Handle error responses
      if (data.detail || data.error) {
        console.error('API Error:', data.detail || data.error);
        setSpecialDays([]);
      } else if (Array.isArray(data)) {
        setSpecialDays(data);
      } else {
        setSpecialDays([]);
      }
    } catch (error) {
      console.error('Failed to fetch special days:', error);
      setSpecialDays([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSpecialDays();
  }, [fetchSpecialDays]);

  const handleAdd = async () => {
    if (!newDate || !newDescription) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/admin/special-days`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          trading_date: newDate, 
          description: newDescription,
          start_time: newStartTime,
          end_time: newEndTime
        }),
      });
      const data = await res.json();
      if (data.error || data.detail) {
        alert(data.error || data.detail);
        return;
      }
      setNewDate('');
      setNewDescription('');
      setNewStartTime('09:15');
      setNewEndTime('15:30');
      fetchSpecialDays();
    } catch (error) {
      console.error('Failed to add special day:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/admin/special-days/${id}`, { 
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      fetchSpecialDays();
    } catch (error) {
      console.error('Failed to delete special day:', error);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  // Common presets
  const presets = [
    { label: 'Mahurat Trading', startTime: '18:15', endTime: '19:15' },
    { label: 'Budget Day', startTime: '09:00', endTime: '15:30' },
    { label: 'Standard Hours', startTime: '09:15', endTime: '15:30' },
  ];

  const applyPreset = (preset) => {
    setNewStartTime(preset.startTime);
    setNewEndTime(preset.endTime);
    if (!newDescription) {
      setNewDescription(preset.label);
    }
  };

  return (
    <div className="space-y-6" data-testid="special-days-tab">
      <div>
        <h2 className="font-heading text-2xl font-bold text-foreground flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-accent" />
          Special Trading Days
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Define special trading sessions like Budget Day or Mahurat Trading with custom hours. 
          These override weekend closures.
        </p>
      </div>

      <div className="panel p-6">
        {/* Presets */}
        <div className="mb-4">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider mr-3">Quick Presets:</span>
          <div className="inline-flex gap-2 mt-2">
            {presets.map((preset) => (
              <button
                key={preset.label}
                onClick={() => applyPreset(preset)}
                className="px-3 py-1 text-xs font-mono bg-surface-highlight border border-border hover:border-accent transition-colors"
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>

        {/* Add Special Day Form */}
        <div className="grid grid-cols-5 gap-4 items-end mb-6">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">Trading Date</label>
            <input
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              className="w-full bg-surface border border-border px-4 py-2.5 text-sm text-foreground focus:outline-none focus:border-accent"
              data-testid="special-day-date-input"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">Description *</label>
            <input
              type="text"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="e.g., Budget Day"
              className="w-full bg-surface border border-border px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-accent"
              data-testid="special-day-description-input"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">Start Time</label>
            <input
              type="time"
              value={newStartTime}
              onChange={(e) => setNewStartTime(e.target.value)}
              className="w-full bg-surface border border-border px-4 py-2.5 text-sm text-foreground font-mono focus:outline-none focus:border-accent"
              data-testid="special-day-start-input"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">End Time</label>
            <input
              type="time"
              value={newEndTime}
              onChange={(e) => setNewEndTime(e.target.value)}
              className="w-full bg-surface border border-border px-4 py-2.5 text-sm text-foreground font-mono focus:outline-none focus:border-accent"
              data-testid="special-day-end-input"
            />
          </div>
          <Button 
            onClick={handleAdd} 
            disabled={!newDate || !newDescription} 
            className="flex items-center gap-2" 
            data-testid="add-special-day-btn"
          >
            <Plus className="w-4 h-4" />
            Add Day
          </Button>
        </div>

        {/* Special Days List */}
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-3">
            Upcoming Special Trading Days ({specialDays.length})
          </h4>
          
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : specialDays.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No special trading days configured.
            </div>
          ) : (
            <div className="space-y-2">
              {specialDays.map((day) => (
                <div
                  key={day.id}
                  className="flex items-center justify-between bg-surface-highlight px-4 py-3 border border-accent/30"
                  data-testid={`special-day-item-${day.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-accent" />
                      <span className="font-mono font-semibold text-foreground">
                        {formatDate(day.trading_date)}
                      </span>
                    </div>
                    <Badge className="bg-accent/20 text-accent border-accent/30">
                      {day.description}
                    </Badge>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      <span className="font-mono">{day.start_time} - {day.end_time}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(day.id)}
                    className="text-sell hover:text-sell/80"
                    data-testid={`delete-special-day-${day.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Info Card */}
      <div className="panel p-4 border-accent/30 bg-accent/5">
        <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-accent" />
          How Special Days Work
        </h4>
        <ul className="text-sm text-muted-foreground space-y-1">
          <li>• Special days <strong>override weekends</strong> — A Sunday Budget Day will be marked as OPEN</li>
          <li>• Custom trading hours apply (e.g., Mahurat Trading: 6:15 PM - 7:15 PM)</li>
          <li>• Holidays still take priority — A holiday on a special day will be CLOSED</li>
        </ul>
      </div>
    </div>
  );
};

// ============================================
// GREEKS VERIFICATION TAB - Verify ATM Options Data
// ============================================
const GreeksVerificationTab = () => {
  const [selectedSymbol, setSelectedSymbol] = useState('BANKNIFTY');
  const [atmData, setAtmData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  };

  const fetchATMData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/gex/atm-options?symbol=${selectedSymbol}`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to fetch ATM options data');
      const data = await res.json();
      setAtmData(data);
      setError(null);
    } catch (err) {
      console.error('ATM data fetch error:', err);
      setError(err.message);
    }
    setLoading(false);
  }, [selectedSymbol]);

  useEffect(() => {
    fetchATMData();
  }, [fetchATMData]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchATMData, 2000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchATMData]);

  const subscribeOptions = async () => {
    try {
      const spotPrice = atmData?.spot_price || (selectedSymbol === 'NIFTY' ? 25450 : 60000);
      await fetch(`${API}/subscribe-options?symbol=${selectedSymbol}&spot_price=${spotPrice}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      setTimeout(fetchATMData, 1000);
    } catch (err) {
      setError(err.message);
    }
  };

  const getSourceBadgeColor = (source) => {
    switch (source) {
      case 'LIVE_GEX': return 'bg-green-500/20 border-green-500/50 text-green-400';
      case 'BLACK_SCHOLES': return 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400';
      default: return 'bg-muted/20 border-muted text-muted-foreground';
    }
  };

  return (
    <div className="space-y-6" data-testid="greeks-verification-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground">Greeks Verification</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Verify real-time Greeks data (Delta, Gamma, Vega, IV) from Accelpix API
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-border bg-muted"
            />
            <span className="text-muted-foreground">Auto-refresh</span>
          </label>
          <Button 
            onClick={fetchATMData} 
            disabled={loading}
            variant="outline"
            size="sm"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Symbol Selector */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Symbol:</span>
          <div className="flex border border-border rounded-lg overflow-hidden">
            {['NIFTY', 'BANKNIFTY'].map((sym) => (
              <button
                key={sym}
                onClick={() => setSelectedSymbol(sym)}
                className={`px-4 py-2 text-sm font-mono transition-colors ${
                  selectedSymbol === sym
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-surface hover:bg-muted/50'
                }`}
              >
                {sym}
              </button>
            ))}
          </div>
        </div>
        <Button onClick={subscribeOptions} variant="outline" size="sm">
          Subscribe Options
        </Button>
      </div>

      {/* Status Card */}
      {atmData && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-surface border border-border rounded-lg p-4">
            <div className="text-xs text-muted-foreground uppercase">Status</div>
            <div className={`text-lg font-bold ${atmData.status === 'OK' ? 'text-buy' : 'text-warning'}`}>
              {atmData.status}
            </div>
          </div>
          <div className="bg-surface border border-border rounded-lg p-4">
            <div className="text-xs text-muted-foreground uppercase">Spot Price</div>
            <div className="text-lg font-bold font-mono text-foreground">
              {atmData.spot_price?.toLocaleString() || '-'}
            </div>
          </div>
          <div className="bg-surface border border-border rounded-lg p-4">
            <div className="text-xs text-muted-foreground uppercase">ATM Strike</div>
            <div className="text-lg font-bold font-mono text-accent">
              {atmData.atm_strike?.toLocaleString() || '-'}
            </div>
          </div>
          <div className="bg-surface border border-border rounded-lg p-4">
            <div className="text-xs text-muted-foreground uppercase">Strikes in Chain</div>
            <div className="text-lg font-bold font-mono text-foreground">
              {atmData.total_strikes_in_chain || 0}
            </div>
          </div>
        </div>
      )}

      {/* Greeks Source Indicator */}
      {atmData && (
        <div className="flex items-center gap-4 p-3 bg-surface border border-border rounded-lg">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Live Greeks:</span>
            <Badge className={getSourceBadgeColor('LIVE_GEX')}>
              {atmData.accelpix_greeks_count || 0}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Black-Scholes Fallback:</span>
            <Badge className={getSourceBadgeColor('BLACK_SCHOLES')}>
              {atmData.black_scholes_count || 0}
            </Badge>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-sell/10 border border-sell/30 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-sell" />
          <span className="text-sell">{error}</span>
        </div>
      )}

      {/* Options Table */}
      {atmData?.options && atmData.options.length > 0 ? (
        <div className="bg-surface border border-border rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border bg-muted/20">
            <h3 className="font-semibold text-foreground">ATM Options Data (ATM ± 2 Strikes)</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Real-time Greeks verification from API feed
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/30 text-muted-foreground text-xs uppercase">
                  <th className="px-4 py-3 text-center" rowSpan="2">Strike</th>
                  <th className="px-4 py-3 text-center border-l border-border" colSpan="6">CALL (CE)</th>
                  <th className="px-4 py-3 text-center border-l border-border" colSpan="6">PUT (PE)</th>
                </tr>
                <tr className="bg-muted/20 text-muted-foreground text-[10px] uppercase">
                  <th className="px-2 py-2 text-center border-l border-border">OI</th>
                  <th className="px-2 py-2 text-center">Delta</th>
                  <th className="px-2 py-2 text-center">Gamma</th>
                  <th className="px-2 py-2 text-center">Vega</th>
                  <th className="px-2 py-2 text-center">IV%</th>
                  <th className="px-2 py-2 text-center">Source</th>
                  <th className="px-2 py-2 text-center border-l border-border">OI</th>
                  <th className="px-2 py-2 text-center">Delta</th>
                  <th className="px-2 py-2 text-center">Gamma</th>
                  <th className="px-2 py-2 text-center">Vega</th>
                  <th className="px-2 py-2 text-center">IV%</th>
                  <th className="px-2 py-2 text-center">Source</th>
                </tr>
              </thead>
              <tbody>
                {atmData.options.map((opt) => (
                  <tr 
                    key={opt.strike} 
                    className={`border-t border-border ${opt.is_atm ? 'bg-accent/10' : ''}`}
                  >
                    <td className={`px-4 py-3 text-center font-mono font-bold ${opt.is_atm ? 'text-accent' : 'text-foreground'}`}>
                      {opt.strike.toLocaleString()}
                      {opt.is_atm && <span className="ml-2 text-xs text-accent">ATM</span>}
                    </td>
                    {/* Call Data */}
                    <td className="px-2 py-3 text-center font-mono text-muted-foreground border-l border-border">
                      {opt.call.oi?.toLocaleString() || '-'}
                    </td>
                    <td className={`px-2 py-3 text-center font-mono ${opt.call.delta > 0 ? 'text-buy' : 'text-sell'}`}>
                      {opt.call.delta?.toFixed(4) || '-'}
                    </td>
                    <td className="px-2 py-3 text-center font-mono text-purple-400">
                      {opt.call.gamma?.toFixed(6) || '-'}
                    </td>
                    <td className="px-2 py-3 text-center font-mono text-cyan-400">
                      {opt.call.vega?.toFixed(2) || '-'}
                    </td>
                    <td className="px-2 py-3 text-center font-mono text-foreground">
                      {opt.call.iv?.toFixed(2) || '-'}%
                    </td>
                    <td className="px-2 py-3 text-center">
                      <Badge className={`text-[9px] ${getSourceBadgeColor(opt.call.greeks_source)}`}>
                        {opt.call.greeks_source === 'LIVE_GEX' ? 'API' : 'B-S'}
                      </Badge>
                    </td>
                    {/* Put Data */}
                    <td className="px-2 py-3 text-center font-mono text-muted-foreground border-l border-border">
                      {opt.put.oi?.toLocaleString() || '-'}
                    </td>
                    <td className={`px-2 py-3 text-center font-mono ${opt.put.delta < 0 ? 'text-sell' : 'text-buy'}`}>
                      {opt.put.delta?.toFixed(4) || '-'}
                    </td>
                    <td className="px-2 py-3 text-center font-mono text-purple-400">
                      {opt.put.gamma?.toFixed(6) || '-'}
                    </td>
                    <td className="px-2 py-3 text-center font-mono text-cyan-400">
                      {opt.put.vega?.toFixed(2) || '-'}
                    </td>
                    <td className="px-2 py-3 text-center font-mono text-foreground">
                      {opt.put.iv?.toFixed(2) || '-'}%
                    </td>
                    <td className="px-2 py-3 text-center">
                      <Badge className={`text-[9px] ${getSourceBadgeColor(opt.put.greeks_source)}`}>
                        {opt.put.greeks_source === 'LIVE_GEX' ? 'API' : 'B-S'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Note */}
          <div className="p-3 bg-muted/10 border-t border-border text-xs text-muted-foreground">
            <strong>Note:</strong> {atmData.note || 'OI data may be 0 if not provided by API.'}
          </div>
        </div>
      ) : (
        <div className="text-center py-12 text-muted-foreground bg-surface border border-border rounded-lg">
          <Eye className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="font-medium">No Options Data Available</p>
          <p className="text-sm mt-1">Click "Subscribe Options" to start receiving Greeks data</p>
        </div>
      )}
    </div>
  );
};

// ============================================
// SYSTEM STATUS TAB
// ============================================
// DATA HEALTH TAB - Monitor tick processing & UI throttling
// ============================================
const DataHealthTab = () => {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  };

  const fetchHealth = useCallback(async () => {
    try {
      const res = await fetch(`${API}/data-health/detailed`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to fetch health data');
      const data = await res.json();
      setHealthData(data);
      setError(null);
    } catch (err) {
      console.error('Health fetch error:', err);
      setError(err.message);
    }
    setLoading(false);
  }, []);

  const resetHealth = async () => {
    try {
      await fetch(`${API}/data-health/reset`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      fetchHealth();
    } catch (err) {
      console.error('Reset failed:', err);
    }
  };

  useEffect(() => {
    fetchHealth();
    // Refresh every 2 seconds for live monitoring
    const interval = setInterval(fetchHealth, 2000);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  const getHealthStatusColor = (status) => {
    switch (status) {
      case 'HEALTHY': return 'bg-buy/20 border-buy text-buy';
      case 'ELEVATED_LATENCY': return 'bg-warning/20 border-warning text-warning';
      case 'HIGH_LATENCY': return 'bg-orange-500/20 border-orange-500 text-orange-400';
      case 'SEQUENCE_GAPS_DETECTED': return 'bg-orange-500/20 border-orange-500 text-orange-400';
      case 'CRITICAL_DATA_LOSS': return 'bg-sell/20 border-sell text-sell';
      default: return 'bg-muted/20 border-muted text-muted-foreground';
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  if (loading && !healthData) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  const health = healthData?.health || {};
  const emitter = healthData?.emitter || {};

  return (
    <div className="space-y-6" data-testid="data-health-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-foreground flex items-center gap-2">
            <Gauge className="w-6 h-6 text-accent" />
            Data Health Monitor
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Monitor tick processing accuracy, latency, and UI throttling performance.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchHealth} size="sm">
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
          <Button variant="destructive" onClick={resetHealth} size="sm">
            Reset Counters
          </Button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-sell/10 border border-sell/30 rounded text-sell">
          {error}
        </div>
      )}

      {/* Health Status Banner */}
      <div className={`p-6 border rounded ${getHealthStatusColor(health.health_status)}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-4 h-4 rounded-full ${health.is_healthy ? 'bg-buy animate-pulse' : 'bg-sell'}`} />
            <div>
              <div className="text-2xl font-bold">{health.health_status || 'UNKNOWN'}</div>
              <div className="text-sm opacity-75">
                {health.is_healthy ? 'All systems operational' : 'Issues detected - check metrics below'}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm opacity-75">Uptime</div>
            <div className="font-mono text-lg">{Math.floor((health.uptime_seconds || 0) / 60)}m {Math.floor((health.uptime_seconds || 0) % 60)}s</div>
          </div>
        </div>
      </div>

      {/* Main Metrics Grid */}
      <div className="grid grid-cols-3 gap-4">
        {/* Ticks Received */}
        <div className="panel p-4 border-2 border-cyan-500/30 bg-cyan-500/5">
          <div className="text-xs uppercase text-muted-foreground mb-1">Ticks Received</div>
          <div className="text-3xl font-bold font-mono text-cyan-400">{formatNumber(health.ticks_received)}</div>
          <div className="text-xs text-muted-foreground mt-1">
            {health.ticks_per_second?.toFixed(1) || '0'} ticks/sec
          </div>
        </div>

        {/* Ticks Processed */}
        <div className="panel p-4 border-2 border-buy/30 bg-buy/5">
          <div className="text-xs uppercase text-muted-foreground mb-1">Ticks Processed</div>
          <div className="text-3xl font-bold font-mono text-buy">{formatNumber(health.ticks_processed)}</div>
          <div className={`text-xs mt-1 ${health.ticks_received === health.ticks_processed ? 'text-buy' : 'text-sell font-bold'}`}>
            {health.ticks_received === health.ticks_processed ? '✓ 100% processed' : `⚠ ${health.data_loss_pct?.toFixed(2)}% loss`}
          </div>
        </div>

        {/* UI Updates */}
        <div className="panel p-4 border-2 border-purple-500/30 bg-purple-500/5">
          <div className="text-xs uppercase text-muted-foreground mb-1">UI Updates Sent</div>
          <div className="text-3xl font-bold font-mono text-purple-400">{formatNumber(health.ui_updates_sent)}</div>
          <div className="text-xs text-muted-foreground mt-1">
            {health.throttle_ratio?.toFixed(1) || '0'}x throttle ratio
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-2 gap-4">
        {/* Processing Latency */}
        <div className="panel p-4">
          <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-accent" />
            Processing Latency
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Average</span>
              <span className={`font-mono ${health.avg_processing_time_ms < 2 ? 'text-buy' : health.avg_processing_time_ms < 5 ? 'text-warning' : 'text-sell'}`}>
                {health.avg_processing_time_ms?.toFixed(3) || '0'} ms
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">P99</span>
              <span className="font-mono text-foreground">{health.p99_processing_time_ms?.toFixed(3) || '0'} ms</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Peak</span>
              <span className="font-mono text-foreground">{health.peak_processing_time_ms?.toFixed(3) || '0'} ms</span>
            </div>
            <div className="pt-2 border-t border-border">
              <div className="text-xs text-muted-foreground mb-1">Target: &lt;2ms</div>
              <div className="h-2 bg-surface rounded overflow-hidden">
                <div 
                  className={`h-full transition-all ${health.avg_processing_time_ms < 2 ? 'bg-buy' : health.avg_processing_time_ms < 5 ? 'bg-warning' : 'bg-sell'}`}
                  style={{ width: `${Math.min((health.avg_processing_time_ms || 0) / 10 * 100, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Latency Distribution */}
        <div className="panel p-4">
          <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-accent" />
            Latency Distribution
          </h3>
          <div className="space-y-2">
            {Object.entries(health.latency_distribution || {}).map(([bucket, count]) => {
              const total = Object.values(health.latency_distribution || {}).reduce((a, b) => a + b, 0) || 1;
              const pct = (count / total) * 100;
              const isGood = bucket === '<1ms' || bucket === '1-2ms';
              return (
                <div key={bucket} className="flex items-center gap-2">
                  <span className="w-16 text-xs text-muted-foreground font-mono">{bucket}</span>
                  <div className="flex-1 h-4 bg-surface rounded overflow-hidden">
                    <div 
                      className={`h-full ${isGood ? 'bg-buy' : bucket === '2-5ms' ? 'bg-warning' : 'bg-sell'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="w-16 text-xs text-right font-mono text-muted-foreground">{formatNumber(count)}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Throttled Emitter Stats */}
      <div className="panel p-4">
        <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
          <Activity className="w-4 h-4 text-accent" />
          Throttled Emitter Status
        </h3>
        <div className="grid grid-cols-4 gap-4">
          <div>
            <div className="text-xs uppercase text-muted-foreground">Emit Rate</div>
            <div className="font-mono text-lg text-foreground">{emitter.emit_rate_hz || 10} Hz</div>
          </div>
          <div>
            <div className="text-xs uppercase text-muted-foreground">Emissions</div>
            <div className="font-mono text-lg text-foreground">{formatNumber(emitter.emissions_count || 0)}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-muted-foreground">Buffer Overwrites</div>
            <div className="font-mono text-lg text-warning">{formatNumber(emitter.buffer_overwrites || 0)}</div>
          </div>
          <div>
            <div className="text-xs uppercase text-muted-foreground">Status</div>
            <div className={`font-mono text-lg ${emitter.running ? 'text-buy' : 'text-sell'}`}>
              {emitter.running ? 'RUNNING' : 'STOPPED'}
            </div>
          </div>
        </div>
      </div>

      {/* Data Integrity Check */}
      {health.ticks_received !== health.ticks_processed && (
        <div className="p-4 bg-sell/10 border border-sell rounded flex items-center gap-4">
          <AlertTriangle className="w-8 h-8 text-sell" />
          <div>
            <div className="font-bold text-sell text-lg">CRITICAL: DATA LOSS DETECTED</div>
            <div className="text-sm text-sell/80">
              {health.ticks_received - health.ticks_processed} ticks were not processed.
              Order flow calculations may be incorrect!
            </div>
          </div>
        </div>
      )}

      {health.sequence_gaps > 0 && (
        <div className="p-4 bg-orange-500/10 border border-orange-500 rounded flex items-center gap-4">
          <AlertTriangle className="w-8 h-8 text-orange-500" />
          <div>
            <div className="font-bold text-orange-400 text-lg">Sequence Gaps Detected</div>
            <div className="text-sm text-orange-300">
              {health.sequence_gaps} sequence gaps detected. Some packets may be arriving out of order or missing.
            </div>
          </div>
        </div>
      )}

      {/* SESSION 99e: Connection Health Report */}
      <ConnectionHealthSection />
    </div>
  );
};

// ============================================
// SESSION 99e: CONNECTION HEALTH SECTION
// ============================================
const ConnectionHealthSection = () => {
  const [connectionData, setConnectionData] = useState(null);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  };

  const fetchConnectionHealth = useCallback(async () => {
    try {
      const res = await fetch(`${API}/truedata/connection-history?limit=50`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setConnectionData(data);
      }
    } catch (err) {
      console.error('Connection health fetch error:', err);
    }
    setLoading(false);
  }, []);

  const clearConnectionHistory = async () => {
    try {
      await fetch(`${API}/truedata/connection-history`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      fetchConnectionHealth();
    } catch (err) {
      console.error('Clear connection history failed:', err);
    }
  };

  useEffect(() => {
    fetchConnectionHealth();
    const interval = setInterval(fetchConnectionHealth, 5000); // Refresh every 5s
    return () => clearInterval(interval);
  }, [fetchConnectionHealth]);

  // Format timestamp to IST
  const formatTime = (timestamp) => {
    if (!timestamp) return '--:--';
    // 147j+1 — handle naive ISO strings as UTC (see other formatTime above).
    let ts = timestamp;
    if (typeof ts === 'string' && !/(Z|[+-]\d{2}:?\d{2})$/.test(ts)) {
      ts = ts + 'Z';
    }
    const date = new Date(ts);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: 'Asia/Kolkata'
    });
  };

  // Get event icon and color
  const getEventStyle = (eventType) => {
    switch (eventType) {
      case 'connect':
        return { icon: '🟢', color: 'text-buy', bg: 'bg-buy/10' };
      case 'disconnect':
        return { icon: '🔴', color: 'text-sell', bg: 'bg-sell/10' };
      case 'reconnect':
        return { icon: '🔄', color: 'text-warning', bg: 'bg-warning/10' };
      case 'zombie':
        return { icon: '💀', color: 'text-purple-400', bg: 'bg-purple-500/10' };
      case 'error':
        return { icon: '⚠️', color: 'text-orange-400', bg: 'bg-orange-500/10' };
      default:
        return { icon: '📡', color: 'text-muted-foreground', bg: 'bg-muted/10' };
    }
  };

  const stats = connectionData?.today_stats || {};
  const events = connectionData?.events || [];
  const hasDisconnects = stats.disconnects > 0 || stats.zombies > 0 || stats.errors > 0;

  return (
    <div className="panel p-4" data-testid="connection-health-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-foreground flex items-center gap-2">
          <Wifi className="w-4 h-4 text-accent" />
          Connection Health (Today)
        </h3>
        <Button variant="outline" size="sm" onClick={clearConnectionHistory}>
          <Trash2 className="w-3 h-3 mr-1" />
          Clear Log
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className={`p-3 rounded ${stats.connects > 0 ? 'bg-buy/10 border border-buy/30' : 'bg-surface'}`}>
          <div className="text-xs uppercase text-muted-foreground">Connects</div>
          <div className="text-xl font-mono text-buy">{stats.connects || 0}</div>
        </div>
        <div className={`p-3 rounded ${stats.disconnects > 0 ? 'bg-sell/10 border border-sell/30' : 'bg-surface'}`}>
          <div className="text-xs uppercase text-muted-foreground">Disconnects</div>
          <div className={`text-xl font-mono ${stats.disconnects > 0 ? 'text-sell' : 'text-foreground'}`}>{stats.disconnects || 0}</div>
        </div>
        <div className={`p-3 rounded ${stats.zombies > 0 ? 'bg-purple-500/10 border border-purple-500/30' : 'bg-surface'}`}>
          <div className="text-xs uppercase text-muted-foreground">Zombies</div>
          <div className={`text-xl font-mono ${stats.zombies > 0 ? 'text-purple-400' : 'text-foreground'}`}>{stats.zombies || 0}</div>
        </div>
        <div className={`p-3 rounded ${stats.errors > 0 ? 'bg-orange-500/10 border border-orange-500/30' : 'bg-surface'}`}>
          <div className="text-xs uppercase text-muted-foreground">Errors</div>
          <div className={`text-xl font-mono ${stats.errors > 0 ? 'text-orange-400' : 'text-foreground'}`}>{stats.errors || 0}</div>
        </div>
      </div>

      {/* Status Banner */}
      {!hasDisconnects && (
        <div className="p-3 bg-buy/10 border border-buy/30 rounded mb-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-buy" />
          <span className="text-buy font-medium">No disconnections today - Connection stable!</span>
        </div>
      )}

      {hasDisconnects && (
        <div className="p-3 bg-sell/10 border border-sell/30 rounded mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-sell" />
          <span className="text-sell font-medium">
            {stats.disconnects || 0} disconnection(s) detected today - Review event log below
          </span>
        </div>
      )}

      {/* Event Log Table */}
      <div className="border border-border rounded overflow-hidden">
        <div className="bg-surface px-3 py-2 border-b border-border">
          <span className="text-sm font-medium text-muted-foreground">Event Log ({events.length} events)</span>
        </div>
        
        {loading ? (
          <div className="p-4 text-center text-muted-foreground">
            <RefreshCw className="w-5 h-5 animate-spin inline mr-2" />
            Loading...
          </div>
        ) : events.length === 0 ? (
          <div className="p-4 text-center text-muted-foreground">
            ✅ No connection events recorded today
          </div>
        ) : (
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface/50 sticky top-0">
                <tr className="text-left text-xs uppercase text-muted-foreground">
                  <th className="px-3 py-2 w-20">Time</th>
                  <th className="px-3 py-2 w-28">Event</th>
                  <th className="px-3 py-2">Reason</th>
                  <th className="px-3 py-2 w-24 text-right">Ticks</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {events.map((event, idx) => {
                  const style = getEventStyle(event.event);
                  return (
                    <tr key={idx} className={`${style.bg} hover:bg-surface/50`}>
                      <td className="px-3 py-2 font-mono text-xs">{formatTime(event.timestamp)}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 ${style.color} font-medium`}>
                          <span>{style.icon}</span>
                          <span className="uppercase text-xs">{event.event}</span>
                        </span>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground text-xs truncate max-w-xs" title={event.reason}>
                        {event.reason || '-'}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs text-muted-foreground">
                        {event.tick_count?.toLocaleString() || '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// CONFIG HISTORY TAB (Audit Trail)
// ============================================
const ConfigHistoryTab = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get auth token from localStorage
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  };

  const fetchHistory = useCallback(async () => {
    try {
      setError(null);
      const res = await fetch(`${API}/admin/config/history?limit=50`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      // API returns {history: [...]} - extract the array
      setHistory(data.history || []);
    } catch (error) {
      console.error('Failed to fetch config history:', error);
      setError(error.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      dateStyle: 'medium',
      timeStyle: 'medium',
    });
  };

  const formatChangeValue = (value) => {
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const getChangeIcon = (changeType) => {
    if (changeType?.includes('weight') || changeType?.includes('score')) {
      return <Gauge className="w-4 h-4 text-purple-400" />;
    }
    if (changeType?.includes('threshold')) {
      return <TrendingUp className="w-4 h-4 text-yellow-400" />;
    }
    return <Settings className="w-4 h-4 text-accent" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-accent animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="config-history-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-foreground flex items-center gap-2">
            <Clock className="w-6 h-6 text-accent" />
            Config History
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Audit trail of all configuration changes. Track who changed what and when.
          </p>
        </div>
        <Button
          onClick={fetchHistory}
          variant="outline"
          className="flex items-center gap-2"
          data-testid="refresh-history-btn"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="panel p-4 bg-sell/10 border-sell/30 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-sell" />
          <div>
            <p className="text-sm font-medium text-sell">Error</p>
            <p className="text-xs text-muted-foreground">{error}</p>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setError(null)}
            className="ml-auto"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* History Timeline */}
      <div className="panel">
        {history.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">No configuration changes yet</p>
            <p className="text-sm">Changes to strategy parameters will appear here</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {history.map((entry, index) => (
              <div key={entry.id || index} className="p-4 hover:bg-surface-highlight/50 transition-colors" data-testid={`history-entry-${index}`}>
                {/* Header Row */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center">
                      <Settings className="w-4 h-4 text-accent" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        Configuration Update
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatTimestamp(entry.timestamp)}
                      </p>
                    </div>
                  </div>
                  <Badge variant="outline" className="bg-surface text-muted-foreground border-border">
                    {entry.updated_by || 'system'}
                  </Badge>
                </div>

                {/* Changes Grid */}
                {entry.changes && Object.keys(entry.changes).length > 0 && (
                  <div className="ml-11 space-y-2">
                    {Object.entries(entry.changes).map(([key, change]) => (
                      <div 
                        key={key} 
                        className="flex items-center gap-3 text-sm bg-surface p-2 rounded border border-border"
                      >
                        {getChangeIcon(key)}
                        <span className="font-mono text-muted-foreground min-w-[180px]">
                          {key.replace(/_/g, ' ')}
                        </span>
                        <span className="text-sell line-through">
                          {formatChangeValue(change.old)}
                        </span>
                        <span className="text-muted-foreground">→</span>
                        <span className="text-buy font-semibold">
                          {formatChangeValue(change.new)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Fallback for old format */}
                {!entry.changes && entry.config && (
                  <div className="ml-11 p-3 bg-surface rounded border border-border">
                    <pre className="text-xs font-mono text-muted-foreground overflow-x-auto">
                      {JSON.stringify(entry.config, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Stats Footer */}
      {history.length > 0 && (
        <div className="text-center text-xs text-muted-foreground">
          Showing {history.length} most recent changes
        </div>
      )}
    </div>
  );
};

// ============================================
// SSOT SYNC STATUS WIDGET (MarketDataCore → HFT/GEX Sync)
// ============================================
const SSOTSyncStatusWidget = () => {
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSyncStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [niftyRes, bnRes] = await Promise.all([
        fetch(`${API}/market-core/sync-status?symbol=NIFTY`),
        fetch(`${API}/market-core/sync-status?symbol=BANKNIFTY`)
      ]);
      
      const nifty = await niftyRes.json();
      const banknifty = await bnRes.json();
      
      setSyncStatus({ nifty, banknifty });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSyncStatus();
    const interval = setInterval(fetchSyncStatus, 5000); // Refresh every 5s
    return () => clearInterval(interval);
  }, [fetchSyncStatus]);

  const getSyncIndicator = (data) => {
    if (!data || data.error) return { icon: '⚪', text: 'N/A', color: 'text-gray-400' };
    
    const isSynced = data.sync_status === 'SYNCED';
    const cvdDrift = data.drift?.cvd_drift || 0;
    
    if (isSynced && cvdDrift === 0) {
      return { icon: '🟢', text: '0', color: 'text-green-400' };
    } else if (cvdDrift < 100) {
      return { icon: '🟡', text: `${cvdDrift}`, color: 'text-yellow-400' };
    } else {
      return { icon: '🔴', text: 'Drift', color: 'text-red-400' };
    }
  };

  const getPhaseLabel = (phase) => {
    switch (phase) {
      case 'PHASE_3_ACTIVE': return 'P3';
      case 'PHASE_2_ACTIVE': return 'P2';
      case 'PHASE_1_SHADOW': return 'P1';
      default: return '??';
    }
  };

  const niftySync = getSyncIndicator(syncStatus?.nifty);
  const bnSync = getSyncIndicator(syncStatus?.banknifty);

  return (
    <div className="panel p-4 border-border" data-testid="ssot-sync-widget">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-sm font-semibold text-foreground flex items-center gap-2">
          <Database className="w-4 h-4 text-cyan-400" />
          SSoT Sync Status
          <span className="text-xs text-muted-foreground font-normal">(MarketDataCore)</span>
        </h3>
        <button 
          onClick={fetchSyncStatus} 
          disabled={loading}
          className="text-muted-foreground hover:text-foreground disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && (
        <div className="text-xs text-red-400 mb-2">{error}</div>
      )}

      <div className="grid grid-cols-2 gap-3">
        {/* NIFTY Sync */}
        <div className="flex items-center gap-2 p-2 bg-surface/50 rounded border border-border/30">
          <span className="text-lg">{niftySync.icon}</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="font-mono text-xs text-cyan-400">NIFTY</span>
              {syncStatus?.nifty?.phase && (
                <span className="text-[10px] px-1 py-0.5 bg-accent/20 text-accent rounded">
                  {getPhaseLabel(syncStatus.nifty.phase)}
                </span>
              )}
            </div>
            <div className={`font-mono text-xs ${niftySync.color}`}>
              SSoT: {niftySync.text}
              {syncStatus?.nifty?.hft_engine?.is_core_synced && (
                <span className="text-green-500 ml-1">✓</span>
              )}
            </div>
          </div>
        </div>

        {/* BANKNIFTY Sync */}
        <div className="flex items-center gap-2 p-2 bg-surface/50 rounded border border-border/30">
          <span className="text-lg">{bnSync.icon}</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="font-mono text-xs text-purple-400">BANKNIFTY</span>
              {syncStatus?.banknifty?.phase && (
                <span className="text-[10px] px-1 py-0.5 bg-accent/20 text-accent rounded">
                  {getPhaseLabel(syncStatus.banknifty.phase)}
                </span>
              )}
            </div>
            <div className={`font-mono text-xs ${bnSync.color}`}>
              SSoT: {bnSync.text}
              {syncStatus?.banknifty?.hft_engine?.is_core_synced && (
                <span className="text-green-500 ml-1">✓</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Info (collapsed by default) */}
      {syncStatus?.nifty && !syncStatus.nifty.error && (
        <details className="mt-3">
          <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
            View Details
          </summary>
          <div className="mt-2 text-xs space-y-1 font-mono">
            <div className="grid grid-cols-2 gap-2 p-2 bg-surface/30 rounded">
              <div>
                <span className="text-muted-foreground">Market Status:</span>
                <span className={`ml-1 ${syncStatus.nifty.core?.is_market_open ? 'text-green-400' : 'text-red-400'}`}>
                  {syncStatus.nifty.core?.market_status || 'UNKNOWN'}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Market Open:</span>
                <span className={`ml-1 ${syncStatus.nifty.core?.is_market_open ? 'text-green-400' : 'text-red-400'}`}>
                  {syncStatus.nifty.core?.is_market_open ? 'Yes' : 'No'}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Core CVD:</span>
                <span className="ml-1 text-foreground">{syncStatus.nifty.core?.cvd?.toLocaleString()}</span>
              </div>
              <div>
                <span className="text-muted-foreground">HFT CVD:</span>
                <span className="ml-1 text-foreground">{syncStatus.nifty.hft_engine?.cvd?.toLocaleString()}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Core Price:</span>
                <span className="ml-1 text-foreground">{syncStatus.nifty.core?.price?.toFixed(2)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">GEX Price:</span>
                <span className="ml-1 text-foreground">{syncStatus.nifty.gex_engine?.spot_price?.toFixed(2)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Ticks:</span>
                <span className="ml-1 text-foreground">{syncStatus.nifty.core?.tick_count}</span>
              </div>
              <div>
                <span className="text-muted-foreground">GEX Synced:</span>
                <span className={`ml-1 ${syncStatus.nifty.gex_engine?.is_core_synced ? 'text-green-400' : 'text-yellow-400'}`}>
                  {syncStatus.nifty.gex_engine?.is_core_synced ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
          </div>
        </details>
      )}
    </div>
  );
};

// ============================================
// ============================================
// SYSTEM MODE CARD (Replay/Live Toggle with Smart Schedule)
// ============================================
const SystemModeCard = () => {
  // STATE: Mode defaults to OFFLINE (never UNKNOWN for deterministic UI)
  const [systemMode, setSystemMode] = useState({ mode: 'OFFLINE', is_connected: false, port: 0 });
  const [schedule, setSchedule] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [resettingCVD, setResettingCVD] = useState(false);
  const [cvdValues, setCvdValues] = useState({ nifty: 0, banknifty: 0 });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // SESSION 63: SSoT Display Status from MarketDataCore
  // This is the TRUTH - buttons reflect this, not local state
  const [displayStatus, setDisplayStatus] = useState('CLOSED');
  
  // SESSION 86o: Live Market Interlock status
  const [marketInterlock, setMarketInterlock] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  };

  // SESSION 86o: Fetch market interlock status
  const fetchInterlock = useCallback(async () => {
    try {
      const res = await fetch(`${API}/system/status`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setMarketInterlock(data.live_market_interlock);
      }
    } catch (err) {
      console.debug('Failed to fetch interlock:', err);
    }
  }, []);

  const fetchMode = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/system/mode`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        // Normalize: Convert any null/unknown/undefined to OFFLINE
        const normalizedMode = (!data.mode || data.mode === 'UNKNOWN' || data.mode === 'unknown') 
          ? 'OFFLINE' 
          : data.mode.toUpperCase();
        setSystemMode({
          ...data,
          mode: normalizedMode
        });
      }
    } catch (err) {
      console.error('Failed to fetch system mode:', err);
      // On error, set to OFFLINE (not UNKNOWN)
      setSystemMode({ mode: 'OFFLINE', is_connected: false, port: 0 });
    }
  }, []);

  // SESSION 63: Fetch SSoT display status from MarketDataCore
  const fetchDisplayStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/market-core/display-status`);
      if (res.ok) {
        const data = await res.json();
        setDisplayStatus(data.display_status || 'CLOSED');
      }
    } catch (err) {
      console.error('Failed to fetch display status:', err);
      setDisplayStatus('CLOSED');
    }
  }, []);

  const fetchSchedule = useCallback(async () => {
    try {
      const res = await fetch(`${API}/schedule/status`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setSchedule(data);
      }
    } catch (err) {
      console.error('Failed to fetch schedule:', err);
    }
  }, []);

  const fetchCVD = useCallback(async () => {
    try {
      const [niftyRes, bnfRes] = await Promise.all([
        fetch(`${API}/god-score/status?symbol=NIFTY`),
        fetch(`${API}/god-score/status?symbol=BANKNIFTY`),
      ]);
      const niftyData = await niftyRes.json();
      const bnfData = await bnfRes.json();
      setCvdValues({
        nifty: niftyData.hft_breakdown?.cvd || 0,
        banknifty: bnfData.hft_breakdown?.cvd || 0,
      });
    } catch (err) {
      console.error('Failed to fetch CVD:', err);
    }
  }, []);

  useEffect(() => {
    fetchMode();
    fetchDisplayStatus();  // SESSION 63: Fetch SSoT status
    fetchSchedule();
    fetchCVD();
    fetchInterlock();  // SESSION 86o: Fetch interlock status
    const interval = setInterval(() => {
      fetchMode();
      fetchDisplayStatus();  // SESSION 63: Poll SSoT status
      fetchSchedule();
      fetchCVD();
      fetchInterlock();  // SESSION 86o: Poll interlock status
    }, 3000);
    return () => clearInterval(interval);
  }, [fetchMode, fetchDisplayStatus, fetchSchedule, fetchCVD, fetchInterlock]);

  // ============================================
  // SESSION 63: ROBUST REPLAY TOGGLE WITH PORT ISOLATION
  // ============================================
  // - START REPLAY: Explicitly targets Port 8082
  // - STOP REPLAY: Explicitly targets Port 8086 (return to live standby)
  // - Anti-spam: isSwitching blocks all buttons during transition
  // - Idempotent: Backend handles already-running/already-stopped states
  // ============================================

  const startReplay = async () => {
    console.log('[AdminPanel] startReplay clicked, displayStatus:', displayStatus);
    
    // Guard: Only allow when market is CLOSED
    if (displayStatus !== 'CLOSED') {
      const msg = `Cannot start replay - current status is ${displayStatus}`;
      console.log('[AdminPanel]', msg);
      setError(msg);
      return;
    }

    setSwitching(true);
    setError(null);
    setSuccess(null);

    try {
      console.log('[AdminPanel] Calling /truedata/start?mode=replay');
      // Explicitly start replay on port 8082 - mode as query param
      const res = await fetch(`${API}/truedata/start?mode=replay`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      console.log('[AdminPanel] Start response:', data);

      if (data.status === 'success') {
        setSuccess(`✅ Replay active on Port ${data.port || 8082}`);
        setTimeout(() => setSuccess(null), 5000);
      } else if (data.status === 'connecting') {
        // SESSION 64: Non-blocking start - feed is connecting in background
        setSuccess(`🔄 Replay mode initiated on Port ${data.port || 8082}. Connecting in background...`);
        setTimeout(() => setSuccess(null), 10000);
      } else {
        setError(data.message || 'Failed to start replay');
      }
    } catch (err) {
      console.error('[AdminPanel] Start error:', err);
      setError(err.message);
    }

    // Refresh status after transition
    await fetchDisplayStatus();
    await fetchMode();
    await fetchCVD();
    setSwitching(false);
  };

  const stopReplay = async () => {
    console.log('[AdminPanel] stopReplay clicked, displayStatus:', displayStatus);
    
    // Guard: Only allow when in REPLAY mode
    if (displayStatus !== 'REPLAY') {
      console.log('[AdminPanel] Cannot stop - not in REPLAY mode');
      setError('No active replay to stop');
      return;
    }

    // Removed confirmation dialog for now - causing issues in some browsers
    // TODO: Re-enable after fixing
    console.log('[AdminPanel] Proceeding to stop replay...');

    console.log('[AdminPanel] Calling /truedata/stop');
    setSwitching(true);
    setError(null);
    setSuccess(null);

    try {
      // Stop the feed (returns to closed/standby)
      const res = await fetch(`${API}/truedata/stop`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      console.log('[AdminPanel] Stop response status:', res.status);
      const data = await res.json();
      console.log('[AdminPanel] Stop response:', data);

      if (data.status === 'success') {
        setSuccess('✅ Replay stopped. System in CLOSED state.');
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(data.message || 'Failed to stop replay');
      }
    } catch (err) {
      console.error('[AdminPanel] Stop error:', err);
      setError(err.message);
    } finally {
      // Always reset switching state
      setSwitching(false);
    }

    // Refresh status after transition
    await fetchDisplayStatus();
    await fetchMode();
  };

  // Legacy mode switch (for compatibility, but buttons now use start/stop)
  const handleModeSwitch = async (newMode) => {
    if (newMode === 'REPLAY') {
      return startReplay();
    } else if (newMode === 'LIVE') {
      // During market hours, this would start live
      // But we block this when market is closed
      if (displayStatus === 'REPLAY') {
        return stopReplay();
      }
      setError('Live mode only available during market hours');
      return;
    }
  };

  const handleResetCVD = async () => {
    if (!window.confirm('🔄 Reset all CVD values to 0?\n\nThis will clear the cumulative delta for NIFTY and BANKNIFTY.')) {
      return;
    }

    setResettingCVD(true);
    setError(null);

    try {
      const res = await fetch(`${API}/hft/reset-cvd`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await res.json();

      if (data.status === 'success') {
        setSuccess('CVD reset to 0 for all engines');
        setTimeout(() => setSuccess(null), 3000);
        fetchCVD();
      } else {
        setError(data.message || 'Failed to reset CVD');
      }
    } catch (err) {
      setError(err.message);
    }
    setResettingCVD(false);
  };

  // SESSION 63: Button state derived from SSoT display_status (NOT local state)
  // LIVE button is green ONLY if displayStatus === 'LIVE'
  // REPLAY button is orange ONLY if displayStatus === 'REPLAY'
  // This ensures buttons reflect the TRUTH from MarketDataCore
  const isSystemLive = displayStatus === 'LIVE';       // SSoT says market is LIVE
  const isSystemReplay = displayStatus === 'REPLAY';   // SSoT says replay is active
  const isSystemClosed = displayStatus === 'CLOSED';   // SSoT says market is closed
  
  // Legacy mode tracking (for port/connection info display)
  const isReplay = systemMode.mode === 'REPLAY';
  const isLive = systemMode.mode === 'LIVE';
  const isOffline = systemMode.mode === 'OFFLINE';

  // SESSION 127: Show full CVD values for comparison (removed K/M formatting)
  const formatCVD = (value) => {
    // Show full numeric value with thousand separators for easy comparison
    return value.toLocaleString();
  };

  return (
    <div className="panel p-4 border-2 border-purple-500/50 mb-6" data-testid="system-mode-card">
      {/* SESSION 63: SSoT-driven - No more Dead Zone banner, use displayStatus instead */}
      {isSystemClosed && !systemMode.is_connected && (
        <div className="mb-3 p-3 rounded bg-slate-800/70 border border-slate-600/50 flex items-center gap-3">
          <span className="text-2xl">🔴</span>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Badge className="bg-slate-600/50 text-slate-300 border-slate-500/50 text-xs font-bold">
                MARKET CLOSED
              </Badge>
              <span className="text-xs text-slate-400">No Active Feed</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Click REPLAY to start historical data playback
            </p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        {/* Left: Title and Status */}
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-purple-500/20">
            <Shield className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground text-sm flex items-center gap-2">
              System Mode
              {/* Connection Status Dot */}
              <span className={`w-2 h-2 rounded-full ${systemMode.is_connected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
            </h3>
            <div className="flex items-center gap-2 mt-1">
              {/* Current Mode Badge - SESSION 63: Based on SSoT displayStatus */}
              <Badge 
                className={`text-xs font-mono ${
                  isSystemLive
                    ? 'bg-green-500/20 text-green-400 border-green-500/30'
                    : isSystemReplay 
                      ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' 
                      : 'bg-slate-600/30 text-slate-400 border-slate-500/30'
                }`}
                data-testid="current-mode-badge"
              >
                {/* SESSION 63: Badge shows SSoT display status */}
                {isSystemLive ? '🟢 LIVE' : isSystemReplay ? '🟠 REPLAY' : '🔴 CLOSED'}
              </Badge>
              <span className="text-xs text-muted-foreground">
                Port {systemMode.port} • {systemMode.is_connected ? 'Connected' : 'Disconnected'}
              </span>
              {/* Status indicator from SSoT */}
              <span className={`text-xs px-1.5 py-0.5 rounded ${
                isSystemLive ? 'bg-green-500/20 text-green-400' : 
                isSystemReplay ? 'bg-orange-500/20 text-orange-400' :
                'bg-slate-500/20 text-slate-400'
              }`}>
                {isSystemLive ? '🟢 Market Open' : isSystemReplay ? '🟠 Replay Active' : '⚫ Market Closed'}
              </span>
            </div>
          </div>
        </div>

        {/* Right: Mode Toggle Buttons - SESSION 63: START/STOP with Anti-spam */}
        <div className="flex items-center gap-2">
          {/* STOP Button - Active when in REPLAY mode */}
          <Button
            onClick={() => {
              console.log('[AdminPanel] STOP button clicked!');
              console.log('[AdminPanel] isSystemReplay:', isSystemReplay);
              console.log('[AdminPanel] switching:', switching);
              console.log('[AdminPanel] displayStatus:', displayStatus);
              stopReplay();
            }}
            disabled={switching || !isSystemReplay}
            size="sm"
            variant="outline"
            className={`text-xs ${
              isSystemReplay
                ? 'border-red-500 text-red-400 hover:bg-red-500/20 hover:text-red-300'
                : 'border-gray-500/30 text-gray-400 opacity-60 cursor-not-allowed'
            }`}
            title={isSystemReplay ? 'Click to stop replay' : 'No active replay'}
            data-testid="stop-replay-btn"
          >
            {switching ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : null}
            ⏹️ STOP
          </Button>
          
          {/* REPLAY Button: START when CLOSED, shows ACTIVE when in replay */}
          {/* SESSION 86p: Disabled when outside replay broadcast windows (WHITELIST) */}
          <Button
            onClick={() => {
              console.log('[AdminPanel] REPLAY button clicked!');
              startReplay();
            }}
            disabled={
              switching || 
              isSystemReplay || 
              isSystemLive || 
              (marketInterlock?.can_enter_replay === false)
            }
            size="sm"
            variant={isSystemReplay ? 'default' : 'outline'}
            className={`text-xs ${
              isSystemReplay 
                ? 'bg-orange-600 hover:bg-orange-700' 
                : marketInterlock?.can_enter_replay === false
                  ? 'border-red-500/30 text-red-400 opacity-50 cursor-not-allowed'
                  : isSystemClosed
                    ? 'border-orange-500/30 text-orange-400 hover:bg-orange-500/10'
                    : 'border-gray-500/30 text-gray-400 opacity-50 cursor-not-allowed'
            }`}
            title={
              marketInterlock?.can_enter_replay === false
                ? `🔒 Replay Offline. ${marketInterlock.schedule_message || 'Weekdays: 18:15-00:30 | Weekends: 09:15-15:30 & 18:15-00:30 IST'}`
                : isSystemReplay 
                  ? 'Replay is ACTIVE (Port 8082)' 
                  : isSystemClosed 
                    ? `Click to START Replay (Port 8082) - ${marketInterlock?.current_window || 'Available'} Window` 
                    : 'Cannot start Replay during market hours'
            }
            data-testid="replay-mode-btn"
          >
            {switching ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : null}
            {marketInterlock?.can_enter_replay === false 
              ? '🔒 OFFLINE' 
              : isSystemReplay 
                ? '🟠 REPLAY ACTIVE' 
                : '▶️ START REPLAY'}
          </Button>
          
          {/* SESSION 86p: Interlock Warning with Schedule */}
          {marketInterlock?.can_enter_replay === false && (
            <div className="text-xs text-red-400 flex items-center gap-1" title={marketInterlock.schedule_message}>
              <AlertTriangle className="w-3 h-3" />
              <span>{marketInterlock.next_window ? `Next: ${marketInterlock.next_window}` : 'Offline'}</span>
            </div>
          )}
        </div>
      </div>

      {/* CVD Status Row */}
      <div className="mt-3 pt-3 border-t border-border/50 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">NIFTY CVD:</span>
            <span className={`font-mono text-sm font-bold ${cvdValues.nifty >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {formatCVD(cvdValues.nifty)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">BNF CVD:</span>
            <span className={`font-mono text-sm font-bold ${cvdValues.banknifty >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {formatCVD(cvdValues.banknifty)}
            </span>
          </div>
        </div>
        <Button
          onClick={handleResetCVD}
          disabled={resettingCVD}
          size="sm"
          variant="outline"
          className="text-xs border-red-500/30 text-red-400 hover:bg-red-500/10"
          data-testid="reset-cvd-btn"
        >
          {resettingCVD ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : null}
          🔄 Reset CVD
        </Button>
      </div>

      {/* Warning Text */}
      <p className="text-xs text-muted-foreground mt-3 border-t border-border/50 pt-2">
        ⚠️ Switching to REPLAY resets CVD to 0. Use REPLAY for testing, LIVE for real-time market data.
      </p>

      {/* Error/Success Messages */}
      {error && (
        <div className="mt-2 p-2 bg-sell/10 border border-sell/30 rounded text-xs text-sell flex items-center gap-2">
          <AlertTriangle className="w-3 h-3" />
          {error}
        </div>
      )}
      {success && (
        <div className="mt-2 p-2 bg-buy/10 border border-buy/30 rounded text-xs text-buy flex items-center gap-2">
          <Check className="w-3 h-3" />
          {success}
        </div>
      )}
    </div>
  );
};


// SESSION 121: TimeMachineTab removed - now using full TimeMachine component instead (imported at top)


const SystemStatusTab = () => {
  const [expiryStatus, setExpiryStatus] = useState(null);
  const [allExpiries, setAllExpiries] = useState([]);
  const [marketStatus, setMarketStatus] = useState(null);
  const [maintenanceStatus, setMaintenanceStatus] = useState(null);
  const [systemHealth, setSystemHealth] = useState(null);
  const [gexDiagnostics, setGexDiagnostics] = useState({ nifty: null, banknifty: null });
  const [healthLoading, setHealthLoading] = useState(false);
  const [sessionResetLoading, setSessionResetLoading] = useState(false);
  const [morningResetLoading, setMorningResetLoading] = useState(false);  // SESSION 86m
  const [loading, setLoading] = useState(true);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);

  // Get auth token from localStorage
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  };

  const fetchStatus = useCallback(async () => {
    try {
      const [todayRes, allRes, marketRes] = await Promise.all([
        fetch(`${API}/admin/today-expiry`),
        fetch(`${API}/admin/expiry`),
        fetch(`${API}/market/status`),
      ]);
      const todayData = await todayRes.json();
      const allData = await allRes.json();
      const marketData = await marketRes.json();
      setExpiryStatus(todayData);
      setAllExpiries(allData);
      setMarketStatus(marketData);
    } catch (error) {
      console.error('Failed to fetch status:', error);
    }
    setLoading(false);
  }, []);

  const fetchMaintenanceStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/system/status`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setMaintenanceStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch maintenance status:', error);
    }
  }, []);

  // Fetch System Health
  const fetchSystemHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const res = await fetch(`${API}/system/health`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setSystemHealth(data);
      }
    } catch (error) {
      console.error('Failed to fetch system health:', error);
    }
    setHealthLoading(false);
  }, []);

  // Fetch GEX Diagnostics (regime flip tracking)
  const fetchGexDiagnostics = useCallback(async () => {
    try {
      const [niftyRes, bnfRes] = await Promise.all([
        fetch(`${API}/gex/diagnostics?symbol=NIFTY`, { headers: getAuthHeaders() }),
        fetch(`${API}/gex/diagnostics?symbol=BANKNIFTY`, { headers: getAuthHeaders() }),
      ]);
      const niftyData = niftyRes.ok ? await niftyRes.json() : null;
      const bnfData = bnfRes.ok ? await bnfRes.json() : null;
      setGexDiagnostics({ nifty: niftyData, banknifty: bnfData });
    } catch (error) {
      console.error('Failed to fetch GEX diagnostics:', error);
    }
  }, []);

  // Trigger Manual Session Reset
  const handleSessionReset = async () => {
    if (!window.confirm('🌅 This will reset all session data (CVD, Volume, High/Low). Continue?')) {
      return;
    }
    
    setSessionResetLoading(true);
    try {
      const res = await fetch(`${API}/system/reset-session`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        alert('✅ Session reset complete. All accumulators cleared.');
        fetchSystemHealth();
      } else {
        alert('❌ Session reset failed: ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Session reset failed:', error);
      alert('❌ Session reset failed: ' + error.message);
    }
    setSessionResetLoading(false);
  };

  const handleForceCleanup = async () => {
    if (!window.confirm('⚠️ This will restart the server and disconnect all clients briefly. Continue?')) {
      return;
    }
    
    setMaintenanceLoading(true);
    try {
      const res = await fetch(`${API}/system/maintenance/force`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        alert('✅ Maintenance initiated. Server will restart in a few seconds.');
      } else {
        alert('❌ Maintenance failed: ' + (data.error || data.message));
      }
    } catch (error) {
      console.error('Force cleanup failed:', error);
      alert('❌ Force cleanup failed: ' + error.message);
    }
    setMaintenanceLoading(false);
  };

  const [morningStartLoading, setMorningStartLoading] = useState(false);
  
  const handleForceMorningStart = async () => {
    if (!window.confirm('☀️ This will initialize the live feed connection. Continue?')) {
      return;
    }
    
    setMorningStartLoading(true);
    try {
      const res = await fetch(`${API}/system/morning-start/force`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        alert('✅ Morning start complete. Live feed connected!');
        fetchMaintenanceStatus(); // Refresh status
      } else {
        alert('❌ Morning start failed: ' + (data.error || data.message));
      }
    } catch (error) {
      console.error('Morning start failed:', error);
      alert('❌ Morning start failed: ' + error.message);
    }
    setMorningStartLoading(false);
  };

  // SESSION 86m: Handle manual morning reset trigger
  const handleMorningReset = async () => {
    if (!window.confirm('🌅 This will run the morning initialization sequence:\n\n1. Clear CVD & Market Regime state\n2. Fetch yesterday\'s Naked POCs\n3. Reconnect TrueData with fresh token\n\nContinue?')) {
      return;
    }
    
    setMorningResetLoading(true);
    try {
      const res = await fetch(`${API}/system/morning-reset?force=false`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.status === 'success' || data.status === 'partial_success' || data.status === 'already_completed') {
        alert(`✅ ${data.message}`);
        fetchMaintenanceStatus(); // Refresh status
      } else {
        alert('❌ Morning reset failed: ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Morning reset failed:', error);
      alert('❌ Morning reset failed: ' + error.message);
    }
    setMorningResetLoading(false);
  };

  useEffect(() => {
    fetchStatus();
    fetchMaintenanceStatus();
    fetchSystemHealth();
    fetchGexDiagnostics();
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchStatus();
      fetchMaintenanceStatus();
      fetchSystemHealth();
      fetchGexDiagnostics();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchStatus, fetchMaintenanceStatus, fetchSystemHealth, fetchGexDiagnostics]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'OPEN': return 'text-buy bg-buy/10 border-buy/30';
      case 'CLOSED': return 'text-sell bg-sell/10 border-sell/30';
      case 'PRE_MARKET': return 'text-accent bg-accent/10 border-accent/30';
      case 'POST_MARKET': return 'text-warning bg-warning/10 border-warning/30';
      default: return 'text-muted-foreground bg-surface border-border';
    }
  };

  return (
    <div className="space-y-6" data-testid="system-status-tab">
      <div>
        <h2 className="font-heading text-2xl font-bold text-foreground flex items-center gap-2">
          <Activity className="w-6 h-6 text-accent" />
          System Status
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Monitor market status, expiry information, and system health.
        </p>
      </div>

      {/* System Mode Card (Replay/Live Toggle) - NEW */}
      <SystemModeCard />

      {/* Market Status Card */}
      {marketStatus && (
        <div className={`panel p-6 border ${getStatusColor(marketStatus.status)}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-semibold text-foreground">Market Status</h3>
            <button onClick={fetchStatus} className="text-muted-foreground hover:text-foreground">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          
          <div className="flex items-center gap-4 mb-4">
            <Badge className={`text-lg px-4 py-2 ${getStatusColor(marketStatus.status)}`}>
              {marketStatus.status}
            </Badge>
            <span className="text-foreground">{marketStatus.reason}</span>
          </div>

          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Current Time (IST):</span>
              <span className="ml-2 font-mono text-foreground">{marketStatus.current_time}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Date:</span>
              <span className="ml-2 font-mono text-foreground">{marketStatus.today_date}</span>
            </div>
            {marketStatus.trading_hours && (
              <div>
                <span className="text-muted-foreground">Trading Hours:</span>
                <span className="ml-2 font-mono text-foreground">
                  {marketStatus.trading_hours.start} - {marketStatus.trading_hours.end}
                </span>
              </div>
            )}
          </div>

          {marketStatus.next_open && !marketStatus.is_open && (
            <div className="mt-4 pt-4 border-t border-border">
              <span className="text-muted-foreground">Next Open:</span>
              <span className="ml-2 font-mono text-accent">{marketStatus.next_open}</span>
            </div>
          )}
        </div>
      )}

      {/* System Health Widget */}
      <div className={`panel p-6 border ${
        systemHealth?.status === 'GREEN' ? 'border-green-500/30' : 
        systemHealth?.status === 'AMBER' ? 'border-yellow-500/30' : 
        systemHealth?.status === 'RED' ? 'border-red-500/30' : 'border-border'
      }`} data-testid="system-health-widget">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold text-foreground flex items-center gap-2">
            <Shield className="w-5 h-5 text-accent" />
            System Health
          </h3>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchSystemHealth}
              disabled={healthLoading}
              data-testid="run-diagnostics-btn"
            >
              {healthLoading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Run Diagnostics
                </>
              )}
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleSessionReset}
              disabled={sessionResetLoading}
              className="text-amber-400 border-amber-400/30 hover:bg-amber-500/10"
              data-testid="session-reset-btn"
            >
              {sessionResetLoading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                '🌅 Reset Session'
              )}
            </Button>
          </div>
        </div>

        {/* Overall Status Badge */}
        {systemHealth && (
          <div className="mb-4">
            <Badge className={`text-lg px-4 py-2 ${
              systemHealth.status === 'GREEN' ? 'bg-green-500/20 text-green-400 border-green-500/50' :
              systemHealth.status === 'AMBER' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50' :
              'bg-red-500/20 text-red-400 border-red-500/50'
            }`}>
              {systemHealth.status === 'GREEN' ? '✅ ALL SYSTEMS HEALTHY' :
               systemHealth.status === 'AMBER' ? '⚠️ MINOR ISSUES DETECTED' :
               '❌ CRITICAL ISSUES'}
            </Badge>
            
            {/* Issues List */}
            {systemHealth.issues?.length > 0 && (
              <div className="mt-2 text-sm text-yellow-400">
                {systemHealth.issues.map((issue, i) => (
                  <div key={i}>• {issue}</div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Health Indicators Grid */}
        {systemHealth && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Feed Status */}
            <div className="flex items-center gap-3 p-3 bg-surface/50 rounded border border-border/30">
              <div className={`w-3 h-3 rounded-full ${
                systemHealth.checks?.feed_status === 'CONNECTED' ? 'bg-green-500' : 'bg-red-500'
              }`} />
              <div>
                <div className="text-xs text-muted-foreground">Feed Status</div>
                <div className={`font-mono text-sm ${
                  systemHealth.checks?.feed_status === 'CONNECTED' ? 'text-green-400' : 'text-red-400'
                }`}>
                  {systemHealth.checks?.feed_status || 'UNKNOWN'}
                </div>
              </div>
            </div>

            {/* Daily Disconnections - MOVED UP for visibility */}
            <div className="flex items-center gap-3 p-3 bg-surface/50 rounded border border-border/30" data-testid="daily-disconnections-top">
              <div className={`w-3 h-3 rounded-full ${
                (systemHealth.checks?.disconnection_count || 0) === 0 ? 'bg-green-500' :
                (systemHealth.checks?.disconnection_count || 0) < 5 ? 'bg-yellow-500' : 'bg-red-500'
              }`} />
              <div>
                <div className="text-xs text-muted-foreground">Disconnects</div>
                <div className={`font-mono text-sm font-bold ${
                  (systemHealth.checks?.disconnection_count || 0) === 0 ? 'text-green-400' :
                  (systemHealth.checks?.disconnection_count || 0) < 5 ? 'text-yellow-400' : 'text-red-400'
                }`}>
                  {systemHealth.checks?.disconnection_count || 0}
                </div>
              </div>
            </div>

            {/* Database Status */}
            <div className="flex items-center gap-3 p-3 bg-surface/50 rounded border border-border/30">
              <div className={`w-3 h-3 rounded-full ${
                systemHealth.checks?.database_status === 'CONNECTED' ? 'bg-green-500' : 'bg-red-500'
              }`} />
              <div>
                <div className="text-xs text-muted-foreground">Database</div>
                <div className={`font-mono text-sm ${
                  systemHealth.checks?.database_status === 'CONNECTED' ? 'text-green-400' : 'text-red-400'
                }`}>
                  {systemHealth.checks?.database_status || 'UNKNOWN'}
                </div>
              </div>
            </div>

            {/* Data Latency */}
            <div className="flex items-center gap-3 p-3 bg-surface/50 rounded border border-border/30">
              <div className={`w-3 h-3 rounded-full ${
                systemHealth.checks?.data_latency_ms < 0 ? 'bg-gray-500' :
                systemHealth.checks?.data_latency_ms < 1000 ? 'bg-green-500' :
                systemHealth.checks?.data_latency_ms < 5000 ? 'bg-yellow-500' : 'bg-red-500'
              }`} />
              <div>
                <div className="text-xs text-muted-foreground">Data Latency</div>
                <div className={`font-mono text-sm ${
                  systemHealth.checks?.data_latency_ms < 0 ? 'text-gray-400' :
                  systemHealth.checks?.data_latency_ms < 1000 ? 'text-green-400' :
                  systemHealth.checks?.data_latency_ms < 5000 ? 'text-yellow-400' : 'text-red-400'
                }`}>
                  {systemHealth.checks?.data_latency_ms < 0 ? 'No Data' :
                   `${systemHealth.checks?.data_latency_ms?.toLocaleString()}ms`}
                </div>
              </div>
            </div>

            {/* Engines Status */}
            <div className="flex items-center gap-3 p-3 bg-surface/50 rounded border border-border/30">
              <div className={`w-3 h-3 rounded-full ${
                systemHealth.checks?.engines_running === '2/2' ? 'bg-green-500' :
                systemHealth.checks?.engines_running === '1/2' ? 'bg-yellow-500' : 'bg-red-500'
              }`} />
              <div>
                <div className="text-xs text-muted-foreground">Engines</div>
                <div className={`font-mono text-sm ${
                  systemHealth.checks?.engines_running === '2/2' ? 'text-green-400' : 'text-yellow-400'
                }`}>
                  {systemHealth.checks?.engines_running || '0/2'}
                </div>
              </div>
            </div>

            {/* Active Connections */}
            <div className="flex items-center gap-3 p-3 bg-surface/50 rounded border border-border/30">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <div>
                <div className="text-xs text-muted-foreground">Connections</div>
                <div className="font-mono text-sm text-blue-400">
                  {systemHealth.checks?.active_connections || 0}
                </div>
              </div>
            </div>

            {/* Errors */}
            <div className="flex items-center gap-3 p-3 bg-surface/50 rounded border border-border/30">
              <div className={`w-3 h-3 rounded-full ${
                systemHealth.checks?.errors_last_hour === 0 ? 'bg-green-500' :
                systemHealth.checks?.errors_last_hour < 10 ? 'bg-yellow-500' : 'bg-red-500'
              }`} />
              <div>
                <div className="text-xs text-muted-foreground">Errors/Hour</div>
                <div className={`font-mono text-sm ${
                  systemHealth.checks?.errors_last_hour === 0 ? 'text-green-400' :
                  systemHealth.checks?.errors_last_hour < 10 ? 'text-yellow-400' : 'text-red-400'
                }`}>
                  {systemHealth.checks?.errors_last_hour || 0}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Server Info */}
        {systemHealth?.server_info && (
          <div className="mt-4 pt-4 border-t border-border text-sm">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <span className="text-muted-foreground">Server Time:</span>
                <div className="font-mono text-foreground">{systemHealth.server_info.server_time_ist}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Market:</span>
                <div className={`font-mono ${systemHealth.server_info.market_open ? 'text-green-400' : 'text-red-400'}`}>
                  {systemHealth.server_info.market_open ? 'OPEN' : 'CLOSED'}
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">Uptime:</span>
                <div className="font-mono text-foreground">
                  {Math.floor((systemHealth.server_info.uptime_seconds || 0) / 3600)}h {Math.floor(((systemHealth.server_info.uptime_seconds || 0) % 3600) / 60)}m
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">Reason:</span>
                <div className="font-mono text-muted-foreground text-xs">{systemHealth.server_info.market_reason}</div>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {!systemHealth && !healthLoading && (
          <div className="text-center py-8 text-muted-foreground">
            <Shield className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Click "Run Diagnostics" to check system health</p>
          </div>
        )}
      </div>

      {/* SSoT Sync Status Widget */}
      <SSOTSyncStatusWidget />

      {/* GEX Engine Diagnostics Card */}
      <div className="panel p-6 border-border" data-testid="gex-diagnostics-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold text-foreground flex items-center gap-2">
            <Activity className="w-5 h-5 text-purple-400" />
            GEX Engine Diagnostics
            <span className="text-xs text-muted-foreground font-normal">(EMA Smoothing + Hysteresis)</span>
          </h3>
          <button onClick={fetchGexDiagnostics} className="text-muted-foreground hover:text-foreground">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* NIFTY GEX Diagnostics */}
          <div className="bg-surface/50 rounded p-4 border border-border/30">
            <div className="flex items-center gap-2 mb-3">
              <span className="font-bold text-cyan-400">NIFTY</span>
              {gexDiagnostics.nifty && (
                <Badge className={`text-xs ${
                  gexDiagnostics.nifty.current_regime === 'POSITIVE_GAMMA' 
                    ? 'bg-green-500/20 text-green-400 border-green-500/30'
                    : gexDiagnostics.nifty.current_regime === 'NEGATIVE_GAMMA'
                    ? 'bg-red-500/20 text-red-400 border-red-500/30'
                    : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                }`}>
                  {gexDiagnostics.nifty.current_regime?.replace('_', ' ') || 'NEUTRAL'}
                </Badge>
              )}
            </div>
            {gexDiagnostics.nifty ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Regime Flips Today:</span>
                  <span className={`font-mono ${gexDiagnostics.nifty.flip_count_today > 10 ? 'text-red-400' : 'text-green-400'}`}>
                    {gexDiagnostics.nifty.flip_count_today}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Zero Gamma (EMA):</span>
                  <span className="font-mono text-foreground">
                    {gexDiagnostics.nifty.zero_gamma_displayed || gexDiagnostics.nifty.ema_zero_gamma?.toFixed(0) || '—'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Hysteresis Buffer:</span>
                  <span className="font-mono text-cyan-400">±{gexDiagnostics.nifty.hysteresis_buffer} pts</span>
                </div>
                {gexDiagnostics.nifty.distance_to_flip && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Distance to Flip:</span>
                    <span className={`font-mono ${gexDiagnostics.nifty.distance_to_flip < 100 ? 'text-amber-400' : 'text-green-400'}`}>
                      {gexDiagnostics.nifty.distance_to_flip?.toFixed(0)} pts
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">Loading...</p>
            )}
          </div>
          
          {/* BANKNIFTY GEX Diagnostics */}
          <div className="bg-surface/50 rounded p-4 border border-border/30">
            <div className="flex items-center gap-2 mb-3">
              <span className="font-bold text-amber-400">BANKNIFTY</span>
              {gexDiagnostics.banknifty && (
                <Badge className={`text-xs ${
                  gexDiagnostics.banknifty.current_regime === 'POSITIVE_GAMMA' 
                    ? 'bg-green-500/20 text-green-400 border-green-500/30'
                    : gexDiagnostics.banknifty.current_regime === 'NEGATIVE_GAMMA'
                    ? 'bg-red-500/20 text-red-400 border-red-500/30'
                    : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                }`}>
                  {gexDiagnostics.banknifty.current_regime?.replace('_', ' ') || 'NEUTRAL'}
                </Badge>
              )}
            </div>
            {gexDiagnostics.banknifty ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Regime Flips Today:</span>
                  <span className={`font-mono ${gexDiagnostics.banknifty.flip_count_today > 10 ? 'text-red-400' : 'text-green-400'}`}>
                    {gexDiagnostics.banknifty.flip_count_today}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Zero Gamma (EMA):</span>
                  <span className="font-mono text-foreground">
                    {gexDiagnostics.banknifty.zero_gamma_displayed || gexDiagnostics.banknifty.ema_zero_gamma?.toFixed(0) || '—'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Hysteresis Buffer:</span>
                  <span className="font-mono text-amber-400">±{gexDiagnostics.banknifty.hysteresis_buffer} pts</span>
                </div>
                {gexDiagnostics.banknifty.distance_to_flip && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Distance to Flip:</span>
                    <span className={`font-mono ${gexDiagnostics.banknifty.distance_to_flip < 200 ? 'text-amber-400' : 'text-green-400'}`}>
                      {gexDiagnostics.banknifty.distance_to_flip?.toFixed(0)} pts
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">Loading...</p>
            )}
          </div>
        </div>
        
        {/* Explanation */}
        <div className="mt-4 pt-4 border-t border-border text-xs text-muted-foreground">
          <p><strong>EMA Smoothing:</strong> Heavy smoothing (α=0.05) on expiry days, lighter (α=0.20) on normal days.</p>
          <p><strong>Hysteresis:</strong> Regime only flips when price moves beyond Zero Gamma ± buffer zone.</p>
        </div>
      </div>

      {/* Server Maintenance Status Card */}
      <div className="panel p-6 border-border" data-testid="maintenance-status-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold text-foreground flex items-center gap-2">
            <Settings className="w-5 h-5 text-accent" />
            Server Maintenance & Automation
          </h3>
          <button onClick={fetchMaintenanceStatus} className="text-muted-foreground hover:text-foreground">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {maintenanceStatus ? (
          <div className="space-y-4">
            {/* Server Start Time - Success Indicator */}
            <div className="bg-buy/10 border border-buy/30 p-4 rounded-lg">
              <div className="flex items-center gap-3 mb-2">
                <Check className="w-5 h-5 text-buy" />
                <span className="font-semibold text-buy">Last Server Reset</span>
              </div>
              <p className="text-lg font-mono text-foreground">
                {maintenanceStatus.server_start_time_ist}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Uptime: {maintenanceStatus.uptime_formatted}
              </p>
            </div>

            {/* SESSION 86m: Morning Reset Health Indicator */}
            {maintenanceStatus.morning_reset && (
              <div className={`p-4 rounded-lg border ${
                maintenanceStatus.morning_reset.status === 'SUCCESS' 
                  ? 'bg-buy/10 border-buy/30' 
                  : maintenanceStatus.morning_reset.status === 'PARTIAL'
                  ? 'bg-yellow-500/10 border-yellow-500/30'
                  : maintenanceStatus.morning_reset.status === 'FAILED'
                  ? 'bg-sell/10 border-sell/30'
                  : 'bg-surface border-border'
              }`}>
                <div className="flex items-center gap-3 mb-2">
                  <Clock className={`w-5 h-5 ${
                    maintenanceStatus.morning_reset.status === 'SUCCESS' ? 'text-buy' :
                    maintenanceStatus.morning_reset.status === 'PARTIAL' ? 'text-yellow-500' :
                    maintenanceStatus.morning_reset.status === 'FAILED' ? 'text-sell' :
                    'text-muted-foreground'
                  }`} />
                  <span className={`font-semibold ${
                    maintenanceStatus.morning_reset.status === 'SUCCESS' ? 'text-buy' :
                    maintenanceStatus.morning_reset.status === 'PARTIAL' ? 'text-yellow-500' :
                    maintenanceStatus.morning_reset.status === 'FAILED' ? 'text-sell' :
                    'text-muted-foreground'
                  }`}>Morning Reset</span>
                  <Badge className={`ml-auto ${
                    maintenanceStatus.morning_reset.status === 'SUCCESS' 
                      ? 'bg-buy/20 text-buy border-buy/50'
                      : maintenanceStatus.morning_reset.status === 'PARTIAL'
                      ? 'bg-yellow-500/20 text-yellow-500 border-yellow-500/50'
                      : maintenanceStatus.morning_reset.status === 'FAILED'
                      ? 'bg-sell/20 text-sell border-sell/50'
                      : 'bg-surface text-muted-foreground border-border'
                  }`}>
                    {maintenanceStatus.morning_reset.is_today 
                      ? maintenanceStatus.morning_reset.display
                      : 'Pending'}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  {maintenanceStatus.morning_reset.is_today 
                    ? `✅ Morning initialization completed at ${maintenanceStatus.morning_reset.display} IST`
                    : '⏳ Waiting for 08:30 AM IST trigger (or manual trigger)'}
                </p>
                {!maintenanceStatus.morning_reset.is_today && (
                  <Button
                    onClick={handleMorningReset}
                    disabled={morningResetLoading}
                    variant="outline"
                    size="sm"
                    className="mt-2 text-accent border-accent/50 hover:bg-accent/10"
                    data-testid="trigger-morning-reset-btn"
                  >
                    {morningResetLoading ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Running...</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" /> Trigger Morning Reset</>
                    )}
                  </Button>
                )}
              </div>
            )}

            {/* Morning Auto-Start Status */}
            {maintenanceStatus.morning_autostart && (
              <div className={`p-4 rounded-lg border ${
                maintenanceStatus.morning_autostart.last_status === 'success' 
                  ? 'bg-accent/10 border-accent/30' 
                  : 'bg-surface border-border'
              }`}>
                <div className="flex items-center gap-3 mb-2">
                  <Sparkles className="w-5 h-5 text-accent" />
                  <span className="font-semibold text-accent">Morning Auto-Start (9:00 AM IST)</span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground block">Last Run:</span>
                    <span className="font-mono text-foreground">
                      {maintenanceStatus.morning_autostart.last_run 
                        ? new Date(maintenanceStatus.morning_autostart.last_run).toLocaleString('en-IN', {timeZone: 'Asia/Kolkata'})
                        : 'Never'}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground block">Status:</span>
                    <Badge className={
                      maintenanceStatus.morning_autostart.last_status === 'success' 
                        ? 'bg-buy/20 text-buy border-buy/50'
                        : maintenanceStatus.morning_autostart.last_status === 'failed'
                        ? 'bg-sell/20 text-sell border-sell/50'
                        : 'bg-surface text-muted-foreground border-border'
                    }>
                      {maintenanceStatus.morning_autostart.last_status || 'Pending'}
                    </Badge>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {maintenanceStatus.morning_autostart.schedule}
                </p>
              </div>
            )}

            {/* Schedulers Overview */}
            {maintenanceStatus.schedulers && (
              <div className="bg-surface p-3 rounded border border-border">
                <span className="text-sm font-semibold text-foreground block mb-2">Active Schedulers:</span>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">☀️ Morning Auto-Start:</span>
                    <span className="font-mono text-foreground">09:00 IST</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">⏰ Market Scheduler:</span>
                    <span className="font-mono text-foreground">09:15 IST</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">🔧 Daily Maintenance:</span>
                    <span className="font-mono text-foreground">20:00 IST</span>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="pt-4 border-t border-border space-y-3">
              {/* Morning Start Button */}
              <Button
                onClick={handleForceMorningStart}
                disabled={morningStartLoading}
                variant="outline"
                className="w-full bg-accent/10 border-accent/30 text-accent hover:bg-accent/20"
                data-testid="force-morning-start-btn"
              >
                {morningStartLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Starting Feed...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Start Morning Sequence Now
                  </>
                )}
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                Initializes live feed connection (disables mock mode)
              </p>

              {/* Force Cleanup Button */}
              <Button
                onClick={handleForceCleanup}
                disabled={maintenanceLoading}
                variant="outline"
                className="w-full bg-sell/10 border-sell/30 text-sell hover:bg-sell/20"
                data-testid="force-cleanup-btn"
              >
                {maintenanceLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Restarting...
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Force Cleanup Now (Emergency)
                  </>
                )}
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                ⚠️ This will restart the server and briefly disconnect all clients
              </p>
            </div>
          </div>
        ) : (
          <div className="text-center py-4 text-muted-foreground">
            Loading maintenance status...
          </div>
        )}
      </div>

      {/* Today's Expiry Status Card */}
      <div className={`panel p-6 ${expiryStatus?.has_expiry_today ? 'border-warning' : 'border-border'}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold text-foreground">Today's Expiry Check</h3>
          <button onClick={fetchStatus} className="text-muted-foreground hover:text-foreground">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Loading...</div>
        ) : expiryStatus?.has_expiry_today ? (
          <div className="bg-warning/10 border border-warning/30 p-4" data-testid="expiry-today-alert">
            <div className="flex items-center gap-3 mb-2">
              <AlertTriangle className="w-5 h-5 text-warning" />
              <span className="font-semibold text-warning">EXPIRY DAY</span>
            </div>
            <p className="text-lg font-mono text-foreground">
              {expiryStatus.message}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {expiryStatus.expiries.map((exp) => (
                <Badge key={exp.symbol} variant="outline" className="bg-warning/20 text-warning border-warning/50 font-mono">
                  {exp.symbol}
                </Badge>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-buy/10 border border-buy/30 p-4" data-testid="no-expiry-alert">
            <div className="flex items-center gap-3">
              <Check className="w-5 h-5 text-buy" />
              <span className="font-semibold text-buy">No Expiry Today</span>
            </div>
          </div>
        )}
      </div>

      {/* All Expiry Information */}
      <div className="panel p-6">
        <h3 className="font-heading text-lg font-semibold text-foreground mb-4">
          Upcoming Expiries
        </h3>
        
        {allExpiries.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No active indices configured. Add indices in the Index Master section.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {allExpiries.map((expiry) => (
              <div
                key={expiry.symbol}
                className={`bg-surface-highlight border p-4 ${expiry.is_today_expiry ? 'border-warning' : 'border-border'}`}
                data-testid={`expiry-card-${expiry.symbol}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono font-bold text-foreground text-lg">{expiry.symbol}</span>
                  <Badge variant={expiry.expiry_cycle === 'Monthly' ? 'default' : 'secondary'} className={expiry.expiry_cycle === 'Monthly' ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' : 'bg-blue-500/20 text-blue-300 border border-blue-500/30'}>
                    {expiry.expiry_cycle}
                  </Badge>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Next Expiry:</span>
                    <span className="font-mono text-foreground">{formatDate(expiry.next_expiry)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Days to Expiry:</span>
                    <span className={`font-mono font-semibold ${expiry.days_to_expiry <= 1 ? 'text-warning' : expiry.days_to_expiry <= 3 ? 'text-accent' : 'text-foreground'}`}>
                      {expiry.days_to_expiry} {expiry.days_to_expiry === 1 ? 'day' : 'days'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Base Day:</span>
                    <span className="text-foreground">{expiry.base_expiry_day}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Lot Size:</span>
                    <span className="font-mono text-foreground">{expiry.lot_size}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// MAIN ADMIN DASHBOARD WITH SIDEBAR
// ============================================
export const AdminDashboard = () => {
  // Session 145 — deep-link support. When the Dhan health banner (rendered
  // app-wide in App.js) fires "Go to Broker Integration" from a non-admin
  // screen, it sets location.hash='#broker' and navigates to /admin. On
  // landing we honour the hash and open the broker tab directly so the
  // user isn't dropped on the default status tab.
  // Session 147j+16bn — In focused mode, default landing is AlphaScout
  // (Largecap) instead of System Status. User asked for AlphaScout-first
  // UX after the TrueData subscription was cancelled.
  const _focusModeAdmin = (process.env.REACT_APP_FOCUS_MODE || 'true').toLowerCase() === 'true';
  const _defaultTab = _focusModeAdmin ? 'alphascout' : 'status';
  const initialTab = (typeof window !== 'undefined'
    && window.location.hash === '#broker') ? 'broker' : _defaultTab;
  const [activeTab, setActiveTab] = useState(initialTab);

  // Clear the hash after consuming it so refreshes don't override the
  // user's subsequent tab picks.
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hash === '#broker') {
      try { window.history.replaceState(null, '', window.location.pathname); } catch (_e) { /* no-op */ }
    }
  }, []);

  return (
    <div className="h-full terminal-bg flex" data-testid="admin-dashboard">
      {/* Inline style for scrollbar visibility */}
      <style>{`
        .admin-main-scroll::-webkit-scrollbar {
          width: 12px !important;
        }
        .admin-main-scroll::-webkit-scrollbar-track {
          background: #21262d !important;
        }
        .admin-main-scroll::-webkit-scrollbar-thumb {
          background: #58a6ff !important;
          border-radius: 6px !important;
          border: 2px solid #21262d !important;
        }
        .admin-main-scroll::-webkit-scrollbar-thumb:hover {
          background: #79b8ff !important;
        }
        .admin-main-scroll {
          scrollbar-width: thin;
          scrollbar-color: #58a6ff #21262d;
        }
      `}</style>
      {/* Left Sidebar */}
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} />

      {/* Main Content Area - Always show scrollbar */}
      <main className="flex-1 p-6 overflow-y-scroll admin-main-scroll">
        {/* 147j+16bw — Removed `max-w-5xl mx-auto` cap. On wide screens
            (1600px+) the 1024px cap left ~300px of empty space on each
            side of the table. Now content fills the available width
            from the sidebar edge; tabs that prefer a narrower form
            (User Management, Settings) apply their own max-width
            internally. */}
        <div className="pb-12">
          {/* Apr-2026 B-UX-2: App-wide Dhan health alert. Silent when healthy.
              Session 142 — defensive render: guard against the occasional HMR
              stale-reference where `DhanHealthBanner` evaluates to undefined
              mid-session. That bug surfaced as a full-screen ErrorBoundary
              (`ReferenceError: DhanHealthBanner is not defined`) on every
              Admin click until the user closed the browser. Wrapping in a
              truthiness check keeps the admin UI usable even if the banner
              module fails to load for any reason. */}
          {typeof DhanHealthBanner === 'function' && (
            <DhanHealthBanner onNavigateToBroker={() => setActiveTab('broker')} />
          )}
          {activeTab === 'analytics' && <AnalyticsTab />}
          {activeTab === 'user-management' && <UserManagementTab />}
          {activeTab === 'broker' && <BrokerIntegrationTab />}
          {activeTab === 'confluence' && <ConfluenceDashboard />}
          {activeTab === 'alphascout' && <AlphaScoutDashboard />}
          {activeTab === 'alphascout-hunter' && <AlphaScoutDashboard profile="midsmall" />}
          {activeTab === 'alphascout-valuehunter' && <AlphaScoutDashboard profile="valuehunter" />}
          {activeTab === 'status' && <SystemStatusTab />}
          {activeTab === 'time-machine' && <TimeMachine />}
          {activeTab === 'data-health' && <DataHealthTab />}
          {activeTab === 'greeks-verify' && <GreeksVerificationTab />}
          {activeTab === 'paper-trades' && <PaperTradeTable />}
          {activeTab === 'settlement-logs' && <SettlementLogsTab />}
          {activeTab === 'smlt-watchlist' && <SMTLWatchlistTab />}
          {activeTab === 'indices' && <IndexMasterTab />}
          {activeTab === 'holidays' && <HolidaysTab />}
          {activeTab === 'strategy' && <StrategyTunerTab />}
          {activeTab === 'signal-config' && <SignalConfigTab />}
          {activeTab === 'config-history' && <ConfigHistoryTab />}
          {activeTab === 'special-days' && <SpecialDaysTab />}
          {activeTab === 'api-reference' && <APIReferenceTab />}
          {activeTab === 'settings' && <SettingsTab />}
          {activeTab === 'signal-intel' && <SignalIntelligenceTab />}
          {activeTab === 'algo-health' && <AlgoHealthTab />}
          {activeTab === 'safeguards' && <SafeguardsTab />}
          {activeTab === 'gui-shadow' && <GUIHierarchyShadowTab />}
          {activeTab === 'dhan-swing' && <DhanSwingTab />}
          {activeTab === 'smartflow' && <SmartFlowTab />}
          {activeTab === 'combined-model' && <ConfluenceTab />}
          {activeTab === 'dhan-tokens' && <DhanTokensTab />}
          {activeTab === 'oi-flow' && <OIFlowTab />}
          {activeTab === 'lodr-orders' && <LODROrdersTab />}
          {activeTab === 'lodr-leaderboard' && <OrderBookLeaderboardTab />}
          {activeTab === 'pre-lodr' && <PreLODRTab />}
          {activeTab === 'model-portfolio' && <ModelPortfolioTab />}
          {activeTab === 'smart-money-portfolio' && <SmartMoneyPortfolioTab />}
          {activeTab === 'midcap-alpha-portfolio' && <SmartMoneyPortfolioTab engine="midcap-alpha" />}
          {activeTab === 'swing-trading' && <SwingTradingTab />}
          {activeTab === 'smart-money-signals' && <SmartMoneySignalsTab />}
          {activeTab === 'help' && <HelpManual />}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
